import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { ManagementComponent } from '../management.component';
import { CookieService } from 'ngx-cookie-service';
import { SharedService } from 'src/app/shared.service';
import { ManagementServiceService } from 'src/app/management-service.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { debounceTime, finalize, distinctUntilChanged } from 'rxjs/operators';
import { B2BService } from 'src/app/b2-b.service';
import { HostListener } from '@angular/core'; // make sure this is imported
import {
  ColDef,
  GridApi,
  ColumnApi,
  PasteStartEvent,
  PasteEndEvent
} from 'ag-grid-community';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { SignalRService, BulkSearchProgressUpdate, BulkSearchCompleteMessage } from 'src/app/services/signalr.service';
import { initializeCartLocal } from 'src/app/utils/cart-initializer.util';

@Component({
  selector: 'app-order-confirmation-new',
  templateUrl: './order-confirmation-new.component.html',
  styleUrls: ['./order-confirmation-new.component.css']
})
export class OrderConfirmationNewComponent implements OnInit, OnDestroy {

  // ✅ Admin-specific localStorage keys (separate from B2B client grid)
  private readonly ADMIN_CART_KEY = 'AdminQuotationCart';
  private readonly ADMIN_GRID_ID_KEY = 'AdminQuotationGridID';
  private readonly ADMIN_TRACKING_KEY = 'AdminQuotationTracking';
  private readonly ADMIN_SELECTED_PARTNER_KEY = 'AdminQuotationSelectedPartner';
  private readonly ADMIN_ORIGINAL_CART_KEY = 'AdminQuotationOriginalCart'; // ✅ Mirror list of original CartItems
  private readonly ADMIN_CLIENT_RESTRICTIONS_KEY = 'AdminQuotationClientRestrictions';
  private readonly ADMIN_STOCK_STATUS_KEY = 'AdminQuotationStockStatus';
  private readonly ADMIN_PARTNER_CLEARED_KEY = 'AdminQuotationPartnerCleared'; // ✅ Flag to distinguish intentional clear from uninitialized state
  private partnerClearedTimers: any[] = []; // ✅ Track delayed re-clear timers so they can be cancelled
  // ✅ ConfigStatus is NOT saved to localStorage - it's determined by grid mode (Admin: ID=-1, Employee: ID=1)

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.gridApi) {
      this.gridApi.resetRowHeights(); // Optional if height changes
      this.gridApi.sizeColumnsToFit(); // Resize columns to fit available space
      // ✅ Sync column widths for ExcelExportControlRow after resize
      setTimeout(() => this.syncColumnWidths(), 100);
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      this.SaveAllSelectedCartItems();
    }
    if (event.ctrlKey && event.key === 'd') {
      event.preventDefault();
      event.stopPropagation();
      // Use the same logic as Delete/Backspace key in suppressKeyboardEvent
      const cellRanges = this.gridApi?.getCellRanges();
      if (cellRanges && cellRanges.length > 0) {
        this.deleteSelectedRows(cellRanges);
      }
    }
  }

  SaveAllSelectedCartItems(): void {
    // Get all rows where IsSelected AND SaveToDB are both true
    const itemsToSave: { cartItem: any; rowIndex: number }[] = [];

    this.gridApi?.forEachNode((node: any, index: number) => {
      const cartItem = node.data?.CartItem;
      if (cartItem?.IsSelected === true && cartItem?.CartItemStatus?.SaveToDB === true) {
        itemsToSave.push({ cartItem, rowIndex: node.rowIndex });
      }
    });

    if (itemsToSave.length === 0) {
      this.parent.openSnackBar('No items selected with SaveToDB enabled', 'OK', 'alert-info-snackbar');
      return;
    }

    console.log(`💾 Saving ${itemsToSave.length} items via Ctrl+S using BULK endpoint`);

    // ✅ Use BULK endpoint to save all items at once
    this.BulkSaveAdminQuotationProducts(itemsToSave);
  }

  DeleteAllSelectedCartItems(): void {
    // Get all rows where IsSelected is true
    const rowsToDelete: any[] = [];

    this.gridApi?.forEachNode((node: any) => {
      const cartItem = node.data?.CartItem;
      if (cartItem?.IsSelected === true) {
        rowsToDelete.push(node.data);
      }
    });

    if (rowsToDelete.length === 0) {
      this.parent.openSnackBar('No items selected', 'OK', 'alert-info-snackbar');
      return;
    }

    console.log(`🗑️ Deleting ${rowsToDelete.length} items via Ctrl+D`);

    // ✅ Remove rows from grid completely
    this.gridApi.applyTransaction({ remove: rowsToDelete });

    // ✅ Re-index the remaining rows for correct # display
    const allData: any[] = [];
    this.gridApi.forEachNode((node, idx) => {
      // Reset GridIndex and clear any stale data references
      node.data.GridIndex = idx;
      node.data.index = idx + 1;
      allData.push(node.data);
    });
    this.gridApi.setRowData(allData);

    // ✅ Refresh grid cells to ensure UI is clean
    this.gridApi.refreshCells({ force: true });

    // ✅ Now persist the updated cart
    this.UpdateCart();

    // Reset header checkbox
    this.headerCheckboxSelected = false;
    this.updateHeaderCheckbox(this.gridApi);

    this.parent.openSnackBar(`Deleted ${rowsToDelete.length} rows`, 'OK', 'light-info-snackbar');
  }

  // SignalR subscriptions for cleanup
  private signalRSubscriptions: Subscription[] = [];
  private signalRConnectionId: string | null = null;
  private signalRProcessedRows: Set<number> = new Set(); // ✅ Track rows already counted via SignalR

  constructor(public parent: ManagementComponent, private managementService:ManagementServiceService, private B2BService:B2BService, private service:SharedService, private cookieService: CookieService, public translate: TranslateService, private router:Router, private _bottomSheet: MatBottomSheet,  private route: ActivatedRoute, private signalRService: SignalRService, private ngZone: NgZone) {
    translate.addLangs(['en', 'ro', 'de', 'es', 'fr', 'it', 'pl', 'pt']);
    translate.setDefaultLang('en');

    const browserLang = this.parent.getLanguage();
    translate.use(browserLang.match(/en|ro|de|es|fr|it|pl|pt/) ? browserLang : 'en');

    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };

    // Debounce search.
    this.brandSearchTermUpdate.pipe(
      debounceTime(500),
      distinctUntilChanged())
      .subscribe(value => {
        this.BrandFilter.SearchTerm = value;

        if(this.BrandFilter.SearchTerm?.length > 2){
          this.GetBrandsFilteredWithSuggestion(value, 1, null);
        }
      });

    // Debounce partner search
    this.partnerSearchTermUpdate.pipe(
      debounceTime(300),
      distinctUntilChanged())
      .subscribe(value => {
        this.partnerPageIndex = 1;
        this.GetPartnersFiltered();
      });

  }

  searchTermUpdate = new Subject<string>();

  // Partner Search Properties
  partnerSearchTerm: string = '';
  partnerSearchTermUpdate = new Subject<string>();
  searchedPartners: any[] = [];
  loadingPartners: boolean = false;
  showPartnerResults: boolean = false;
  partnerPageIndex: number = 1;
  partnerPageSize: number = 20;
  partnerTotalPages: number = 1;

  // Currency selection properties
  currencies: any[] = [];
  filteredCurrencies: any[] = [];
  selectedCurrency: any = null;

  // Partner Contractual Conditions
  partnerContractualConditions: any = null;
  currencySearchTerm: string = '';
  showCurrencyResults: boolean = false;

  // ✅ Currency column dropdown properties
  showPurchaseCurrencyDropdown: boolean = false;
  purchaseCurrencyDropdownPosition: { top: string; left: string; width: string } = { top: '0px', left: '0px', width: '60px' };
  purchaseCurrencyRowIndex: number = -1;

  // ✅ Selected agents for offer request (multiple selection)
  selectedAgents: any[] = [];

  // ✅ New agent email input (for adding agents on the fly)
  newAgentEmail: string = '';
  isValidEmail: boolean = false;

  // ⚠️ Offer Request Master Data (contains OrderConsolidationTypes) - for OfferRequests
  OfferRequestMasterData: {[k: string]: any} = {};

  // ✅ Offer Master Data (contains OrderConsolidationTypes) - for Offers
  OfferMasterData: {[k: string]: any} = {};

  // ✅ Shipping configuration selections
  selectedShippingCompany: any = null;
  selectedShippingWay: any = null;
  selectedShippingResponsability: any = null;
  selectedPaymentType: any = null;
  selectedPaymentTerms: any = null;
  selectedShippingServiceType: any = null;
  selectedOrderDeliveryPreference: any = { ID: -1 }; // Default ID -1
  selectedOrderConsolidationType: any = { ID: -1 }; // Default ID -1

  // ✅ Shipping configuration dropdown visibility flags
  showShippingCompanyResults: boolean = false;
  showShippingWayResults: boolean = false;
  showShippingResponsabilityResults: boolean = false;
  showPaymentTypeResults: boolean = false;
  showPaymentTermsResults: boolean = false;
  showShippingServiceTypeResults: boolean = false;
  showOrderDeliveryPreferenceResults: boolean = false;
  showOrderConsolidationTypeResults: boolean = false;

  // ✅ Discount Type dropdown state (for Order Confirmation and Offer popups)
  showDiscountTypeDropdownOC: boolean = false;
  showDiscountTypeDropdownOffer: boolean = false;

  // ✅ Shipping configuration focus methods (to open dropdowns)
  onShippingCompanyFocus() {
    console.log('🔍 Shipping Company clicked');
    console.log('🔍 ShippingCompanies:', this.parent.CartMasterData?.ShippingCompanies);
    this.showShippingCompanyResults = true;
  }

  onShippingWayFocus() {
    console.log('🔍 Shipping Way clicked');
    console.log('🔍 ShippingWays:', this.parent.CartMasterData?.ShippingWays);
    this.showShippingWayResults = true;
  }

  onShippingResponsabilityFocus() {
    console.log('🔍 Shipping Responsability clicked');
    console.log('🔍 ShippingResponsabilities:', this.parent.CartMasterData?.ShippingResponsabilities);
    this.showShippingResponsabilityResults = true;
  }

  onPaymentTypeFocus() {
    console.log('🔍 Payment Type clicked');
    console.log('🔍 CartPayments:', this.parent.CartMasterData?.CartPayments);
    this.showPaymentTypeResults = true;
  }

  onPaymentTermsFocus() {
    console.log('🔍 Payment Terms clicked');
    console.log('🔍 PaymentTerms:', this.parent.CartMasterData?.PaymentTerms);
    this.showPaymentTermsResults = true;
  }

  onShippingServiceTypeFocus() {
    console.log('🔍 Service Type clicked');
    console.log('🔍 ShippingServiceTypes:', this.parent.CartMasterData?.ShippingServiceTypes);
    this.showShippingServiceTypeResults = true;
  }

  onOrderDeliveryPreferenceFocus() {
    console.log('🔍 Order Delivery Preference clicked');
    console.log('🔍 OrderDeliveryPreferences:', this.OfferMasterData?.OrderDeliveryPreferences);
    this.showOrderDeliveryPreferenceResults = true;
  }

  onOrderConsolidationTypeFocus() {
    console.log('🔍 Order Consolidation Type clicked');
    console.log('🔍 OrderConsolidationTypes:', this.OfferMasterData?.OrderConsolidationTypes);
    this.showOrderConsolidationTypeResults = true;
  }

  // ✅ Shipping configuration selection methods
  selectShippingCompany(company: any) {
    console.log('✅ Selected Shipping Company:', company);
    this.selectedShippingCompany = company;
    this.showShippingCompanyResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.ShippingCompany = company;
      console.log('✅ Cart.ShippingCompany updated:', this.parent.Cart.ShippingCompany);
    }
  }

  selectShippingWay(way: any) {
    console.log('✅ Selected Shipping Way:', way);
    this.selectedShippingWay = way;
    this.showShippingWayResults = false;
  }

  selectWeightMeasurementType(wmt: any) {
    console.log('✅ Selected Weight Measurement Type:', wmt);
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.WeightMeasurementType = wmt;
      console.log('✅ Cart.WeightMeasurementType updated:', this.parent.Cart.WeightMeasurementType);
    }
    // ✅ Update Offer object (if exists)
    if (this.parent.Offer) {
      this.parent.Offer.WeightMeasurementType = wmt;
    }
  }

  // ✅ Validate weight input and set ForcedUIWeight flag
  validateWeightInput() {
    if (this.parent.Cart) {
      const weight = this.parent.Cart.Weight;

      // If empty, null, undefined, or "0", set to "0" and flag to false
      if (!weight || weight === '' || weight === '0' || parseFloat(weight) === 0) {
        this.parent.Cart.Weight = '0';
        this.parent.Cart.ForcedUIWeight = false;
        console.log('⚠️ Weight is empty or 0, set ForcedUIWeight flag to false');
      } else {
        this.parent.Cart.ForcedUIWeight = true;
        console.log('✅ Weight is valid:', weight, 'ForcedUIWeight flag set to true');
      }
    }
  }

  selectShippingResponsability(resp: any) {
    console.log('✅ Selected Shipping Responsability:', resp);
    this.selectedShippingResponsability = resp;
    this.showShippingResponsabilityResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.ShippingResponsability = resp;
      console.log('✅ Cart.ShippingResponsability updated:', this.parent.Cart.ShippingResponsability);

      // ✅ If RequiresInsurance is false or null, set both Insurance flags to false
      if (!resp?.RequiresInsurance) {
        this.parent.Cart.HasInsurance = false;
        if (this.parent.Cart.ShippingResponsability) {
          this.parent.Cart.ShippingResponsability.Insurance = false;
        }
        console.log('✅ Cart.HasInsurance and ShippingResponsability.Insurance set to false (RequiresInsurance is false/null)');
      }
    }
  }

  selectPaymentType(payment: any) {
    console.log('✅ Selected Payment Type:', payment);
    this.selectedPaymentType = payment;
    this.showPaymentTypeResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.PaymentType = payment;
      console.log('✅ Cart.PaymentType updated:', this.parent.Cart.PaymentType);
    }
  }

  selectPaymentTerms(terms: any) {
    console.log('✅ Selected Payment Terms:', terms);
    this.selectedPaymentTerms = terms;
    this.showPaymentTermsResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.PaymentTerm = terms;
      console.log('✅ Cart.PaymentTerm updated:', this.parent.Cart.PaymentTerm);
    }
  }

  selectShippingServiceType(service: any) {
    console.log('✅ Selected Service Type:', service);
    this.selectedShippingServiceType = service;
    this.showShippingServiceTypeResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.ShippingServiceType = service;
      console.log('✅ Cart.ShippingServiceType updated:', this.parent.Cart.ShippingServiceType);
    }
  }

  selectOrderDeliveryPreference(pref: any) {
    console.log('✅ Selected Order Delivery Preference:', pref);
    this.selectedOrderDeliveryPreference = pref;
    this.showOrderDeliveryPreferenceResults = false;
    // ✅ Update Cart object
    if (this.parent.Cart) {
      this.parent.Cart.DeliveryPreference = pref;
      console.log('✅ Cart.DeliveryPreference updated:', this.parent.Cart.DeliveryPreference);
    }
  }

  selectOrderConsolidationType(type: any) {
    console.log('✅ Selected Order Consolidation Type:', type);
    this.selectedOrderConsolidationType = type;
    this.showOrderConsolidationTypeResults = false;
    // ✅ Update Offer/OfferRequest object
    if (this.parent.Offer) {
      this.parent.Offer.ConsolidationType = type;
      console.log('✅ Offer.ConsolidationType updated:', this.parent.Offer.ConsolidationType);
    }
    if (this.parent.OfferRequest) {
      this.parent.OfferRequest.ConsolidationType = type;
      console.log('✅ OfferRequest.ConsolidationType updated:', this.parent.OfferRequest.ConsolidationType);
    }
  }

  // ✅ Clear/Deselect methods for each control
  clearShippingCompany(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Shipping Company');
    this.selectedShippingCompany = null;
    if (this.parent.Cart) {
      this.parent.Cart.ShippingCompany = null;
      console.log('✅ Cart.ShippingCompany set to null');
    }
    this.showShippingCompanyResults = false;
  }

  clearShippingResponsability(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Shipping Responsability');
    this.selectedShippingResponsability = null;
    if (this.parent.Cart) {
      this.parent.Cart.ShippingResponsability = null;
      console.log('✅ Cart.ShippingResponsability set to null');
    }
    this.showShippingResponsabilityResults = false;
  }

  clearPaymentType(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Payment Type');
    this.selectedPaymentType = null;
    if (this.parent.Cart) {
      this.parent.Cart.PaymentType = null;
      console.log('✅ Cart.PaymentType set to null');
    }
    this.showPaymentTypeResults = false;
  }

  clearPaymentTerms(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Payment Terms');
    this.selectedPaymentTerms = null;
    if (this.parent.Cart) {
      this.parent.Cart.PaymentTerm = null;
      console.log('✅ Cart.PaymentTerm set to null');
    }
    this.showPaymentTermsResults = false;
  }

  clearShippingServiceType(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Shipping Service Type');
    this.selectedShippingServiceType = null;
    if (this.parent.Cart) {
      this.parent.Cart.ShippingServiceType = null;
      console.log('✅ Cart.ShippingServiceType set to null');
    }
    this.showShippingServiceTypeResults = false;
  }

  clearOrderDeliveryPreference(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Order Delivery Preference');
    this.selectedOrderDeliveryPreference = { ID: -1 }; // Reset to default
    if (this.parent.Cart) {
      this.parent.Cart.DeliveryPreference = { ID: -1 };
      console.log('✅ Cart.DeliveryPreference set to default');
    }
    this.showOrderDeliveryPreferenceResults = false;
  }

  clearOrderConsolidationType(event: any): void {
    event.stopPropagation(); // ✅ Prevent dropdown from opening
    console.log('❌ Clearing Order Consolidation Type');
    this.selectedOrderConsolidationType = { ID: -1 }; // Reset to default
    if (this.parent.Offer) {
      this.parent.Offer.ConsolidationType = { ID: -1 };
      console.log('✅ Offer.ConsolidationType set to default');
    }
    if (this.parent.OfferRequest) {
      this.parent.OfferRequest.ConsolidationType = { ID: -1 };
      console.log('✅ OfferRequest.ConsolidationType set to default');
    }
    this.showOrderConsolidationTypeResults = false;
  }

  // ✅ Check if any shipping dropdown is open
  isAnyShippingDropdownOpen(): boolean {
    return this.showShippingCompanyResults ||
           this.showShippingWayResults ||
           this.showShippingResponsabilityResults ||
           this.showPaymentTypeResults ||
           this.showPaymentTermsResults ||
           this.showShippingServiceTypeResults ||
           this.showOrderDeliveryPreferenceResults ||
           this.showOrderConsolidationTypeResults;
  }

  // ✅ Close all shipping dropdowns
  closeAllShippingDropdowns() {
    this.showShippingCompanyResults = false;
    this.showShippingWayResults = false;
    this.showShippingResponsabilityResults = false;
    this.showPaymentTypeResults = false;
    this.showPaymentTermsResults = false;
    this.showShippingServiceTypeResults = false;
    this.showOrderDeliveryPreferenceResults = false;
    this.showOrderConsolidationTypeResults = false;
  }

  // ✅ Discount methods for Order Confirmation and Offer popups
  selectDiscountType(type: any, popup: 'OC' | 'Offer'): void {
    if (!this.parent.Cart) this.parent.Cart = {};
    if (!this.parent.Cart.Total) this.parent.Cart.Total = {};
    if (!this.parent.Cart.Total.Discount) {
      this.parent.Cart.Total.Discount = { Type: null, Value: null };
    }
    this.parent.Cart.Total.Discount.Type = type;

    if (popup === 'OC') {
      this.showDiscountTypeDropdownOC = false;
    } else {
      this.showDiscountTypeDropdownOffer = false;
    }
  }

  validateDiscountInput(event: KeyboardEvent): boolean {
    const charCode = event.key;
    // Allow: numbers (0-9), comma (,), and dot (.)
    if (/^[0-9,.]$/.test(charCode)) {
      return true;
    }
    event.preventDefault();
    return false;
  }

  // ✅ Apply discount for Offer popup
  ApplyOfferDiscount(): void {
    if (!this.parent.Cart?.Total?.Discount?.Type || !this.parent.Cart?.Total?.Discount?.Value) {
      return;
    }

    // Build Offer object from Cart
    const offer = {
      Cart: JSON.parse(JSON.stringify(this.parent.Cart)),
      Partner: this.parent.B2BPartner,
      Type: this.parent.CartMasterData?.OrderTypes?.find((x: any) => x.ID === 4) // Offer type
    };

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Offer: offer
    };

    this.parent.loading = true;

    this.service.ApplyOfferDiscount(request).subscribe((response: any) => {
      if (response?.Status !== 1) {
        this.parent.openSnackBar(response?.Message || 'Error applying discount', 'OK', 'alert-info-snackbar');
      } else {
        // Update Cart with the returned Offer.Cart (which has updated Total)
        this.parent.Cart = response.Offer.Cart;
        this.parent.openSnackBar(response?.Message || 'Discount applied successfully', 'OK', 'light-info-snackbar');
      }
      this.parent.loading = false;
    });
  }

  // ✅ Clear discount for Offer popup
  ClearOfferDiscount(): void {
    // Create a copy with null Cart.Total.Discount to clear it
    const cartCopy = JSON.parse(JSON.stringify(this.parent.Cart));
    if (cartCopy?.Total) {
      cartCopy.Total.Discount = null;
    }

    const offer = {
      Cart: cartCopy,
      Partner: this.parent.B2BPartner,
      Type: this.parent.CartMasterData?.OrderTypes?.find((x: any) => x.ID === 4)
    };

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Offer: offer
    };

    this.parent.loading = true;

    this.service.ApplyOfferDiscount(request).subscribe((response: any) => {
      if (response?.Status !== 1) {
        this.parent.openSnackBar(response?.Message || 'Error clearing discount', 'OK', 'alert-info-snackbar');
      } else {
        // Update Cart with the returned Offer.Cart (which has updated Total without discount)
        this.parent.Cart = response.Offer.Cart;
        // Reset local discount fields
        if (this.parent.Cart?.Total) {
          this.parent.Cart.Total.Discount = { Type: null, Value: null };
        }
        this.parent.openSnackBar('Discount cleared successfully', 'OK', 'light-info-snackbar');
      }
      this.parent.loading = false;
    });
  }

  // ✅ Apply discount for Order Confirmation popup
  ApplyOrderConfirmationDiscount(): void {
    if (!this.parent.Cart?.Total?.Discount?.Type || !this.parent.Cart?.Total?.Discount?.Value) {
      return;
    }

    // Build OrderConfirmation object from Cart
    const orderConfirmation = {
      Cart: JSON.parse(JSON.stringify(this.parent.Cart)),
      Partner: this.parent.B2BPartner,
      Type: this.parent.CartMasterData?.OrderTypes?.find((x: any) => x.ID === 1) // Order Confirmation type
    };

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      OrderConfirmation: orderConfirmation
    };

    this.parent.loading = true;

    this.service.ApplyOrderConfirmationDiscount(request).subscribe((response: any) => {
      if (response?.Status !== 1) {
        this.parent.openSnackBar(response?.Message || 'Error applying discount', 'OK', 'alert-info-snackbar');
      } else {
        // Update Cart with the returned OrderConfirmation.Cart (which has updated Total)
        this.parent.Cart = response.OrderConfirmation.Cart;
        this.parent.openSnackBar(response?.Message || 'Discount applied successfully', 'OK', 'light-info-snackbar');
      }
      this.parent.loading = false;
    });
  }

  // ✅ Clear discount for Order Confirmation popup
  ClearOrderConfirmationDiscount(): void {
    // Create a copy with null Cart.Total.Discount to clear it
    const cartCopy = JSON.parse(JSON.stringify(this.parent.Cart));
    if (cartCopy?.Total) {
      cartCopy.Total.Discount = null;
    }

    const orderConfirmation = {
      Cart: cartCopy,
      Partner: this.parent.B2BPartner,
      Type: this.parent.CartMasterData?.OrderTypes?.find((x: any) => x.ID === 1)
    };

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      OrderConfirmation: orderConfirmation
    };

    this.parent.loading = true;

    this.service.ApplyOrderConfirmationDiscount(request).subscribe((response: any) => {
      if (response?.Status !== 1) {
        this.parent.openSnackBar(response?.Message || 'Error clearing discount', 'OK', 'alert-info-snackbar');
      } else {
        // Update Cart with the returned OrderConfirmation.Cart (which has updated Total without discount)
        this.parent.Cart = response.OrderConfirmation.Cart;
        // Reset local discount fields
        if (this.parent.Cart?.Total) {
          this.parent.Cart.Total.Discount = { Type: null, Value: null };
        }
        this.parent.openSnackBar('Discount cleared successfully', 'OK', 'light-info-snackbar');
      }
      this.parent.loading = false;
    });
  }

  // ✅ Get project discount amount (OriginalTotal - Total after discount)
  getProjectDiscountAmount(): string {
    const originalTotal = this.parent.Cart?.OriginalTotal?.TotalQtyPriceNOVAT || 0;
    const discountedTotal = this.parent.Cart?.Total?.TotalQtyPriceNOVAT || 0;
    const discountAmount = originalTotal - discountedTotal;
    return discountAmount.toFixed(2);
  }

  // ✅ Get absolute total (Subtotal after discount + Shipping Cost)
  getAbsoluteTotal(): string {
    const subtotalAfterDiscount = this.parent.Cart?.Total?.TotalQtyPriceNOVAT || 0;
    const shippingCost = this.parent.Cart?.ShippingCost?.UIUnitPrice || 0;
    const absoluteTotal = subtotalAfterDiscount + shippingCost;
    return absoluteTotal.toFixed(2);
  }

  // ✅ Excel export column control flags
  // ListPrice & Discount default false, rest default true (to match DB defaults)
  includeListPriceInExport: boolean = false;
  includeDiscountInExport: boolean = false;
  includeETAInExport: boolean = true; // Maps to StandardDelivery in PDFColumns
  includeSupplierInExport: boolean = true;
  includeHSCodeInExport: boolean = true;
  includeCOOInExport: boolean = true;
  includeWeightInExport: boolean = true;

  // ✅ Build PDFColumns object from checkbox states for saving to DB
  getPDFColumns(): any {
    return {
      ID: 0,
      OrderDBID: 0, // Will be set by backend after order is created
      ListPrice: this.includeListPriceInExport,
      Discount: this.includeDiscountInExport,
      StandardDelivery: this.includeETAInExport, // ETA checkbox maps to StandardDelivery
      HSCode: this.includeHSCodeInExport,
      COO: this.includeCOOInExport,
      Weight: this.includeWeightInExport
    };
  }

  // ✅ Toggle all export columns
  toggleAllExportColumns(checked: boolean) {
    this.includeListPriceInExport = checked;
    this.includeDiscountInExport = checked;
    this.includeETAInExport = checked;
    this.includeSupplierInExport = checked;
    this.includeHSCodeInExport = checked;
    this.includeCOOInExport = checked;
    this.includeWeightInExport = checked;
    // ✅ Sync Cart.UIGridDisablingProperties for export
    this.syncCartDisabledState();
  }

  /**
   * ✅ Sync Cart.UIGridDisablingProperties based on export column checkbox states
   * Uses the CartItemUIGridDisablingProperties model from Cart.cs
   * When a checkbox is unchecked, the corresponding property = true (disabled)
   * When a checkbox is checked, the corresponding property = false (enabled)
   * This is set at the Cart level, not per-item, for PDF export
   */
  syncCartDisabledState(): void {
    // ✅ Set UIGridDisablingProperties at Cart level (not per-item)
    if (this.parent.Cart) {
      this.parent.Cart.UIGridDisablingProperties = {
        ListPrice: !this.includeListPriceInExport,
        Discount: !this.includeDiscountInExport,
        ETA: !this.includeETAInExport,
        HSCode: !this.includeHSCodeInExport,
        COO: !this.includeCOOInExport,
        Weight: !this.includeWeightInExport
      };
    }

    console.log('✅ Cart.UIGridDisablingProperties synced:', {
      ListPrice: !this.includeListPriceInExport,
      Discount: !this.includeDiscountInExport,
      ETA: !this.includeETAInExport,
      HSCode: !this.includeHSCodeInExport,
      COO: !this.includeCOOInExport,
      Weight: !this.includeWeightInExport
    });
  }

  /**
   * ✅ Handle individual export column checkbox change
   * Updates the flag and syncs CartItem.Disabled state
   */
  onExportColumnChange(columnName: string, checked: boolean): void {
    switch (columnName) {
      case 'ListPrice':
        this.includeListPriceInExport = checked;
        break;
      case 'Discount':
        this.includeDiscountInExport = checked;
        break;
      case 'ETA':
        this.includeETAInExport = checked;
        break;
      case 'HSCode':
        this.includeHSCodeInExport = checked;
        break;
      case 'COO':
        this.includeCOOInExport = checked;
        break;
      case 'Weight':
        this.includeWeightInExport = checked;
        break;
    }
    this.syncCartDisabledState();
  }

  // ✅ Check if all export columns are selected
  get allExportColumnsSelected(): boolean {
    return this.includeListPriceInExport &&
           this.includeDiscountInExport &&
           this.includeETAInExport &&
           this.includeSupplierInExport &&
           this.includeHSCodeInExport &&
           this.includeCOOInExport &&
           this.includeWeightInExport;
  }

  // ✅ Helper method to check if agent is selected
  isAgentSelected(agent: any): boolean {
    return this.selectedAgents?.some(a => a.ID === agent.ID) || false;
  }

  // ✅ Store country shipping options response
  CountryShippingOptionsResponse: any = null;

  // ✅ Get last used payment/shipment settings for partner
  // ✅ Get Partner Contractual Conditions and set payment term
  GetPartnerContractualConditions(partnerID: string, callback?: () => void) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: partnerID
    };

    this.service.GetPartnerContractualConditions(request).subscribe(response => {
      console.log('🔍 GetPartnerContractualConditions response:', response);

      if (response["Status"] === 1) {
        this.partnerContractualConditions = response["ContractualConditions"];
        console.log('✅ Partner Contractual Conditions loaded:', this.partnerContractualConditions);

        // ✅ Auto-select all contractual conditions fields

        // 1. Payment Terms (already implemented)
        if (this.partnerContractualConditions?.SellPaymentTerms) {
          const sellPaymentTerm = this.partnerContractualConditions.SellPaymentTerms;
          console.log('🔍 SellPaymentTerms from conditions:', sellPaymentTerm);

          const matchingPaymentTerm = this.parent.CartMasterData?.PaymentTerms?.find(
            (pt: any) => pt.ID === sellPaymentTerm.ID
          );

          if (matchingPaymentTerm) {
            this.selectedPaymentTerms = matchingPaymentTerm;
            this.parent.Cart.PaymentTerm = matchingPaymentTerm;
            console.log('✅ Payment term set from contractual conditions:', matchingPaymentTerm.Name);
          } else {
            this.selectedPaymentTerms = sellPaymentTerm;
            this.parent.Cart.PaymentTerm = sellPaymentTerm;
            console.log('✅ Payment term set directly from contractual conditions:', sellPaymentTerm.Name);
          }
        }

        // 2. Shipping Company
        if (this.partnerContractualConditions?.ShippingCompany) {
          const shippingCompany = this.partnerContractualConditions.ShippingCompany;
          const matchingCompany = this.parent.CartMasterData?.ShippingCompanies?.find(
            (c: any) => c.ID === shippingCompany.ID
          );
          if (matchingCompany) {
            this.selectedShippingCompany = matchingCompany;
            this.parent.Cart.ShippingCompany = matchingCompany;
            console.log('✅ Shipping Company set from contractual conditions:', matchingCompany.Name);
          }
        }

        // 3. Shipping Responsability
        if (this.partnerContractualConditions?.ShippingResponsability) {
          const shippingResp = this.partnerContractualConditions.ShippingResponsability;
          const matchingResp = this.parent.CartMasterData?.ShippingResponsabilities?.find(
            (r: any) => r.ID === shippingResp.ID
          );
          if (matchingResp) {
            this.selectedShippingResponsability = matchingResp;
            this.parent.Cart.ShippingResponsability = matchingResp;
            console.log('✅ Shipping Responsability set from contractual conditions:', matchingResp.Name);
          }
        }

        // 4. Payment Type
        if (this.partnerContractualConditions?.PaymentType) {
          const paymentType = this.partnerContractualConditions.PaymentType;
          const matchingType = this.parent.CartMasterData?.PaymentTypes?.find(
            (t: any) => t.ID === paymentType.ID
          );
          if (matchingType) {
            this.selectedPaymentType = matchingType;
            this.parent.Cart.PaymentType = matchingType;
            console.log('✅ Payment Type set from contractual conditions:', matchingType.Name);
          }
        }

        // 5. Shipping Service Type
        if (this.partnerContractualConditions?.ShippingServiceType) {
          const serviceType = this.partnerContractualConditions.ShippingServiceType;
          const matchingService = this.parent.CartMasterData?.ShippingServiceTypes?.find(
            (s: any) => s.ID === serviceType.ID
          );
          if (matchingService) {
            this.selectedShippingServiceType = matchingService;
            this.parent.Cart.ShippingServiceType = matchingService;
            console.log('✅ Shipping Service Type set from contractual conditions:', matchingService.Name);
          }
        }

        // 6. Delivery Preference
        if (this.partnerContractualConditions?.DeliveryPreference) {
          const deliveryPref = this.partnerContractualConditions.DeliveryPreference;
          const matchingPref = this.parent.CartMasterData?.DeliveryPreferences?.find(
            (p: any) => p.ID === deliveryPref.ID
          );
          if (matchingPref) {
            this.parent.Cart.DeliveryPreference = matchingPref;
            console.log('✅ Delivery Preference set from contractual conditions:', matchingPref.Name);
          }
        }

        // 7. Consolidation Type
        if (this.partnerContractualConditions?.ConsolidationType) {
          const consolidationType = this.partnerContractualConditions.ConsolidationType;
          const matchingConsolidation = this.parent.CartMasterData?.ConsolidationTypes?.find(
            (c: any) => c.ID === consolidationType.ID
          );
          if (matchingConsolidation) {
            this.selectedOrderConsolidationType = matchingConsolidation;
            if (this.parent.Offer) {
              this.parent.Offer.ConsolidationType = matchingConsolidation;
            }
            if (this.parent.OfferRequest) {
              this.parent.OfferRequest.ConsolidationType = matchingConsolidation;
            }
            console.log('✅ Consolidation Type set from contractual conditions:', matchingConsolidation.Name);
          }
        }

      } else {
        console.log('⚠️ No contractual conditions found in response');
      }

      // ✅ Call callback after processing (to chain GetPartnerLastB2BPaymentShipmentSettings)
      if (callback) {
        callback();
      }
    }, error => {
      console.error('❌ Error getting partner contractual conditions:', error);

      // ✅ Still call callback even on error
      if (callback) {
        callback();
      }
    });
  }

  // ✅ Helper method to set default "Advanced Payment" (ID = -1)
  setDefaultAdvancedPayment() {
    if (!this.parent.CartMasterData?.PaymentTerms) {
      console.log('⚠️ CartMasterData.PaymentTerms not loaded yet');
      return;
    }

    console.log('🔍 Available Payment Terms:', this.parent.CartMasterData.PaymentTerms.map((pt: any) => `${pt.Name} (ID: ${pt.ID})`));

    // ✅ Find "Advanced Payment" by ID = -1 (from ePaymentTerms enum)
    const advancedPayment = this.parent.CartMasterData.PaymentTerms.find(
      (pt: any) => pt.ID === -1
    );

    if (advancedPayment) {
      this.selectedPaymentTerms = advancedPayment;
      this.parent.Cart.PaymentTerm = advancedPayment;
      console.log('✅ Default payment term set to Advanced Payment:', advancedPayment.Name,
                  `(ID: ${advancedPayment.ID})`,
                  `(${this.useAdminGrid ? 'Admin Grid - can change' : 'Employee Grid - locked'})`);
    } else {
      console.log('⚠️ Advanced Payment (ID: -1) not found in PaymentTerms list');
    }
  }

  // ✅ Helper method to set default "EXW" Shipping Responsability (ID = 0)
  setDefaultEXWShippingResponsability() {
    if (!this.parent.CartMasterData?.ShippingResponsabilities) {
      console.log('⚠️ CartMasterData.ShippingResponsabilities not loaded yet');
      return;
    }

    console.log('🔍 Available Shipping Responsabilities:', this.parent.CartMasterData.ShippingResponsabilities.map((sr: any) => `${sr.Name} (ID: ${sr.ID})`));

    // ✅ Find "EXW" by ID = 0
    const exwResponsability = this.parent.CartMasterData.ShippingResponsabilities.find(
      (sr: any) => sr.ID === 0
    );

    if (exwResponsability) {
      this.selectedShippingResponsability = exwResponsability;
      this.parent.Cart.ShippingResponsability = exwResponsability;
      console.log('✅ Default shipping responsability set to EXW:', exwResponsability.Name, `(ID: ${exwResponsability.ID})`);
    } else {
      console.log('⚠️ EXW (ID: 0) not found in ShippingResponsabilities list');
    }
  }

  GetPartnerLastB2BPaymentShipmentSettings(partnerID: string) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: partnerID
    };

    this.B2BService.GetPartnerLastB2BPaymentShipmentSettings(request).subscribe(response => {
      console.log('🔍 GetPartnerLastB2BPaymentShipmentSettings response:', response);

      if (response["Status"] === 1) {
        // ✅ Set selected values directly from response objects
        if (response["PaymentType"]) {
          this.selectedPaymentType = response["PaymentType"];
          console.log('✅ Set Payment Type:', this.selectedPaymentType);
        }

        // ✅ Only set PaymentTerm if NOT already set by contractual conditions
        if (response["PaymentTerm"]) {
          if (!this.partnerContractualConditions?.SellPaymentTerms) {
            // No contractual conditions, use last B2B settings
            this.selectedPaymentTerms = response["PaymentTerm"];
            this.parent.Cart.PaymentTerm = response["PaymentTerm"];
            console.log('✅ Set Payment Terms from last B2B settings:', this.selectedPaymentTerms);
          } else {
            // Contractual conditions exist, don't overwrite
            console.log('⏭️ Skipping Payment Terms from last B2B settings - using contractual conditions instead');
          }
        } else {
          // ✅ No last B2B payment term AND no contractual conditions → Set Advanced Payment
          if (!this.partnerContractualConditions?.SellPaymentTerms) {
            console.log('ℹ️ No payment term from last B2B settings and no contractual conditions');
            this.setDefaultAdvancedPayment();
          }
        }

        // ✅ ALWAYS set Shipping Responsability to EXW (ID = 0), ignore response value
        this.setDefaultEXWShippingResponsability();

        if (response["ShippingServiceType"]) {
          this.selectedShippingServiceType = response["ShippingServiceType"];
          console.log('✅ Set Shipping Service Type:', this.selectedShippingServiceType);
        }

        if (response["ShippingCompany"]) {
          this.selectedShippingCompany = response["ShippingCompany"];
          console.log('✅ Set Shipping Company:', this.selectedShippingCompany);
        }

        // ✅ Store these values in the Cart object
        this.UpdateCartWithShippingConfig();

        // ✅ Close all dropdowns after initialization
        this.closeAllShippingDropdowns();
        console.log('✅ ShippingConfigContainer controls initialized from last used settings');
      } else {
        // ✅ Status != 1 (error or no data) → Set defaults
        console.log('⚠️ GetPartnerLastB2BPaymentShipmentSettings returned Status != 1');
        if (!this.partnerContractualConditions?.SellPaymentTerms) {
          console.log('ℹ️ No contractual conditions, setting default Advanced Payment');
          this.setDefaultAdvancedPayment();
        }
        // ✅ ALWAYS set EXW shipping responsability
        this.setDefaultEXWShippingResponsability();
      }
    }, error => {
      // ✅ Error in API call → Set defaults
      console.error('❌ Error in GetPartnerLastB2BPaymentShipmentSettings:', error);
      if (!this.partnerContractualConditions?.SellPaymentTerms) {
        console.log('ℹ️ No contractual conditions, setting default Advanced Payment');
        this.setDefaultAdvancedPayment();
      }
      // ✅ ALWAYS set EXW shipping responsability
      this.setDefaultEXWShippingResponsability();
    });
  }

  // ✅ Update Cart object with current shipping configuration selections
  private UpdateCartWithShippingConfig(): void {
    if (!this.parent.Cart) {
      console.log('❌ Cart not available');
      return;
    }

    // ✅ Store in Cart object
    if (this.selectedPaymentType) {
      this.parent.Cart.PaymentType = this.selectedPaymentType;
    }
    if (this.selectedPaymentTerms) {
      this.parent.Cart.PaymentTerm = this.selectedPaymentTerms;
    }
    if (this.selectedShippingResponsability) {
      this.parent.Cart.ShippingResponsability = this.selectedShippingResponsability;
    }
    if (this.selectedShippingServiceType) {
      this.parent.Cart.ShippingServiceType = this.selectedShippingServiceType;
    }
    if (this.selectedShippingCompany) {
      this.parent.Cart.ShippingCompany = this.selectedShippingCompany;
    }

    console.log('✅ Cart updated with shipping configuration:', this.parent.Cart);
  }

  // ✅ Populate shipping config controls FROM Cart object (when cart is initialized)
  private PopulateShippingConfigFromCart(): void {
    if (!this.parent.Cart) {
      console.log('❌ Cart not available for populating shipping config');
      return;
    }

    // ✅ Populate from Cart properties
    if (this.parent.Cart.PaymentType) {
      this.selectedPaymentType = this.parent.Cart.PaymentType;
      console.log('✅ Populated selectedPaymentType from Cart:', this.selectedPaymentType);
    }

    if (this.parent.Cart.PaymentTerm) {
      this.selectedPaymentTerms = this.parent.Cart.PaymentTerm;
      console.log('✅ Populated selectedPaymentTerms from Cart:', this.selectedPaymentTerms);
    }

    if (this.parent.Cart.ShippingResponsability) {
      this.selectedShippingResponsability = this.parent.Cart.ShippingResponsability;
      console.log('✅ Populated selectedShippingResponsability from Cart:', this.selectedShippingResponsability);
    }

    if (this.parent.Cart.ShippingServiceType) {
      this.selectedShippingServiceType = this.parent.Cart.ShippingServiceType;
      console.log('✅ Populated selectedShippingServiceType from Cart:', this.selectedShippingServiceType);
    }

    if (this.parent.Cart.ShippingCompany) {
      this.selectedShippingCompany = this.parent.Cart.ShippingCompany;
      console.log('✅ Populated selectedShippingCompany from Cart:', this.selectedShippingCompany);
    }

    console.log('✅ Shipping config controls populated from Cart');
  }

  // ✅ Get country shipping options (available shipping companies for a country)
  GetCountryShippingOptions() {
    const currentUIPartner = this.parent.B2BPartner;

    if (!currentUIPartner?.ID || !currentUIPartner?.BillingAddress?.Country?.ID) {
      console.log('❌ Partner or Country not available');
      return;
    }

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Country: currentUIPartner.BillingAddress.Country,
      DirectionID: 1, // 1 = receiver/export
      PartnerID: currentUIPartner.ID
    };

    console.log('🔍 GetCountryShippingOptions request:', request);

    this.managementService.GetCountryShippingOptions(request).subscribe(response => {
      console.log('🔍 GetCountryShippingOptions response:', response);

      if (response["Status"] === 1) {
        // ✅ Store the complete response
        this.CountryShippingOptionsResponse = response;
        console.log('✅ Country Shipping Options retrieved:', this.CountryShippingOptionsResponse);
      } else {
        console.log('❌ Failed to get country shipping options:', response["Message"]);
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
    });
  }

  private pastedLineCount: number = 0;
  private pastedRows: { rowIndex: number; name: string; quantity: any }[] = [];
  private pasteSubject = new Subject<void>();

  private readonly MAX_PASTE_ROWS = 250;

  // ✅ Flag to prevent duplicate backend calls during grid edit
  private isProcessingGridEdit: boolean = false;
  private allowedPastedRowIndices: Set<number> = new Set();

  // ✅ Track rows being updated from backend to prevent re-triggering onCellValueChanged
  private rowsBeingUpdatedFromBackend: Set<number> = new Set();

  private pendingSearchRequests: number = 0;
  private pasteBatchInProgress: boolean = false;

  private pendingPasteRowIndexes: Set<number> = new Set();
  private pasteSyncTimer: any = null;

  // ✅ IndexedDB for large cart storage (AdminQuotationCart & AdminQuotationOriginalCart)
  private readonly IDB_NAME = 'FoxlineAdminQuotation';
  private readonly IDB_STORE = 'cartStore';
  private idbDatabase: IDBDatabase | null = null;

  private async openIndexedDB(): Promise<IDBDatabase> {
    if (this.idbDatabase) return this.idbDatabase;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.IDB_NAME, 1);

      request.onerror = () => {
        console.error('❌ IndexedDB open error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.idbDatabase = request.result;
        resolve(request.result);
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.IDB_STORE)) {
          db.createObjectStore(this.IDB_STORE, { keyPath: 'key' });
        }
      };
    });
  }

  private async idbSet(key: string, value: any): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.IDB_STORE, 'readwrite');
        const store = transaction.objectStore(this.IDB_STORE);
        const request = store.put({ key, value });
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`❌ IndexedDB set error for key: ${key}`, error);
    }
  }

  private async idbGet(key: string): Promise<any> {
    try {
      const db = await this.openIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.IDB_STORE, 'readonly');
        const store = transaction.objectStore(this.IDB_STORE);
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result?.value ?? null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`❌ IndexedDB get error for key: ${key}`, error);
      return null;
    }
  }

  private async idbRemove(key: string): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.IDB_STORE, 'readwrite');
        const store = transaction.objectStore(this.IDB_STORE);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`❌ IndexedDB remove error for key: ${key}`, error);
    }
  }

  // ✅ Wrapper methods for cart storage (fire-and-forget, no await needed in callers)
  private saveCartToIDB(): void {
    // ✅ Debug: Log SaveToDB flags being saved to IndexedDB
    const items = this.parent.Cart?.Items ?? [];
    const itemsWithSaveToDB = items.filter((item: any) => item?.CartItemStatus?.SaveToDB != null);
    console.log(`💾💾💾 saveCartToIDB: Saving ${items.length} items, ${itemsWithSaveToDB.length} have SaveToDB flag`);
    itemsWithSaveToDB.forEach((item: any, idx: number) => {
      console.log(`💾💾💾 saveCartToIDB: Item UIID=${item.UIID} SaveToDB=${item.CartItemStatus.SaveToDB}`);
    });

    this.idbSet(this.ADMIN_CART_KEY, this.parent.Cart).catch(e => console.error('Failed to save cart to IDB:', e));
  }

  private saveOriginalCartToIDB(): void {
    this.idbSet(this.ADMIN_ORIGINAL_CART_KEY, this.OriginalCartItems).catch(e => console.error('Failed to save original cart to IDB:', e));
  }

  private async loadCartFromIDB(): Promise<any> {
    const cart = await this.idbGet(this.ADMIN_CART_KEY);
    // ✅ Debug: Log SaveToDB flags loaded from IndexedDB
    const items = cart?.Items ?? [];
    const itemsWithSaveToDB = items.filter((item: any) => item?.CartItemStatus?.SaveToDB != null);
    console.log(`📥📥📥 loadCartFromIDB: Loaded ${items.length} items, ${itemsWithSaveToDB.length} have SaveToDB flag`);
    itemsWithSaveToDB.forEach((item: any, idx: number) => {
      console.log(`📥📥📥 loadCartFromIDB: Item UIID=${item.UIID} SaveToDB=${item.CartItemStatus.SaveToDB}`);
    });
    return cart;
  }

  private async loadOriginalCartFromIDB(): Promise<any> {
    return await this.idbGet(this.ADMIN_ORIGINAL_CART_KEY);
  }

  private clearCartFromIDB(): void {
    this.idbRemove(this.ADMIN_CART_KEY).catch(e => console.error('Failed to clear cart from IDB:', e));
    this.idbRemove(this.ADMIN_ORIGINAL_CART_KEY).catch(e => console.error('Failed to clear original cart from IDB:', e));
  }

  // ✅ Safe localStorage wrapper for non-cart keys (tracking, grid ID, partner)
  private safeLocalStorageSet(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`❌ localStorage error for key: ${key}`, error);
    }
  }

  namePlaceholder: string = '';
  specialPriceCellTranslation: string = '';
  MOQAdjustmentTooltip: string = '';
  SaveToDBTooltip: string = '';
  NotSaveToDBTooltip: string = '';
  brandSearchTermUpdate = new Subject<string>();
  CurrentCartItemRow:any;
  Language:any;

  AskAddNewRFQQuestion:boolean = false;
  SendEmail:boolean = false; // Default to false (do not send email)
  showSendEmailResults:boolean = false;
  sendEmailOptions = [
    { value: true, label: 'Send Email' },
    { value: false, label: 'Do Not Send Email' }
  ];

  // ✅ Stock Readjusted Popup
  showStockReadjustedPopup: boolean = false;
  stockReadjustedMessage: string = '';
  stockReadjustedProducts: string[] = [];

  // ✅ Partner Details Popup
  ShowPartnerDetailsPopup: boolean = false;
  PartnerDetails: any = null;
  ShowBillingInfo: boolean = true;
  ShowShippingInfo: boolean = false;
  ShowAgentsInfo: boolean = false;
  ShowProfitSettings: boolean = false;
  ShowProcessingSettings: boolean = false;
  ShowB2BRestrictions: boolean = false;
  ShowContractualConditions: boolean = false;
  ShowAcceptedEmailDomains: boolean = false;

  // ✅ Accepted Email Domains
  AcceptedDomains: any[] = [];
  NewAcceptedEmailDomain: string = '';
  DeletingAcceptedDomain: any;
  ShowDeleteAcceptedDomainQuestion: boolean = false;
  InvalidAcceptedDomainFormat: boolean = false;

  // ✅ Partner Details Popup - Dropdown States
  showPartnerBillingCountryDropdown: boolean = false;
  showPartnerBankDropdown: boolean = false;
  showPartnerShippingAddressDropdown: boolean = false;
  showPartnerShippingCountryDropdown: boolean = false;
  showPartnerAgentsDropdown: boolean = false;
  showPartnerAgentShippingDropdown: boolean = false;

  // ✅ Processing Settings Dropdown States
  showProcessingCurrencyDropdown: boolean = false;
  showProcessingDecimalDropdown: boolean = false;
  showProcessingPartnerCompanyDropdown: boolean = false;

  // ✅ Contractual Conditions Dropdown States
  showContractualBuyPaymentTermsDropdown: boolean = false;
  showContractualSellPaymentTermsDropdown: boolean = false;
  showContractualShippingCompanyDropdown: boolean = false;
  showContractualShippingResponsabilityDropdown: boolean = false;
  showContractualPaymentTypeDropdown: boolean = false;
  showContractualShippingServiceTypeDropdown: boolean = false;
  showContractualConsolidationTypeDropdown: boolean = false;
  showContractualDeliveryPreferenceDropdown: boolean = false;

  // ✅ B2B Settings Dropdown States
  showB2BCategoryDropdown: boolean = false;

  // ✅ Partner Details Dropdown Data (loaded from API)
  B2BPartnerCategories: any[] = [];
  Currencies: any[] = [];
  DecimalPrecisions: any[] = [];
  Companies: any[] = [];

  // ✅ B2B Year Settings
  NewYearSettings: {[k: string]: any} = {};
  CurrentYearSetting: any = null;
  CurrentYearSettings: any = null;
  ShowDeleteYearSettingsQuestion: boolean = false;
  ShowYearSettingsBrandChange: boolean = false;
  ShowNewYearSettingsBrandChange: boolean = false;
  YearSettingBrands: any[] = [];
  YearSettingBrandSearchTerm: string = '';

  // ✅ Partner Details Popup - Current selections
  CurrentPartnerShippingAddress: any = null;
  CurrentPartnerAgent: any = null;
  PartnerAgentsList: any[] = [];

  // ✅ Partner Details Popup - Master Data (loaded from parent or cache)
  PartnerCountries: any[] = [];
  PartnerBanks: any[] = [];

  // ✅ Add New Partner Popup
  ShowAddNewPartnerPopup: boolean = false;
  NewPartnerName: string = '';
  NewPartnerVAT: string = '';
  NewPartnerAddressLine: string = '';
  NewPartnerEmail: string = '';  // ✅ Email field for ContactInfo
  NewPartnerSelectedCountry: any = null;
  NewPartnerCountrySearchTerm: string = '';
  NewPartnerFilteredCountries: any[] = [];
  NewPartnerSaving: boolean = false;

  // ✅ Custom Dropdown States
  showDeliveryPreferenceResults:boolean = false;
  showShippingAddressResults:boolean = false;
  showAgentsResults:boolean = false;
  showConsolidationTypeResults:boolean = false;
  showConfigStatusResults:boolean = false;

  // ✅ Section Collapse States (Apple-style collapsible sections)
  sectionCollapsed: { [key: string]: boolean } = {
    contactInfoOC: false,
    termsOC: false,
    shippingOC: false,
    shippingRFQ: false,
    contactInfoRFQ: false,
    contactInfoOffer: false,
    termsOffer: false,
    shippingOffer: false
  };

  toggleSection(sectionKey: string): void {
    this.sectionCollapsed[sectionKey] = !this.sectionCollapsed[sectionKey];
  }

  // ✅ Order Confirmation Popup Dropdown States
  showPaymentTermDropdownOC: boolean = false;
  showPaymentTypeDropdownOC: boolean = false;
  showShippingCompanyDropdownOC: boolean = false;
  showWeightMeasurementTypeDropdownOC: boolean = false;
  showShippingResponsabilityDropdownOC: boolean = false;
  showShippingServiceTypeDropdownOC: boolean = false;

  // ✅ Offer Popup Dropdown States
  showPaymentTermDropdownOffer: boolean = false;
  showPaymentTypeDropdownOffer: boolean = false;
  showShippingCompanyDropdownOffer: boolean = false;
  showWeightMeasurementTypeDropdownOffer: boolean = false;
  showShippingResponsabilityDropdownOffer: boolean = false;
  showShippingServiceTypeDropdownOffer: boolean = false;

  // ✅ Selected Config Status (for admin grid filtering)
  selectedConfigStatus: any = null;
  showStockInclusionPopup: boolean = false;
  pendingConfigStatus: any = null;

  OpenAskAddNewRFQQuestion(){
    // this.OfferRequest.DebatorPO = "";
    this.AskAddNewRFQQuestion = true;
    this.SendEmail = false; // Reset to default (do not send email)
    this.InitializeCart();
    this.InitializeAgentsAndShippingAddress();
    // ✅ Auto-select first shipping address and agent if none selected
    this.AutoSelectFirstShippingAddressAndAgent();
    // ✅ Reset dropdown states
    this.closeAllCustomDropdowns();
  }

  onSendEmailFocus() {
    this.showSendEmailResults = true;
  }

  selectSendEmailOption(option: any) {
    this.SendEmail = option.value;
    this.showSendEmailResults = false;
  }

  getSendEmailLabel(): string {
    const option = this.sendEmailOptions.find(o => o.value === this.SendEmail);
    return option ? option.label : 'Select Option';
  }

  // ✅ Custom Dropdown Methods
  closeAllCustomDropdowns() {
    this.showSendEmailResults = false;
    this.showDeliveryPreferenceResults = false;
    this.showShippingAddressResults = false;
    this.showAgentsResults = false;
    this.showConsolidationTypeResults = false;
    this.showConfigStatusResults = false;
  }

  onDeliveryPreferenceFocus() {
    this.closeAllCustomDropdowns();
    this.showDeliveryPreferenceResults = true;
  }

  selectDeliveryPreference(preference: any) {
    this.parent.Cart.DeliveryPreference = preference;
    this.showDeliveryPreferenceResults = false;
  }

  getDeliveryPreferenceLabel(): string {
    return this.parent.Cart?.DeliveryPreference?.Name || 'Select Delivery Preference';
  }

  onShippingAddressFocus() {
    this.closeAllCustomDropdowns();
    this.showShippingAddressResults = true;
  }

  selectShippingAddress(address: any, isOfferPopup: boolean = false) {
    // ✅ Always update IsSelected on the source B2BPartner.ShippingAddresses (master list)
    if (this.parent.B2BPartner?.ShippingAddresses) {
      this.parent.B2BPartner.ShippingAddresses.forEach((a: any) => {
        a.IsSelected = a.ID === address.ID;
      });
    }

    if (isOfferPopup) {
      this.parent.Offer.ShippingAddress = address;
      // Update IsSelected flags on Offer.Cart.Partner.ShippingAddresses
      if (this.parent.Offer?.Cart?.Partner?.ShippingAddresses) {
        this.parent.Offer.Cart.Partner.ShippingAddresses.forEach((a: any) => {
          a.IsSelected = a.ID === address.ID;
        });
      }
    } else {
      this.parent.OfferRequest.ShippingAddress = address;
      // Update IsSelected flags on OfferRequest.Cart.Partner.ShippingAddresses
      if (this.parent.OfferRequest?.Cart?.Partner?.ShippingAddresses) {
        this.parent.OfferRequest.Cart.Partner.ShippingAddresses.forEach((a: any) => {
          a.IsSelected = a.ID === address.ID;
        });
      }
    }
    this.showShippingAddressResults = false;
  }

  getShippingAddressLabel(isOfferPopup: boolean = false): string {
    const address = isOfferPopup ? this.parent.Offer?.ShippingAddress : this.parent.OfferRequest?.ShippingAddress;
    if (address) {
      return `${address.Country?.Name} - ${address.City} - ${address.AddressLine}`;
    }
    return 'Select Shipping Address';
  }

  onAgentsFocus() {
    this.closeAllCustomDropdowns();
    this.showAgentsResults = true;
  }

  toggleAgent(agent: any) {
    const index = this.selectedAgents.findIndex((a: any) => a.ID === agent.ID);
    if (index > -1) {
      this.selectedAgents.splice(index, 1);
    } else {
      this.selectedAgents.push(agent);
    }
  }

  getAgentsLabel(): string {
    if (this.selectedAgents.length === 0) {
      return 'Select Agents';
    }
    if (this.selectedAgents.length === 1) {
      return this.selectedAgents[0].Name;
    }
    return `${this.selectedAgents.length} agents selected`;
  }

  // ✅ Validate email format
  onNewAgentEmailChange() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.isValidEmail = emailRegex.test(this.newAgentEmail);
  }

  // ✅ Create new partner agent from email input
  createNewAgentFromEmail() {
    if (!this.isValidEmail || !this.newAgentEmail) {
      return;
    }

    // ✅ Find Department with ID == 4 from CartMasterData
    const department = this.parent.CartMasterData?.Departments?.find((d: any) => d.ID === 4);

    if (!department) {
      this.parent.openSnackBar('Department not found', 'OK', 'alert-info-snackbar');
      return;
    }

    // ✅ Create new PartnerAgent object
    const newAgent = {
      ID: 0,
      Name: this.newAgentEmail,
      Email: this.newAgentEmail,
      Phone: null,
      PartnerID: this.parent.B2BPartner?.ID,
      AllocatedShippingAddress: null,
      Department: department,
      IsSelected: false,
      Disabled: false,
      LastModified: false
    };

    // ✅ Call backend to save the new agent
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerAgent: newAgent
    };

    this.parent.loading = true;
    this.service.AddUpdatePartnerAgent(request).subscribe((response: any) => {
      this.parent.loading = false;

      if (response["Status"] !== 1) {
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        return;
      }

      // ✅ Success: add to partner's agents list and select it
      const savedAgent = response["PartnerAgent"];

      // ✅ Add to parent's B2BPartner.PartnerAgents list at the beginning
      if (!this.parent.B2BPartner.PartnerAgents) {
        this.parent.B2BPartner.PartnerAgents = [];
      }
      this.parent.B2BPartner.PartnerAgents.unshift(savedAgent);

      // ✅ Clear all previous selections and select ONLY the newly created agent
      this.selectedAgents = [savedAgent];

      // ✅ Clear input and reset validation
      this.newAgentEmail = '';
      this.isValidEmail = false;

      // ✅ Show success message
      this.parent.openSnackBar(response["Message"], "OK", "light-info-snackbar");
    }, (error: any) => {
      this.parent.loading = false;
      this.parent.openSnackBar('Error creating agent', 'OK', 'alert-info-snackbar');
    });
  }

  onConsolidationTypeFocus() {
    this.closeAllCustomDropdowns();
    this.showConsolidationTypeResults = true;
  }

  selectConsolidationType(type: any, isOfferPopup: boolean = false) {
    if (isOfferPopup) {
      this.parent.Offer.ConsolidationType = type;
    } else {
      this.parent.OfferRequest.ConsolidationType = type;
    }
    this.showConsolidationTypeResults = false;
  }

  getConsolidationTypeLabel(isOfferPopup: boolean = false): string {
    const type = isOfferPopup ? this.parent.Offer?.ConsolidationType : this.parent.OfferRequest?.ConsolidationType;
    return type?.Name || 'Select Consolidation Type';
  }

  // ✅ Config Status Dropdown Methods
  onConfigStatusFocus() {
    this.closeAllCustomDropdowns();
    this.showConfigStatusResults = true;
  }

  selectConfigStatus(status: any) {
    this.selectedConfigStatus = status;
    this.showConfigStatusResults = false;

    console.log('✅ Config Status selected:', status.Name, '(ID:', status.ID, ')');
    console.log('✅ This ConfigStatus object will now be sent in all B2BAdminSettings requests');

    // ✅ Intercept: show stock inclusion popup before bulk update
    this.pendingConfigStatus = status;
    this.showStockInclusionPopup = true;
  }

  handleStockInclusionChoice(includeStock: boolean): void {
    this.showStockInclusionPopup = false;
    console.log('✅ Stock inclusion choice:', includeStock ? 'YES' : 'NO');

    // ✅ If user chose NO, deactivate the "Includes also Stocks" toggle
    if (!includeStock) {
      this.stockStatusToggle = false;
      this.onStockStatusToggle();
    }

    this.bulkUpdateRowsForConfigStatus(this.pendingConfigStatus, includeStock);
  }

  getConfigStatusLabel(): string {
    if (this.selectedConfigStatus?.Name) {
      return `${this.selectedConfigStatus.Name} (${this.selectedConfigStatus.Description})`;
    }
    return 'All Statuses';
  }

  /**
   * ✅ Bulk-update all active rows (CartItemStatus.ID === 1) when ConfigStatus threshold changes.
   * For each qualifying row, calls GetIndustryProductCalculatedSideOffers, picks the best offer
   * (lowest ListPrice), and applies the same transfer logic as SelectSupplierFromPopup.
   */
  bulkUpdateRowsForConfigStatus(configStatus: any, includeStock: boolean = true): void {
    if (!this.gridApi) return;

    // ✅ Collect all qualifying rows (CartItemStatus.ID === 1)
    const qualifyingRows: { rowNode: any, cartItem: any, rowIndex: number }[] = [];
    this.gridApi.forEachNode((node: any) => {
      const ci = node.data?.CartItem;
      if (ci?.CartItemStatus?.ID === 1 && ci?.Product?.ID) {
        qualifyingRows.push({ rowNode: node, cartItem: ci, rowIndex: node.rowIndex });
      }
    });

    if (qualifyingRows.length === 0) {
      console.log('⚠️ No active rows (CartItemStatus.ID === 1) to update');
      return;
    }

    console.log(`🔄 Bulk ConfigStatus update: ${qualifyingRows.length} active rows to refresh`);

    // ✅ Global loading flag — stays ON until ALL requests complete
    this.bulkConfigStatusLoading = true;
    let pendingCount = qualifyingRows.length;

    const onRequestComplete = () => {
      pendingCount--;
      if (pendingCount <= 0) {
        this.bulkConfigStatusLoading = false;
        console.log('✅ Bulk ConfigStatus update complete — all rows processed');
      }
    };

    // ✅ Process each row individually
    qualifyingRows.forEach(({ rowNode, cartItem, rowIndex }) => {
      const productId = cartItem.Product.ID;
      const offerId = cartItem.SelectedOffer?.ID || cartItem.Product?.ListPriceOffers?.[0]?.ID || 0;

      const request = {
        User: this.parent.User,
        Language: this.parent.getLanguage(),
        PartnerID: this.parent.B2BPartner?.ID,
        IndustryProductID: productId,
        SelectedOfferID: offerId,
        ApplyClientRestrictions: this.applyClientRestrictions,
        BypassBlockedFlag: true,
        StockStatus: this.StockStatus,
        B2BAdminSettings: {
          AdminToken: this.parent.User?.Id,
          PrefferedCurrency: this.selectedCurrency,
          ConfigStatus: configStatus
        }
      };

      // ✅ Show loading state on the row
      cartItem.LoadingCell = true;
      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

      this.B2BService.GetIndustryProductCalculatedSideOffers(request).subscribe(response => {
        cartItem.LoadingCell = false;

        if (response["Status"] === 1) {
          const cartItems = JSON.parse(JSON.stringify(response["CartItems"]));
          if (!cartItems || cartItems.length === 0) {
            console.log(`⚠️ Row ${rowIndex}: No offers returned for product ID ${productId}`);
            rowNode.setData(rowNode.data);
            this.gridApi.redrawRows({ rowNodes: [rowNode] });
            return;
          }

          // ✅ Filter by stock preference if user chose to exclude stock
          let filteredItems = cartItems;
          if (!includeStock) {
            const nonStockItems = cartItems.filter((item: any) => !item?.SelectedOffer?.IsStockOffer);
            if (nonStockItems.length > 0) {
              filteredItems = nonStockItems;
            }
            // If all offers are stock, fall back to full list
          }

          // ✅ Sort by ListPrice ascending → first item = best offer
          filteredItems.sort((a: any, b: any) => {
            const priceA = a?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
            const priceB = b?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
            return priceA - priceB;
          });

          const bestOffer = filteredItems[0]?.SelectedOffer;
          if (!bestOffer) {
            console.log(`⚠️ Row ${rowIndex}: Best offer has no SelectedOffer`);
            rowNode.setData(rowNode.data);
            this.gridApi.redrawRows({ rowNodes: [rowNode] });
            return;
          }

          // ✅ Apply SelectSupplierFromPopup logic — transfer all relevant fields
          if (!cartItem.SelectedOffer) {
            cartItem.SelectedOffer = {};
          }

          // 1. Supplier (Partner) — deep cloned
          cartItem.SelectedOffer.Partner = JSON.parse(JSON.stringify(bestOffer.Partner));

          // 2. Purchase price & currency
          cartItem.SelectedOffer.ListPrice = bestOffer.ListPrice;
          cartItem.SelectedOffer.PurchaseCurrency = bestOffer.PurchaseCurrency || bestOffer.Currency;

          // 3. Offer ID — always carry
          cartItem.SelectedOffer.ID = bestOffer.ID || 0;
          if (!bestOffer.ID) {
            cartItem.SelectedOffer.Configuration = null;
          }

          // 4. Stock fields (drives GridValid row highlighting)
          cartItem.SelectedOffer.IsStockOffer = bestOffer.IsStockOffer ?? false;
          cartItem.SelectedOffer.StockQuantity = bestOffer.StockQuantity ?? 0;
          cartItem.SelectedOffer.IsManufacturerStockOffer = bestOffer.IsManufacturerStockOffer ?? false;

          // 5. LeadTime (deep cloned) & MOQ
          cartItem.SelectedOffer.LeadTime = bestOffer.LeadTime
            ? JSON.parse(JSON.stringify(bestOffer.LeadTime))
            : null;
          cartItem.SelectedOffer.MOQ = bestOffer.MOQ ?? null;

          console.log(`✅ Row ${rowIndex}: Updated to best offer — Supplier: ${bestOffer.Partner?.Name}, Price: ${bestOffer.ListPrice}, OfferID: ${bestOffer.ID}, IsStock: ${bestOffer.IsStockOffer}`);
        }

        // ✅ Refresh grid row + re-evaluate rowClassRules (stock highlighting)
        rowNode.setData(rowNode.data);
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
        this.gridApi.redrawRows({ rowNodes: [rowNode] });
        onRequestComplete();
      }, error => {
        cartItem.LoadingCell = false;
        console.error(`❌ Row ${rowIndex}: Error fetching side offers for product ID ${productId}:`, error);
        rowNode.setData(rowNode.data);
        this.gridApi.redrawRows({ rowNodes: [rowNode] });
        onRequestComplete();
      });
    });
  }

  AskAddNewRFQasPOQuestion:boolean = false;

  // ✅ Result popup state (shown after registration attempt)
  ShowOCResultPopup: boolean = false;
  OCResultIsSuccess: boolean = false;
  OCResultErrorMessage: string = '';
  OCResultReferenceNumber: string = '';
  OCResultPartnerName: string = '';
  OCResultPartnerVAT: string = '';
  OCResultTotal: string = '';
  OCResultCurrency: string = '';

  OCCopiedToClipboard: boolean = false;

  CloseOCResultPopup() {
    this.ShowOCResultPopup = false;
    this.OCResultErrorMessage = '';
    this.OCResultReferenceNumber = '';
    this.OCResultPartnerName = '';
    this.OCResultPartnerVAT = '';
    this.OCResultTotal = '';
    this.OCResultCurrency = '';
    this.OCCopiedToClipboard = false;
  }

  copyInjectionError() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = months[now.getMonth()];
    const year = now.getFullYear();
    const text = `${this.OCResultReferenceNumber} - Injection Error: ${this.OCResultErrorMessage} | ${day}/${month}/${year}`;
    navigator.clipboard.writeText(text).then(() => {
      this.OCCopiedToClipboard = true;
      setTimeout(() => { this.OCCopiedToClipboard = false; }, 2500);
    });
  }

  OpenAskAddNewRFQasPOQuestion(){
    // this.OfferRequest.DebatorPO = "";
    this.AskAddNewRFQasPOQuestion = true;
    this.SendEmail = false; // Reset to default (do not send email)
    this.InitializeCart();
    this.InitializeAgentsAndShippingAddress();
    // ✅ Auto-select first shipping address and agent if none selected
    this.AutoSelectFirstShippingAddressAndAgent();
    // ✅ Reset dropdown states
    this.closeAllCustomDropdowns();
  }

  AskAddNewOfferQuestion:boolean = false;

  OpenAskAddNewOfferQuestion(){
    this.AskAddNewOfferQuestion = true;
    this.SendEmail = false; // Reset to default (do not send email)
    this.InitializeCart();
    this.InitializeAgentsAndShippingAddress();
    // ✅ Auto-select first shipping address and agent if none selected
    this.AutoSelectFirstShippingAddressAndAgent();
    // ✅ Reset dropdown states
    this.closeAllCustomDropdowns();
  }

  // ✅ Initialize selected agents and shipping address from last offer
  InitializeAgentsAndShippingAddress() {
    // ✅ Check if partner is selected
    if (!this.parent.B2BPartner?.ID) {
      this.selectedAgents = [];
      this.parent.Offer.ShippingAddress = null;
      return;
    }

    // ✅ Call backend to get last selected agents and address from previous offer request
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner.ID
    };

    this.parent.loading = true;

    this.managementService.GetOfferLastSelectedAgentsAndAddressByPartnerID(request).subscribe((response: any) => {
      this.parent.loading = false;

      if (response["Status"] !== 1) {
        // ✅ If error or no previous offer, fall back to default selection
        this.fallbackToDefaultSelection();
        return;
      }

      // ✅ Pre-select agents from last offer (if any matched)
      if (response["LastSelectedPartnerAgents"] && response["LastSelectedPartnerAgents"].length > 0) {
        this.selectedAgents = response["LastSelectedPartnerAgents"];
      } else {
        // ✅ No agents matched - fall back to agents with IsSelected == true
        this.fallbackToDefaultAgentSelection();
      }

      // ✅ Pre-select shipping address from last offer (if matched)
      if (response["LastSelectedShippingAddress"]) {
        this.parent.Offer.ShippingAddress = response["LastSelectedShippingAddress"];
      } else {
        // ✅ No address matched - fall back to default selection
        this.fallbackToDefaultAddressSelection();
      }
    }, (error: any) => {
      this.parent.loading = false;
      console.error('Error getting last selected agents and address:', error);
      // ✅ On error, fall back to default selection
      this.fallbackToDefaultSelection();
    });
  }

  // ✅ Fallback to default selection (agents with IsSelected == true, first address)
  private fallbackToDefaultSelection() {
    this.fallbackToDefaultAgentSelection();
    this.fallbackToDefaultAddressSelection();
  }

  // ✅ Fallback to agents with IsSelected == true
  private fallbackToDefaultAgentSelection() {
    if (this.parent.B2BPartner?.PartnerAgents?.length > 0) {
      this.selectedAgents = this.parent.B2BPartner.PartnerAgents.filter((agent: any) => agent.IsSelected === true);
    } else {
      this.selectedAgents = [];
    }
  }

  // ✅ Fallback to first address or address with IsSelected == true
  private fallbackToDefaultAddressSelection() {
    if (this.parent.B2BPartner?.ShippingAddresses?.length > 0) {
      const selectedAddress = this.parent.B2BPartner.ShippingAddresses.find((addr: any) => addr.IsSelected === true);
      if (selectedAddress) {
        this.parent.Offer.ShippingAddress = selectedAddress;
      } else {
        // If no address is selected, take the first one
        this.parent.Offer.ShippingAddress = this.parent.B2BPartner.ShippingAddresses[0];
      }
    } else {
      this.parent.Offer.ShippingAddress = null;
    }
  }

  // ✅ Auto-select first shipping address and first agent if none selected
  private AutoSelectFirstShippingAddressAndAgent(): void {
    // ✅ Use setTimeout to wait for InitializeAgentsAndShippingAddress backend call to complete
    setTimeout(() => {
      // ✅ Auto-select first shipping address if none selected
      // Determine which object to use based on which popup is open
      const isOfferPopup = this.AskAddNewOfferQuestion;
      const isRFQasPOPopup = this.AskAddNewRFQasPOQuestion;
      const isRFQPopup = this.AskAddNewRFQQuestion;

      if (this.parent.B2BPartner?.ShippingAddresses?.length > 0) {
        const firstAddress = this.parent.B2BPartner.ShippingAddresses[0];

        // ✅ Set shipping address based on which popup is open
        if (isOfferPopup && !this.parent.Offer?.ShippingAddress) {
          this.parent.Offer.ShippingAddress = firstAddress;
          console.log('✅ Auto-selected first shipping address (Offer):', firstAddress);
        } else if ((isRFQasPOPopup || isRFQPopup) && !this.parent.OfferRequest?.ShippingAddress) {
          this.parent.OfferRequest.ShippingAddress = firstAddress;
          console.log('✅ Auto-selected first shipping address (OfferRequest):', firstAddress);
        }
      }

      // ✅ Auto-select first agent if none selected
      if (!this.selectedAgents || this.selectedAgents.length === 0) {
        if (this.parent.B2BPartner?.PartnerAgents?.length > 0) {
          const firstAgent = this.parent.B2BPartner.PartnerAgents[0];
          this.selectedAgents = [firstAgent];
          console.log('✅ Auto-selected first agent:', firstAgent);
        }
      }
    }, 500); // ✅ Wait 500ms for backend call to complete
  }

  // ✅ NEW: Add New Offer using Offers.BL (for quotations/new page)
  async AddNewOffer(POButtonPressed: boolean = false, cart: any = null) {
    // ✅ Before sending, update IsSelected flags on all agents based on selectedAgents array
    if (this.parent.B2BPartner?.PartnerAgents?.length > 0) {
      this.parent.B2BPartner.PartnerAgents.forEach((agent: any) => {
        agent.IsSelected = this.selectedAgents.some((selectedAgent: any) => selectedAgent.ID === agent.ID);
      });
    }

    // ✅ IMPORTANT: Collect CartItems from grid BEFORE sending to preserve SaveToDB flag
    const items: any[] = [];
    this.gridApi.forEachNode((node) => {
      const cartItem = node.data?.CartItem;
      if (cartItem) {
        const clonedItem = JSON.parse(JSON.stringify(cartItem)); // deep copy to avoid reference issues
        clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
        items.push(clonedItem);
      }
    });

    // ✅ Call parent's service directly to avoid navigation
    if (!cart) {
      this.parent.GetCart();
    } else {
      this.parent.Cart = JSON.parse(JSON.stringify(cart));
    }

    // ✅ OVERRIDE Cart.Items with grid items to preserve SaveToDB flag
    this.parent.Cart.Items = items;

    this.parent.Offer.UserUploader = this.parent.User;
    this.parent.Offer.AgentManager = this.parent.B2BAgent;
    this.parent.Offer.Company = this.parent.Company;
    // ✅ Cart includes: PaymentType, PaymentTerm, ShippingResponsability, ShippingServiceType, ShippingCompany
    this.parent.Offer.Cart = this.parent.Cart;
    // ✅ Ensure Cart.Partner has the B2BPartner with updated ShippingAddresses (including IsSelected flags)
    this.parent.Offer.Cart.Partner = this.parent.B2BPartner;
    this.parent.Offer.Type = this.parent.CartMasterData.OrderTypes.find((t: any) => t.ID === 2); // ✅ Offer (ID = 2)
    this.parent.Offer.PaymentType = this.parent.Cart.PaymentType;
    this.parent.Offer.DeliveryPreference = this.parent.Cart.DeliveryPreference;
    this.parent.Offer.ConsolidationType = this.parent.Offer.ConsolidationType; // ✅ Include ConsolidationType
    this.parent.Offer.ShippingAddress = this.parent.Offer.ShippingAddress; // ✅ Include ShippingAddress
    this.parent.Offer.Partner = this.parent.B2BPartner; // ✅ Include Partner
    this.parent.Offer.PartnerAgents = this.selectedAgents; // ✅ Include PartnerAgents
    this.parent.Offer.PDFColumns = this.getPDFColumns(); // ✅ Include PDF column visibility settings

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Offer: this.parent.Offer, // ✅ TS variable is Offer, backend expects "Offer" property name
      POButtonPressed: POButtonPressed,
      SendEmail: this.SendEmail, // ✅ Include SendEmail flag
    };

    this.parent.loading = true;

    this.managementService.AddNewOffer(request).subscribe(response => {
      if (response["Status"] !== 1) {
        // ✅ Show error message
        this.parent.loading = false;
        this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      } else if (response["Status"] === 1) {
        // ✅ Get the Offer object from response
        const offer = response["Offer"];
        this.ClearGrid();
        // ✅ Use ClearAdminSelectedPartnerByUser so the "No Partner" state persists across refresh
        this.ClearAdminSelectedPartnerByUser();

        if (!offer) {
          console.error('❌ No Offer in response');
          this.parent.loading = false;
          return;
        }

        console.log('✅ Offer created successfully', offer);

        // ✅ Export Offer PDF
        const exportRequest = {
          User: this.parent.User,
          Language: this.parent.getLanguage(),
          Offer: offer
        };

        this.managementService.ExportOffer(exportRequest).subscribe(
          exportResponse => {
            this.parent.loading = false;

            if (exportResponse["Status"] === 1) {
              const document = exportResponse["Document"];
              this.parent.download(document.Name, document.PDFStream);
            }

            // ✅ Show success message
            this.parent.openSnackBar(
              'Offer sent successfully!',
              'OK',
              'light-success-snackbar',
              3000
            );

            // ✅ Clear grid and reset to fresh 50 rows (keep partner selected)
            this.ClearGridAndResetToFresh();

            // ✅ Hide all confirmation question dialogs
            this.AskAddNewRFQasPOQuestion = false;
            this.AskAddNewRFQQuestion = false;
            this.AskAddNewOfferQuestion = false;
          },
          error => {
            // ✅ Handle export error - still close popup since offer was created
            this.parent.loading = false;
            console.error('❌ Export Offer failed:', error);

            this.parent.openSnackBar(
              'Offer created but export failed. Please try exporting from the Offers page.',
              'OK',
              'alert-info-snackbar',
              5000
            );

            // ✅ Clear grid and reset to fresh 50 rows (keep partner selected)
            this.ClearGridAndResetToFresh();

            // ✅ Hide all confirmation question dialogs
            this.AskAddNewRFQasPOQuestion = false;
            this.AskAddNewRFQQuestion = false;
            this.AskAddNewOfferQuestion = false;
          }
        );
      }
    });
  }

  // ✅ Register as Client Order Confirmation - uses AddOfferRequestWithOrderConfirmation (GlobalBL)
  // Creates an OfferRequest first, then converts it to an OrderConfirmation and injects into external system
  async AddNewOfferRequest(POButtonPressed: boolean = false, cart: any = null) {
    // ✅ Before sending, update IsSelected flags on all agents based on selectedAgents array
    if (this.parent.B2BPartner?.PartnerAgents?.length > 0) {
      this.parent.B2BPartner.PartnerAgents.forEach((agent: any) => {
        agent.IsSelected = this.selectedAgents.some((selectedAgent: any) => selectedAgent.ID === agent.ID);
      });
    }

    // ✅ IMPORTANT: Collect CartItems from grid BEFORE sending to preserve SaveToDB flag
    const items: any[] = [];
    this.gridApi.forEachNode((node) => {
      const cartItem = node.data?.CartItem;
      if (cartItem) {
        const clonedItem = JSON.parse(JSON.stringify(cartItem)); // deep copy to avoid reference issues
        clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
        items.push(clonedItem);
      }
    });

    // ✅ Call parent's service directly to avoid navigation
    if (!cart) {
      this.parent.GetCart();
    } else {
      this.parent.Cart = JSON.parse(JSON.stringify(cart));
    }

    // ✅ OVERRIDE Cart.Items with grid items to preserve SaveToDB flag
    this.parent.Cart.Items = items;

    this.parent.OfferRequest.UserUploader = this.parent.User;
    this.parent.OfferRequest.AgentManager = this.parent.B2BAgent;
    this.parent.OfferRequest.Company = this.parent.Company;
    // ✅ Cart includes: PaymentType, PaymentTerm, ShippingResponsability, ShippingServiceType, ShippingCompany
    this.parent.OfferRequest.Cart = this.parent.Cart;
    // ✅ Ensure Cart.Partner has the B2BPartner with updated ShippingAddresses (including IsSelected flags)
    this.parent.OfferRequest.Cart.Partner = this.parent.B2BPartner;
    this.parent.OfferRequest.Type = this.parent.CartMasterData.OrderTypes.find((t: any) => t.ID === 4); // ✅ OfferRequest (ID = 4)
    this.parent.OfferRequest.PaymentType = this.parent.Cart.PaymentType;
    this.parent.OfferRequest.DeliveryPreference = this.parent.Cart.DeliveryPreference;
    this.parent.OfferRequest.ConsolidationType = this.parent.OfferRequest.ConsolidationType; // ✅ Include ConsolidationType
    this.parent.OfferRequest.ShippingAddress = this.parent.OfferRequest.ShippingAddress; // ✅ Include ShippingAddress
    this.parent.OfferRequest.Partner = this.parent.B2BPartner; // ✅ Include Partner
    this.parent.OfferRequest.PartnerAgents = this.selectedAgents; // ✅ Include PartnerAgents
    this.parent.OfferRequest.PDFColumns = this.getPDFColumns(); // ✅ Include PDF column visibility settings

    // ✅ Use AddOfferRequestWithOrderConfirmation endpoint (GlobalBL)
    // This creates an OfferRequest first, then converts it to an OrderConfirmation
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      OfferRequest: this.parent.OfferRequest, // ✅ OfferRequest property for GlobalBL endpoint
      SendEmail: this.SendEmail, // ✅ Include SendEmail flag
      POButtonPressed: true, // ✅ Always set to true to trigger OC creation + injection
    };

    this.parent.loading = true;

    // ✅ Call AddOfferRequestWithOrderConfirmation via GlobalBL
    this.managementService.AddOfferRequestWithOrderConfirmation(request).subscribe(response => {
      if (response["Status"] !== 1) {
        // ✅ Show error popup
        this.parent.loading = false;
        this.OCResultIsSuccess = false;
        this.OCResultErrorMessage = response["Message"] || 'Unknown error';
        this.OCResultReferenceNumber = '';
        this.ShowOCResultPopup = true;

        // ✅ Hide registration popup
        this.AskAddNewRFQasPOQuestion = false;
        this.AskAddNewRFQQuestion = false;
        this.AskAddNewOfferQuestion = false;
      } else if (response["Status"] === 1) {
        // ✅ Get the OrderConfirmation object from response
        const orderConfirmation = response["OrderConfirmation"];
        this.ClearGrid();
        // ✅ Use ClearAdminSelectedPartnerByUser so the "No Partner" state persists across refresh
        this.ClearAdminSelectedPartnerByUser();

        if (!orderConfirmation) {
          console.error('❌ No OrderConfirmation in response');
          this.parent.loading = false;
          return;
        }

        console.log('✅ Order Confirmation created successfully', orderConfirmation);

        // ✅ Check for injection error on the returned OC
        const injectionError = orderConfirmation.InjectionError;
        const hasInjectionError = injectionError?.HasError === true;

        // ✅ Capture OC details for result popup
        this.OCResultReferenceNumber = orderConfirmation.ReferenceNumber || '';
        this.OCResultPartnerName = orderConfirmation.Cart?.Partner?.Name || '';
        this.OCResultPartnerVAT = orderConfirmation.Cart?.Partner?.BillingAddress?.VATNumber || orderConfirmation.Cart?.Partner?.VAT || '';
        this.OCResultTotal = orderConfirmation.Cart?.Total?.TotalQtyPriceNOVAT?.toFixed(2) || '0.00';
        this.OCResultCurrency = orderConfirmation.Cart?.Currency?.Symbol || orderConfirmation.Cart?.Currency?.Name || '';

        // ✅ Export OrderConfirmation PDF
        const exportRequest = {
          User: this.parent.User,
          Language: this.parent.getLanguage(),
          ID: orderConfirmation.ID
        };

        this.managementService.ExportOrderConfirmation(exportRequest).subscribe(
          exportResponse => {
            this.parent.loading = false;

            if (exportResponse["Status"] === 1) {
              const document = exportResponse["Document"];
              this.parent.download(document.Name, document.PDFStream);
            }

            // ✅ Clear grid and reset to fresh 50 rows (keep partner selected)
            this.ClearGridAndResetToFresh();

            // ✅ Hide all confirmation question dialogs
            this.AskAddNewRFQasPOQuestion = false;
            this.AskAddNewRFQQuestion = false;
            this.AskAddNewOfferQuestion = false;

            // ✅ Show result popup (success or injection error)
            if (hasInjectionError) {
              this.OCResultIsSuccess = false;
              this.OCResultErrorMessage = injectionError.Message || 'Injection error (no details)';
            } else {
              this.OCResultIsSuccess = true;
              this.OCResultErrorMessage = '';
            }
            this.ShowOCResultPopup = true;
          },
          error => {
            // ✅ Handle export error - still close popup since order confirmation was created
            this.parent.loading = false;
            console.error('❌ Export Order Confirmation failed:', error);

            // ✅ Clear grid and reset to fresh 50 rows (keep partner selected)
            this.ClearGridAndResetToFresh();

            // ✅ Hide all confirmation question dialogs
            this.AskAddNewRFQasPOQuestion = false;
            this.AskAddNewRFQQuestion = false;
            this.AskAddNewOfferQuestion = false;

            // ✅ Show result popup — OC was created but export failed, check injection error too
            if (hasInjectionError) {
              this.OCResultIsSuccess = false;
              this.OCResultErrorMessage = injectionError.Message || 'Injection error (no details)';
            } else {
              this.OCResultIsSuccess = false;
              this.OCResultErrorMessage = 'Order Confirmation created but PDF export failed';
            }
            this.ShowOCResultPopup = true;
          }
        );
      }
    });
  }

  // ✅ Grid mode toggle (true = admin grid, false = employee grid)
  useAdminGrid: boolean = false;
  private gridModeInitialized: boolean = false;

  // ✅ Client Restrictions toggle (default: false = no restrictions)
  applyClientRestrictions: boolean = false;

  // ✅ Stock Status filter toggle (ON=null=All items, OFF=0=Non-stock items only)
  stockStatusToggle: boolean = true; // true=All (null), false=Non-stock only (0)
  get StockStatus(): number | null { return this.stockStatusToggle ? null : 0; }

  onStockStatusToggle(): void {
    console.log('✅ Stock Status toggled:', this.stockStatusToggle ? 'All Items (null)' : 'Non-Stock Only (0)');
    this.safeLocalStorageSet(this.ADMIN_STOCK_STATUS_KEY, String(this.stockStatusToggle));
  }

  // ✅ Check if current user is Administrator (role ID == 3)
  // Falls back to QuotationRights if CurrentUIPageRights is not set for this URL
  isAdministrator(): boolean {
    const rights = this.parent.User?.UserManagementInfo?.ManagementRights;
    const roleID = rights?.CurrentUIPageRights?.UserPageRole?.ID
                ?? rights?.QuotationRights?.UserPageRole?.ID;
    const isAdmin = roleID === 3;

    // ✅ Pre-select Admin Grid ONLY on first load if user is administrator
    if (isAdmin && !this.gridModeInitialized) {
      this.useAdminGrid = true;
      this.gridModeInitialized = true;
      console.log('🔐 Admin user detected - Pre-selecting Admin Grid');

      // ✅ Set ConfigStatus to VisibleOnlyByAdmin (ID=-1) for admin grid
      this.setDefaultConfigStatus();
    }

    return isAdmin;
  }

  /**
   * Check if there are any RED ROWS (CartItemStatus.ID === 0) without a brand selected
   * Used to disable "Register as Client Order Confirmation" and "Register New Offer" buttons
   */
  hasRedRowsWithoutBrand(): boolean {
    if (!this.parent.Cart?.Items || this.parent.Cart.Items.length === 0) {
      return false;
    }

    return this.parent.Cart.Items.some((item: any) => {
      return item?.CartItemStatus?.ID === 0 && !(item?.Product?.Brand?.ID > 0);
    });
  }

  /**
   * Check if there are any RED ROWS at all (CartItemStatus.ID === 0)
   * Used for Employee Grid to disable buttons when ANY red rows exist
   */
  hasAnyRedRows(): boolean {
    if (!this.parent.Cart?.Items || this.parent.Cart.Items.length === 0) {
      return false;
    }

    return this.parent.Cart.Items.some((item: any) => {
      return item?.CartItemStatus?.ID === 0;
    });
  }

  /**
   * Check if the grid has at least one valid cart item (CartItemStatus.ID === 1)
   * Empty rows (ID === -2) and awaiting selection rows (ID === -1) don't count
   */
  hasValidCartItems(): boolean {
    if (!this.parent.Cart?.Items || this.parent.Cart.Items.length === 0) {
      return false;
    }

    return this.parent.Cart.Items.some((item: any) => {
      const statusId = item?.CartItemStatus?.ID;
      // Valid rows (1) and red rows (0) are considered "filled" rows
      return statusId === 1 || statusId === 0;
    });
  }

  /**
   * Determine if "Register as Client Order Confirmation" button should be disabled
   * Admin Grid: Allow RED ROWS if they have Brand.ID > 0
   * Employee Grid: Disallow ANY RED ROWS
   * ALWAYS: Disable if grid has no valid product rows
   */
  shouldDisableRegisterButton(): boolean {
    // ✅ Always disable if grid is empty (no valid product rows)
    if (!this.hasValidCartItems()) {
      return true;
    }

    if (this.useAdminGrid) {
      // Admin Grid: Only disable if there are RED ROWS without brand
      return this.hasRedRowsWithoutBrand();
    } else {
      // Employee Grid: Disable if there are ANY RED ROWS
      return this.hasAnyRedRows();
    }
  }

  // ✅ Handle client restrictions toggle change
  onClientRestrictionsToggle(): void {
    console.log('✅ Client Restrictions toggled:', this.applyClientRestrictions ? 'ON (with restrictions)' : 'OFF (no restrictions)');
    this.safeLocalStorageSet(this.ADMIN_CLIENT_RESTRICTIONS_KEY, String(this.applyClientRestrictions));
  }

  // ✅ Toggle between admin and employee grids
  toggleGridMode(isAdmin: boolean): void {
    this.useAdminGrid = isAdmin;
    console.log('🔄 Grid mode switched to:', this.useAdminGrid ? 'Admin Grid' : 'Employee Grid');

    // ✅ Set ConfigStatus based on grid mode
    this.setDefaultConfigStatus();

    // Force grid refresh after toggle
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
      }
    }, 100);
  }

  columnDefs = [
    {
      headerName: '',
      field: '_insertRow',
      editable: false,
      minWidth: 28,
      maxWidth: 28,
      width: 28,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      resizable: false,
      headerClass: 'insert-row-col-header',
      cellClass: 'insert-row-col-cell',
      cellRenderer: (params: any) => {
        return `<button class="insert-row-btn" data-row="${params.rowIndex}" title="Insert row below"><i class="material-icons" style="pointer-events:none;">add</i></button>`;
      },
      onCellClicked: (params: any) => {
        const target = params.event?.target as HTMLElement;
        const insertBtn = target?.closest('.insert-row-btn') as HTMLElement;
        if (insertBtn) {
          params.event?.stopPropagation();
          const rowIdx = parseInt(insertBtn.getAttribute('data-row') ?? '0', 10);
          this.insertRowAtIndex(rowIdx + 1);
        }
      }
    },
    {
      headerName: '',
      field: 'index',
      editable: false,
      minWidth: 45,
      maxWidth: 55,
      width: 50,
      suppressMenu: true,
      headerClass: 'quotation-index-header',
      cellRenderer: (params: any) => {
        const rowIndex = params.rowIndex + 1;
        const isSelected = params.data?.CartItem?.IsSelected === true;
        return `<div style="display: flex; align-items: center; width: 100%; height: 100%; padding: 0 4px 0 0; margin-left: -8px;">
                  <input type="checkbox" ${isSelected ? 'checked' : ''} class="cart-item-checkbox" data-row="${params.rowIndex}" />
                  <span style="cursor: pointer; margin-left: auto;">${rowIndex}</span>
                </div>`;
      },
      onCellClicked: (params: any) => {
        const target = params.event?.target as HTMLElement;
        if (target?.classList?.contains('cart-item-checkbox')) {
          const cartItem = params.data?.CartItem;
          if (cartItem) {
            cartItem.IsSelected = !cartItem.IsSelected;
            params.api.refreshCells({ rowNodes: [params.node], columns: ['index'], force: true });
          }
        } else {
          const statusId = params.data?.CartItem?.CartItemStatus?.ID;
          const isRedRow = statusId === 0;
          const isValidRow = statusId === 1;
          const isEmptyRow = statusId === -2;
          const isAwaitingSelection = statusId === -1;
          if (isRedRow || isValidRow || isEmptyRow || isAwaitingSelection) {
            this.OpenRedRowEditPopup(params.data.CartItem, params.rowIndex);
          }
        }
      }
    },
    {
      headerName: 'Brand',
      field: 'brand',
      editable: (params: any) => {
        // ✅ Make editable ONLY for RED ROWS (CartItemStatus.ID === 0)
        const statusId = params.data?.CartItem?.CartItemStatus?.ID;
        return statusId === 0;
      },
      singleClickEdit: true,  // ✅ Single-click to edit for RED ROWS
      minWidth: 125,
      flex: 0.75,
      suppressMenu: true,
      cellRenderer: (params: any) => {
        // ✅ For RED ROWS (editable), don't use custom renderer - just return plain text
        const statusId = params.data?.CartItem?.CartItemStatus?.ID;
        if (statusId === 0) {
          return params.value ?? '';
        }
        // ✅ For other rows, use the custom renderer with button
        return this.BrandCellRenderer(params);
      },
      cellClassRules: {
        'GridAwaitingSelection': (p: any) => p.data?.CartItem?.CartItemStatus?.ID === -1
      }
    },
    {
      headerName: 'PartNumber',
      field: 'partNumber',
      editable: false,
      minWidth: 100,
      flex: 1,
      suppressMenu: true
    },
    {
      headerName: 'Name',
      field: 'name',
      editable: true,
      minWidth: 250,
      flex: 15,
      suppressMenu: true,
      cellRenderer: this.NameCellRenderer.bind(this),
      cellClassRules: {
       'GridAwaitingSelection': (p: any) => p.data?.CartItem?.CartItemStatus?.ID === -1
      }
    },
    {
      headerName: 'Qty',
      field: 'quantity',
      editable: true,
      singleClickEdit: false,  // ✅ Edit on double-click
      type: 'numericColumn',
      valueParser: this.numberParser,
      minWidth: 60,
      flex: 0.75,
      suppressMenu: true,
      cellRenderer: (params: any) => {
        const qty = params.value ?? '';
        const offer = params.data?.CartItem?.SelectedOffer;
        const hasMOQ = offer?.MOQ != null && offer.MOQ > 1;
        const style = hasMOQ ? 'color:#d32f2f;font-weight:700;' : '';
        const title = hasMOQ ? ` title="MOQ: ${offer.MOQ}"` : '';
        return `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;${style}"${title}>${qty}</div>`;
      },
      tooltipValueGetter: function (params) {
        const selectedOffer = params.data?.CartItem?.SelectedOffer;
        // Show MOQ adjustment tooltip if applicable
        if (selectedOffer?.ChangedQunatityFromMOQ) {
          return params.context.componentParent.MOQAdjustmentTooltip;
        }
        // Show stock quantity tooltip if StockQuantity > 0 and NOT a manufacturer stock offer
        if (selectedOffer?.StockQuantity > 0 && selectedOffer?.IsManufacturerStockOffer !== true) {
          return `Stock: ${selectedOffer.StockQuantity}`;
        }
        return null;
      },
      tooltipComponentParams: {
        class: 'custom-moq-tooltip'
      }
    },
    {
      headerName: 'List-Price',
      field: 'officialListPrice',
      editable: false, // ✅ NOT editable on employee grid
      minWidth: 90,
      flex: 2,
      suppressMenu: true,
      cellRenderer: (p: any) => this.officialListPriceCellRenderer(p),
      cellClass: 'listprice-cell'
    },
    {
      headerName: '%Discount',
      field: 'officialDiscount',
      editable: false,
      minWidth: 75,
      flex: 0.75,
      suppressMenu: true,
      type: 'rightAligned',
      cellRenderer: (p: any) => this.officialDiscountCellRenderer(p)
    },
    {
      headerName: this.getFinalPriceHeader(),
      field: 'price',
      editable: false,
      minWidth: 100,
      flex: 2,
      suppressMenu: true,
      cellRenderer: this.priceCellRenderer.bind(this),
      cellClass: 'yourprice-cell'
    },
    {
      headerName: this.getTotalHeader(),
      field: 'total',
      editable: false,
      minWidth: 90,
      flex: 2,
      suppressMenu: true,
      cellClass: 'yourprice-cell'
    },
    {
      headerName: 'Standard-Delivery',
      field: 'eta',
      editable: true,
      minWidth: 140,
      flex: 2,
      suppressMenu: true,
      cellRenderer: this.ETACellRenderer.bind(this),
      onCellClicked: (params: any) => this.onETAConfirmClick(params),
      tooltipValueGetter: (params: any) => {
        const cartItem = params.data?.CartItem;
        const isTrusted = cartItem?.Product?.Stats?.TrustedManufacturerLeadTime === true;
        if (!isTrusted) return null;

        const manufacturerStock = cartItem?.Product?.Stats?.ManufacturerStock;

        const stockText = manufacturerStock != null ? manufacturerStock : '-';

        return `Manufacturer stock: ${stockText}\nTrusted information`;
      },
      tooltipComponentParams: {
        class: 'trusted-tooltip'
      }
    },
    {
      headerName: 'HSCode',
      field: 'hscode',
      editable: false,
      minWidth: 100,
      flex: 0.75,
      suppressMenu: true
    },
    {
      headerName: 'COO',
      field: 'coo',
      editable: true,
      singleClickEdit: true,  // ✅ Single-click to edit
      minWidth: 75,
      flex: 0.75,
      suppressMenu: true
    },
    {
      headerName: 'Weight',
      field: 'weight',
      editable: false,
      minWidth: 75,
      flex: 0.75,
      suppressMenu: true
    },
    // 🔵 NEW: PDF icon column (header shows icon; rows show light-blue icon only if DatasheetLink exists)
    {
      headerName: '',
      field: 'datasheet',
      minWidth: 35,
      maxWidth: 50,
      width: 40,
      suppressMenu: true,
      suppressSorting: true,
      editable: false,
      suppressSizeToFit: true,
      headerComponentParams: {
        template: `
          <div class="ag-cell-label-container" role="presentation" style="justify-content:center;">
            <span class="ag-header-cell-text">
              <i class="material-icons pdf-header-icon" title="Datasheet" style="font-size:17px;">file_present</i>
            </span>
          </div>
        `
      },
      cellRenderer: (p: any) => {
        const link = p?.data?.CartItem?.Product?.Stats?.DatasheetLink;
        const hasLink = typeof link === 'string' && link.trim().length > 0;
        if (!hasLink) return ''; // no button if no link

        // escape quotes to avoid breaking the attribute
        const safeHref = String(link).replace(/"/g, '&quot;');

        return `
          <button
            class="pdf-datasheet-button material-icon-trigger"
            data-row="${p.rowIndex}"
            data-href="${safeHref}"
            title="Open datasheet"
            aria-label="Open datasheet">
            <i class="material-icons pdf-cell-icon" style="font-size:17px;margin-right:7px;">file_present</i>
          </button>
        `;
      }
    },
    {
      headerName: '',
      field: 'delete',
      minWidth: 30,
      maxWidth: 35,
      width: 32,
      suppressMenu: true,
      suppressSorting: true,
      editable: false,
      suppressSizeToFit: true,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
      cellRenderer: (params: any) => {
        const cartItem = params.data?.CartItem;
        const statusId = cartItem?.CartItemStatus?.ID;
        const rowIndex = params.rowIndex;

        // ✅ Same principle as popup: show button when CartItemStatus.ID is not Empty(-2) and not AwaitingSelection(-1)
        if (statusId == null || statusId === -2 || statusId === -1) {
          return '';
        }

        // ✅ Red Row = CartItemStatus.ID === 0
        const isRedRow = statusId === 0;

        // ✅ New Offer = SelectedOffer.ID is 0 or null/undefined
        const offerId = cartItem?.SelectedOffer?.ID || 0;
        const isNewOffer = offerId === 0;

        // ✅ Show ONLY ONE button at a time (mutually exclusive):
        // Green button: For red rows OR new offers (Save as New)
        // Blue button: For existing offers (Save/Update to DB)
        const showGreenButton = isRedRow || isNewOffer;

        return `<div style="display: flex; gap: 4px; align-items: center; justify-content: center;">
                  ${showGreenButton ?
                    `<div class="save-to-db-icon save-new-icon" data-row="${rowIndex}" data-action="save-new" style="width: 18px; height: 18px; background-color: #4CAF50; border-radius: 3px; display: flex; align-items: center; justify-content: center; cursor: pointer;" title="Save as New">
                      <i class="material-icons" style="font-size: 14px; color: white;">add</i>
                    </div>`
                    :
                    `<div class="save-to-db-icon save-update-icon" data-row="${rowIndex}" data-action="save-update" style="width: 18px; height: 18px; background-color: #2196F3; border-radius: 3px; display: flex; align-items: center; justify-content: center; cursor: pointer;" title="Save/Update to DB">
                      <i class="material-icons" style="font-size: 14px; color: white;">save</i>
                    </div>`
                  }
                </div>`;
      },
      onCellClicked: (params: any) => {
        const cartItem = params.data?.CartItem;
        const rowIndex = params.rowIndex;
        const target = params.event?.target as HTMLElement;

        // Find which button was clicked
        const saveIcon = target.closest('.save-to-db-icon') as HTMLElement;
        if (!saveIcon) return;

        const action = saveIcon.getAttribute('data-action');
        console.log('🔵 [GRID BUTTON CLICKED - Employee Grid] Row:', rowIndex, 'Action:', action);

        if (cartItem?.CartItemStatus) {
          // ✅ Check if multiple rows are selected
          const selectedItems: { cartItem: any, rowIndex: number }[] = [];
          this.gridApi?.forEachNode((node: any) => {
            const nodeCartItem = node.data?.CartItem;
            if (nodeCartItem?.IsSelected === true && nodeCartItem?.CartItemStatus?.ID >= 0) {
              selectedItems.push({ cartItem: nodeCartItem, rowIndex: node.rowIndex });
            }
          });

          // ✅ If multiple rows selected, use BULK endpoint
          if (selectedItems.length > 1) {
            console.log(`🔵 [EMPLOYEE GRID] Multiple rows selected (${selectedItems.length}) - calling BulkSaveAdminQuotationProducts`);
            this.BulkSaveAdminQuotationProducts(selectedItems);
          } else {
            // ✅ Single row - use individual endpoint
            if (action === 'save-new') {
              console.log('🟢 [GREEN BUTTON] Save as New - calling SaveAdminQuotationProduct');
              this.SaveAdminQuotationProduct(cartItem, rowIndex);
            } else if (action === 'save-update') {
              console.log('🔵 [BLUE BUTTON] Save/Update - calling SaveAdminQuotationProduct');
              this.SaveAdminQuotationProduct(cartItem, rowIndex);
            }
          }
        }
      }
    }
  ];

  // ✅ Admin-specific column definitions (narrower Name column + Supplier column)
  adminColumnDefs = [
    {
      headerName: '',
      field: '_insertRow',
      editable: false,
      minWidth: 28,
      maxWidth: 28,
      width: 28,
      suppressMenu: true,
      suppressMovable: true,
      suppressSizeToFit: true,
      resizable: false,
      headerClass: 'insert-row-col-header',
      cellClass: 'insert-row-col-cell',
      cellRenderer: (params: any) => {
        return `<button class="insert-row-btn" data-row="${params.rowIndex}" title="Insert row below"><i class="material-icons" style="pointer-events:none;">add</i></button>`;
      },
      onCellClicked: (params: any) => {
        const target = params.event?.target as HTMLElement;
        const insertBtn = target?.closest('.insert-row-btn') as HTMLElement;
        if (insertBtn) {
          params.event?.stopPropagation();
          const rowIdx = parseInt(insertBtn.getAttribute('data-row') ?? '0', 10);
          this.insertRowAtIndex(rowIdx + 1);
        }
      }
    },
    {
      headerName: '',
      field: 'index',
      editable: false,
      minWidth: 45,
      maxWidth: 55,
      width: 50,
      suppressMenu: true,
      headerClass: 'quotation-index-header',
      cellRenderer: (params: any) => {
        const rowIndex = params.rowIndex + 1;
        const isSelected = params.data?.CartItem?.IsSelected === true;
        return `<div style="display: flex; align-items: center; width: 100%; height: 100%; padding: 0 4px 0 0; margin-left: -8px;">
                  <input type="checkbox" ${isSelected ? 'checked' : ''} class="cart-item-checkbox" data-row="${params.rowIndex}" />
                  <span style="cursor: pointer; margin-left: auto;">${rowIndex}</span>
                </div>`;
      },
      onCellClicked: (params: any) => {
        const target = params.event?.target as HTMLElement;
        if (target?.classList?.contains('cart-item-checkbox')) {
          const cartItem = params.data?.CartItem;
          if (cartItem) {
            cartItem.IsSelected = !cartItem.IsSelected;
            params.api.refreshCells({ rowNodes: [params.node], columns: ['index'], force: true });
          }
        } else {
          const statusId = params.data?.CartItem?.CartItemStatus?.ID;
          const isRedRow = statusId === 0;
          const isValidRow = statusId === 1;
          const isEmptyRow = statusId === -2;
          const isAwaitingSelection = statusId === -1;
          if (isRedRow || isValidRow || isEmptyRow || isAwaitingSelection) {
            this.OpenRedRowEditPopup(params.data.CartItem, params.rowIndex);
          }
        }
      }
    },
    {
      headerName: 'Brand',
      field: 'brand',
      editable: (params: any) => {
        // ✅ Make editable ONLY for RED ROWS (CartItemStatus.ID === 0)
        const statusId = params.data?.CartItem?.CartItemStatus?.ID;
        return statusId === 0;
      },
      singleClickEdit: true,  // ✅ Single-click to edit for RED ROWS
      minWidth: 90,
      maxWidth: 120,
      flex: 0.5,
      suppressMenu: true,
      cellRenderer: (params: any) => {
        // ✅ For RED ROWS (editable), don't use custom renderer - just return plain text
        const statusId = params.data?.CartItem?.CartItemStatus?.ID;
        if (statusId === 0) {
          return params.value ?? '';
        }
        // ✅ For other rows, use the custom renderer with button
        return this.BrandCellRenderer(params);
      },
      cellClassRules: {
        'GridAwaitingSelection': (p: any) => p.data?.CartItem?.CartItemStatus?.ID === -1
      }
    },
    {
      headerName: 'PartNumber',
      field: 'partNumber',
      editable: true, // ✅ Editable (validation in onCellValueChanged restricts to red rows)
      minWidth: 100,
      flex: 1,
      suppressMenu: true,
      valueGetter: (params: any) => {
        const partNumber = params.data?.CartItem?.Product?.PartNumber;
        return partNumber ?? '';
      },
      valueSetter: (params: any) => {
        // ✅ Write the value back to CartItem.Product.PartNumber
        if (!params.data.CartItem) {
          params.data.CartItem = {};
        }
        if (!params.data.CartItem.Product) {
          params.data.CartItem.Product = {};
        }
        params.data.CartItem.Product.PartNumber = params.newValue;
        return true; // ✅ Tell AG Grid the value was set successfully
      }
    },
    {
      headerName: 'Name',
      field: 'name',
      editable: true,
      minWidth: 180,
      flex: 6,
      suppressMenu: true,
      cellRenderer: this.NameCellRenderer.bind(this),
      cellClassRules: {
       'GridAwaitingSelection': (p: any) => p.data?.CartItem?.CartItemStatus?.ID === -1
      }
    },
    {
      headerName: 'Qty',
      field: 'quantity',
      editable: true,
      singleClickEdit: false,  // ✅ Edit on double-click
      type: 'numericColumn',
      valueParser: this.numberParser,
      minWidth: 60,
      flex: 0.75,
      suppressMenu: true,
      cellRenderer: (params: any) => {
        const qty = params.value ?? '';
        const offer = params.data?.CartItem?.SelectedOffer;
        const hasMOQ = offer?.MOQ != null && offer.MOQ > 1;
        const style = hasMOQ ? 'color:#d32f2f;font-weight:700;' : '';
        const title = hasMOQ ? ` title="MOQ: ${offer.MOQ}"` : '';
        return `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;${style}"${title}>${qty}</div>`;
      },
      tooltipValueGetter: function (params) {
        const selectedOffer = params.data?.CartItem?.SelectedOffer;
        // Show MOQ adjustment tooltip if applicable
        if (selectedOffer?.ChangedQunatityFromMOQ) {
          return params.context.componentParent.MOQAdjustmentTooltip;
        }
        // Show stock quantity tooltip if StockQuantity > 0 and NOT a manufacturer stock offer
        if (selectedOffer?.StockQuantity > 0 && selectedOffer?.IsManufacturerStockOffer !== true) {
          return `Stock: ${selectedOffer.StockQuantity}`;
        }
        return null;
      },
      tooltipComponentParams: {
        class: 'custom-moq-tooltip'
      }
    },
    {
      headerName: this.getListPriceHeader(),
      field: 'officialListPrice',
      editable: true, // ✅ Editable on ALL rows
      singleClickEdit: true,  // ✅ Edit on single click
      minWidth: 70,
      flex: 2,
      suppressMenu: true,
      cellRenderer: (p: any) => this.officialListPriceCellRenderer(p),
      cellClass: 'listprice-cell'
    },
    {
      headerName: '%Discount',
      field: 'officialDiscount',
      editable: true, // ✅ Always editable - validation happens in onCellValueChanged
      singleClickEdit: true,  // ✅ Edit on single click
      minWidth: 65,
      maxWidth: 85,
      flex: 0.6,
      suppressMenu: true,
      type: 'rightAligned',
      cellRenderer: (p: any) => this.officialDiscountCellRenderer(p)
      // ✅ NO valueSetter - let AG Grid handle the value directly
      // All validation and backend calls happen in onCellValueChanged
    },
    {
      headerName: this.getFinalPriceHeader(),
      field: 'price',
      editable: true,
      singleClickEdit: true,  // ✅ Edit on single click
      minWidth: 100,
      flex: 2,
      suppressMenu: true,
      cellRenderer: this.priceCellRenderer.bind(this),
      cellClass: 'yourprice-cell'
      // ✅ NO valueSetter - let AG Grid handle the value directly
      // All validation and backend calls happen in onCellValueChanged
    },
    {
      headerName: this.getTotalHeader(),
      field: 'total',
      editable: false,
      minWidth: 90,
      flex: 2,
      suppressMenu: true,
      cellClass: 'yourprice-cell'
    },
    {
      headerName: 'Standard-Delivery',
      field: 'eta',
      editable: true,
      minWidth: 140,
      flex: 2,
      suppressMenu: true,
      cellRenderer: this.ETACellRenderer.bind(this),
      onCellClicked: (params: any) => this.onETAConfirmClick(params),
      tooltipValueGetter: (params: any) => {
        const cartItem = params.data?.CartItem;
        const isTrusted = cartItem?.Product?.Stats?.TrustedManufacturerLeadTime === true;
        if (!isTrusted) return null;

        const manufacturerStock = cartItem?.Product?.Stats?.ManufacturerStock;

        const stockText = manufacturerStock != null ? manufacturerStock : '-';

        return `Manufacturer stock: ${stockText}\nTrusted information`;
      },
      tooltipComponentParams: {
        class: 'trusted-tooltip'
      }
    },
    {
      headerName: 'Supplier',
      field: 'supplier',
      editable: true,
      singleClickEdit: true,
      minWidth: 120,
      flex: 2,
      suppressMenu: true,
      valueGetter: (params: any) => {
        const cartItem = params.data?.CartItem;
        const partnerName = cartItem?.SelectedOffer?.Partner?.Name;
        const configName = cartItem?.SelectedOffer?.Configuration?.Name;

        // Primary: Partner.Name (if exists and not empty)
        if (partnerName && partnerName.trim() !== '') {
          return partnerName;
        }

        // Fallback: Configuration.Name
        return configName ?? '';
      },
      valueSetter: (params: any) => {
        // ✅ Write the value back to CartItem.SelectedOffer.Partner.Name
        if (!params.data.CartItem) {
          params.data.CartItem = {};
        }
        if (!params.data.CartItem.SelectedOffer) {
          params.data.CartItem.SelectedOffer = {};
        }
        if (!params.data.CartItem.SelectedOffer.Partner) {
          params.data.CartItem.SelectedOffer.Partner = {};
        }
        params.data.CartItem.SelectedOffer.Partner.Name = params.newValue;
        return true;
      }
    },
    {
      headerName: this.getPurchaseHeader(),
      field: 'purchase',
      editable: true, // ✅ Editable like List-Price, Discount, Final-Price (validation in onCellValueChanged)
      singleClickEdit: true,  // ✅ Edit on single click
      minWidth: 95,
      maxWidth: 140,
      flex: 1.7,
      suppressMenu: true,
      valueGetter: (params: any) => {
        const listPrice = params.data?.CartItem?.SelectedOffer?.ListPrice;
        return (listPrice != null && listPrice !== '') ? listPrice : '';
      },
      valueSetter: (params: any) => {
        // ✅ Write the value back to CartItem.SelectedOffer.ListPrice
        // This allows AG Grid to update the cell value immediately
        if (!params.data.CartItem) {
          params.data.CartItem = {};
        }
        if (!params.data.CartItem.SelectedOffer) {
          params.data.CartItem.SelectedOffer = {};
        }
        params.data.CartItem.SelectedOffer.ListPrice = params.newValue;
        return true; // ✅ Tell AG Grid the value was set successfully
      },
      cellStyle: (params: any) => {
        const purchasePrice = params.data?.CartItem?.SelectedOffer?.ListPrice;
        const finalPrice = params.data?.CartItem?.Price?.UIUnitPrice;

        if (purchasePrice != null && finalPrice != null && purchasePrice > 0 && finalPrice > 0) {
          if (purchasePrice < finalPrice) {
            return { color: '#32aa3a' }; // Green - good margin
          } else {
            return { color: '#d32f2f', fontWeight: 'bold' }; // Red + Bold - no margin or loss
          }
        }
        return null;
      }
    },
    {
      headerName: 'PD',
      field: 'purchaseDiscount',
      editable: true,
      singleClickEdit: true,
      minWidth: 75,
      maxWidth: 85,
      flex: 0.6,
      suppressMenu: true,
      type: 'rightAligned',
      valueGetter: (params: any) => {
        const pdValue = params.data?.CartItem?.Price?.PurchaseDiscount?.Value;
        return pdValue != null ? pdValue : null;
      },
      valueSetter: (params: any) => {
        if (!params.data.CartItem) params.data.CartItem = {};
        if (!params.data.CartItem.Price) params.data.CartItem.Price = {};
        if (!params.data.CartItem.Price.PurchaseDiscount) params.data.CartItem.Price.PurchaseDiscount = {};
        params.data.CartItem.Price.PurchaseDiscount.Value = params.newValue;
        return true;
      },
      valueFormatter: (params: any) => {
        if (params.value == null) return '';
        return `${Number(params.value).toFixed(2)}%`;
      },
      cellStyle: (params: any) => {
        if (params.value != null && params.value < 0) {
          return { color: '#d32f2f', fontWeight: 'bold' };
        }
        return null;
      }
    },
    {
      headerName: 'Currency',
      field: 'purchaseCurrency',
      editable: false,
      minWidth: 50,
      maxWidth: 60,
      flex: 0.5,
      suppressMenu: true,
      cellStyle: { cursor: 'pointer', textAlign: 'center' },
      valueGetter: (params: any) => {
        // ✅ Display PurchaseCurrency (the currency we buy from supplier), NOT Currency (selling currency)
        const selectedOffer = params.data?.CartItem?.SelectedOffer;
        if (!selectedOffer) return '';

        // ✅ If SelectedOffer.PurchaseCurrency is not set, try to get from Product.ListPriceOffers[0].PurchaseCurrency
        if (!selectedOffer.PurchaseCurrency) {
          const listPriceOfferPurchaseCurrency = params.data?.CartItem?.Product?.ListPriceOffers?.[0]?.PurchaseCurrency;
          if (listPriceOfferPurchaseCurrency) {
            selectedOffer.PurchaseCurrency = JSON.parse(JSON.stringify(listPriceOfferPurchaseCurrency));
          } else if (selectedOffer.Currency) {
            // ✅ Fallback: deep copy Currency to PurchaseCurrency to avoid reference issues
            selectedOffer.PurchaseCurrency = JSON.parse(JSON.stringify(selectedOffer.Currency));
          }
        }

        return selectedOffer.PurchaseCurrency?.Symbol || '';
      },
      onCellClicked: (params: any) => {
        const rowIndex = params.rowIndex;
        const cellElement = params.event?.target?.closest('.ag-cell') as HTMLElement | null;
        if (!cellElement) return;

        // ✅ Use fixed positioning (viewport coordinates) so z-index works with fixed overlay
        const cellRect = cellElement.getBoundingClientRect();
        const top = cellRect.bottom;
        const left = cellRect.left;
        const width = Math.max(cellRect.width, 120);

        this.purchaseCurrencyDropdownPosition = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
        this.purchaseCurrencyRowIndex = rowIndex;
        this.showPurchaseCurrencyDropdown = true;
      }
    },
    {
      headerName: 'HSCode',
      field: 'hscode',
      editable: true, // ✅ Editable (validation in onCellValueChanged restricts to red rows)
      singleClickEdit: true,  // ✅ Edit on single click
      minWidth: 70,
      maxWidth: 90,
      flex: 0.6,
      suppressMenu: true,
      valueGetter: (params: any) => {
        const hsCode = params.data?.CartItem?.Product?.Stats?.HSCode;
        return hsCode ?? '';
      },
      valueSetter: (params: any) => {
        // ✅ Write the value back to CartItem.Product.Stats.HSCode
        if (!params.data.CartItem) {
          params.data.CartItem = {};
        }
        if (!params.data.CartItem.Product) {
          params.data.CartItem.Product = {};
        }
        if (!params.data.CartItem.Product.Stats) {
          params.data.CartItem.Product.Stats = {};
        }
        params.data.CartItem.Product.Stats.HSCode = params.newValue;
        return true; // ✅ Tell AG Grid the value was set successfully
      }
    },
    {
      headerName: 'COO',
      field: 'coo',
      editable: true,
      singleClickEdit: true,  // ✅ Single-click to edit
      minWidth: 50,
      maxWidth: 70,
      flex: 0.5,
      suppressMenu: true,
      cellRenderer: (params: any) => this.COOCellRenderer(params)
    },
    {
      headerName: 'Weight',
      field: 'weight',
      editable: true,  // ✅ Make editable as text (Admin Grid)
      singleClickEdit: true,  // ✅ Edit on single click instead of double click
      minWidth: 80,
      maxWidth: 110,
      flex: 0.7,
      suppressMenu: true
    },
    // 🔵 PDF icon column (header shows icon; rows show light-blue icon only if DatasheetLink exists)
    {
      headerName: '',
      field: 'datasheet',
      minWidth: 35,
      maxWidth: 50,
      flex: 0.4,
      suppressMenu: true,
      suppressSorting: true,
      editable: false,
      suppressSizeToFit: true,
      headerComponentParams: {
        template: `
          <div class="ag-cell-label-container" role="presentation" style="justify-content:center;">
            <span class="ag-header-cell-text">
              <i class="material-icons pdf-header-icon" title="Datasheet" style="font-size:17px;">file_present</i>
            </span>
          </div>
        `
      },
      cellRenderer: (p: any) => {
        const link = p?.data?.CartItem?.Product?.Stats?.DatasheetLink;
        const hasLink = typeof link === 'string' && link.trim().length > 0;
        if (!hasLink) return ''; // no button if no link

        // escape quotes to avoid breaking the attribute
        const safeHref = String(link).replace(/"/g, '&quot;');

        return `
          <button
            class="pdf-datasheet-button material-icon-trigger"
            data-row="${p.rowIndex}"
            data-href="${safeHref}"
            title="Open datasheet"
            aria-label="Open datasheet">
            <i class="material-icons pdf-cell-icon" style="font-size:17px;margin-right:7px;">file_present</i>
          </button>
        `;
      }
    },
    {
      headerName: '',
      field: 'delete',
      minWidth: 30,
      maxWidth: 35,
      width: 32,
      suppressMenu: true,
      suppressSorting: true,
      editable: false,
      suppressSizeToFit: true,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
      cellRenderer: (params: any) => {
        const cartItem = params.data?.CartItem;
        const statusId = cartItem?.CartItemStatus?.ID;
        const rowIndex = params.rowIndex;

        // ✅ Same principle as popup: show button when CartItemStatus.ID is not Empty(-2) and not AwaitingSelection(-1)
        if (statusId == null || statusId === -2 || statusId === -1) {
          return '';
        }

        // ✅ Red Row = CartItemStatus.ID === 0
        const isRedRow = statusId === 0;

        // ✅ New Offer = SelectedOffer.ID is 0 or null/undefined
        const offerId = cartItem?.SelectedOffer?.ID || 0;
        const isNewOffer = offerId === 0;

        // ✅ Show ONLY ONE button at a time (mutually exclusive):
        // Green button: For red rows OR new offers (Save as New)
        // Blue button: For existing offers (Save/Update to DB)
        const showGreenButton = isRedRow || isNewOffer;

        return `<div style="display: flex; gap: 4px; align-items: center; justify-content: center;">
                  ${showGreenButton ?
                    `<div class="save-to-db-icon save-new-icon" data-row="${rowIndex}" data-action="save-new" style="width: 18px; height: 18px; background-color: #4CAF50; border-radius: 3px; display: flex; align-items: center; justify-content: center; cursor: pointer;" title="Save as New">
                      <i class="material-icons" style="font-size: 14px; color: white;">add</i>
                    </div>`
                    :
                    `<div class="save-to-db-icon save-update-icon" data-row="${rowIndex}" data-action="save-update" style="width: 18px; height: 18px; background-color: #2196F3; border-radius: 3px; display: flex; align-items: center; justify-content: center; cursor: pointer;" title="Save/Update to DB">
                      <i class="material-icons" style="font-size: 14px; color: white;">save</i>
                    </div>`
                  }
                </div>`;
      },
      onCellClicked: (params: any) => {
        const cartItem = params.data?.CartItem;
        const rowIndex = params.rowIndex;
        const target = params.event?.target as HTMLElement;

        // Find which button was clicked
        const saveIcon = target.closest('.save-to-db-icon') as HTMLElement;
        if (!saveIcon) return;

        const action = saveIcon.getAttribute('data-action');
        console.log('🟣 [GRID BUTTON CLICKED - Admin Grid] Row:', rowIndex, 'Action:', action);

        if (cartItem?.CartItemStatus) {
          // ✅ Check if multiple rows are selected
          const selectedItems: { cartItem: any, rowIndex: number }[] = [];
          this.gridApi?.forEachNode((node: any) => {
            const nodeCartItem = node.data?.CartItem;
            if (nodeCartItem?.IsSelected === true && nodeCartItem?.CartItemStatus?.ID >= 0) {
              selectedItems.push({ cartItem: nodeCartItem, rowIndex: node.rowIndex });
            }
          });

          // ✅ If multiple rows selected, use BULK endpoint
          if (selectedItems.length > 1) {
            console.log(`🟣 [ADMIN GRID] Multiple rows selected (${selectedItems.length}) - calling BulkSaveAdminQuotationProducts`);
            this.BulkSaveAdminQuotationProducts(selectedItems);
          } else {
            // ✅ Single row - use individual endpoint
            if (action === 'save-new') {
              console.log('🟢 [GREEN BUTTON] Save as New - calling SaveAdminQuotationProduct');
              this.SaveAdminQuotationProduct(cartItem, rowIndex);
            } else if (action === 'save-update') {
              console.log('🔵 [BLUE BUTTON] Save/Update - calling SaveAdminQuotationProduct');
              this.SaveAdminQuotationProduct(cartItem, rowIndex);
            }
          }
        }
      }
    }
  ];

  rowData: any[] = Array.from({ length: 50 }, (_, i) => ({
    index: i + 1,
    brand: '',
    partNumber: '',
    name: '',
    quantity: null,
    price: null,
    total: null,
    eta: '',
    hscode: '',
    coo: '',
    weight: '',
    CartItem: {}
  }));
  defaultColDef = {
    resizable: true,
    // sortable: true,
    // filter: true,
    flex: 1,
    minWidth: 100,
    cellClass: (params: any) => {
      return params.colDef.field === 'name' ? 'cell-white' : 'cell-gray';
    }
  };

  gridOptions = {
    rowHeight: 30,
    headerHeight: 30,
    enableRangeSelection: true,
    suppressClipboardApi: true,
    enableFillHandle: true,
    enableClipboard:true,
    suppressClipboardPaste: false,
    suppressLastEmptyLineOnPaste: true,
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    stopEditingWhenCellsLoseFocus: true,
    singleClickEdit: false,
    undoRedoCellEditing: true,
    tooltipShowDelay: 0,      // Show immediately
    tooltipHideDelay: 10000,   // Optional: auto-hide after 2s
    enableBrowserTooltips: true,
    context: {
      componentParent: this
    },
    undoRedoCellEditingLimit: 20,
    domLayout: 'autoHeight',
    getContextMenuItems: (params: any) => {
      const cellRanges = params.api.getCellRanges();
      const hasSelection = cellRanges && cellRanges.length > 0;

      const menuItems: any[] = [
        {
          name: 'Paste',
          action: () => {
            this.triggerPasteFromContextMenu(params);
          },
          icon: '<i class="material-icons" style="font-size: 16px;">content_paste</i>',
          shortcut: 'Ctrl+V'
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'separator'
      ];

      // ✅ Insert Row Above / Below options
      const clickedRowIndex = params.node?.rowIndex;
      if (clickedRowIndex != null && clickedRowIndex >= 0) {
        menuItems.push({
          name: 'Insert Row Above',
          action: () => {
            this.insertRowAtIndex(clickedRowIndex);
          },
          icon: '<i class="material-icons" style="font-size: 16px; color: #4caf50;">arrow_upward</i>'
        });
        menuItems.push({
          name: 'Insert Row Below',
          action: () => {
            this.insertRowAtIndex(clickedRowIndex + 1);
          },
          icon: '<i class="material-icons" style="font-size: 16px; color: #4caf50;">arrow_downward</i>'
        });
        menuItems.push('separator');
      }

      // Add delete option if there's a selection
      if (hasSelection) {
        menuItems.push({
          name: 'Delete Selected Rows',
          action: () => {
            this.deleteSelectedRows(cellRanges);
          },
          icon: '<i class="material-icons" style="font-size: 16px; color: #f44336;">delete</i>',
          shortcut: 'Delete'
        });
        menuItems.push('separator');
      }

      menuItems.push('export');

      return menuItems;
    },
    /** <-- replace getRowClass with this */
    rowClassRules: {
      // GridAwaitingSelection: p => p.data?.CartItem?.CartItemStatus?.ID === -1,
      GridNotValid:         p => p.data?.CartItem?.CartItemStatus?.ID === 0,
      GridValid:            (p) => {
        const isStock = p.data?.CartItem?.SelectedOffer?.IsStockOffer === true;
        const hasQty = p.data?.CartItem?.SelectedOffer?.StockQuantity > 0;
        const isNotMfgStock = p.data?.CartItem?.SelectedOffer?.IsManufacturerStockOffer !== true;
        const result = isStock && hasQty && isNotMfgStock;
        console.log(`🟢 GridValid check: isStock=${isStock}, hasQty=${hasQty}, isNotMfgStock=${isNotMfgStock}, result=${result}`);
        return result;
      },
      GridAdminPriceEdit:   p => p.data?.CartItem?.AdminPriceEdit === true,
    },
    onGridReady: (params: any) => {
      params.api.sizeColumnsToFit();
      // Inject header checkbox after grid is ready
      setTimeout(() => {
        const headerCell = document.querySelector('.quotation-index-header');
        if (headerCell) {
          const headerText = headerCell.querySelector('.ag-header-cell-text');
          if (headerText) {
            headerText.innerHTML = `<input type="checkbox" class="quotation-header-checkbox" />`;
            const checkbox = headerText.querySelector('.quotation-header-checkbox') as HTMLInputElement;
            if (checkbox) {
              checkbox.checked = this.headerCheckboxSelected;
              checkbox.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleAllSelected(params.api);
              });
            }
          }
        }
      }, 100);
    },
    onRangeSelectionChanged: (event: any) => {
      if (event.finished) {
        const cellRanges = event.api.getCellRanges();
        if (cellRanges && cellRanges.length > 0) {
          const selectedRowIndices = new Set<number>();
          cellRanges.forEach((range: any) => {
            const startRow = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
            const endRow = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);
            for (let i = startRow; i <= endRow; i++) {
              selectedRowIndices.add(i);
            }
          });
          // Only update IsSelected if more than 1 row is selected (actual multi-select)
          if (selectedRowIndices.size > 1) {
            selectedRowIndices.forEach((rowIndex: number) => {
              const rowNode = event.api.getDisplayedRowAtIndex(rowIndex);
              if (rowNode?.data?.CartItem) {
                rowNode.data.CartItem.IsSelected = true;
              }
            });
            event.api.refreshCells({ columns: ['index'], force: true });
          }
        }
      }
    },
    onCellValueChanged: (event: any) => this.onCellValueChanged(event),
    // ✅ Grid-level cell click: deselect all if clicking on an unselected row (not on checkbox)
    onCellClicked: (event: any) => {
      const target = event.event?.target as HTMLElement;
      // Don't interfere with checkbox clicks, button clicks, or material icons
      if (target?.classList?.contains('cart-item-checkbox') ||
          target?.closest('button') ||
          target?.closest('.material-icons')) {
        return;
      }
      // If clicking on a row that is NOT selected, deselect all selected rows
      const clickedRowIsSelected = event.data?.CartItem?.IsSelected === true;
      if (!clickedRowIsSelected && this.hasSelectedCartItems()) {
        this.deselectAllCartItems();
      }
    },
    onPasteEnd: (params: any) => this.handlePaste(params),
    // ✅ Intercept clipboard data BEFORE AG Grid pastes - add rows if needed
    processDataFromClipboard: (params: any) => {
      const data = params.data; // 2D array of clipboard data
      if (!data || data.length === 0) return data;

      // Get focused cell to determine start row
      const focusedCell = this.gridApi?.getFocusedCell();
      const startRowIndex = focusedCell?.rowIndex ?? 0;

      // Calculate how many rows we need
      const pasteRowCount = data.length;
      const requiredRowCount = startRowIndex + pasteRowCount;
      const currentRowCount = this.gridApi?.getDisplayedRowCount() ?? 0;

      // ✅ Add more rows if needed BEFORE AG Grid pastes
      if (requiredRowCount > currentRowCount) {
        const rowsToAdd = requiredRowCount - currentRowCount;
        console.log(`📋 Adding ${rowsToAdd} rows before paste (need ${requiredRowCount}, have ${currentRowCount})`);
        this.addMoreRows(rowsToAdd);
      }

      return data; // Return unmodified data - we just needed to add rows
    },
    getRowClass: (params: any) => {
      const CartItem = params.data?.CartItem;
      if (!CartItem) return null;
  
      return null; // No class
    },
    suppressKeyboardEvent: (params) => {
      const e = params.event as KeyboardEvent;

      // Helper: are we currently editing inside an input-like element?
      const tgt = e.target as HTMLElement | null;
      const isInputish =
        params.editing ||
        (tgt &&
          (tgt.tagName === 'INPUT' ||
           tgt.tagName === 'TEXTAREA' ||
           (tgt as any).isContentEditable === true));

      const isPaste =
        (e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === 'v' || e.code === 'KeyV');

      if (isPaste) {
        // ✅ Mark as Ctrl+V paste (keyboard paste)
        this.isPasting = true;
        this.pasteEventType = 2; // eB2BUserTrackingEventType.CopyPasteKeyBoard

        if (!isInputish) {
          // Not inside an editor: commit any edit, then let AG Grid handle multi-cell paste
          params.api.stopEditing();
          return false; // allow paste to proceed to the grid
        }
        // Inside an editor: let the browser paste into the input
        return false; // don't block it
      }

      // ✅ Handle TAB key when editing - stop editing and let AG Grid navigate
      const isTab = e.key === 'Tab';
      if (isTab && params.editing) {
        // Stop editing and let AG Grid's tabToNextCell handle navigation
        params.api.stopEditing();
        return false; // Let AG Grid handle TAB navigation
      }

      // Handle DELETE key for selected rows
      const isDelete = e.key === 'Delete' || e.key === 'Backspace';
      if (isDelete && !isInputish) {
        // Get selected cell ranges
        const cellRanges = params.api.getCellRanges();
        if (cellRanges && cellRanges.length > 0) {
          this.deleteSelectedRows(cellRanges);
          return true; // suppress the default delete behavior
        }
      }

      // Let everything else behave normally
      return false;
    },
    onPasteStart: (params) => {
      console.log('🔍 onPasteStart triggered', params);
    },
    tabToNextCell: (params) => {
      const { previousCellPosition } = params;

      if (!previousCellPosition) {
        return previousCellPosition;
      }

      const rowIndex = previousCellPosition.rowIndex;
      const colId = previousCellPosition.column.getColId();
      const totalRows = params.api.getDisplayedRowCount();

      // ✅ Check if we're on the last row - stay on current cell
      if (rowIndex >= totalRows - 1) {
        return previousCellPosition; // Stay on current cell, don't exit grid
      }

      // ✅ Special handling for BRAND column - only navigate to editable cells (RED ROWS)
      if (colId === 'brand') {
        // ✅ Check if current row is a RED ROW (editable brand cell)
        const currentRowNode = params.api.getDisplayedRowAtIndex(rowIndex);
        const currentStatusId = currentRowNode?.data?.CartItem?.CartItemStatus?.ID;

        // ✅ Only apply special logic if we're currently IN an editable brand cell (RED ROW)
        if (currentStatusId === 0) {
          // ✅ Find next RED ROW (editable brand cell)
          for (let i = rowIndex + 1; i < totalRows; i++) {
            const nextRowNode = params.api.getDisplayedRowAtIndex(i);
            if (nextRowNode && nextRowNode.data && nextRowNode.data.CartItem) {
              const statusId = nextRowNode.data.CartItem.CartItemStatus?.ID;
              // ✅ Found a RED ROW (editable)
              if (statusId === 0) {
                return {
                  rowIndex: i,
                  column: previousCellPosition.column,
                  rowPinned: previousCellPosition.rowPinned
                };
              }
            }
          }
          // ✅ No more RED ROWS found - stay on current cell
          return previousCellPosition;
        }
        // ✅ If not in a RED ROW, fall through to default behavior
      }

      // ✅ For other columns (or non-editable brand cells), use default behavior
      // ✅ Check if next row has data (not empty)
      const nextRowNode = params.api.getDisplayedRowAtIndex(rowIndex + 1);

      if (!nextRowNode || !nextRowNode.data || !nextRowNode.data.CartItem) {
        return previousCellPosition; // Stay on current cell, don't exit grid, don't trigger infinite loop
      }

      // ✅ Tab ALWAYS goes DOWN in the same column (for all columns)
      return {
        rowIndex: rowIndex + 1,
        column: previousCellPosition.column,
        rowPinned: previousCellPosition.rowPinned
      };
    },
    navigateToNextCell: (params) => {
      const { previousCellPosition, key } = params;
      if (!previousCellPosition) return null;
  
      const colId = previousCellPosition.column.getColId();
      const rowIndex = previousCellPosition.rowIndex;
  
      // Handle Enter key only for 'quantity' column
      if (key === 'ArrowDown' || key === 'Enter') {
        if (colId === 'quantity') {
          return {
            rowIndex: rowIndex + 1,
            column: previousCellPosition.column,
            rowPinned: previousCellPosition.rowPinned
          };
        }
      }
  
      // Fallback to default
      return null;
    }
  };
  
  private gridApi!: GridApi;
  private columnApi!: ColumnApi;

  // ✅ Store actual column widths from ag-Grid for ExcelExportControlRow alignment
  columnWidths: { [key: string]: number } = {};

  /**
   * ✅ Sync column widths from ag-Grid to ExcelExportControlRow
   * Called after grid ready and on resize
   */
  syncColumnWidths(): void {
    if (!this.columnApi) return;

    const allColumns = this.columnApi.getAllDisplayedColumns();
    if (!allColumns || allColumns.length === 0) return;

    const widths: { [key: string]: number } = {};
    allColumns.forEach((col: any) => {
      const field = col.getColDef().field;
      if (field) {
        widths[field] = col.getActualWidth();
      }
    });

    this.columnWidths = widths;
    console.log('✅ Column widths synced:', this.columnWidths);
  }

  /**
   * ✅ Get width style for ExcelExportControlRow cell
   */
  getColumnWidth(field: string): { [key: string]: string } {
    const width = this.columnWidths[field];
    if (width) {
      return { 'width': width + 'px', 'min-width': width + 'px', 'max-width': width + 'px', 'flex': 'none' };
    }
    return {};
  }

  // ✅ Track header checkbox state for multi-select
  headerCheckboxSelected: boolean = false;

  toggleAllSelected(api: any) {
    this.headerCheckboxSelected = !this.headerCheckboxSelected;
    api.forEachNode((node: any) => {
      if (node.data?.CartItem) {
        node.data.CartItem.IsSelected = this.headerCheckboxSelected;
      }
    });
    api.refreshCells({ columns: ['index'], force: true });
    this.updateHeaderCheckbox(api);
  }

  updateHeaderCheckbox(api: any) {
    const headerCheckbox = document.querySelector('.quotation-header-checkbox') as HTMLInputElement;
    if (headerCheckbox) {
      headerCheckbox.checked = this.headerCheckboxSelected;
    }
  }

  // ✅ Check if any cart items are selected (for overlay visibility)
  hasSelectedCartItems(): boolean {
    let hasSelected = false;
    this.gridApi?.forEachNode((node: any) => {
      if (node.data?.CartItem?.IsSelected === true) {
        hasSelected = true;
      }
    });
    return hasSelected;
  }

  // ✅ Get all selected cart items (for bulk operations)
  getSelectedCartItems(): any[] {
    const selectedItems: any[] = [];
    this.gridApi?.forEachNode((node: any) => {
      if (node.data?.CartItem?.IsSelected === true) {
        selectedItems.push({ data: node.data, node: node, rowIndex: node.rowIndex });
      }
    });
    return selectedItems;
  }

  // ✅ Deselect all cart items (called by overlay click)
  deselectAllCartItems(): void {
    this.gridApi?.forEachNode((node: any) => {
      if (node.data?.CartItem) {
        node.data.CartItem.IsSelected = false;
      }
    });
    this.headerCheckboxSelected = false;
    this.gridApi?.refreshCells({ columns: ['index'], force: true });
    this.updateHeaderCheckbox(this.gridApi);
  }

  /**
   * Inject the header checkbox into the index column header
   */
  injectHeaderCheckbox() {
    const headerCell = document.querySelector('.quotation-index-header');
    if (headerCell) {
      const headerText = headerCell.querySelector('.ag-header-cell-text');
      if (headerText && !headerText.querySelector('.quotation-header-checkbox')) {
        headerText.innerHTML = `<input type="checkbox" class="quotation-header-checkbox" />`;
        const checkbox = headerText.querySelector('.quotation-header-checkbox') as HTMLInputElement;
        if (checkbox) {
          checkbox.checked = this.headerCheckboxSelected;
          checkbox.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleAllSelected(this.gridApi);
          });
        }
      }
    }
  }

  /**
   * Get List-Price header with currency symbol
   */
  getListPriceHeader(): string {
    const currencySymbol = this.selectedCurrency?.Symbol || '';
    return currencySymbol ? `${currencySymbol} List-Price` : 'List-Price';
  }

  /**
   * Get Final-Price header with currency symbol
   */
  getFinalPriceHeader(): string {
    const currencySymbol = this.selectedCurrency?.Symbol || '';
    return currencySymbol ? `${currencySymbol} Final-Price` : 'Final-Price';
  }

  /**
   * Get Total header with currency symbol
   */
  getTotalHeader(): string {
    const currencySymbol = this.selectedCurrency?.Symbol || '';
    return currencySymbol ? `${currencySymbol} Total` : 'Total';
  }

  /**
   * Get Purchase header (no currency symbol - currency is in separate column)
   */
  getPurchaseHeader(): string {
    return 'Purchase';
  }

  priceCellRenderer(params: any): string {
    const price = params.value ?? '';
    const cartItem = params.data?.CartItem;
    const translate = params.context?.translate;
    const rowIndex = params.rowIndex;

    let showTriangle = false;
    let tooltipText = '';
    let priceClass = '';

    // Check for MOQ-based special offer
    let moq = null;
    if (Array.isArray(cartItem?.SearchResults)) {
      const special = cartItem.SearchResults.find((x: any) => x.SpecialOffer === true && x.MOQ > 1 && x.FKBrandID == cartItem?.Product?.Brand?.ID);
      if (special) {
        if(special?.Price?.UIUnitPrice < cartItem?.SelectedOffer?.SellPrice?.UIUnitPrice){
          moq = special?.MOQ ?? 0;
          showTriangle = true;

          const unitPrice = special?.Price?.UIUnitPrice;
          const currencyName = special?.Price?.Currency?.Symbol ?? special?.Price?.Currency?.Name ?? '';
          const priceStr = typeof unitPrice === 'number' && isFinite(unitPrice) ? unitPrice.toFixed(2) : (unitPrice ?? '');

          tooltipText = `Special: ${priceStr} ${currencyName} - min. x${moq}`;
        }
      }
    }

    // Check for SpecialPriceList flag
    const isSpecialList = cartItem?.SelectedOffer?.Configuration?.SpecialPriceList === true;
    if (isSpecialList) {
      showTriangle = true;
      if (!tooltipText) {
        tooltipText = translate?.instant('IncreaseQuantityForSpecialPrice') ?? 'Special price available';
      }
      priceClass = 'special-price-highlight';
    }

    const triangle = showTriangle
    ? `<button class="price-corner-triangle dropdown-trigger"
               data-row="${rowIndex}"
               data-type="productSpecials"
               data-tooltip="${tooltipText}"
               aria-label="${tooltipText}"></button>`
    : '';

    return `
      <div class="price-cell-wrapper">
        <span class="${priceClass}">${price}</span>
        ${triangle}
      </div>
    `;
  }

  ETACellRenderer(params: any): string {
    const rowIndex = params.rowIndex;
    const cartItem = params.data?.CartItem;

    // Build ETA text + "(Stock)" just like your old valueGetter
    const base = (params.data?.eta || '').toString().trim();
    const isStock = cartItem?.SelectedOffer?.IsStockOffer;
    const isManufacturerStock = cartItem?.SelectedOffer?.IsManufacturerStockOffer === true;
    // Only show "(Stock)" if it's stock AND NOT manufacturer stock
    const showStock = isStock && isManufacturerStock !== true;
    const etaText = (base + (showStock ? (base ? ' ' : '') + '(Stock)' : '')).trim() || '------';

    // ✅ Trusted Manufacturer Lead Time feature
    const isTrusted = cartItem?.Product?.Stats?.TrustedManufacturerLeadTime === true;

    const trustedIcon = isTrusted
      ? `<i class="material-icons trusted-icon"
            style="color: #32aa3a; font-size: 18px; margin-right: 6px; vertical-align: middle; cursor: help;">verified</i>`
      : '';

    // ✅ Confirmed Lead Time icon (red X or green check)
    const leadTime = cartItem?.SelectedOffer?.LeadTime;
    const hasLeadTime = leadTime != null && leadTime?.Units > 0;
    const confirmed = leadTime?.Confirmed === true;

    const confirmTooltip = confirmed ? this.translate.instant('LeadTimeConfirmed') : this.translate.instant('LeadTimeNotConfirmed');
    const confirmIcon = hasLeadTime
      ? `<i class="material-icons" data-action="toggleConfirm" title="${confirmTooltip}" style="position:absolute;right:5px;top:50%;transform:translateY(-50%);font-size:14px;cursor:pointer;color:${confirmed ? '#4caf50' : '#f44336'};">${confirmed ? 'check_circle' : 'cancel'}</i>`
      : '';

    return `<span style="display:flex;align-items:center;">${trustedIcon}${etaText}</span>${confirmIcon}`;
  }

  onETAConfirmClick(params: any): void {
    const event = params.event as MouseEvent;
    const target = event?.target as HTMLElement;
    if (!target || !target.closest('[data-action="toggleConfirm"]')) return;

    const clickedCartItem = params.data?.CartItem;
    if (!clickedCartItem?.SelectedOffer?.LeadTime) return;

    const newValue = !clickedCartItem.SelectedOffer.LeadTime.Confirmed;

    // Get all selected rows (or just the clicked one)
    const selectedRows = this.getSelectedCartItems();
    const isBulk = selectedRows.length > 1;
    const rowsToUpdate = isBulk ? selectedRows : [{ data: params.data }];

    rowsToUpdate.forEach((row: any) => {
      const ci = row.data?.CartItem || row.CartItem;
      if (ci?.SelectedOffer?.LeadTime && ci.SelectedOffer.LeadTime.Units > 0) {
        ci.SelectedOffer.LeadTime.Confirmed = newValue;
      }
    });

    this.gridApi.refreshCells({ force: true });

    // Trigger save
    this.UpdateCart();
  }

  // add to the component class
  BrandCellRenderer(params: any): string {
    const brand = (params.value ?? '').toString().trim();
    const rowIndex = params.rowIndex;
    const cartItem = params.data?.CartItem;

    const statusId = cartItem?.CartItemStatus?.ID;

    // ✅ RED ROWS (statusId === 0): NO BUTTON - editable text field for brand identification
    // ✅ Other rows: Show button for brand alternatives (awaiting selection / valid w/ collateral)
    const showExistingButton =
      cartItem?.WasSearchedFor == true &&
      (statusId === -1 || statusId === 1);

    const existingBtn = showExistingButton
      ? `<button class="dropdown-trigger dropdown-trigger-brand material-icon-trigger"
                   data-row="${rowIndex}"
                   data-type="brand-collateral"
                   title="View brand alternatives">
           <i class="material-icons">expand_more</i>
         </button>`
      : '';

    return `
      <span class="brand-cell-content">
        <span class="brand-text">${brand}</span>
        <span class="cell-actions">
          ${existingBtn}
        </span>
      </span>
    `;
  }

  // ✅ Supplier cell renderer - buttons for red rows AND green rows (button triggers popup via global click handler)
  SupplierCellRenderer(params: any): string {
    const supplier = (params.value ?? '').toString().trim();
    const rowIndex = params.rowIndex;
    const cartItem = params.data?.CartItem;
    const statusId = cartItem?.CartItemStatus?.ID;
    const partner = cartItem?.SelectedOffer?.Partner;
    const hasPartner = partner && partner.ID && partner.ID.toString().length > 0;

    const isRedRow = statusId === 0;
    const isGreenRow = statusId === 1;

    let button = '';

    // ✅ For RED ROWS: Show partner search or clear button
    if (isRedRow) {
      if (hasPartner) {
        // Show close icon (X) to clear partner - styled exactly like delete column red X
        button = `<i class="material-icons clear-supplier-button"
                     data-row="${rowIndex}"
                     data-type="clear-supplier"
                     style="font-size: 18px; color: #f44336; cursor: pointer;"
                     title="Clear supplier">close</i>`;
      } else {
        // Show dropdown button to search for partner
        button = `<button class="dropdown-trigger dropdown-trigger-supplier material-icon-trigger"
                     data-row="${rowIndex}"
                     data-type="supplier-search"
                     title="Search for supplier">
             <i class="material-icons">expand_more</i>
           </button>`;
      }
    }

    // ✅ For GREEN ROWS: Show dropdown button to open supplier alternatives popup (like name column)
    if (isGreenRow) {
      button = `<button class="dropdown-trigger material-icon-trigger"
                        style="position: absolute; right: 5px; top: 2.5px;"
                        data-row="${rowIndex}"
                        data-type="supplierAlternatives"
                        title="View Supplier Alternatives">
                  <i class="material-icons">expand_more</i>
                </button>`;
    }

    // ✅ Return with button - uses same pattern as name column (button handled by global click listener)
    return `<span class="supplier-text">${supplier}</span>${button}`;
  }

  COOCellRenderer(params: any): string {
    const coo = (params.value ?? '').toString().trim();
    const rowIndex = params.rowIndex;
    const cartItem = params.data?.CartItem;
    const statusId = cartItem?.CartItemStatus?.ID;

    // ✅ Make clickable for RED ROWS (CartItemStatus.ID === 0) AND VALID ROWS (CartItemStatus.ID === 1)
    const isRedRow = statusId === 0;
    const isValidRow = statusId === 1;
    const isClickable = isRedRow || isValidRow;

    if (isClickable) {
      // ✅ Make entire cell clickable (whether COO is empty or has value)
      return `
        <span class="coo-cell-clickable"
              data-row="${rowIndex}"
              data-type="coo-search"
              style="display: flex; align-items: center; width: 100%; height: 100%; cursor: pointer;"
              title="Click to select country of origin">
          <span class="coo-text">${coo}</span>
        </span>
      `;
    }

    // ✅ Non-clickable rows: just display the text
    return `<span class="coo-text">${coo}</span>`;
  }

  WeightCellRenderer(params: any): string {
    const weight = (params.value ?? '').toString().trim();

    // ✅ Just display the text - no clickable container (editable via single-click editing)
    return `<span class="weight-text">${weight}</span>`;
  }
  
  NameCellRenderer(params: any): string {
    const eta = params.value?.toString().trim() ?? '';
    const isEmpty = eta.length === 0;
    const rowIndex = params.rowIndex;
    const cartItem = params.data?.CartItem;
    const placeholder = this.namePlaceholder || 'Search product or part number...';

    // 🔸 Show fallback dropdown when in "awaiting selection" state (ID === -1)
    const showFallbackDropdown = cartItem?.CartItemStatus?.ID === -1;

    // ✅ Show product alternatives dropdown when CartItemStatus.ID === 1 (Valid) - for other offers and similar products
    const hasDropdown = cartItem?.CartItemStatus?.ID === 1;
    const dropdownButton = hasDropdown
      ? `<button class="dropdown-trigger material-icon-trigger" style="position: absolute; right: 5px; top: 2.5px;" data-row="${rowIndex}" data-type="productalternatives" title="View Product-Alternatives">
           <i class="material-icons">expand_more</i>
         </button>`
      : '';

    const fallbackButton = showFallbackDropdown
      ? `<button class="dropdown-trigger material-icon-trigger fallbackButtonTrigger" style="position: absolute; right: 5px; top: 2.5px;" data-row="${rowIndex}" data-type="fallback" title="Show UI fallback offers">
           <i class="material-icons">expand_more</i>
         </button>`
      : '';

    // ✅ Plus button for awaiting selection rows - opens Red Row Edit Popup
    const plusButton = showFallbackDropdown
      ? `<button class="dropdown-trigger awaiting-selection-plus-button" style="position: absolute; right: 32px; top: 2.5px;" data-row="${rowIndex}" data-type="awaiting-selection-edit" title="Edit product details">
           <i class="material-icons">add</i>
         </button>`
      : '';

    const displayText = isEmpty
      ? `<span class="name-placeholder">${placeholder}</span>`
      : `<span class="name-text">${eta}</span>`;

    return `${displayText}${dropdownButton}${plusButton}${fallbackButton}`;
  }

  officialListPriceCellRenderer = (params: any): string => {
    const olp = params.data?.CartItem?.Product?.Stats?.StandardListPrice;
    const amount = olp?.UIUnitPrice;

    // If price is missing -> empty string
    if (
      amount == null ||
      (typeof amount !== 'number' && isNaN(Number(amount)))
    ) {
      return '';
    }

    const amtStr = typeof amount === 'number' ? amount.toFixed(2) : String(amount);
    return `<span class="official-list-price">${amtStr}</span>`;
  };
  
  officialDiscountCellRenderer = (params: any): string => {
    const raw = params.data?.CartItem?.Product?.Stats?.StandardListPrice?.Discount?.Value;

    // No info -> empty
    if (raw == null || raw === '') return '';

    // Format number nicely (keep integers, max 2 decimals, trim trailing zeros)
    const n = Number(raw);
    const valStr = Number.isFinite(n)
      ? (n % 1 === 0
          ? String(n)
          : n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'))
      : String(raw).trim();

    return `<span class="official-discount">${valStr}</span>`;
  };

  isHovering = false;

  ngOnInit(): void {
    // ✅ Load admin-selected partner override from localStorage (if exists)
    this.LoadAdminSelectedPartner();

    // ✅ Load toggle states from localStorage
    try {
      const storedRestrictions = localStorage.getItem(this.ADMIN_CLIENT_RESTRICTIONS_KEY);
      if (storedRestrictions !== null) {
        this.applyClientRestrictions = storedRestrictions === 'true';
      }
      const storedStockStatus = localStorage.getItem(this.ADMIN_STOCK_STATUS_KEY);
      if (storedStockStatus !== null) {
        this.stockStatusToggle = storedStockStatus === 'true';
      }
    } catch (error) {
      console.error('Error loading toggle states from localStorage:', error);
    }

    // ✅ Clear old ConfigStatus from localStorage (no longer used)
    try {
      localStorage.removeItem('AdminQuotationConfigStatus');
    } catch (error) {
      console.error('Error clearing old ConfigStatus from localStorage:', error);
    }

    // ✅ Set default ConfigStatus based on grid mode (no localStorage)
    // Admin Grid: VisibleOnlyByAdmin (ID=-1), Employee Grid: Active (ID=1)
    // Note: useAdminGrid is set by isAdministrator() when template renders
    // We retry multiple times to ensure master data is loaded
    setTimeout(() => {
      this.setDefaultConfigStatus();
      this.setDefaultEXWShippingResponsability(); // ✅ Also set default EXW
    }, 300);

    setTimeout(() => {
      this.setDefaultConfigStatus();
      this.setDefaultEXWShippingResponsability(); // ✅ Also set default EXW
    }, 800);

    setTimeout(() => {
      this.setDefaultConfigStatus();
      this.setDefaultEXWShippingResponsability(); // ✅ Also set default EXW
    }, 1500);

    // ✅ Load last used payment/shipment settings if partner already selected
    if (this.parent.B2BPartner?.ID) {
      this.GetPartnerLastB2BPaymentShipmentSettings(this.parent.B2BPartner.ID);
      // ✅ Load country shipping options for the partner
      this.GetCountryShippingOptions();
    }

    // ✅ Load currencies and set default
    this.GetCurrencies();

    // ✅ Load Offer Request Master Data (OrderConsolidationTypes, etc.)
    this.GetOfferMasterDataRequest();

    // ✅ Load Offer Master Data (OrderConsolidationTypes, etc.)
    this.GetOfferMasterData();

    // ✅ Load LeadTimeTypes for Red Row ETA popup
    this.GetLeadTimeTypes();

    // Register PDF export tracking callback with parent
    this.parent.registerPDFExportCallback(() => {
      this.TrackB2BUserGridEvent(4); // eB2BUserTrackingEventType.ExportOfficialOfferPDF
    });

    this.translate.use(this.parent.getLanguage()).subscribe(() => {
      // Load translations at once
      this.translate.get([
        'SearchProductOrPartNumberHere',
        'MOQAdjustmentClientUI',
        'WillBeSavedToDatabase',
        'WillNotBeSavedToDatabase'
      ]).subscribe(translations => {
        this.namePlaceholder = translations['SearchProductOrPartNumberHere'];
        this.MOQAdjustmentTooltip = translations['MOQAdjustmentClientUI'];
        this.specialPriceCellTranslation = translations["IncreaseQuantityForSpecialPrice"];
        this.SaveToDBTooltip = translations['WillBeSavedToDatabase'];
        this.NotSaveToDBTooltip = translations['WillNotBeSavedToDatabase'];

        // Now that translations are ready, proceed
        this.InitializeFilter();
        this.GetB2BHomePageMessage();
        this.CartItemRows = [];

      });
    });

      // ✅ Intercept and count clipboard lines — used only for info
      // document.addEventListener('paste', (event: ClipboardEvent) => {
      //   const text = event.clipboardData?.getData('text/plain') ?? '';
      //   const lines = text.split(/\r?\n/).filter(line => line.trim());
      //   this.pastedLineCount = lines.length;
      // });

    // Debounce paste handling
    this.pasteSubject
        .pipe(debounceTime(250))
        .subscribe(() => {
          const unique = new Map<number, { name: string; quantity: any }>();
          this.pastedRows.forEach(r => unique.set(r.rowIndex, { name: r.name, quantity: r.quantity }));
          this.pastedRows = [];

          const entries = Array.from(unique.entries());
          if (entries.length === 0) return;

          // ✅ Filter to only searchable entries
          const searchableEntries = entries.filter(([rowIndex]) => this.shouldSearch(rowIndex));
          if (searchableEntries.length === 0) return;

          // ✅ Process in batches of 50 to avoid blocking the app
          this.processPasteInBatches(searchableEntries, 50);
        });

        document.addEventListener('copy', this.onCopyHandler.bind(this));
  }

  onCopyHandler(e: ClipboardEvent): void {
    // ✅ SIMPLIFIED: Match list-prices-ag-grid implementation exactly for consistent clipboard behavior
    // The simpler approach: only intercept copy when grid has focus AND a cell range is selected
    if (!this.gridApi || !this.gridApi.getFocusedCell()) return;

    const range = this.gridApi.getCellRanges()?.[0];
    if (!range) return;

    const rowStart = range.startRow.rowIndex;
    const rowEnd = range.endRow.rowIndex;
    const allCols = range.columns;

    // Step 1: Collect all data from selected rows
    const rawRows: string[][] = [];
    const colValuePresence = new Array(allCols.length).fill(false); // Track non-empty columns
    let totalSum = 0; // Track sum of Total column
    let totalColIndex = -1; // Index of 'total' column
    let priceColIndex = -1; // Index of 'price' (Final-Price) column

    // ✅ Helper to get display value for columns that use cellRenderers with nested data
    const getDisplayValue = (colField: string, rowNode: any): string => {
      const data = rowNode?.data;
      const cartItem = data?.CartItem;

      switch (colField) {
        case 'officialListPrice': {
          const amount = cartItem?.Product?.Stats?.StandardListPrice?.UIUnitPrice;
          if (amount == null || (typeof amount !== 'number' && isNaN(Number(amount)))) return '';
          return typeof amount === 'number' ? amount.toFixed(2) : String(amount);
        }
        case 'officialDiscount': {
          const raw = cartItem?.Product?.Stats?.StandardListPrice?.Discount?.Value;
          if (raw == null || raw === '') return '';
          const n = Number(raw);
          return Number.isFinite(n)
            ? (n % 1 === 0 ? String(n) : n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'))
            : String(raw).trim();
        }
        default:
          return (this.gridApi.getValue(this.columnApi.getColumn(colField), rowNode) ?? '').toString().trim();
      }
    };

    for (let i = rowStart; i <= rowEnd; i++) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
      const row: string[] = [];

      allCols.forEach((col, colIndex) => {
        const colField = col.getColDef().field;
        const value = getDisplayValue(colField, rowNode);
        row.push(value);
        if (value.length > 0) {
          colValuePresence[colIndex] = true;
        }

        // Track Total column for sum calculation
        if (colField === 'total') {
          totalColIndex = colIndex;
          const numValue = parseFloat(value.replace(/[^\d.-]/g, ''));
          if (!isNaN(numValue)) {
            totalSum += numValue;
          }
        }

        // Track Final-Price column for "Total:" label
        if (colField === 'price') {
          priceColIndex = colIndex;
        }
      });

      rawRows.push(row);
    }

    // Step 2: Build list of columns to keep (those with at least one non-empty value AND enabled in export checkboxes)
    // Map column fields to their export checkbox states
    const isColumnEnabledForExport = (colField: string): boolean => {
      switch (colField) {
        case 'officialListPrice': return this.includeListPriceInExport;
        case 'officialDiscount': return this.includeDiscountInExport;
        case 'eta': return this.includeETAInExport;
        case 'hscode': return this.includeHSCodeInExport;
        case 'coo': return this.includeCOOInExport;
        case 'weight': return this.includeWeightInExport;
        default: return true; // All other columns are always included
      }
    };

    const includedColIndexes = allCols
      .map((_, idx) => idx)
      .filter(idx => {
        const colField = allCols[idx].getColDef().field;
        const hasValues = colValuePresence[idx];
        const isEnabled = isColumnEnabledForExport(colField);
        // Include column only if it has values AND is enabled in export checkboxes
        return hasValues && isEnabled;
      });

    const includedCols = includedColIndexes.map(idx => allCols[idx]);

    // Find the position of total and price columns in the included columns
    const includedTotalColPos = includedColIndexes.indexOf(totalColIndex);
    const includedPriceColPos = includedColIndexes.indexOf(priceColIndex);

    // Step 3: Build plain text (fallback)
    let plainText = rawRows
      .map(row => includedColIndexes.map(i => row[i]).join('\t'))
      .join('\n');

    // Add total row to plain text
    if (includedTotalColPos >= 0) {
      const totalRow = new Array(includedColIndexes.length).fill('');
      if (includedPriceColPos >= 0) {
        totalRow[includedPriceColPos] = 'Total:';
      }
      totalRow[includedTotalColPos] = totalSum.toFixed(2);
      plainText += '\n' + totalRow.join('\t');
    }

    // Step 4: Build HTML table
    let html = '<table border="1" cellpadding="4" cellspacing="0" style="border-collapse: collapse;">';

    // Header
    html += '<thead><tr>';
    for (const col of includedCols) {
      html += `<th style="background: #020044; color: #ffffff; font-size: 14px; font-weight: normal;">${col.getColDef().headerName || ''}</th>`;
    }
    html += '</tr></thead>';

    // Body
    html += '<tbody>';
    for (const row of rawRows) {
      html += '<tr>';
      for (const i of includedColIndexes) {
        html += `<td style="font-size: 12px;">${row[i]}</td>`;
      }
      html += '</tr>';
    }

    // Add Total row
    if (includedTotalColPos >= 0) {
      html += '<tr style="font-weight: bold; background: #f0f0f0;">';
      for (let j = 0; j < includedColIndexes.length; j++) {
        if (j === includedPriceColPos) {
          html += `<td style="font-size: 12px; text-align: right;">Total:</td>`;
        } else if (j === includedTotalColPos) {
          html += `<td style="font-size: 12px;">${totalSum.toFixed(2)}</td>`;
        } else {
          html += `<td style="font-size: 12px;"></td>`;
        }
      }
      html += '</tr>';
    }

    html += '</tbody></table>';

    // Step 5: Apply to clipboard
    e.preventDefault();
    e.clipboardData.setData('text/plain', plainText);
    e.clipboardData.setData('text/html', html);
  }

  // ✅ Batch processing for paste operations - prevents app from blocking
  private pasteBatchQueue: [number, { name: string; quantity: any }][] = [];
  private currentBatchPending: Set<number> = new Set();
  private readonly PASTE_BATCH_SIZE = 100; // ✅ Increased from 50 to 100 for fewer HTTP requests

  // ✅ Progress tracking for paste operations
  showPasteProgressPopup: boolean = false;
  pasteProgressPercent: number = 0;
  pasteProgressTotal: number = 0;
  pasteProgressCompleted: number = 0;
  private pendingBatchResults: Map<number, any> = new Map(); // Store results until batch/all complete

  private processPasteInBatches(entries: [number, { name: string; quantity: any }][], batchSize: number): void {
    // Store all entries in queue
    this.pasteBatchQueue = [...entries];
    this.currentBatchPending.clear();
    this.pendingBatchResults.clear();

    // ✅ Initialize progress tracking
    this.pasteProgressTotal = entries.length;
    this.pasteProgressCompleted = 0;
    this.pasteProgressPercent = 0;
    this.showPasteProgressPopup = true;

    console.log(`📋 Starting batch processing: ${entries.length} items in batches of ${batchSize}`);
    console.log(`📋 showPasteProgressPopup = ${this.showPasteProgressPopup}, Currency.ID = ${this.parent?.Cart?.Currency?.ID}, B2BPartner = ${!!this.parent?.B2BPartner}`);

    // Start processing first batch
    this.processNextBatch();
  }

  private processNextBatch(): void {
    if (this.pasteBatchQueue.length === 0) {
      // ✅ All batches complete - apply all pending results to grid
      console.log('✅ All paste batches completed');
      this.applyPendingResultsToGrid();
      this.triggerFinalCartUpdate();

      // ✅ Hide progress popup and loading overlay
      this.showPasteProgressPopup = false;
      this.pasteProgressPercent = 100;
      this.loadingProducts = false;
      this.parent.loading = false;

      // ✅ Force a full grid refresh to ensure all cells render properly
      // This handles edge cases where SignalR connection issues may have caused rendering problems
      if (this.gridApi) {
        setTimeout(() => {
          this.gridApi.refreshCells({ force: true });
          console.log('✅ Final grid refresh completed');
        }, 100);
      }

      // ✅ Track paste event ONCE after all batches complete
      if (this.pasteEventType !== null) {
        this.TrackB2BUserGridEvent(this.pasteEventType);
        this.pasteEventType = null;
        this.isPasting = false;
      }
      return;
    }

    // Take next batch from queue
    const batch = this.pasteBatchQueue.splice(0, this.PASTE_BATCH_SIZE);
    this.currentBatchPending.clear();

    console.log(`📋 Processing batch of ${batch.length} items using BULK API (${this.pasteBatchQueue.length} remaining)`);

    // Add all row indices to pending set
    batch.forEach(([rowIndex]) => {
      this.currentBatchPending.add(rowIndex);
      this.pendingPasteRowIndexes.add(rowIndex);
    });

    // ✅ Use BULK API - single request for entire batch
    this.BulkSearchIndustryProductB2BOffersWithMasterData(batch);

    // Fallback timeout for this batch (60 seconds for bulk - more time since it's one request)
    setTimeout(() => {
      if (this.currentBatchPending.size > 0) {
        console.warn(`⚠️ Bulk batch timeout: ${this.currentBatchPending.size} items did not complete, moving to next batch`);
        // ✅ Update progress for timed out items
        this.pasteProgressCompleted += this.currentBatchPending.size;
        this.updatePasteProgress();
        this.currentBatchPending.clear();
        this.processNextBatch();
      }
    }, 60000);
  }

  private onBatchRowCompleted(rowIndex: number): void {
    this.currentBatchPending.delete(rowIndex);
    this.pendingPasteRowIndexes.delete(rowIndex);

    // ✅ Update progress
    this.pasteProgressCompleted++;
    this.updatePasteProgress();

    // When current batch is complete, process next batch
    if (this.currentBatchPending.size === 0) {
      console.log('✅ Batch complete, processing next...');

      // ✅ If total items > 50, apply results after each batch for visual feedback
      if (this.pasteProgressTotal > 50) {
        this.applyPendingResultsToGrid();
      }

      this.processNextBatch();
    }
  }

  private updatePasteProgress(): void {
    if (this.pasteProgressTotal > 0) {
      this.pasteProgressPercent = Math.round((this.pasteProgressCompleted / this.pasteProgressTotal) * 100);
    }
  }

  private applyPendingResultsToGrid(): void {
    if (this.pendingBatchResults.size === 0) return;

    console.log(`📋 Applying ${this.pendingBatchResults.size} pending results to grid`);

    const rowNodes: any[] = [];

    // Apply all pending results to the grid using the existing applyCartItemToRow method
    this.pendingBatchResults.forEach((cartItem, rowIndex) => {
      this.applyCartItemToRow(rowIndex, cartItem);
      // Collect row nodes for batch refresh
      const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
      if (rowNode) rowNodes.push(rowNode);
    });

    // Clear pending results after applying
    this.pendingBatchResults.clear();

    // ✅ Force refresh all affected cells to ensure rendering
    if (this.gridApi && rowNodes.length > 0) {
      this.gridApi.refreshCells({ rowNodes, force: true });
    }
  }

  private onPasteRowCompleted(rowIndex: number): void {
    this.pendingPasteRowIndexes.delete(rowIndex);

    if (this.pendingPasteRowIndexes.size === 0) {
      this.triggerFinalCartUpdate();

      // ✅ Track paste event ONCE after all rows complete
      if (this.pasteEventType !== null) {
        this.TrackB2BUserGridEvent(this.pasteEventType);
        this.pasteEventType = null;
        this.isPasting = false;
      }
    }
  }

  private resetPasteCompletionTimer(): void {
    if (this.pasteSyncTimer) {
      clearTimeout(this.pasteSyncTimer);
    }

    this.pasteSyncTimer = setTimeout(() => {
      if (this.pendingPasteRowIndexes.size > 0) {
        console.warn('⚠️ Fallback: Force cart update after timeout');
        this.pendingPasteRowIndexes.clear();
        this.triggerFinalCartUpdate();

        // ✅ Track paste event even on timeout
        if (this.pasteEventType !== null) {
          this.TrackB2BUserGridEvent(this.pasteEventType);
          this.pasteEventType = null;
          this.isPasting = false;
        }
      }
    }, 10000); // Fallback timeout: 10 seconds
  }

  private triggerFinalCartUpdate(): void {
    this.UpdateCart();
    console.log('✅ All pasted rows processed, cart updated.');
  }

    /** Return true if we are allowed to search for this row */
  private shouldSearch(rowIndex: number): boolean {
    const node = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
    const wasSearched = node?.data?.CartItem?.WasSearchedFor === true;
    return !wasSearched; // allow when false/undefined/null
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;

    // ✅ Update column headers with currency symbol (in case currencies loaded before grid was ready)
    this.updateColumnHeaders();

    // ✅ Inject header checkbox (after updateColumnHeaders which calls refreshHeader)
    setTimeout(() => this.injectHeaderCheckbox(), 150);

    // ✅ Fix grid rendering issues on navigation - ensure proper sizing after container is laid out
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
        this.gridApi.resetRowHeights();
        this.gridApi.redrawRows();
        // ✅ Sync column widths for ExcelExportControlRow after grid is sized
        this.syncColumnWidths();
      }
    }, 200);

    // ✅ Try to load cart from IndexedDB (async)
    this.loadCartFromIDB().then(storedCart => {
      if (storedCart) {
        this.GetCart();
      } else {
        // ✅ No cart saved - add 50 empty rows
        this.addMoreRows(50);
      }
    }).catch(() => {
      // ✅ IndexedDB error - add 50 empty rows as fallback
      this.addMoreRows(50);
    });

    // Listen to raw paste to count rows
    // const gridElement = document.querySelector('.ag-root') as HTMLElement;
    // if (gridElement) {
    //   gridElement.addEventListener('paste', (e: ClipboardEvent) => {
    //     const text = e.clipboardData?.getData('text/plain') ?? '';
    //     this.pastedLineCount = text.split(/\r?\n/).filter(line => line.trim()).length;
    //   });
    // }

    // Attach custom grid cell click listener
    setTimeout(() => {
      const gridElement = document.querySelector('.ag-root');
      if (gridElement) {
        gridElement.addEventListener('click', this.onGridCellButtonClick.bind(this));
        gridElement.addEventListener('mouseover', this.onGridCellMouseOver.bind(this));
      }
    }, 0);
  }

  /**
   * Handle mouseover events on grid cells to show tooltips for red-row-save-icon
   */
  onGridCellMouseOver(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const redRowSaveIcon = target.closest('.red-row-save-icon') as HTMLElement | null;
    if (redRowSaveIcon) {
      const tooltipKey = redRowSaveIcon.getAttribute('data-tooltip-key');
      if (tooltipKey) {
        const tooltipText = this.parent.translate.instant(tooltipKey);
        redRowSaveIcon.setAttribute('title', tooltipText);
      }
    }
  }

  ClickedRow:any;
  ClickedRowIndex:number;
  ShowBrandCollateral: boolean = false;
  BrandPopupPosition: { top: string; left: string; width: string } = {
    top: '0px',
    left: '0px',
    width: '0px'
  };

  onGridCellButtonClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;
  
    // 1) 🔵 Datasheet (PDF) click — new
    const pdfBtn = target.closest('.pdf-datasheet-button') as HTMLElement | null;
    if (pdfBtn) {
      const href = (pdfBtn.getAttribute('data-href') || '').trim();
      if (href) {
        // open safely in a new tab
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return; // stop further handling
    }
  
    // 2) 🗑 Delete row — unchanged behavior
    if (target.closest('.delete-row-button')) {
      const cell = target.closest('.ag-cell') as HTMLElement;
      const rowElement = cell?.parentElement as HTMLElement;
      const rowIndex = Array.from(rowElement?.parentElement?.children || []).indexOf(rowElement);
      if (rowIndex >= 0) this.deleteRow(rowIndex);
      return;
    }
  
    // 3) ➕ Awaiting Selection Plus Button - opens Red Row Edit Popup
    const awaitingSelectionPlusBtn = target.closest('.awaiting-selection-plus-button') as HTMLElement | null;
    if (awaitingSelectionPlusBtn) {
      const rowIndex = Number(awaitingSelectionPlusBtn.getAttribute('data-row'));
      const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
      const cartItem = rowNode?.data?.CartItem;
      if (cartItem) {
        this.OpenRedRowEditPopup(cartItem, rowIndex);
      }
      return;
    }

    // 3a) ✏️ Revert pencil icon click
    const pencilIcon = target.closest('.revert-pencil-icon') as HTMLElement | null;
    if (pencilIcon) {
      const rowIndex = Number(pencilIcon.getAttribute('data-row'));
      this.RevertRowIndex = rowIndex;
      this.ShowRevertConfirmationPopup = true;
      return;
    }

    // 3b) ✅/❌ Red row save icon click (toggle SaveToDB)
    const redRowSaveIcon = target.closest('.red-row-save-icon') as HTMLElement | null;
    if (redRowSaveIcon) {
      const rowIndex = Number(redRowSaveIcon.getAttribute('data-row'));
      this.ToggleRedRowSaveToDB(rowIndex);
      return;
    }

    // 3c) 🔍 Supplier search button click
    const supplierButton = target.closest('.dropdown-trigger-supplier') as HTMLElement | null;
    if (supplierButton) {
      console.log('🔍 Supplier button clicked!');
      const rowIndex = Number(supplierButton.getAttribute('data-row'));
      const type = supplierButton.getAttribute('data-type');
      console.log('🔍 Row index:', rowIndex, 'Type:', type);

      if (type === 'supplier-search') {
        console.log('🔍 Type is supplier-search, calculating position...');
        // Calculate popup position (same as brand popup)
        const cellElement = target.closest('.ag-cell') as HTMLElement | null;
        if (!cellElement) {
          console.log('❌ Cell element not found');
          return;
        }

        const gridContainer = document.querySelector('.ag-theme-alpine') as HTMLElement;
        if (!gridContainer) {
          console.log('❌ Grid container not found');
          return;
        }

        const cellRect = cellElement.getBoundingClientRect();
        const containerRect = gridContainer.getBoundingClientRect();
        const top = cellRect.bottom - containerRect.top;
        const left = cellRect.left - containerRect.left;
        const width = cellRect.width * 2.5; // ✅ Make popup wider (2.5x cell width)

        this.SupplierPopupPosition = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
        console.log('🔍 Popup position:', this.SupplierPopupPosition);

        this.OpenPartnerSearchPopup(rowIndex);
      }
      return;
    }

    // 3d) ❌ Clear supplier button click
    const clearSupplierButton = target.closest('.clear-supplier-button') as HTMLElement | null;
    if (clearSupplierButton) {
      const rowIndex = Number(clearSupplierButton.getAttribute('data-row'));
      console.log('❌ Clear supplier button clicked for row:', rowIndex);
      this.ClearSupplier(rowIndex);
      return;
    }

    // 3e) 📦 Supplier cell click (entire cell is clickable on green rows)
    const supplierCell = target.closest('.supplier-cell-clickable-content') as HTMLElement | null;
    if (supplierCell) {
      console.log('📦 Supplier cell clicked!');
      const rowIndex = Number(supplierCell.getAttribute('data-row'));
      const type = supplierCell.getAttribute('data-type');
      console.log('📦 Row index:', rowIndex, 'Type:', type);

      if (type === 'supplierAlternatives') {
        console.log('📦 Type is supplierAlternatives, calculating position...');
        // Calculate popup position (from Supplier column to end of COO column)
        const supplierCellElement = target.closest('.ag-cell') as HTMLElement | null;
        if (!supplierCellElement) {
          console.log('❌ Supplier cell element not found');
          return;
        }

        const gridContainer = document.querySelector('.OwnAGGridContainer') as HTMLElement;
        if (!gridContainer) {
          console.log('❌ Grid container not found');
          return;
        }

        // Find the COO column cell in the same row
        const rowElement = supplierCellElement.parentElement as HTMLElement;
        const cooCellElement = rowElement?.querySelector('[col-id="coo"]') as HTMLElement | null;

        if (!cooCellElement) {
          console.log('❌ COO cell element not found');
          return;
        }

        const supplierRect = supplierCellElement.getBoundingClientRect();
        const cooRect = cooCellElement.getBoundingClientRect();
        const containerRect = gridContainer.getBoundingClientRect();

        const top = supplierRect.bottom - containerRect.top;
        const left = supplierRect.left - containerRect.left;
        // ✅ Calculate width from Supplier column start to COO column end (relative to container)
        const cooRight = cooRect.right - containerRect.left;
        const width = cooRight - left;

        this.SearchResultsPosition = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
        console.log('📦 Popup position (Supplier to COO):', this.SearchResultsPosition);

        // Get the row data
        this.gridApi.forEachNode((node: any) => {
          if (node.rowIndex === rowIndex) {
            this.ClickedRow = node.data;
            this.ClickedRowIndex = rowIndex;
          }
        });

        const cartItem = this.ClickedRow?.CartItem;
        const productId = cartItem?.Product?.ID;
        const offerId = cartItem?.Product?.ListPriceOffers?.[0]?.ID;

        // Show popup and call API
        this.ShowSupplierAlternatives = true;
        this.ShowETAAlternatives = false;
        this.ShowSearchResultsAlternatives = false;

        if (productId) {
          // ✅ Pass true for fromSupplierCellClick to use ConfigStatus ID=-9000 on admin grid
          this.GetIndustryProductCalculatedSideOffers(rowIndex, productId, offerId, true);
        }
      }
      return;
    }

    // 3f) 🌍 COO cell click (entire cell is clickable on red rows)
    const cooCell = target.closest('.coo-cell-clickable') as HTMLElement | null;
    if (cooCell) {
      console.log('🔍 COO cell clicked!');
      const rowIndex = Number(cooCell.getAttribute('data-row'));
      const type = cooCell.getAttribute('data-type');
      console.log('🔍 Row index:', rowIndex, 'Type:', type);

      if (type === 'coo-search') {
        console.log('🔍 Type is coo-search, calculating position...');
        // Calculate popup position (same as brand popup)
        const cellElement = target.closest('.ag-cell') as HTMLElement | null;
        if (!cellElement) {
          console.log('❌ Cell element not found');
          return;
        }

        const gridContainer = document.querySelector('.ag-theme-alpine') as HTMLElement;
        if (!gridContainer) {
          console.log('❌ Grid container not found');
          return;
        }

        const cellRect = cellElement.getBoundingClientRect();
        const containerRect = gridContainer.getBoundingClientRect();
        const top = cellRect.bottom - containerRect.top;
        const left = cellRect.left - containerRect.left;
        const width = cellRect.width * 2.5; // Make popup wider (2.5x cell width)

        this.COOPopupPosition = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
        console.log('✅ COO Popup position:', this.COOPopupPosition);

        this.OpenCOOSearchPopup(rowIndex);
      }
      return;
    }

    // 3) 🔽 Any dropdown trigger (brand + others) + clickable ETA text
    const trigger = target.closest('.dropdown-trigger, .dropdown-trigger-brand, .eta-text-clickable') as HTMLElement | null;
    if (!trigger) return;

    const rowIndex = Number(trigger.getAttribute('data-row'));
    const type = trigger.getAttribute('data-type') || 'name';
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
    const cartItem = rowNode?.data?.CartItem;
    if (!cartItem) return;

    this.ClickedRow = JSON.parse(JSON.stringify(rowNode?.data));
    this.ClickedRowIndex = rowIndex;
  
    const cellElement = trigger.closest('.ag-cell') as HTMLElement;
    const containerElement = document.querySelector('.OwnAGGridContainer') as HTMLElement;
    if (!cellElement || !containerElement) return;
  
    const cellRect = cellElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    const top  = cellRect.top  - containerRect.top + cellElement.offsetHeight + 5;
  
    // 3a) BRAND collateral popup — unchanged
    if (type === 'brand-collateral') {
      const left = cellRect.left - containerRect.left;
      const width = cellRect.width;
  
      this.BrandPopupPosition = { top: `${top}px`, left: `${left}px`, width: `${width}px` };
  
      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.ShowFallbackSelection = false;
  
      this.SearchIndustryProductNameAndPartNumberParrallelB2BBrandsVersion2(rowIndex, cartItem);
      return;
    }
  
    // 3b) BRAND suggest popup — unchanged
    if (type === 'brand-suggest') {
      this.BrandPopupPosition = {
        top: `${top}px`,
        left: `${cellRect.left - containerRect.left}px`,
        width: `${cellRect.width}px`
      };
  
      const implicitProduct = (cartItem?.UIProductValue ?? '');
      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.ShowFallbackSelection = false;
      this.ShowBrandCollateral = false;
      this.BrandSelectList = [];
  
      this.InitializeBrandFilter();
      this.GetBrandsFilteredWithSuggestion('', 1, implicitProduct);
      return;
    }
  
    // 3c) Full-width overlays (fallback / alternatives / ETAs) — unchanged
    const fullLeft = 5; // align with grid container’s left
    const fullWidth = containerRect.width;
    this.SearchResultsPosition = { top: `${top}px`, left: `${fullLeft}px`, width: `${fullWidth}px` };
  
    if (type === 'fallback') {
      this.ShowUISideOffers(rowIndex, cartItem);
      return;
    }

    // ✅ NEW: Red Row ETA popup - positioned from Standard-Delivery column left edge to Purchase column right edge
    if (type === 'RedRowETA' || type === 'ValidRowETA') {
      // ✅ Get the purchase column element to calculate width
      const targetElement = event.target as HTMLElement;
      const purchaseColumnElement = targetElement.closest('.ag-row')?.querySelector('[col-id="purchase"]');
      let popupWidth = containerRect.width - (cellRect.left - containerRect.left); // Default: full width

      if (purchaseColumnElement) {
        const purchaseRect = purchaseColumnElement.getBoundingClientRect();
        // ✅ Width from Standard-Delivery left edge to Purchase right edge
        popupWidth = (purchaseRect.right - cellRect.left);
      }

      // ✅ Position from the left edge of the Standard-Delivery column
      this.SearchResultsPosition = {
        top: `${top}px`,
        left: `${cellRect.left - containerRect.left}px`,
        width: `${popupWidth}px`
      };

      this.CurrentRedRowCartItem = cartItem;
      this.CurrentRedRowIndex = rowIndex; // ✅ Store row index

      // ✅ Populate dropdowns from existing LeadTime ONLY if there are values
      if (cartItem?.SelectedOffer?.LeadTime &&
          cartItem.SelectedOffer.LeadTime.Type &&
          cartItem.SelectedOffer.LeadTime.Units > 0) {
        // ✅ Pre-select existing values - match by Type.ID
        const existingTypeId = cartItem.SelectedOffer.LeadTime.Type.ID;
        this.SelectedLeadTimeType = this.LeadTimeTypes.find(t => t.ID === existingTypeId) || null;
        this.SelectedLeadTimeUnits = cartItem.SelectedOffer.LeadTime.Units;

        // ✅ Pre-populate smart input with existing value
        this.LeadTimeSmartInput = `${this.SelectedLeadTimeUnits} ${this.SelectedLeadTimeType?.Name || ''}`;

        console.log('✅ Pre-selected existing LeadTime:', this.SelectedLeadTimeType, this.SelectedLeadTimeUnits);
      } else {
        // ✅ No existing values - set to empty/0
        this.SelectedLeadTimeType = null;
        this.SelectedLeadTimeUnits = 0;
        this.LeadTimeSmartInput = '';
        console.log('✅ No existing LeadTime - dropdowns empty');
      }

      // ✅ Clear suggestions and inline suggestion
      this.LeadTimeSuggestions = [];
      this.LeadTimeInlineSuggestion = '';

      this.ShowRedRowETAPopup = true;
      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.ShowFallbackSelection = false;
      this.ShowBrandCollateral = false;
      return;
    }

    // ✅ NEW: Supplier alternatives popup
    if (type === 'supplierAlternatives') {
      this.ShowSupplierAlternatives = true;
      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;

      const productId = cartItem?.Product?.ID;
      const offerId = cartItem?.Product?.ListPriceOffers?.[0]?.ID;

      if (productId) {
        this.GetIndustryProductCalculatedSideOffers(
          rowIndex,
          productId,
          offerId
        );
      }
      return;
    }

    if (type === 'productalternatives' || type === 'ALLETAs' || type === 'productSpecials') {
      this.ShowETAAlternatives = true;
      this.ShowSearchResultsAlternatives = false;

      const productIds = (cartItem?.SearchResults ?? []).map((s: any) => s.ProductID);
      const offerId = cartItem?.Product?.ListPriceOffers?.[0]?.ID;

      if (type === 'productalternatives') {
        // ✅ Open Tab 2 (Other Offers) by default when clicking Name button
        this.selectedNamePopupTab = 2;

        // ✅ Load Tab 2 data immediately on popup open
        this.GetIndustryProductCalculatedSideOffers(
          rowIndex,
          this.ClickedRow?.CartItem?.Product?.ID,
          offerId
        );
        return;
      }

      if (type === 'ALLETAs') {
        // ✅ This is now handled by Name button, so open Tab 2 (Other Offers)
        this.selectedNamePopupTab = 2;

        this.GetIndustryProductCalculatedSideOffers(
          rowIndex,
          this.ClickedRow?.CartItem?.Product?.ID,
          offerId
        );
        return;
      }

      if (type === 'productSpecials') {
        // ✅ Open Tab 2 (Other Offers) for special offers
        this.selectedNamePopupTab = 2;

        if (productIds?.length) {
          this.GetIndustryProductCalculatedSideOffersWithCollateral(
            rowIndex,
            productIds,
            offerId,
            this.ClickedRow?.CartItem?.Product?.Brand,
            true,  // BestOfferOnly
            true,  // GetAlsoCollateralSpecialOffers
            true   // GetOnlySpecialOffers
          );
        }
        return;
      }
    }
  
    // 3d) Default: show full search results alternatives — unchanged
    this.ShowSearchResultsAlternatives = true;
    this.ShowETAAlternatives = false;
    this.SearchResultAlternatives = JSON.parse(JSON.stringify(cartItem?.SearchResults ?? []));
  }
  

  ShowFallbackSelection:boolean = false;
  FallbackOffers:any[];

  // ✅ NEW: Red Row ETA popup
  ShowRedRowETAPopup: boolean = false;
  LeadTimeTypes: any[] = [];
  LeadTimeUnits: number[] = Array.from({ length: 31 }, (_, i) => i); // 0-30
  SelectedLeadTimeType: any = null;
  SelectedLeadTimeUnits: number = 0;
  CurrentRedRowCartItem: any = null; // Track which red row we're editing
  CurrentRedRowIndex: number = -1; // Track the row index

  // ✅ Tab selection for unified Name popup (Tab 2 = Other Offers, Tab 3 = Similar Products)
  selectedNamePopupTab: number = 2; // Default to Tab 2 (Other Offers)

  // ✅ Method to handle tab switching and reload data
  onNamePopupTabChange(tabNumber: number) {
    this.selectedNamePopupTab = tabNumber;

    if (!this.ClickedRow) return;

    const cartItem = this.ClickedRow.CartItem;
    const rowIndex = this.ClickedRow.rowIndex;
    const offerId = cartItem?.Product?.ListPriceOffers?.[0]?.ID;
    const productIds = (cartItem?.SearchResults ?? []).map((s: any) => s.ProductID);

    if (tabNumber === 2) {
      // ✅ Tab 2: Load "Other Offers" data (same product, different configs)
      this.GetIndustryProductCalculatedSideOffers(
        rowIndex,
        cartItem?.Product?.ID,
        offerId
      );
    } else if (tabNumber === 3) {
      // ✅ Tab 3: Load "Similar Products" data (alternative products)
      if (this.isAdministrator()) {
        // ✅ Admin: Load full grid data with pricing
        if (productIds?.length) {
          this.GetIndustryProductCalculatedSideOffersWithCollateral(
            rowIndex,
            productIds,
            offerId,
            cartItem?.Product?.Brand,
            true,  // BestOfferOnly
            true,  // GetAlsoCollateralSpecialOffers
            false  // GetOnlySpecialOffers
          );
        }
      } else {
        // ✅ Regular User: Show simple list from SearchResults
        this.SearchResultAlternatives = JSON.parse(JSON.stringify(cartItem?.SearchResults ?? []));
      }
    }
  }

  // ✅ NEW: Smart autocomplete for lead time input
  LeadTimeSmartInput: string = '';
  LeadTimeInlineSuggestion: string = ''; // ✅ Faded inline suggestion
  LeadTimeSuggestions: any[] = []; // ✅ Keep for internal logic

  // ✅ NEW: Product Box and Condition Statuses for Red Row Edit Popup
  ProductBoxStatuses: any[] = [];
  ProductConditionStatuses: any[] = [];
  ProductManufacturingStatuses: any[] = [];
  ProductVersionStatuses: any[] = [];

  // ✅ NEW: Conditions tab dropdown states
  showRedRowEditManufacturingStatusDropdown: boolean = false;
  showRedRowEditVersionStatusDropdown: boolean = false;
  showRedRowEditOfferBoxStatusDropdown: boolean = false;
  showRedRowEditOfferConditionStatusDropdown: boolean = false;

  // ✅ NEW: Intermediate variables for Offer statuses (avoids template binding to ListPriceOffers[0])
  // These avoid complex optional chaining in templates that causes compilation memory issues
  RedRowEditOfferBoxStatus: any = null;
  RedRowEditOfferConditionStatus: any = null;

  // ✅ NEW: Mirror list of original CartItems (non-edited versions)
  OriginalCartItems: any[] = [];

  // ✅ NEW: Revert confirmation popup
  ShowRevertConfirmationPopup: boolean = false;
  RevertRowIndex: number = -1;

  // ✅ NEW: Partner search popup for red rows
  ShowPartnerSearchPopup: boolean = false;
  PartnerSearchRowIndex: number = -1;
  PartnerSearchTerm: string = '';
  PartnerSearchResults: any[] = [];
  LoadingPartners: boolean = false;
  SupplierPopupPosition: { top: string; left: string; width: string } = {
    top: '0px',
    left: '0px',
    width: '0px'
  };

  // ✅ NEW: COO (Country of Origin) search popup for red rows
  ShowCOOSearchPopup: boolean = false;
  COOSearchRowIndex: number = -1;
  COOSearchTerm: string = '';
  COOSearchResults: any[] = [];
  COOPopupPosition: { top: string; left: string; width: string } = {
    top: '0px',
    left: '0px',
    width: '0px'
  };

  // ✅ NEW: Weight popup for red rows
  ShowWeightPopup: boolean = false;
  WeightMeasurementTypes: any[] = [];
  WeightUnits: number[] = Array.from({ length: 101 }, (_, i) => i); // 0-100
  SelectedWeightType: any = null;
  SelectedWeightUnits: number = 0;
  CurrentWeightRowCartItem: any = null; // Track which red row we're editing
  CurrentWeightRowIndex: number = -1;

  // ✅ NEW: Comprehensive Red Row Edit Popup
  ShowRedRowEditPopup: boolean = false;
  RedRowEditPopupPosition: any = {};
  CurrentRedRowEditCartItem: any = null;
  CurrentRedRowEditRowIndex: number = -1;
  // ✅ Clone of CartItem for popup editing - all controls bind directly to this
  RedRowEditCartItemClone: any = null;

  // Red Row Edit - Brand
  RedRowEditBrandSearchTerm: string = '';
  RedRowEditBrandSearchResults: any[] = [];
  RedRowEditLoadingBrands: boolean = false;
  RedRowEditSelectedBrand: any = null;

  // Red Row Edit - Lead Time (Smart Autocomplete)
  RedRowEditSelectedLeadTimeType: any = null;
  RedRowEditSelectedLeadTimeUnits: number = 0;
  RedRowEditLeadTimeSmartInput: string = '';
  RedRowEditLeadTimeInlineSuggestion: string = '';
  RedRowEditLeadTimeSuggestions: any[] = [];

  // Red Row Edit - Supplier
  RedRowEditPartnerSearchTerm: string = '';
  RedRowEditPartnerSearchResults: any[] = [];
  RedRowEditLoadingPartners: boolean = false;
  RedRowEditSelectedPartner: any = null;
  RedRowEditOfferIdFromSideOffer: number = 0; // ✅ Track offer ID when selected from side offers (0 = manual entry)
  RedRowEditOriginalOfferId: number = 0; // ✅ Track original offer ID when popup opens
  RedRowEditOriginalPartnerId: number = 0; // ✅ Track original Partner ID to detect supplier change

  // Red Row Edit - Supplier Alternatives
  RedRowEditSideOffers: any[] = [];
  RedRowEditLoadingSideOffers: boolean = false;
  RedRowEditShowSupplierAlternatives: boolean = false;

  // Red Row Edit - HSCode
  RedRowEditHSCode: string = '';

  // Red Row Edit - COO
  RedRowEditCOOSearchTerm: string = '';
  RedRowEditCOOSearchResults: any[] = [];
  RedRowEditSelectedCOO: any = null;

  // Red Row Edit - Weight
  RedRowEditSelectedWeightType: any = null;
  RedRowEditSelectedWeightUnits: number = 0;

  // Red Row Edit - BoxStatus
  RedRowEditSelectedBoxStatus: any = null;

  // Red Row Edit - ConditionStatus
  RedRowEditSelectedConditionStatus: any = null;

  // Red Row Edit - NEW FIELDS
  RedRowEditPartNumber: string = '';
  RedRowEditName: string = '';
  RedRowEditQuantity: number = 0;
  RedRowEditListPrice: number = 0;
  RedRowEditDiscount: number = 0;
  RedRowEditFinalPrice: number = 0;
  RedRowEditSelectedCurrency: any = null;
  RedRowEditCurrencyDropdownOpen: boolean = false;
  RedRowEditWeightInput: string = '';

  // Red Row Edit - Purchase fields (admin only)
  RedRowEditPurchasePrice: number = 0;
  RedRowEditSelectedPurchaseCurrency: any = null;
  RedRowEditPurchaseCurrencyDropdownOpen: boolean = false;

  // Red Row Edit - Dropdown States (Lead Time removed - now using smart autocomplete)
  RedRowEditWeightTypeDropdownOpen: boolean = false;
  RedRowEditWeightUnitsDropdownOpen: boolean = false;
  RedRowEditBoxStatusDropdownOpen: boolean = false;
  RedRowEditConditionStatusDropdownOpen: boolean = false;

  // Red Row Edit - Save to Database loading state
  RedRowEditSaving: boolean = false;

  // ✅ Delete Offer confirmation popup state
  ShowDeleteOfferConfirmation: boolean = false;
  RedRowEditDeletingOffer: boolean = false;

  // ✅ Track if supplier changed (for button display)
  RedRowEditSupplierChanged: boolean = false;

  // ✅ Check if popup will create a new offer (supplier changed)
  get RedRowEditIsNewOffer(): boolean {
    // If user selected a side offer with valid ID, it's NOT a new offer
    if (this.RedRowEditOfferIdFromSideOffer > 0) {
      return false;
    }

    // Original offer had no ID → always new offer
    if (this.RedRowEditOriginalOfferId === 0) {
      return true;
    }

    // Use tracked flag for supplier change
    return this.RedRowEditSupplierChanged;
  }

  // Red Row Edit - Tab State (for categorized popup)
  RedRowEditSelectedTab: string = 'General';

  // Red Row Edit - Stats Fields (General tab)
  RedRowEditDatasheetLink: string = '';
  RedRowEditGroupName: string = '';

  // Red Row Edit - MOQ Fields
  RedRowEditOfferMOQ: number | null = null;      // SelectedOffer.MOQ (General tab)
  RedRowEditProductMOQ: number | null = null;    // Product.MOQ (Master-Data tab)

  // Red Row Edit - EAN Field (Master-Data tab)
  RedRowEditEAN: string = '';

  // Red Row Edit - Description Field (Master-Data tab)
  RedRowEditDescription: string = '';

  // Red Row Edit - Stats Fields (Dimensions tab)
  RedRowEditLength: number | null = null;
  RedRowEditWidth: number | null = null;
  RedRowEditHeight: number | null = null;

  // Red Row Edit - Availability tab fields
  // CurrentStock is bound directly to CurrentRedRowEditCartItem.SelectedOffer.StockQuantityClone in HTML
  RedRowEditManufacturerStock: number | null = null;
  RedRowEditManufacturerStockLeadTimeSmartInput: string = '';
  RedRowEditManufacturerStockLeadTimeInlineSuggestion: string = '';
  RedRowEditManufacturerStockLeadTimeSuggestions: any[] = [];
  RedRowEditManufacturerStockLeadTimeType: any = null;
  RedRowEditManufacturerStockLeadTimeUnits: number = 0;
  RedRowEditManufacturerOverrequestStockLeadTimeSmartInput: string = '';
  RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion: string = '';
  RedRowEditManufacturerOverrequestStockLeadTimeSuggestions: any[] = [];
  RedRowEditManufacturerOverrequestStockLeadTimeType: any = null;
  RedRowEditManufacturerOverrequestStockLeadTimeUnits: number = 0;
  RedRowEditTrustedManufacturerLeadTime: boolean = false;

  // ✅ DB Values for current offer (raw values from tblIndustryProducts_listPriceOffers)
  RedRowEditDBOfferMOQ: number | null = null;
  RedRowEditDBOfferLeadTimeUIOutput: string = '';
  RedRowEditDBOfferLoading: boolean = false;

  ShowUISideOffers(rowIndex: number, cartItem:any): void {
    console.log('🟡 ShowUISideOffers triggered for row', rowIndex);
    this.ShowFallbackSelection = true;
    this.FallbackOffers = null;
    this.FallbackOffers = [];
    // You can implement the logic later, e.g., open a popup or fallback search.

    this.FallbackOffers = cartItem?.FallbackOffers;
  }

  deleteRow(rowIndex: number): void {
    const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
    if (!rowNode) return;
  
    const dataToRemove = rowNode.data;
  
    // Remove row from AG Grid
    this.gridApi.applyTransaction({ remove: [dataToRemove] });
  
    // ✅ Optional: Re-index the remaining rows for correct # display
    const allData: any[] = [];
    this.gridApi.forEachNode((node, idx) => {
      node.data.index = idx + 1;
      allData.push(node.data);
    });
    this.gridApi.setRowData(allData); // reset with updated indices
  
    // ✅ Now persist the updated cart
    this.UpdateCart();
  }
  
  CloseGridPopupsOverlay(){
    this.ShowSearchResultsAlternatives = false;
    this.ShowETAAlternatives = false;
    this.ShowSupplierAlternatives = false; // ✅ NEW: Close supplier alternatives
    this.ShowFallbackSelection = false;
    this.ShowBrandCollateral = false;
    this.ShowBrandCollateralNewProduct = false;
    this.ShowRedRowETAPopup = false; // ✅ NEW
    this.ShowWeightPopup = false; // ✅ NEW
    this.ShowRedRowEditPopup = false; // ✅ NEW
    this.ClosePartnerSearchPopup(); // ✅ NEW
    this.CloseCOOSearchPopup(); // ✅ NEW
    this.showPurchaseCurrencyDropdown = false; // ✅ Close purchase currency dropdown
    this.ClosePartnerDetailsPopup(); // ✅ Close partner details popup
    this.showStockInclusionPopup = false; // ✅ Close stock inclusion popup
  }

  // ✅ Close COO search popup
  CloseCOOSearchPopup() {
    this.ShowCOOSearchPopup = false;
    this.COOSearchTerm = '';
    this.COOSearchResults = [];
  }

  ShowSearchResultsAlternatives:boolean = false;
  ShowETAAlternatives:boolean = false;
  ShowSupplierAlternatives:boolean = false; // ✅ NEW: Supplier alternatives popup
  SearchResultAlternatives:any[];
  AlternativeProducts:any[] = []; // ✅ NEW: Alternative products for Tab 2 (grid format)

  showSearchResultDropdown(rowIndex: number, searchResults: any[]): void {
    this.ShowSearchResultsAlternatives = true;
    this.SearchResultAlternatives = JSON.parse(JSON.stringify(searchResults));
  
    setTimeout(() => {
      const cellButton = document.querySelector(`.dropdown-trigger[data-row="${rowIndex}"]`) as HTMLElement;
      const gridContainer = document.querySelector('.OwnAGGridContainer') as HTMLElement;
  
      if (!cellButton || !gridContainer) return;
  
      // Accumulate offset relative to grid container
      let offsetTop = 0;
      let offsetLeft = 0;
      let el: HTMLElement | null = cellButton;
  
      while (el && el !== gridContainer) {
        offsetTop += el.offsetTop;
        offsetLeft += el.offsetLeft;
        el = el.offsetParent as HTMLElement;
      }
  
      this.SearchResultsPosition = {
        top: `${offsetTop + cellButton.offsetHeight}px`, // below the cell
        left: `${offsetLeft}px`,                         // aligned left
        width: '450px'                                   // match 'name' column width
      };
    }, 0);
  }

  SearchResultsPosition: { top: string; left: string; width: string } = {
    top: '0px',
    left: '0px',
    width: '0px'
  };

  onCellValueChanged(event: any): void {
    console.log('🚨🚨🚨 onCellValueChanged ENTRY POINT - event:', event);

    const field = event.colDef.field;
    const rowIndex = event.rowIndex;

    console.log('🚨 field:', field, 'rowIndex:', rowIndex);

    const rowNode = event.api.getDisplayedRowAtIndex(rowIndex);

    console.log('🔍 onCellValueChanged fired for field:', field, 'rowIndex:', rowIndex);

    if (!rowNode) {
      console.log('🚨 NO ROWNODE - returning');
      return;
    }

    // ✅ Skip if this row is being updated from backend response (prevent infinite loop)
    if (this.rowsBeingUpdatedFromBackend.has(rowIndex)) {
      console.log('⏭️ Skipping onCellValueChanged - row is being updated from backend');
      return;
    }

    // ---------- Quantity changes ----------
    if (field === 'quantity') {
      const cartItem = rowNode.data?.CartItem;

      // ✅ Skip if no CartItem or empty row (CartItemStatus.ID === -2) - paste/search will handle quantity in the initial search
      // This ensures that when pasting Name+Qty together, the bulk search handles the quantity
      // But when pasting Qty-only into rows that ALREADY have a CartItem, we process the quantity change below
      if (!cartItem || cartItem?.CartItemStatus?.ID === -2) {
        console.log('⏭️ Skipping quantity change - no CartItem or empty row (search will include quantity)');
        return;
      }

      // ✅ REMOVED BLOCKING CONDITION: Previously this blocked quantity changes when isPasting or pendingPasteRowIndexes.has(rowIndex)
      // This was wrong because when pasting Qty-only into rows that already have products, we NEED to call ChangeGridQuantityVersion2
      // The check above (CartItemStatus.ID === -2) is sufficient to prevent duplicate quantity handling during Name+Qty paste
      // This now matches the behavior of list-prices-ag-grid which has no such blocking condition

      // Normalize to numbers and compare numerically
      const newNum = Number(event.newValue);
      const oldNum = Number(event.oldValue);

      // Only act when the new value is a number AND actually changed
      if (Number.isFinite(newNum) && newNum !== oldNum) {
        // ✅ Check for multi-select bulk update
        const selectedItems = this.getSelectedCartItems();
        const isBulkUpdate = selectedItems.length > 1;

        // ✅ Check if this is a RED ROW (NotValid) on ADMIN GRID
        const isRedRow = cartItem?.CartItemStatus?.ID === 0;
        const isAdminGrid = this.useAdminGrid;

        // ✅ Helper function to apply quantity to a single cart item (for red rows)
        const applyQuantityToRedRow = (itemCartItem: any, itemRowIndex: number) => {
          if (!itemCartItem) return;
          itemCartItem.Quantity = newNum;

          // ✅ If the red row has a stock offer, route through ChangeGridQuantityVersion2
          // so the overflow splitting logic can trigger (same as normal rows).
          if (itemCartItem.SelectedOffer?.IsStockOffer === true) {
            console.log('🔴 RED ROW with STOCK OFFER - routing through ChangeGridQuantityVersion2 for overflow detection');
            this.ChangeGridQuantityVersion2(itemRowIndex, newNum, itemCartItem);
            return;
          }

          // For red rows without stock offer, use AdminEditGridCell
          if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
          if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
          if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

          this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
        };

        if (isBulkUpdate && isAdminGrid) {
          console.log(`📦 BULK UPDATE: Applying quantity ${newNum} to ${selectedItems.length} selected items`);
          selectedItems.forEach((item: any) => {
            const itemCartItem = item.data?.CartItem;
            const itemIsRedRow = itemCartItem?.CartItemStatus?.ID === 0;
            if (itemIsRedRow) {
              applyQuantityToRedRow(itemCartItem, item.rowIndex);
            } else {
              // For normal rows, update quantity directly
              if (itemCartItem) {
                itemCartItem.Quantity = newNum;
                this.ChangeGridQuantityVersion2(item.rowIndex, newNum, itemCartItem);
              }
            }
          });
          this.gridApi?.refreshCells({ force: true });
        } else if (isRedRow && isAdminGrid) {
          console.log('🔴 RED ROW on ADMIN GRID - Quantity change via AdminEditGridCell');
          applyQuantityToRedRow(cartItem, rowIndex);
        } else {
          // ✅ NORMAL ROW - Use existing ChangeGridQuantityVersion2
          this.ChangeGridQuantityVersion2(rowIndex, newNum, cartItem);
        }
      }
      return; // nothing else to do
    }

    // ---------- PartNumber changes (PartNumber column - VALID and RED ROWS on ADMIN GRID) ----------
    if (field === 'partNumber') {
      console.log('🔍 onCellValueChanged for PartNumber:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Only allow editing on VALID (status 1) and RED ROWS (status 0) on ADMIN GRID
      const statusID = cartItem?.CartItemStatus?.ID;
      const isValidOrRedRow = statusID === 0 || statusID === 1;
      const isAdminGrid = this.useAdminGrid;

      if (!isValidOrRedRow || !isAdminGrid) {
        console.log('❌ PartNumber can only be edited on VALID or RED ROWS in ADMIN GRID, reverting');
        return;
      }

      // ✅ Validate part number value (allow empty or string, same as HSCode)
      const newPartNumber = (event.newValue ?? '').toString().trim();

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply part number to a single cart item
      const applyPartNumberToItem = (itemCartItem: any, itemRowIndex: number) => {
        if (!itemCartItem) return;
        if (!itemCartItem.Product) itemCartItem.Product = {};
        itemCartItem.Product.PartNumber = newPartNumber;

        if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
        if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
        if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

        this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
      };

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying PartNumber ${newPartNumber} to ${selectedItems.length} selected items`);
        selectedItems.forEach((item: any) => {
          applyPartNumberToItem(item.data?.CartItem, item.rowIndex);
        });
        this.gridApi?.refreshCells({ force: true });
      } else if (cartItem) {
        console.log('✅ PartNumber change on ADMIN GRID');
        applyPartNumberToItem(cartItem, rowIndex);
      }
      return;
    }

    // ---------- List-Price changes (List-Price column - ADMIN GRID ONLY) ----------
    if (field === 'officialListPrice') {
      console.log('🔍 onCellValueChanged for List-Price:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate list price value
      const parsedListPrice = this.parseNumberFromAnyFormat(event.newValue);
      if (parsedListPrice === null || isNaN(parsedListPrice) || parsedListPrice < 0) {
        console.log('❌ Invalid List-Price value, reverting');
        rowNode.setDataValue('officialListPrice', event.oldValue);
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply list price to a single cart item
      const applyListPriceToItem = (itemCartItem: any, itemRowIndex: number) => {
        if (!itemCartItem) return;
        itemCartItem.AdminPriceEdit = true;

        if (!itemCartItem.Product) itemCartItem.Product = {};
        if (!itemCartItem.Product.Stats) itemCartItem.Product.Stats = {};
        if (!itemCartItem.Product.Stats.StandardListPrice) itemCartItem.Product.Stats.StandardListPrice = {};

        itemCartItem.Product.Stats.StandardListPrice.UIUnitPrice = parsedListPrice;
        if (!itemCartItem.Product.Stats.StandardListPrice.Currency) {
          itemCartItem.Product.Stats.StandardListPrice.Currency = this.selectedCurrency;
        }

        if (!itemCartItem.Price) itemCartItem.Price = {};
        itemCartItem.Price.IsForcedPrice = true;

        this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
      };

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying List-Price ${parsedListPrice} to ${selectedItems.length} selected items`);
        selectedItems.forEach((item: any) => {
          applyListPriceToItem(item.data?.CartItem, item.rowIndex);
        });
        this.gridApi?.refreshCells({ columns: ['delete'], force: true });
      } else if (cartItem) {
        console.log('🔍 List-Price change via AdminEditGridCell');
        applyListPriceToItem(cartItem, rowIndex);

        // ✅ Refresh the delete column to show pencil icon
        if (this.gridApi && this.useAdminGrid) {
          setTimeout(() => {
            this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['delete'], force: true });
          }, 0);
        }
      }
      return;
    }

    // ---------- Discount changes (Discount column) ----------
    if (field === 'officialDiscount') {
      console.log('🔍 onCellValueChanged for discount:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate discount value
      const parsedDiscount = this.parseNumberFromAnyFormat(event.newValue);
      if (parsedDiscount === null || isNaN(parsedDiscount) || parsedDiscount < 0 || parsedDiscount > 100) {
        console.log('❌ Invalid discount value, reverting');
        rowNode.setDataValue('officialDiscount', event.oldValue);
        return;
      }

      // ✅ Check if ListPrice exists (for the edited row)
      const listPrice = cartItem?.Product?.Stats?.StandardListPrice?.UIUnitPrice;
      if (listPrice == null || listPrice <= 0) {
        console.log('❌ No valid ListPrice, reverting');
        rowNode.setDataValue('officialDiscount', event.oldValue);
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying discount ${parsedDiscount} to ${selectedItems.length} selected items`);
        // ✅ Prepare items for bulk update
        const bulkItems: { cartItem: any, rowIndex: number }[] = [];
        selectedItems.forEach((item: any) => {
          const itemCartItem = item.data?.CartItem;
          if (itemCartItem) {
            const itemListPrice = itemCartItem?.Product?.Stats?.StandardListPrice?.UIUnitPrice;
            if (itemListPrice != null && itemListPrice > 0) {
              itemCartItem.AdminPriceEdit = true;
              if (itemCartItem.Product?.Stats?.StandardListPrice?.Discount) {
                itemCartItem.Product.Stats.StandardListPrice.Discount.Value = parsedDiscount;
              }
              if (!itemCartItem.Price) itemCartItem.Price = {};
              itemCartItem.Price.IsForcedPrice = true;
              bulkItems.push({ cartItem: itemCartItem, rowIndex: item.rowIndex });
            }
          }
        });
        // ✅ Call bulk method to process all items in parallel
        if (bulkItems.length > 0) {
          this.AdminEditGridCellBulk(bulkItems, 'discount');
        }
      } else {
        // Single item update
        if (cartItem) {
          cartItem.AdminPriceEdit = true;
          if (cartItem.Product?.Stats?.StandardListPrice?.Discount) {
            cartItem.Product.Stats.StandardListPrice.Discount.Value = parsedDiscount;
          }
          if (!cartItem.Price) cartItem.Price = {};
          cartItem.Price.IsForcedPrice = true;
          console.log('✅ Set CartItem.Price.IsForcedPrice = true (Discount edit)');

          if (this.gridApi && this.useAdminGrid) {
            setTimeout(() => {
              this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['delete'], force: true });
            }, 0);
          }
          console.log('🔍 Calling AdminEditGridCell for discount');
          this.AdminEditGridCell(cartItem, rowIndex, 'discount');
        }
      }
      return;
    }

    // ---------- Price changes (Final-Price column) ----------
    if (field === 'price') {
      console.log('🔍 onCellValueChanged for price:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate price value
      const parsedPrice = this.parseNumberFromAnyFormat(event.newValue);
      if (parsedPrice === null || isNaN(parsedPrice) || parsedPrice < 0) {
        console.log('❌ Invalid price value, reverting');
        rowNode.setDataValue('price', event.oldValue);
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying price ${parsedPrice} to ${selectedItems.length} selected items`);
        // ✅ Prepare items for bulk update
        const bulkItems: { cartItem: any, rowIndex: number }[] = [];
        selectedItems.forEach((item: any) => {
          const itemCartItem = item.data?.CartItem;
          if (itemCartItem) {
            const isRedRow = itemCartItem?.CartItemStatus?.ID === 0;
            if (isRedRow && this.useAdminGrid) {
              if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
              if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
              itemCartItem.SelectedOffer.SellPrice.UIUnitPrice = parsedPrice;
              itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
              if (!itemCartItem.Price) itemCartItem.Price = {};
              itemCartItem.Price.UIUnitPrice = parsedPrice;
              itemCartItem.Price.IsForcedPrice = true;
            } else {
              itemCartItem.AdminPriceEdit = true;
              if (itemCartItem.SelectedOffer) {
                itemCartItem.SelectedOffer.FinalPrice = parsedPrice;
                if (itemCartItem.SelectedOffer.SellPrice) {
                  itemCartItem.SelectedOffer.SellPrice.UIUnitPrice = parsedPrice;
                }
              }
              if (!itemCartItem.Price) itemCartItem.Price = {};
              itemCartItem.Price.UIUnitPrice = parsedPrice;
              itemCartItem.Price.IsForcedPrice = true;
            }
            bulkItems.push({ cartItem: itemCartItem, rowIndex: item.rowIndex });
          }
        });
        // ✅ Call bulk method to process all items in parallel
        if (bulkItems.length > 0) {
          this.AdminEditGridCellBulk(bulkItems, 'price');
        }
      } else {
        // Single item update
        if (cartItem) {
          const isRedRow = cartItem?.CartItemStatus?.ID === 0;
          const isAdminGrid = this.useAdminGrid;

          if (isRedRow && isAdminGrid) {
            console.log('🔴 RED ROW on ADMIN GRID - Final-Price change via AdminEditGridCell');
            if (!cartItem.SelectedOffer) cartItem.SelectedOffer = {};
            if (!cartItem.SelectedOffer.SellPrice) cartItem.SelectedOffer.SellPrice = {};
            cartItem.SelectedOffer.SellPrice.UIUnitPrice = parsedPrice;
            cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
            if (!cartItem.Price) cartItem.Price = {};
            cartItem.Price.UIUnitPrice = parsedPrice;
            cartItem.Price.IsForcedPrice = true;
            console.log('✅ Set CartItem.Price.IsForcedPrice = true (RED ROW Final-Price edit)');
            console.log('🔍 Calling AdminEditGridCell for RED ROW price');
            this.AdminEditGridCell(cartItem, rowIndex, 'price');
          } else {
            cartItem.AdminPriceEdit = true;
            if (cartItem.SelectedOffer) {
              cartItem.SelectedOffer.FinalPrice = parsedPrice;
              if (cartItem.SelectedOffer.SellPrice) {
                cartItem.SelectedOffer.SellPrice.UIUnitPrice = parsedPrice;
              }
            }
            if (!cartItem.Price) cartItem.Price = {};
            cartItem.Price.UIUnitPrice = parsedPrice;
            cartItem.Price.IsForcedPrice = true;
            console.log('✅ Set CartItem.Price.UIUnitPrice =', parsedPrice, 'and IsForcedPrice = true (NORMAL ROW Final-Price edit)');
            console.log('🔍 Calling AdminEditGridCell for price');
            this.AdminEditGridCell(cartItem, rowIndex, 'price');
          }
        }

        // Refresh the purchase column and delete column to update color/bold styling and pencil icon
        if (this.gridApi && this.useAdminGrid) {
          setTimeout(() => {
            this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['purchase', 'purchaseDiscount', 'delete'], force: true });
          }, 0);
        }
      }
      return;
    }

    // ---------- Purchase Price changes (Purchase column - ADMIN GRID) ----------
    if (field === 'purchase') {
      console.log('🔍 onCellValueChanged for Purchase Price:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate purchase price value (same pattern as List-Price)
      const parsedPurchasePrice = this.parseNumberFromAnyFormat(event.newValue);
      if (parsedPurchasePrice === null || isNaN(parsedPurchasePrice) || parsedPurchasePrice < 0) {
        console.log('❌ Invalid Purchase Price value, skipping');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply purchase price to a single cart item
      const applyPurchasePriceToItem = (itemCartItem: any, itemRowIndex: number) => {
        if (!itemCartItem) return;

        if (!itemCartItem.OriginalPurchasePrice) {
          itemCartItem.OriginalPurchasePrice = itemCartItem.SelectedOffer?.ListPrice;
        }

        if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
        itemCartItem.SelectedOffer.ListPrice = parsedPurchasePrice;

        if (itemCartItem.Product?.ListPriceOffers?.length > 0) {
          itemCartItem.Product.ListPriceOffers[0].ListPrice = parsedPurchasePrice;
        }

        itemCartItem.AdminPriceEdit = true;

        if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
        if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

        this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
      };

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying purchase price ${parsedPurchasePrice} to ${selectedItems.length} selected items`);
        selectedItems.forEach((item: any) => {
          applyPurchasePriceToItem(item.data?.CartItem, item.rowIndex);
        });
        this.gridApi?.refreshCells({ columns: ['purchase', 'purchaseDiscount', 'delete'], force: true });
      } else if (cartItem) {
        console.log('✅ Purchase Price change on ADMIN GRID');
        applyPurchasePriceToItem(cartItem, rowIndex);
      }
      return;
    }

    // ---------- Purchase Discount (PD) changes (PD column - ADMIN GRID) ----------
    if (field === 'purchaseDiscount') {
      console.log('🔍 onCellValueChanged for PurchaseDiscount:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate PD value (allow negative values, but must be a valid number)
      const parsedPD = this.parseNumberFromAnyFormat(event.newValue);
      if (parsedPD === null || isNaN(parsedPD)) {
        console.log('❌ Invalid PD value, skipping');
        return;
      }

      // ✅ Get ListPrice to reverse-calculate purchase price
      const listPrice = cartItem?.Product?.Stats?.StandardListPrice?.UIUnitPrice;
      if (listPrice == null || listPrice <= 0) {
        console.log('❌ No valid ListPrice for PD reverse calculation, skipping');
        return;
      }

      // ✅ Reverse-calculate purchase price: PurchaseCost = ListPrice × (1 - PD / 100)
      const newPurchasePrice = Math.round((listPrice * (1 - parsedPD / 100)) * 100) / 100;
      console.log(`✅ PD=${parsedPD}% → Purchase Price=${newPurchasePrice} (ListPrice=${listPrice})`);

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply PD-derived purchase price to a single cart item
      const applyPDToItem = (itemCartItem: any, itemRowIndex: number) => {
        if (!itemCartItem) return;

        if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
        itemCartItem.SelectedOffer.ListPrice = newPurchasePrice;

        if (itemCartItem.Product?.ListPriceOffers?.length > 0) {
          itemCartItem.Product.ListPriceOffers[0].ListPrice = newPurchasePrice;
        }

        if (!itemCartItem.Price) itemCartItem.Price = {};
        if (!itemCartItem.Price.PurchaseDiscount) itemCartItem.Price.PurchaseDiscount = {};
        itemCartItem.Price.PurchaseDiscount.Value = parsedPD;

        itemCartItem.AdminPriceEdit = true;

        if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
        if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

        this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
      };

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying PD ${parsedPD}% to ${selectedItems.length} selected items`);
        selectedItems.forEach((item: any) => {
          applyPDToItem(item.data?.CartItem, item.rowIndex);
        });
        this.gridApi?.refreshCells({ columns: ['purchase', 'purchaseDiscount', 'delete'], force: true });
      } else if (cartItem) {
        console.log('✅ PD change on ADMIN GRID');
        applyPDToItem(cartItem, rowIndex);
      }
      return;
    }

    // ---------- HSCode changes (HSCode column - ADMIN GRID) ----------
    if (field === 'hscode') {
      console.log('🔍 onCellValueChanged for HSCode:', event.newValue);
      const cartItem = rowNode.data?.CartItem;

      // ✅ Validate HSCode value (allow empty or string)
      const newHSCode = (event.newValue ?? '').toString().trim();

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply HSCode to a single cart item
      const applyHSCodeToItem = (itemCartItem: any, itemRowIndex: number) => {
        if (!itemCartItem) return;
        if (!itemCartItem.Product) itemCartItem.Product = {};
        if (!itemCartItem.Product.Stats) itemCartItem.Product.Stats = {};
        itemCartItem.Product.Stats.HSCode = newHSCode;

        if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
        if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
        if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

        this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
      };

      if (isBulkUpdate) {
        console.log(`📦 BULK UPDATE: Applying HSCode ${newHSCode} to ${selectedItems.length} selected items`);
        selectedItems.forEach((item: any) => {
          applyHSCodeToItem(item.data?.CartItem, item.rowIndex);
        });
        this.gridApi?.refreshCells({ force: true });
      } else if (cartItem) {
        console.log('✅ HSCode change on ADMIN GRID');
        applyHSCodeToItem(cartItem, rowIndex);
      }
      return;
    }

    // ---------- ETA changes (Standard-Delivery column - FREE TEXT) ----------
    if (field === 'eta') {
      console.log('🔍 onCellValueChanged for ETA (Standard-Delivery):', event.newValue);
      const newETA = (event.newValue ?? '').toString().trim();
      const oldETA = (event.oldValue ?? '').toString().trim();  // ✅ Store old value for revert
      const cartItem = rowNode.data?.CartItem;

      console.log('✅ ETA text changed from:', oldETA, 'to:', newETA);
      console.log('✅ CartItem:', cartItem);

      // ✅ Don't call backend if the text is empty or just dashes
      if (!newETA || newETA === '------') {
        console.log('⚠️ Skipping backend call - empty or default ETA');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Call backend to identify lead time from text
      const request = {
        User: this.parent.User,
        Language: this.parent.getLanguage(),
        CellText: newETA,  // ✅ Backend expects CellText, not LeadTimeText
        IsProlonged: false
      };

      console.log('📤 Calling IdentifyGridLeadTime with request:', request);

      this.managementService.IdentifyGridLeadTime(request).subscribe(response => {
        console.log('📥 IdentifyGridLeadTime response:', response);
        if (response["Status"] === 1) {
          console.log('✅ Backend identified lead time:', response);

          // ✅ Helper function to apply lead time to a single cart item
          const applyLeadTimeToItem = (itemCartItem: any, itemNode: any, itemRowIndex: number) => {
            if (!response["LeadTime"] || !itemCartItem) return;

            if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
            itemCartItem.SelectedOffer.LeadTime = JSON.parse(JSON.stringify(response["LeadTime"]));
            itemCartItem.SelectedOffer.IsStockOffer = response["IsStock"];

            if (!itemCartItem.Product) itemCartItem.Product = {};
            if (!itemCartItem.Product.ListPriceOffers || itemCartItem.Product.ListPriceOffers.length === 0) {
              itemCartItem.Product.ListPriceOffers = [{}];
            }
            itemCartItem.Product.ListPriceOffers[0].LeadTime = JSON.parse(JSON.stringify(response["LeadTime"]));
            itemCartItem.Product.ListPriceOffers[0].IsStockOffer = response["IsStock"];
            itemCartItem.Product.ListPriceOffers[0].AdminGridStockMime = response["IsStock"];

            if (response["IsStock"] === true) {
              itemCartItem.SelectedOffer.StockQuantity = 999999;
              itemCartItem.Product.ListPriceOffers[0].StockQuantity = 999999;
            } else {
              itemCartItem.SelectedOffer.StockQuantity = 0;
              itemCartItem.Product.ListPriceOffers[0].StockQuantity = 0;
            }

            const cartIndex = this.parent.Cart.Items.findIndex((item: any) => item.UIID === itemCartItem.UIID);
            if (cartIndex >= 0) {
              this.parent.Cart.Items[cartIndex] = JSON.parse(JSON.stringify(itemCartItem));
            }

            itemNode.data.CartItem = JSON.parse(JSON.stringify(itemCartItem));
            const leadTime = itemCartItem.SelectedOffer.LeadTime;
            const etaDisplay = leadTime?.UIOutput ?? (leadTime?.Units > 0 ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}` : '------');
            itemNode.data.eta = etaDisplay;

            if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
            if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
            this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
          };

          // ✅ Check for bulk update
          if (isBulkUpdate) {
            console.log(`📦 BULK UPDATE: Applying lead time to ${selectedItems.length} selected items`);
            selectedItems.forEach((item: any) => {
              applyLeadTimeToItem(item.data?.CartItem, item.node, item.rowIndex);
            });
            this.gridApi?.refreshCells({ force: true });
            this.gridApi?.redrawRows();
          } else {
            // Single item update
            applyLeadTimeToItem(cartItem, rowNode, rowIndex);
            this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
            this.gridApi.redrawRows({ rowNodes: [rowNode] });
          }

          // ✅ Save to localStorage (pass red row flag for precheck gate)
          this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);
        } else {
          // ❌ Backend failed to parse - revert to old value
          console.warn('⚠️ Backend could not identify lead time:', response["Message"]);

          // ✅ Revert the grid cell to old value
          rowNode.data.eta = oldETA;
          console.log('🔄 Reverted ETA cell to old value:', oldETA);

          // ✅ Refresh the grid cell to show reverted value
          this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

          // ✅ Show error message to user
          this.parent.openSnackBar(response["Message"] || 'Could not parse lead time', "OK", "alert-info-snackbar");
        }
      }, error => {
        // ❌ Network error - revert to old value
        console.error('❌ Error calling IdentifyGridLeadTime:', error);

        // ✅ Revert the grid cell to old value
        rowNode.data.eta = oldETA;
        console.log('🔄 Reverted ETA cell to old value due to error:', oldETA);

        // ✅ Refresh the grid cell to show reverted value
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

        // ✅ Show error message to user
        this.parent.openSnackBar('Error parsing lead time', "OK", "alert-info-snackbar");
      });

      return;
    }

    // ---------- COO changes (COO column - Country identification via backend) ----------
    if (field === 'coo') {
      console.log('🔍 onCellValueChanged for COO:', event.newValue);
      const newCOO = (event.newValue ?? '').toString().trim();
      const oldCOO = (event.oldValue ?? '').toString().trim();
      const cartItem = rowNode.data?.CartItem;

      console.log('✅ COO text changed from:', oldCOO, 'to:', newCOO);

      // ✅ Skip if empty
      if (!newCOO) {
        console.log('⚠️ Empty COO value');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Call backend to identify country from text
      const request = {
        User: this.parent.User,
        Language: this.parent.getLanguage(),
        CellText: newCOO  // ✅ Backend expects CellText
      };

      console.log('📤 Calling IdentifyGridCOO with request:', request);

      this.managementService.IdentifyGridCOO(request).subscribe(response => {
        console.log('📥 IdentifyGridCOO response:', response);
        if (response["Status"] === 1) {
          console.log('✅ Backend identified country:', response);

          // ✅ Helper function to apply COO to a single cart item
          const applyCOOToItem = (itemCartItem: any, itemNode: any, itemRowIndex: number) => {
            if (!itemCartItem) return;
            if (!itemCartItem.Product) itemCartItem.Product = {};
            if (!itemCartItem.Product.Stats) itemCartItem.Product.Stats = {};

            itemCartItem.Product.Stats.COO = response["Country"];
            const cooDisplay = response["Country"]?.ShortName ?? '';
            itemNode.data.coo = cooDisplay;

            const cartIndex = this.parent.Cart.Items.findIndex((item: any) => item.UIID === itemCartItem.UIID);
            if (cartIndex >= 0) {
              this.parent.Cart.Items[cartIndex] = JSON.parse(JSON.stringify(itemCartItem));
            }

            if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
            if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
            if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

            this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
          };

          // ✅ Apply to all selected items or single item
          if (isBulkUpdate) {
            console.log(`📦 BULK UPDATE: Applying COO to ${selectedItems.length} selected items`);
            selectedItems.forEach((item: any) => {
              applyCOOToItem(item.data?.CartItem, item.node, item.rowIndex);
            });
            this.gridApi?.refreshCells({ force: true });
          } else if (cartItem) {
            applyCOOToItem(cartItem, rowNode, rowIndex);
            this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          }

          // ✅ Save to localStorage (pass red row flag for precheck gate)
          this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);

          // ✅ Stop editing (de-focus) the cell
          this.gridApi.stopEditing();
        } else {
          // ❌ Backend failed to identify country - revert to old value
          console.warn('⚠️ Backend could not identify country:', response["Message"]);
          rowNode.data.coo = oldCOO;
          this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          this.parent.openSnackBar(response["Message"] || 'Could not identify country', "OK", "alert-info-snackbar");
        }
      }, () => {
        // ❌ Network error - revert to old value
        console.error('❌ Error calling IdentifyGridCOO');
        rowNode.data.coo = oldCOO;
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
        this.parent.openSnackBar('Error identifying country', "OK", "alert-info-snackbar");
      });

      return;
    }

    // ---------- BRAND changes (Brand column - Brand identification via backend) ----------
    if (field === 'brand') {
      console.log('🔍 onCellValueChanged for Brand:', event.newValue);
      const newBrand = (event.newValue ?? '').toString().trim();
      const oldBrand = (event.oldValue ?? '').toString().trim();
      const cartItem = rowNode.data?.CartItem;

      // ✅ Skip if empty
      if (!newBrand) {
        console.log('⚠️ Empty Brand value');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Call backend to identify brand from text
      const request = {
        User: this.parent.User,
        Language: this.parent.getLanguage(),
        CellText: newBrand
      };

      this.managementService.IdentifyGridBrand(request).subscribe(response => {
        if (response["Status"] === 1) {
          console.log('✅ Backend identified brand:', response["Brand"]);

          // ✅ Helper function to apply brand to a single cart item
          const applyBrandToItem = (itemCartItem: any, itemNode: any) => {
            if (!itemCartItem) return;
            if (!itemCartItem.Product) itemCartItem.Product = {};
            itemCartItem.Product.Brand = response["Brand"];
            const brandDisplay = response["Brand"]?.Name ?? '';
            itemNode.data.brand = brandDisplay;

            const cartIndex = this.parent.Cart.Items.findIndex((item: any) => item.UIID === itemCartItem.UIID);
            if (cartIndex >= 0) {
              this.parent.Cart.Items[cartIndex] = JSON.parse(JSON.stringify(itemCartItem));
            }
          };

          // ✅ Apply to all selected items or single item
          if (isBulkUpdate) {
            console.log(`📦 BULK UPDATE: Applying brand to ${selectedItems.length} selected items`);
            selectedItems.forEach((item: any) => {
              applyBrandToItem(item.data?.CartItem, item.node);
            });
            this.gridApi?.refreshCells({ force: true });
          } else if (cartItem) {
            applyBrandToItem(cartItem, rowNode);
            this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          }

          this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);
          this.gridApi.stopEditing();
        } else {
          console.warn('⚠️ Backend could not identify brand:', response["Message"]);
          rowNode.data.brand = oldBrand;
          this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          this.parent.openSnackBar(response["Message"] || 'Could not identify brand', "OK", "alert-info-snackbar");
        }
      }, () => {
        console.error('❌ Error calling IdentifyGridBrand');
        rowNode.data.brand = oldBrand;
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
        this.parent.openSnackBar('Could not identify brand', "OK", "alert-info-snackbar");
      });

      return;
    }

    // ---------- SUPPLIER changes (Supplier column - Partner identification via backend) ----------
    if (field === 'supplier') {
      console.log('🔍 onCellValueChanged for Supplier:', event.newValue);
      const newSupplier = (event.newValue ?? '').toString().trim();
      const oldSupplier = (event.oldValue ?? '').toString().trim();
      const cartItem = rowNode.data?.CartItem;

      // ✅ Skip if empty
      if (!newSupplier) {
        console.log('⚠️ Empty Supplier value');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Helper function to apply supplier result to a single cart item
      const applySupplierToItem = (itemCartItem: any, itemNode: any, identifiedOffer: any, identifiedPartner: any) => {
        if (!itemCartItem) return;
        if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};

        itemCartItem.SelectedOffer.Partner = identifiedPartner;
        itemCartItem.SelectedOffer.ID = (identifiedOffer.ID != null && identifiedOffer.ID > 0) ? identifiedOffer.ID : 0;
        itemCartItem.SelectedOffer.ListPrice = identifiedOffer.ListPrice;
        itemCartItem.SelectedOffer.Currency = identifiedOffer.Currency;
        itemCartItem.SelectedOffer.PurchaseCurrency = identifiedOffer.Currency;

        if (itemCartItem.Product?.ListPriceOffers?.[0]) {
          itemCartItem.Product.ListPriceOffers[0].PurchaseCurrency = identifiedOffer.Currency;
          itemCartItem.Product.ListPriceOffers[0].Currency = identifiedOffer.Currency;
        }

        itemNode.data.supplier = identifiedPartner?.Name ?? '';
        itemNode.data.purchase = identifiedOffer.ListPrice;
        itemNode.data.purchaseCurrency = identifiedOffer.Currency?.Symbol || identifiedOffer.Currency?.Name || null;

        const cartIndex = this.parent.Cart.Items.findIndex((ci: any) => ci.UIID === itemCartItem.UIID);
        if (cartIndex >= 0) {
          this.parent.Cart.Items[cartIndex] = itemCartItem;
        }
      };

      if (isBulkUpdate) {
        // ✅ BULK UPDATE: Search for each selected item's product individually in parallel
        console.log(`📦 BULK UPDATE: Searching supplier "${newSupplier}" for ${selectedItems.length} selected items in parallel`);
        this.parent.loading = true;

        // ✅ Create individual requests for each selected item's product
        const requests = selectedItems.map((item: any) => {
          const itemCartItem = item.data?.CartItem;
          const itemProductId = itemCartItem?.Product?.ID;
          return this.managementService.IdentifyPartnerWithOffers({
            User: this.parent.User,
            Language: this.parent.getLanguage(),
            CellText: newSupplier,
            CompanyID: this.parent.B2BPartner?.FK_CompanyID || 0,
            ProductID: itemProductId || null,
            FallbackCurrency: this.selectedCurrency  // ✅ Pass selected currency as fallback
          });
        });

        // ✅ Execute all requests in parallel using forkJoin
        forkJoin(requests).subscribe(responses => {
          this.parent.loading = false;
          const updatedNodes: any[] = [];
          let successCount = 0;

          responses.forEach((response: any, index: number) => {
            const item = selectedItems[index];
            const itemCartItem = item.data?.CartItem;
            const itemNode = item.node;

            if (response["Status"] === 1 && response["Offers"] && response["Offers"].length > 0) {
              const identifiedOffer = response["Offers"][0];
              const identifiedPartner = identifiedOffer?.Partner;

              if (identifiedPartner) {
                applySupplierToItem(itemCartItem, itemNode, identifiedOffer, identifiedPartner);
                updatedNodes.push(itemNode);
                successCount++;
                console.log(`✅ Row ${item.rowIndex}: Found ${identifiedPartner.Name} with price ${identifiedOffer.ListPrice}`);
              }
            } else {
              console.warn(`⚠️ Row ${item.rowIndex}: Could not find supplier for product`);
            }
          });

          if (updatedNodes.length > 0) {
            this.gridApi?.redrawRows();
          }
          this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);
          this.gridApi.stopEditing();
          console.log(`📦 BULK UPDATE: ${successCount}/${selectedItems.length} items updated with supplier`);
        }, () => {
          this.parent.loading = false;
          console.error('❌ Error in bulk IdentifyPartnerWithOffers');
          this.parent.openSnackBar('Could not identify supplier for some items', "OK", "alert-info-snackbar");
        });
      } else {
        // ✅ Single item update
        const productId = cartItem?.Product?.ID;
        const request = {
          User: this.parent.User,
          Language: this.parent.getLanguage(),
          CellText: newSupplier,
          CompanyID: this.parent.B2BPartner?.FK_CompanyID || 0,
          ProductID: productId || null,
          FallbackCurrency: this.selectedCurrency  // ✅ Pass selected currency as fallback
        };

        this.managementService.IdentifyPartnerWithOffers(request).subscribe(response => {
          if (response["Status"] === 1 && response["Offers"] && response["Offers"].length > 0) {
            const identifiedOffer = response["Offers"][0];
            const identifiedPartner = identifiedOffer?.Partner;

            if (!identifiedPartner) {
              this.parent.openSnackBar('Supplier found but missing partner details', "OK", "alert-info-snackbar");
              return;
            }

            applySupplierToItem(cartItem, rowNode, identifiedOffer, identifiedPartner);
            this.gridApi.redrawRows({ rowNodes: [rowNode] });
            this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);
            this.gridApi.stopEditing();
          } else {
            console.warn('⚠️ Backend could not identify partner:', response["Message"]);
            rowNode.data.supplier = oldSupplier;
            this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
            this.parent.openSnackBar(response["Message"] || 'Could not identify supplier', "OK", "alert-info-snackbar");
          }
        }, () => {
          console.error('❌ Error calling IdentifyPartnerWithOffers');
          rowNode.data.supplier = oldSupplier;
          this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          this.parent.openSnackBar('Could not identify supplier', "OK", "alert-info-snackbar");
        });
      }

      return;
    }

    // ---------- WEIGHT changes (Weight column - FREE TEXT) ----------
    if (field === 'weight') {
      console.log('🔍 onCellValueChanged for Weight:', event.newValue);
      const newWeight = (event.newValue ?? '').toString().trim();
      const oldWeight = (event.oldValue ?? '').toString().trim();
      const cartItem = rowNode.data?.CartItem;

      // ✅ Don't call backend if the text is empty
      if (!newWeight) {
        console.log('⚠️ Skipping backend call - empty weight');
        return;
      }

      // ✅ Check for multi-select bulk update
      const selectedItems = this.getSelectedCartItems();
      const isBulkUpdate = selectedItems.length > 1;

      // ✅ Call backend to identify weight from text
      const request = {
        User: this.parent.User,
        Language: this.parent.getLanguage(),
        CellText: newWeight
      };

      this.managementService.IdentifyGridWeight(request).subscribe(response => {
        if (response["Status"] === 1) {
          console.log('✅ Backend identified weight:', response);

          // ✅ Helper function to apply weight to a single cart item
          const applyWeightToItem = (itemCartItem: any, itemNode: any, itemRowIndex: number) => {
            if (!itemCartItem) return;
            if (!itemCartItem.Product) itemCartItem.Product = {};
            if (!itemCartItem.Product.Stats) itemCartItem.Product.Stats = {};

            itemCartItem.Product.Stats.Weight = response["Weight"];
            itemCartItem.Product.Stats.WeightMeasurementType = JSON.parse(JSON.stringify(response["WeightMeasurementType"]));

            const weight = itemCartItem.Product.Stats.Weight;
            const weightType = itemCartItem.Product.Stats.WeightMeasurementType;
            const weightDisplay = weight && weightType ? `${weight} ${weightType.Name}` : '';
            itemNode.data.weight = weightDisplay;

            const cartIndex = this.parent.Cart.Items.findIndex((item: any) => item.UIID === itemCartItem.UIID);
            if (cartIndex >= 0) {
              this.parent.Cart.Items[cartIndex] = JSON.parse(JSON.stringify(itemCartItem));
            }

            if (!itemCartItem.SelectedOffer) itemCartItem.SelectedOffer = {};
            if (!itemCartItem.SelectedOffer.SellPrice) itemCartItem.SelectedOffer.SellPrice = {};
            if (!itemCartItem.SelectedOffer.SellPrice.Currency) itemCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

            this.AdminEditGridCell(itemCartItem, itemRowIndex, 'price');
          };

          // ✅ Apply to all selected items or single item
          if (isBulkUpdate) {
            console.log(`📦 BULK UPDATE: Applying weight to ${selectedItems.length} selected items`);
            selectedItems.forEach((item: any) => {
              applyWeightToItem(item.data?.CartItem, item.node, item.rowIndex);
            });
            this.gridApi?.refreshCells({ force: true });
          } else if (cartItem) {
            applyWeightToItem(cartItem, rowNode, rowIndex);
            this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          }

          this.UpdateCart(cartItem?.CartItemStatus?.ID === 0);
        } else {
          console.warn('⚠️ Backend could not identify weight:', response["Message"]);
          rowNode.data.weight = oldWeight;
          this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
          this.parent.openSnackBar(response["Message"] || 'Could not parse weight', "OK", "alert-info-snackbar");
        }
      }, () => {
        console.error('❌ Error calling IdentifyGridWeight');
        rowNode.data.weight = oldWeight;
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
        this.parent.openSnackBar('Error parsing weight', "OK", "alert-info-snackbar");
      });

      return;
    }

    // ---------- Name changes (your existing flow, unchanged) ----------
    if (field !== 'name') return;

    const newValue = (event.newValue ?? '').toString().trim();
    if (!newValue) return;

    const oldValue = (event.oldValue ?? '').toString().trim();
    if (oldValue !== newValue && oldValue.length > 0 && newValue.length > 0) {
      if (rowNode?.data?.CartItem?.WasSearchedFor === true) {
        rowNode.data.CartItem.WasSearchedFor = false;
      }
    }

    const wasSearched = rowNode?.data?.CartItem?.WasSearchedFor === true;
    if (wasSearched) return;

    // ✅ Detect if this is keyboard typing (not from paste)
    if (this.currentSearchSource === null && !this.isPasting) {
      this.currentSearchSource = 'keyboard';
    }

    const quantity = rowNode?.data?.quantity ?? '';
    this.pastedRows.push({ rowIndex, name: newValue, quantity });
    this.pasteSubject.next();
  }
  
  handlePaste(params: any): void {
    this.gridApi.stopEditing(); // ✅ Prevent AG Grid from showing paste confirmation

    this.pastedRows = null;
    this.pastedRows = [];
  
    const seen = new Set<number>();
    const allPastedRows: { rowIndex: number; name: string; quantity: any }[] = [];
  
    params.api.forEachNode((node: any) => {
      if (!node || node.rowIndex == null) return;
  
      const rowIndex = node.rowIndex;
      const name = node.data?.name ?? '';
      if (!name || !name.trim()) return;
  
      if (!seen.has(rowIndex)) {
        const quantity = node.data?.quantity ?? '';
        allPastedRows.push({ rowIndex, name, quantity });
        seen.add(rowIndex);
      }
    });
  
    let trimmedRows = allPastedRows;
    if (allPastedRows.length > this.MAX_PASTE_ROWS) {
      trimmedRows = allPastedRows.slice(0, this.MAX_PASTE_ROWS);
  
      for (let i = this.MAX_PASTE_ROWS; i < allPastedRows.length; i++) {
        const rowIndex = allPastedRows[i].rowIndex;
        const node = this.gridApi.getDisplayedRowAtIndex(rowIndex);
        if (node?.data) {
          node.setDataValue('name', '');
          node.setDataValue('quantity', null);
        }
      }
  
      this.parent.openSnackBar(
        `⚠️ Paste limited to ${this.MAX_PASTE_ROWS} items.`,
        'OK',
        'alert-info-snackbar'
      );
    }
  
    const lastRowIndex = trimmedRows.length > 0
      ? trimmedRows[trimmedRows.length - 1].rowIndex
      : 0;
  
    const currentRowCount = this.gridApi.getDisplayedRowCount();
    const requiredRowCount = lastRowIndex + 1;
  
    const finalize = () => {
      this.pastedRows.push(...trimmedRows);
      this.pasteSubject.next();
  
      // ✅ RESET counter after successful paste
      this.pastedLineCount = 0;
    };
  
    if (requiredRowCount > currentRowCount) {
      const rowsToAdd = requiredRowCount - currentRowCount;
      this.addMoreRows(rowsToAdd);
  
      setTimeout(finalize, 50);
    } else {
      finalize();
    }
  
    console.log('📋 Trimmed + cleaned paste rows:', trimmedRows.length);
  }

  // Delete selected rows based on cell ranges
  deleteSelectedRows(cellRanges: any[]): void {
    try {
      // Collect all unique row indices from all cell ranges
      const rowIndicesToDelete = new Set<number>();

      cellRanges.forEach(range => {
        const startRow = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
        const endRow = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

        // Add all row indices in this range
        for (let i = startRow; i <= endRow; i++) {
          rowIndicesToDelete.add(i);
        }
      });

      if (rowIndicesToDelete.size === 0) {
        return;
      }

      // Convert to sorted array (delete from bottom to top to avoid index shifting)
      const sortedIndices = Array.from(rowIndicesToDelete).sort((a, b) => b - a);

      console.log('🗑️ Deleting rows:', sortedIndices);

      // Get the row nodes and their UIIDs
      const rowsToDelete: any[] = [];
      sortedIndices.forEach(rowIndex => {
        const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
        if (rowNode && rowNode.data) {
          rowsToDelete.push({
            rowIndex: rowIndex,
            uiid: rowNode.data.CartItem?.UIID,
            data: rowNode.data
          });
        }
      });

      // Remove from parent.Cart.Items if they have UIIDs
      rowsToDelete.forEach(row => {
        if (row.uiid) {
          this.parent.Cart.Items = this.parent.Cart.Items.filter((item: any) => item.UIID !== row.uiid);
        }
      });

      // Remove rows from grid (from bottom to top)
      const transaction: any = {
        remove: rowsToDelete.map(r => r.data)
      };
      this.gridApi.applyTransaction(transaction);

      // Update cart
      this.UpdateCart();

      // Check if grid is now empty and reset GridID + tracking data
      const remainingRows = this.gridApi.getDisplayedRowCount();
      if (remainingRows === 0) {
        this.resetGridID();
        localStorage.removeItem(this.ADMIN_TRACKING_KEY); // ✅ Also delete tracking data
        console.log('🔄 Grid emptied - GridID and tracking data reset');
      }

      // Show feedback
      this.parent.openSnackBar(
        `Deleted ${rowsToDelete.length} row(s)`,
        'OK',
        'alert-info-snackbar'
      );

      console.log('✅ Rows deleted successfully');

    } catch (error) {
      console.error('❌ Error deleting rows:', error);
      this.parent.openSnackBar(
        'Error deleting rows',
        'OK',
        'alert-info-snackbar'
      );
    }
  }

  // Trigger paste from context menu using Clipboard API
  async triggerPasteFromContextMenu(params: any): Promise<void> {
    try {
      // ✅ Mark as right-click paste (mouse paste)
      this.isPasting = true;
      this.pasteEventType = 3; // eB2BUserTrackingEventType.CopyPasteMouse

      // Read from clipboard
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText || clipboardText.trim() === '') {
        this.parent.openSnackBar('Clipboard is empty', 'OK', 'alert-info-snackbar');
        this.isPasting = false;
        this.pasteEventType = null;
        return;
      }

      // Get the focused cell or first cell if none focused
      let startRowIndex = 0;
      let startColKey = 'name';

      const focusedCell = params.api.getFocusedCell();
      if (focusedCell) {
        startRowIndex = focusedCell.rowIndex;
        startColKey = focusedCell.column.getColId();
      }

      // Parse clipboard data (tab-separated for columns, newline for rows)
      const rows = clipboardText.split(/\r?\n/).filter(line => line.trim() !== '');

      // Process each row
      const allPastedRows: { rowIndex: number; name: string; quantity: any }[] = [];

      for (let i = 0; i < rows.length; i++) {
        const rowIndex = startRowIndex + i;
        const rowData = rows[i].split('\t');

        // Ensure we have enough rows in the grid
        const currentRowCount = this.gridApi.getDisplayedRowCount();
        if (rowIndex >= currentRowCount) {
          this.addMoreRows(rowIndex - currentRowCount + 1);
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode) continue;

        // Determine which columns to paste based on start column
        let nameValue = '';
        let quantityValue = '';

        if (startColKey === 'name') {
          nameValue = rowData[0] || '';
          quantityValue = rowData[1] || '';
        } else if (startColKey === 'quantity') {
          quantityValue = rowData[0] || '';
        } else {
          // For other columns, just paste into name
          nameValue = rowData[0] || '';
        }

        // Update the grid cells
        if (nameValue) {
          rowNode.setDataValue('name', nameValue);
        }
        if (quantityValue) {
          rowNode.setDataValue('quantity', quantityValue);
        }

        // Track for search
        if (nameValue) {
          allPastedRows.push({ rowIndex, name: nameValue, quantity: quantityValue });
        }
      }

      // Limit to MAX_PASTE_ROWS
      let trimmedRows = allPastedRows;
      if (allPastedRows.length > this.MAX_PASTE_ROWS) {
        trimmedRows = allPastedRows.slice(0, this.MAX_PASTE_ROWS);
        this.parent.openSnackBar(
          `⚠️ Paste limited to ${this.MAX_PASTE_ROWS} items.`,
          'OK',
          'alert-info-snackbar'
        );
      }

      // Trigger search for pasted items
      this.pastedRows.push(...trimmedRows);
      this.pasteSubject.next();

      console.log('📋 Context menu paste completed:', trimmedRows.length, 'rows');

    } catch (error) {
      console.error('❌ Paste from context menu failed:', error);
      this.parent.openSnackBar(
        'Paste failed. Please use Ctrl+V or ensure clipboard permissions are granted.',
        'OK',
        'alert-info-snackbar'
      );
    }
  }


  LoadingSideOffers:boolean = false;
  SideOffers:any[];

  // ✅ Supplier popup search properties
  SupplierPopupSearchTerm: string = '';
  SupplierPopupSearchResults: any[] = [];
  LoadingSupplierPopupSearch: boolean = false;
  OriginalSideOffers: any[] = []; // Keep original side offers in memory

  GetIndustryProductCalculatedSideOffersWithCollateral(rowIndex:number, IndustryProductIDs:number[], offerId:number, UIBrand:any, BestOfferOnly:boolean, GetAlsoCollateralSpecialOffers:boolean, GetOnlySpecialOffers: boolean){
    var request = {
      User:this.parent.User,
      Language:this.parent.getLanguage(),
      PartnerID:this.parent.B2BPartner?.ID,
      IndustryProductIDs:IndustryProductIDs,
      SelectedOfferID:offerId,
      BestOfferOnly:BestOfferOnly,
      GetAlsoCollateralSpecialOffers:GetAlsoCollateralSpecialOffers,
      GetOnlySpecialOffers:GetOnlySpecialOffers,
      Brand:UIBrand,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      StockStatus: this.StockStatus,
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    // ✅ Clear AlternativeProducts for Tab 2
    this.AlternativeProducts = [];

    this.LoadingSideOffers = true;

    this.B2BService.GetIndustryProductCalculatedSideOffersWithCollateral(request).subscribe(response => {

      if(response["Status"] != 1){
        // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      else if(response["Status"] == 1){
        // ✅ Populate AlternativeProducts for Tab 2 (alternative products grid)
        this.AlternativeProducts = JSON.parse(JSON.stringify(response["CartItems"]));
      }

      this.LoadingSideOffers = false;
     });
  }

  GetIndustryProductCalculatedSideOffers(rowIndex:number, IndustryProductID:number, offerId:number, fromSupplierCellClick: boolean = false){
    // ✅ Determine ConfigStatus: if admin grid + supplier cell click, use VisibleOnlyForAdminPurchases (ID=-9000)
    let configStatusToUse = this.selectedConfigStatus;

    if (this.useAdminGrid && fromSupplierCellClick) {
      // ✅ Find ConfigStatus with ID == -9000 (VisibleOnlyForAdminPurchases)
      const adminPurchasesStatus = this.parent.masterData?.ConfigStatuses?.find((s: any) => s.ID === -9000);
      if (adminPurchasesStatus) {
        configStatusToUse = adminPurchasesStatus;
        console.log('✅ Supplier cell clicked on admin grid - using ConfigStatus:', adminPurchasesStatus.Name, '(ID:', adminPurchasesStatus.ID, ')');
      }
    }

    var request = {
      User:this.parent.User,
      Language:this.parent.getLanguage(),
      PartnerID:this.parent.B2BPartner?.ID,
      IndustryProductID:IndustryProductID,
      SelectedOfferID:offerId,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      StockStatus: this.StockStatus,
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: configStatusToUse,
        UserViewsPurchasePrices: fromSupplierCellClick // ✅ Prevent currency exchange on ListPrice when viewing purchase prices
      }
    };

    this.SideOffers = null;
    this.SideOffers = [];

    this.LoadingSideOffers = true;

    this.B2BService.GetIndustryProductCalculatedSideOffers(request).subscribe(response => {

      if(response["Status"] != 1){
        // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      else if(response["Status"] == 1){
        const cartItems = JSON.parse(JSON.stringify(response["CartItems"]));

        // ✅ Sort by ListPrice ascending (best price first)
        this.SideOffers = cartItems.sort((a: any, b: any) => {
          const priceA = a?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
          const priceB = b?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
          return priceA - priceB;
        });

        // ✅ Mark the currently selected offer based on SelectedOffer.ID from the row
        const currentSelectedOfferId = this.ClickedRow?.CartItem?.SelectedOffer?.ID;
        if (currentSelectedOfferId) {
          this.SideOffers.forEach((offer: any) => {
            offer.IsSelected = (offer?.SelectedOffer?.ID === currentSelectedOfferId);
          });
        }

        // ✅ Store original side offers in memory
        this.OriginalSideOffers = JSON.parse(JSON.stringify(this.SideOffers));

        // ✅ Clear search term when new data arrives
        this.SupplierPopupSearchTerm = '';
        this.SupplierPopupSearchResults = [];
      }

      this.LoadingSideOffers = false;
     });
  }

  GetIndustryProductByStockOverRequestedQuantity(rowIndex:number, overFlowingCartItem:any){
    var request = {
      User:this.parent.User,
      Language:this.parent.getLanguage(),
      PartnerID:this.parent.B2BPartner?.ID,
      CartItem:overFlowingCartItem,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
       B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    this.SideOffers = null;
    this.SideOffers = [];

    this.LoadingSideOffers = true;
  
    this.B2BService.GetIndustryProductByStockOverRequestedQuantity(request).subscribe(response => {

      if(response["Status"] != 1){
        // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      else if(response["Status"] == 1){
        // Check if no other offers are available
        if(response["NoOtherOffersAvailable"] == true){
          this.stockReadjustedMessage = response["NoOtherOffersMessage"];
          this.stockReadjustedProducts = [];
          this.showStockReadjustedPopup = true;
        }
        this.SideOffers = JSON.parse(JSON.stringify(response["CartItems"]));
      }

      this.LoadingSideOffers = false;
     });
  }

  // ✅ Search partners in supplier popup
  SearchSupplierPopup() {
    const searchTerm = this.SupplierPopupSearchTerm?.trim() || '';

    // If search term is empty, restore original side offers
    if (searchTerm.length === 0) {
      this.SupplierPopupSearchResults = [];
      return;
    }

    // Require at least 2 characters
    if (searchTerm.length < 2) {
      this.SupplierPopupSearchResults = [];
      return;
    }

    const request = {
      SearchTerm: searchTerm,
      CountryID: null,
      PageIndex: 1,
      PageSize: 20,
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      CompanyID: null
    };

    this.LoadingSupplierPopupSearch = true;

    this.service.GetPartnersFiltered(request).subscribe(response => {
      if (response["Status"] == 1) {
        this.SupplierPopupSearchResults = response["Partners"] || [];
      } else {
        this.SupplierPopupSearchResults = [];
      }
      this.LoadingSupplierPopupSearch = false;
    }, error => {
      this.SupplierPopupSearchResults = [];
      this.LoadingSupplierPopupSearch = false;
    });
  }

  // ✅ Select a partner from search results (no configuration, no price)
  SelectPartnerFromSearch(partner: any) {
    console.log('✅ SelectPartnerFromSearch called with:', partner);

    // Get the row node from the grid
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.ClickedRowIndex);
    if (!rowNode) {
      console.log('❌ Row node not found at index:', this.ClickedRowIndex);
      return;
    }

    const cartItem = rowNode.data?.CartItem;
    if (!cartItem) {
      console.log('❌ CartItem not found in row data');
      return;
    }

    // ✅ Create a minimal SelectedOffer with ID=0 (no existing offer) and just Partner info
    cartItem.SelectedOffer = {
      ID: 0, // ✅ No existing offer for this supplier
      Partner: {
        ID: partner.ID,
        Name: partner.Name
      },
      Configuration: null,
      ListPrice: null,
      Currency: this.selectedCurrency, // ✅ Use selected currency from dropdown
      StockQuantity: null, // ✅ Explicitly NULL - no stock info for manual supplier selection
      IsStockOffer: false, // ✅ Explicitly FALSE - not a stock offer
      LeadTime: null,
      MOQ: null
    };

    // ✅ Ensure SellPrice exists with Currency for price editing
    if (!cartItem.SelectedOffer.SellPrice) {
      cartItem.SelectedOffer.SellPrice = {};
    }
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

    // ✅ Update CartItem.Product.ListPriceOffers[0] with the same minimal offer
    if (cartItem.Product && cartItem.Product.ListPriceOffers && cartItem.Product.ListPriceOffers.length > 0) {
      cartItem.Product.ListPriceOffers[0] = JSON.parse(JSON.stringify(cartItem.SelectedOffer));
    }

    // ✅ Set IsForcedPrice to TRUE
    if (!cartItem.Price) {
      cartItem.Price = {};
    }
    cartItem.Price.IsForcedPrice = true;

    // ✅ Ensure Price.Currency is set for grid display
    if (!cartItem.Price.Currency) {
      cartItem.Price.Currency = this.selectedCurrency;
    }

    // ✅ Mark as admin-edited (shows pencil icon in rightmost column)
    cartItem.AdminPriceEdit = true;

    console.log('✅ Updated CartItem.SelectedOffer with partner only:', cartItem.SelectedOffer);
    console.log('✅ Set SelectedOffer.ID = 0 (no existing offer)');
    console.log('✅ Set Currency to:', this.selectedCurrency?.Symbol);
    console.log('✅ Set CartItem.Price.IsForcedPrice to TRUE');
    console.log('✅ Set CartItem.AdminPriceEdit to TRUE');

    // ✅ Refresh the grid row to show updated data
    rowNode.setData(rowNode.data);
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

    // ✅ Close the popup
    this.ShowSupplierAlternatives = false;

    // ✅ Update cart (calls backend InitializeCart to recalculate and sync)
    this.UpdateCart();

    // ✅ Call AdminEditGridCell to trigger SaveToDB evaluation
    this.AdminEditGridCell(cartItem, this.ClickedRowIndex, 'price');

    console.log('✅ Partner selected from search, SelectedOffer.ID=0, SaveToDB triggered via AdminEditGridCell');
  }

  // ✅ Select a side offer from the Supplier Alternatives popup
  SelectSideOffer(selectedCartItem: any) {
    console.log('✅ SelectSideOffer called with:', selectedCartItem);

    // Get the row node from the grid
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.ClickedRowIndex);
    if (!rowNode) {
      console.log('❌ Row node not found at index:', this.ClickedRowIndex);
      return;
    }

    const cartItem = rowNode.data?.CartItem;
    if (!cartItem) {
      console.log('❌ CartItem not found in row data');
      return;
    }

    // ✅ Update CartItem.SelectedOffer with the clicked popup row's SelectedOffer
    cartItem.SelectedOffer = JSON.parse(JSON.stringify(selectedCartItem.SelectedOffer));

    // ✅ Update CartItem.Product.ListPriceOffers[0] with the clicked popup row's SelectedOffer
    if (cartItem.Product && cartItem.Product.ListPriceOffers && cartItem.Product.ListPriceOffers.length > 0) {
      cartItem.Product.ListPriceOffers[0] = JSON.parse(JSON.stringify(selectedCartItem.SelectedOffer));
    }

    // ✅ Only reset IDs to 0 if the incoming offer has ID = 0 (new offer from grid) - AFTER overwriting!
    if (selectedCartItem?.SelectedOffer?.ID === 0 || !selectedCartItem?.SelectedOffer?.ID) {
      cartItem.SelectedOffer.ID = 0;
      cartItem.SelectedOffer.Configuration = null;  // ✅ Clear Configuration when creating new offer
     
    }

    // ✅ Set IsForcedPrice to TRUE when selecting a supplier alternative
    if (!cartItem.Price) {
      cartItem.Price = {};
    }
    cartItem.Price.IsForcedPrice = true;

    // ✅ Mark as admin-edited (shows pencil icon in rightmost column)
    cartItem.AdminPriceEdit = true;

    console.log('✅ Updated CartItem.SelectedOffer:', cartItem.SelectedOffer);
    console.log('✅ Updated CartItem.Product.ListPriceOffers[0]:', cartItem.Product?.ListPriceOffers?.[0]);
    console.log('✅ Set CartItem.Price.IsForcedPrice to TRUE');
    console.log('✅ Set CartItem.AdminPriceEdit to TRUE');

    // ✅ Refresh the grid row to show updated data
    rowNode.setData(rowNode.data);
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

    // ✅ Close the popup
    this.ShowSupplierAlternatives = false;

    // ✅ Update cart (calls backend InitializeCart to recalculate and sync)
    this.UpdateCart();

    console.log('✅ Supplier alternative selected, IsForcedPrice set, AdminPriceEdit set, and UpdateCart() called');
  }

  // ✅ Simple supplier selection from Name popup Tab 1 - only updates Supplier, Purchase price, and SelectedOffer.ID
  SelectSupplierFromPopup(selectedOffer: any) {
    console.log('✅ SelectSupplierFromPopup called with:', selectedOffer);

    // Get the row node from the grid
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.ClickedRowIndex);
    if (!rowNode) {
      console.log('❌ Row node not found at index:', this.ClickedRowIndex);
      return;
    }

    const cartItem = rowNode.data?.CartItem;
    if (!cartItem) {
      console.log('❌ CartItem not found in row data');
      return;
    }

    const clickedOffer = selectedOffer?.SelectedOffer;
    if (!clickedOffer) {
      console.log('❌ No SelectedOffer in clicked item');
      return;
    }

    // ✅ 1. Update Supplier (Partner)
    if (!cartItem.SelectedOffer) {
      cartItem.SelectedOffer = {};
    }
    cartItem.SelectedOffer.Partner = JSON.parse(JSON.stringify(clickedOffer.Partner));

    // ✅ 2. Update Purchase price (ListPrice and PurchaseCurrency)
    cartItem.SelectedOffer.ListPrice = clickedOffer.ListPrice;
    cartItem.SelectedOffer.PurchaseCurrency = clickedOffer.PurchaseCurrency || clickedOffer.Currency;

    // ✅ 3. Always carry the clicked offer's ID (so the row reflects the actual offer selected)
    cartItem.SelectedOffer.ID = clickedOffer.ID || 0;
    if (!clickedOffer?.ID) {
      cartItem.SelectedOffer.Configuration = null;  // ✅ Clear Configuration when creating new offer
    }

    // ✅ 4. Update stock fields (drives GridValid row highlighting)
    cartItem.SelectedOffer.IsStockOffer = clickedOffer.IsStockOffer ?? false;
    cartItem.SelectedOffer.StockQuantity = clickedOffer.StockQuantity ?? 0;
    cartItem.SelectedOffer.IsManufacturerStockOffer = clickedOffer.IsManufacturerStockOffer ?? false;

    // ✅ 5. Update LeadTime and MOQ (different supplier = different ETA/MOQ)
    cartItem.SelectedOffer.LeadTime = clickedOffer.LeadTime
      ? JSON.parse(JSON.stringify(clickedOffer.LeadTime))
      : null;
    cartItem.SelectedOffer.MOQ = clickedOffer.MOQ ?? null;

    console.log('✅ Updated Supplier:', cartItem.SelectedOffer.Partner?.Name);
    console.log('✅ Updated Purchase Price:', cartItem.SelectedOffer.ListPrice);
    console.log('✅ Updated Offer ID:', cartItem.SelectedOffer.ID);
    console.log('✅ Updated IsStockOffer:', cartItem.SelectedOffer.IsStockOffer, 'StockQty:', cartItem.SelectedOffer.StockQuantity);

    // ✅ Refresh the grid row to show updated data + re-evaluate rowClassRules (stock highlighting)
    rowNode.setData(rowNode.data);
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
    this.gridApi.redrawRows({ rowNodes: [rowNode] });

    // ✅ Close the popup
    this.ShowETAAlternatives = false;
  }

  // Track if current search is from paste or keyboard
  private currentSearchSource: 'keyboard' | 'paste-keyboard' | 'paste-mouse' | null = null;
  private isPasting: boolean = false;
  private pasteEventType: number | null = null; // Store which paste event type (2 or 3)

  SearchIndustryProductB2BOffersVersion2(rowIndex: number, searchTerm: string, quantity: any, callback?: () => void) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      BrandID: null,
      SearchTerm: searchTerm,
      Quantity: quantity,
      Pager: { PageIndex: 1, PageSize: 15 },
      MaxRank: 10,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      StockStatus: this.StockStatus,
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
    if (rowNode?.data?.CartItem) {
      rowNode.data.CartItem.LoadingCell = true;
      this.gridApi.refreshCells({
        rowNodes: [rowNode],
        columns: ['name'],
        force: true
      });
    }

    this.loadingProducts = true;

    // ✅ Use Combined endpoint - handles short search terms (< 5 chars) via parallel FTS + ExactMatch
    this.B2BService.SearchIndustryProductB2BOffersVersion2Combined(request).subscribe(response => {
      if (response["Status"] === 1 || response["Status"] === -2) {
        const c = JSON.parse(JSON.stringify(response["CartItem"]));
        c.LoadingCell = false;

        // ✅ For RED ROWS (CartItemStatus.ID == 0), initialize Currency in SelectedOffer.SellPrice
        if (c?.CartItemStatus?.ID === 0) {
          if (!c.SelectedOffer) c.SelectedOffer = {};
          if (!c.SelectedOffer.SellPrice) c.SelectedOffer.SellPrice = {};
          if (!c.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
            c.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
            console.log('✅ Initialized Currency for RED ROW:', this.selectedCurrency.Symbol);
          }
        }

        this.applyCartItemToRow(rowIndex, c);

        this.loadingProducts = false;
        this.parent.loading = false;

        if(response["Status"] === -2){
          this.parent.InitializeExitBlockedAccount(response["Message"]);
        }

        // ✅ Track keyboard typing event (only if not from paste)
        if (this.currentSearchSource === 'keyboard') {
          this.TrackB2BUserGridEvent(1); // eB2BUserTrackingEventType.Keyboard
          this.currentSearchSource = null;
        }
      }

      // this.loadingProducts = false;
      // ✅ Inform that this row's async is done
      if (callback) callback();
    });
  }

  SearchIndustryProductB2BOffersWithMasterData(rowIndex: number, searchTerm: string, quantity: any, callback?: () => void, deferGridUpdate: boolean = false) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      BrandID: null,
      SearchTerm: searchTerm,
      Quantity: quantity,
      Pager: { PageIndex: 1, PageSize: 15 },
      MaxRank: 10,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      StockStatus: this.StockStatus,
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    // ✅ Only show loading indicator if not deferring (batch mode doesn't show per-cell loading)
    if (!deferGridUpdate) {
      const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
      if (rowNode?.data?.CartItem) {
        rowNode.data.CartItem.LoadingCell = true;
        this.gridApi.refreshCells({
          rowNodes: [rowNode],
          columns: ['name'],
          force: true
        });
      }
    }

    this.loadingProducts = true;

    // ✅ Use Combined endpoint - handles short search terms (< 5 chars) via parallel FTS + ExactMatch
    this.B2BService.SearchIndustryProductB2BOffersWithMasterDataCombined(request).subscribe(response => {
      if (response["Status"] === 1 || response["Status"] === -2) {
        const c = JSON.parse(JSON.stringify(response["CartItem"]));
        c.LoadingCell = false;

        // ✅ For RED ROWS (CartItemStatus.ID == 0), initialize Currency in SelectedOffer.SellPrice
        if (c?.CartItemStatus?.ID === 0) {
          if (!c.SelectedOffer) c.SelectedOffer = {};
          if (!c.SelectedOffer.SellPrice) c.SelectedOffer.SellPrice = {};
          if (!c.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
            c.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
            console.log('✅ Initialized Currency for RED ROW (MasterData):', this.selectedCurrency.Symbol);
          }
        }

        // ✅ If deferring, store result for later batch apply; otherwise apply immediately
        if (deferGridUpdate) {
          this.pendingBatchResults.set(rowIndex, c);
        } else {
          this.applyCartItemToRow(rowIndex, c);
          this.loadingProducts = false;
          this.parent.loading = false;
        }
        // ✅ Don't set loadingProducts = false here when deferring - it will be set when all batches complete

        if(response["Status"] === -2){
          this.parent.InitializeExitBlockedAccount(response["Message"]);
        }

        // ✅ Track keyboard typing event (only if not from paste)
        if (this.currentSearchSource === 'keyboard') {
          this.TrackB2BUserGridEvent(1); // eB2BUserTrackingEventType.Keyboard
          this.currentSearchSource = null;
        }
      }

      // this.loadingProducts = false;
      // ✅ Inform that this row's async is done
      if (callback) callback();
    });
  }

  /**
   * ✅ BULK API with SignalR - Real-time progress updates as each product completes
   * Connects to SignalR hub and receives updates for each item individually
   */
  async BulkSearchIndustryProductB2BOffersWithMasterData(batch: [number, { name: string; quantity: any }][]) {
    // Build search items array
    const searchItems = batch.map(([rowIndex, payload]) => ({
      SearchTerm: payload.name,
      Quantity: payload.quantity?.toString() || '1',
      RowIndex: rowIndex,
      BrandID: null
    }));

    this.loadingProducts = true;

    // Try to connect to SignalR for real-time updates
    let connectionId: string | null = null;
    try {
      connectionId = await this.signalRService.connect();
      this.signalRConnectionId = connectionId;
      console.log('✅ SignalR connected for bulk search:', connectionId);

      // Subscribe to real-time progress updates
      const progressSub = this.signalRService.searchItemCompleted$.subscribe((update: BulkSearchProgressUpdate) => {
        this.handleSignalRProgressUpdate(update);
      });
      this.signalRSubscriptions.push(progressSub);

    } catch (error) {
      console.warn('⚠️ SignalR connection failed, falling back to standard bulk:', error);
      connectionId = null;
    }

    const batchId = this.generateBatchId();
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      SearchItems: searchItems,
      Pager: { PageIndex: 1, PageSize: 15 },
      MaxRank: 10,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      },
      SignalRConnectionId: connectionId, // ✅ Include SignalR connection ID
      BatchId: batchId,
      StockStatus: this.StockStatus
    };

    this.B2BService.BulkSearchIndustryProductB2BOffersWithMasterData(request).subscribe(response => {
      // Clean up SignalR subscriptions for this batch
      this.cleanupSignalRSubscriptions();

      if (response["Status"] === 1) {
        const results = response["Results"] || [];
        console.log(`✅ Bulk API completed with ${results.length} results (SignalR already processed: ${this.signalRProcessedRows.size})`);

        // Process any results not already handled by SignalR
        results.forEach((result: any) => {
          const rowIndex = result.RowIndex;

          // ✅ Skip if already processed via SignalR (prevent double-counting)
          if (this.signalRProcessedRows.has(rowIndex)) {
            return;
          }

          const c = JSON.parse(JSON.stringify(result.CartItem));
          c.LoadingCell = false;

          // ✅ For RED ROWS (CartItemStatus.ID == 0), initialize Currency in SelectedOffer.SellPrice
          if (c?.CartItemStatus?.ID === 0) {
            if (!c.SelectedOffer) c.SelectedOffer = {};
            if (!c.SelectedOffer.SellPrice) c.SelectedOffer.SellPrice = {};
            if (!c.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
              c.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
            }
          }

          // Store result for batch apply
          this.pendingBatchResults.set(rowIndex, c);

          // Mark this row as completed
          this.currentBatchPending.delete(rowIndex);
          this.pendingPasteRowIndexes.delete(rowIndex);
          this.pasteProgressCompleted++;
        });

        // Update progress
        this.updatePasteProgress();

        // ✅ Apply results to grid
        this.applyPendingResultsToGrid();

        // ✅ Clear SignalR processed rows for next batch
        this.signalRProcessedRows.clear();

        // Process next batch
        console.log('✅ Bulk batch complete, processing next...');
        this.processNextBatch();
      } else {
        console.error('❌ Bulk search failed:', response["Message"]);
        // Mark all items in batch as failed and move on
        batch.forEach(([rowIndex]) => {
          // ✅ Skip if already counted via SignalR
          if (!this.signalRProcessedRows.has(rowIndex)) {
            this.currentBatchPending.delete(rowIndex);
            this.pendingPasteRowIndexes.delete(rowIndex);
            this.pasteProgressCompleted++;
          }
        });
        this.signalRProcessedRows.clear();
        this.updatePasteProgress();
        this.processNextBatch();
      }
    }, error => {
      console.error('❌ Bulk search error:', error);
      this.cleanupSignalRSubscriptions();
      // Mark all items in batch as failed and move on
      batch.forEach(([rowIndex]) => {
        // ✅ Skip if already counted via SignalR
        if (!this.signalRProcessedRows.has(rowIndex)) {
          this.currentBatchPending.delete(rowIndex);
          this.pendingPasteRowIndexes.delete(rowIndex);
          this.pasteProgressCompleted++;
        }
      });
      this.signalRProcessedRows.clear();
      this.updatePasteProgress();
      this.processNextBatch();
    });
  }

  /**
   * Handle real-time progress update from SignalR
   * Runs inside NgZone to ensure Angular change detection triggers
   */
  private handleSignalRProgressUpdate(update: BulkSearchProgressUpdate): void {
    // ✅ Run inside Angular zone to trigger change detection for real-time UI updates
    this.ngZone.run(() => {
      const rowIndex = update.RowIndex;

      // ✅ Skip if already processed (prevent double-counting)
      if (this.signalRProcessedRows.has(rowIndex)) {
        return;
      }
      this.signalRProcessedRows.add(rowIndex);

      if (update.CartItem) {
        const c = JSON.parse(JSON.stringify(update.CartItem));
        c.LoadingCell = false;

        // ✅ For RED ROWS (CartItemStatus.ID == 0), initialize Currency
        if (c?.CartItemStatus?.ID === 0) {
          if (!c.SelectedOffer) c.SelectedOffer = {};
          if (!c.SelectedOffer.SellPrice) c.SelectedOffer.SellPrice = {};
          if (!c.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
            c.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
          }
        }

        // Store result for batch apply
        this.pendingBatchResults.set(rowIndex, c);
      }

      // Mark this row as completed
      this.currentBatchPending.delete(rowIndex);
      this.pendingPasteRowIndexes.delete(rowIndex);
      this.pasteProgressCompleted++;

      // Update progress bar in real-time
      this.updatePasteProgress();

      // ✅ Apply results to grid immediately for real-time visual feedback
      if (this.pendingBatchResults.size > 0 && this.pendingBatchResults.size % 5 === 0) {
        // Apply every 5 items for smooth visual updates
        this.applyPendingResultsToGrid();
      }

      console.log(`📊 SignalR Progress: ${update.CompletedCount}/${update.TotalCount} (${update.ProgressPercent}%)`);
    });
  }

  /**
   * Generate unique batch ID for SignalR tracking
   */
  private generateBatchId(): string {
    return `batch_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Cleanup SignalR subscriptions after batch completes
   */
  private cleanupSignalRSubscriptions(): void {
    this.signalRSubscriptions.forEach(sub => sub.unsubscribe());
    this.signalRSubscriptions = [];
  }

  SwitchGridProductOffer(item:any){
    var request = {
      User:this.parent.User,
      Language:this.parent.getLanguage(),
      PartnerID:this.parent.B2BPartner?.ID,
      SelectedOffer:item.Product?.ListPriceOffers[0],
      CartItem:this.ClickedRow?.CartItem,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };
  
    this.LoadingSideOffers = true;
  
    this.B2BService.SwitchGridProductOffer(request).subscribe(response => {
  
      if(response["Status"] != 1){
        // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      else if(response["Status"] == 1){
        var c = JSON.parse(JSON.stringify(response["CartItem"]));
        c.LoadingCell = false;
        this.applyCartItemToRow(this.ClickedRowIndex, c);
        this.UpdateCart();
      }
  
      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.LoadingSideOffers = false;
     });
  }

  SwitchGridBrandVersion2(b:any){
    var request = {
      User:this.parent.User,
      Language:this.parent.getLanguage(),
      PartnerID:this.parent.B2BPartner?.ID,
      CartItem:this.ClickedRow?.CartItem,
      Pager: { PageIndex: 1, PageSize: 25 },
      MaxRank: 100,
      UISelectedBrand:JSON.parse(JSON.stringify(b)),
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
    };
  
    this.LoadingSideOffers = true;
  
    this.B2BService.SwitchGridBrandVersion2(request).subscribe(response => {
  
      if(response["Status"] != 1){
        // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      }
      else if(response["Status"] == 1){
        var c = JSON.parse(JSON.stringify(response["CartItem"]));
        c.LoadingCell = false;
        this.applyCartItemToRow(this.ClickedRowIndex, c);
        this.UpdateCart();

        //if still is awaiting selection:
        if(c?.CartItemStatus?.ID == -1){
          this.ShowUISideOffers(this.ClickedRowIndex, this.ClickedRow?.CartItem);
          this.ShowFallbackSelection = true;
        }
      }
  
      this.LoadingSideOffers = false;
      this.ShowBrandCollateral = false;
     });
  }

  BrandCollateralList:any[];
  BrandSelectList:any[];

  SearchIndustryProductB2BBrandsVersion2(rowIndex: number, cartItem: any) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      CurrentUISelectedBrand: cartItem?.Product?.Brand?.ID,
      SearchTerm: cartItem?.UIProductValue,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      ConfigStatusThreshold: this.selectedConfigStatus?.ID ?? 0,
      StockStatus: this.StockStatus,
    };
  
    this.ShowBrandCollateral = true;
    this.LoadingSideOffers = true;
  
    this.B2BService.SearchIndustryProductB2BBrandsVersion2(request).subscribe(response => {
      if (response["Status"] === 1) {
        const brands = JSON.parse(JSON.stringify(response["Brands"] ?? []));
  
        // put brands on the specific row
        const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
        if (rowNode?.data) {
          const ci = rowNode.data.CartItem ?? (rowNode.data.CartItem = {});
          ci.CollateralBrands = brands;
  
          // (optional) persist to cart/IndexedDB
          if (this.parent.Cart?.Items?.[rowIndex]) {
            this.parent.Cart.Items[rowIndex].CollateralBrands = JSON.parse(JSON.stringify(brands));
            this.saveCartToIDB();
          }

          // refresh brand cell so button/state reflects
          this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['brand'], force: true });
        }

        // show the brand popup now; position already computed in click handler
        this.BrandCollateralList = brands;
      }

      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.LoadingSideOffers = false;
    });
  }

  SearchIndustryProductNameAndPartNumberParrallelB2BBrandsVersion2(rowIndex: number, cartItem: any) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      CurrentUISelectedBrand: cartItem?.Product?.Brand?.ID,
      CartItem:JSON.parse(JSON.stringify(cartItem)),
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      ConfigStatusThreshold: this.selectedConfigStatus?.ID ?? 0,
      StockStatus: this.StockStatus,
    };

    this.ShowBrandCollateral = true;
    this.LoadingSideOffers = true;

    this.B2BService.SearchIndustryProductNameAndPartNumberParrallelB2BBrandsVersion2(request).subscribe(response => {
      if (response["Status"] === 1) {
        const brands = JSON.parse(JSON.stringify(response["Brands"] ?? []));

        // put brands on the specific row
        const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
        if (rowNode?.data) {
          const ci = rowNode.data.CartItem ?? (rowNode.data.CartItem = {});
          ci.CollateralBrands = brands;

          // (optional) persist to cart/IndexedDB
          if (this.parent.Cart?.Items?.[rowIndex]) {
            this.parent.Cart.Items[rowIndex].CollateralBrands = JSON.parse(JSON.stringify(brands));
            this.saveCartToIDB();
          }

          // refresh brand cell so button/state reflects
          this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['brand'], force: true });
        }

        // show the brand popup now; position already computed in click handler
        this.BrandCollateralList = brands;
      }

      this.ShowETAAlternatives = false;
      this.ShowSearchResultsAlternatives = false;
      this.LoadingSideOffers = false;
    });
  }

  SwitchGridResultProduct(SelectedResult:any){

    if(SelectedResult.Price?.UIUnitPrice > 0){
      var request = {
        User:this.parent.User,
        Language:this.parent.getLanguage(),
        PartnerID:this.parent.B2BPartner?.ID,
        SelectedResult:SelectedResult,
        InitialCartItem:this.ClickedRow?.CartItem,
        ApplyClientRestrictions:this.applyClientRestrictions,
        ConfigStatusThreshold: -10000,
        BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
        B2BAdminSettings: {
          AdminToken: this.parent.User?.Id,
          PrefferedCurrency: this.selectedCurrency,
          ConfigStatus: this.selectedConfigStatus
        }
      };
    
      this.LoadingSideOffers = true;
    
      this.B2BService.SwitchGridResultProduct(request).subscribe(response => {
    
        if(response["Status"] != 1){
          // this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
        }
        else if(response["Status"] == 1){
          var c = JSON.parse(JSON.stringify(response["CartItem"]));
          c.LoadingCell = false;
          this.applyCartItemToRow(this.ClickedRowIndex, c);
          this.UpdateCart();
        }
    
        this.ShowETAAlternatives = false;
        this.ShowSearchResultsAlternatives = false;
        this.LoadingSideOffers = false;
        this.ShowFallbackSelection = false;
       });
    }
  }

  // --- Quantity concurrency control ---
private qtyBusy = new Map<string, boolean>();       // rowKey -> in-flight?
private qtyPending = new Map<string, number>();     // rowKey -> latest desired qty while busy
private qtyInFlight = 0;                            // total in-flight qty updates
private qtyCartFlushTimer: any = null;              // coalesce UpdateCart()

private getRowKeyForQuantity(rowIndex: number, item: any): string {
  // Prefer persistent UIID from your backend; fall back to rowIndex.
  return item?.UIID || `row-${rowIndex}`;
}

private deferCartUpdateAfterQty(): void {
  if (this.qtyCartFlushTimer) clearTimeout(this.qtyCartFlushTimer);
  // small delay to coalesce completions finishing nearly at once
  this.qtyCartFlushTimer = setTimeout(() => {
    this.qtyCartFlushTimer = null;
    this.UpdateCart();
  }, 150);
}

  private insertedOverflowUIIDs = new Set<string>();

  ChangeGridQuantityVersion2(rowIndex: number, quantity: any, item: any): void {
    // Normalize and validate quantity just once
    const q = Number(quantity);
    if (!Number.isFinite(q)) return;
  
    const rowKey = this.getRowKeyForQuantity(rowIndex, item);
  
    // If this row is already updating, just remember the latest requested qty
    if (this.qtyBusy.get(rowKey)) {
      this.qtyPending.set(rowKey, q); // coalesce to the latest value
      return;
    }
  
    this.qtyBusy.set(rowKey, true);
    this.qtyInFlight++;
  
    // Optional: per-row UI hint
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
    if (rowNode?.data?.CartItem) {
      rowNode.data.CartItem.LoadingCell = true;
      this.gridApi.refreshCells({
        rowNodes: [rowNode],
        columns: ['quantity', 'price', 'total', 'eta', 'name'],
        force: true
      });
    }
  
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      CartItem: item,
      Quantity: q,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
    };

    this.loadingProducts = true;

    this.B2BService.ChangeGridQuantityVersion2(request)
      .pipe(finalize(() => {
        // Row complete
        this.qtyBusy.delete(rowKey);
        this.qtyInFlight = Math.max(0, this.qtyInFlight - 1);
  
        // If a newer qty was requested during the in-flight call, run it now.
        const pending = this.qtyPending.get(rowKey);
        if (pending !== undefined && pending !== q) {
          this.qtyPending.delete(rowKey);
  
          // Get latest cartItem (may have been replaced by server)
          const latestNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
          const latestItem = latestNode?.data?.CartItem ?? item;
          this.ChangeGridQuantityVersion2(rowIndex, pending, latestItem);
          return; // don't schedule cart update yet; next call will handle it
        }
        this.qtyPending.delete(rowKey);
  
        // When all rows are done, coalesce a single UpdateCart()
        if (this.qtyInFlight === 0) {
          this.deferCartUpdateAfterQty();
        }
      }))
      .subscribe(response => {
        if (response["Status"] !== 1) {
          this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
          if (rowNode?.data?.CartItem) {
            rowNode.data.CartItem.LoadingCell = false;
            this.gridApi.refreshCells({ rowNodes: [rowNode], columns: ['quantity'], force: true });
          }
          return;
        }

        // Apply the first returned item (your original behavior)
        const items = JSON.parse(JSON.stringify(response["CartItems"] ?? []));
        const first = items.find(Boolean);
        if (first) {
          console.log(`📦 ChangeGridQuantityV2 response: requested=${q}, returned=${first.Quantity}, StockOverRequestedQty=${first.SelectedOffer?.StockOverRequestedQuantity ?? 'NOT SET'}, IsStockOffer=${first.SelectedOffer?.IsStockOffer}`);

          // ✅ ADMIN OVERFLOW DETECTION: If admin mode and returned qty < requested qty,
          // ensure StockOverRequestedQuantity is set so overflow logic in applyCartItemToRow triggers.
          // The backend may not always set StockOverRequestedQuantity for admin requests,
          // so we compute it as a frontend fallback: overflow = requestedQty - returnedQty.
          if (this.useAdminGrid && first.SelectedOffer && first.SelectedOffer.IsStockOffer === true) {
            const returnedQty = Number(first.Quantity) || 0;
            const overflowQty = q - returnedQty;
            if (overflowQty > 0 && !(first.SelectedOffer.StockOverRequestedQuantity > 0)) {
              console.log(`🔧 ADMIN OVERFLOW FALLBACK: Setting StockOverRequestedQuantity=${overflowQty} (requested=${q}, returned=${returnedQty})`);
              first.SelectedOffer.StockOverRequestedQuantity = overflowQty;
            }
          }

          this.applyCartItemToRow(rowIndex, first);
        }
      });
  }
  
  
  private applyCartItemToRow(rowIndex: number, cartItem: any): void {
    if (!this.gridApi || !cartItem) return;
  
    const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
    if (!rowNode) return;
  
    const brand = cartItem?.Product?.Brand?.Name ?? '';
    const name = cartItem?.UIProductValue ?? '';
    const partNumber = cartItem?.Product?.PartNumber ?? '';
    const quantity = cartItem?.Quantity ?? '';
  
    // ✅ Price logic: show '' if SellPrice is not > 0
    const sellPrice = cartItem?.SelectedOffer?.SellPrice?.UIUnitPrice;
    const showPrice = sellPrice > 0;
    const price = showPrice ? sellPrice : '';

    const total = cartItem?.Price?.UIQtyPrice ?? '';

    const eta = cartItem?.SelectedOffer?.LeadTime?.UIOutput ?? '------';

    const hscode = cartItem?.Product?.Stats?.HSCode ?? '';
    const coo = cartItem?.Product?.Stats?.COO?.ShortName ?? '';
    const weight = cartItem?.Product?.Stats?.Weight != null
      ? `${cartItem.Product.Stats.Weight} ${cartItem.Product.Stats.WeightMeasurementType?.Name ?? ''}`
      : '';
  
    rowNode.setData({
      index: rowIndex + 1,
      brand,
      partNumber,
      name,
      quantity,
      price,
      total,
      eta,
      hscode,
      coo,
      weight,
      CartItem: JSON.parse(JSON.stringify(cartItem))
    });

    // ✅ Update mirror list ONLY if this is a NEW product (CartItemStatus.ID changed from 0 to 1)
    // Do NOT update if it's an admin edit (AdminPriceEdit flag exists)
    const existingOriginal = this.OriginalCartItems?.[rowIndex];
    const wasRedRow = existingOriginal?.CartItemStatus?.ID === 0;
    const isNowValid = cartItem?.CartItemStatus?.ID === 1;
    const hasAdminEdits = cartItem?.AdminPriceEdit === true;

    if (this.useAdminGrid && this.OriginalCartItems && rowIndex >= 0 && rowIndex < this.OriginalCartItems.length) {
      // Only update mirror if: was red row (0) and now valid (1), and no admin edits
      if (wasRedRow && isNowValid && !hasAdminEdits) {
        const cleanCartItem = JSON.parse(JSON.stringify(cartItem));
        delete cleanCartItem.AdminPriceEdit; // ✅ Ensure mirror has no edit flags
        this.OriginalCartItems[rowIndex] = cleanCartItem;
        this.saveOriginalCartToIDB();
        console.log(`🪞 Updated mirror list at index ${rowIndex} (NEW product found, was red row)`);
      } else {
        console.log(`🪞 Skipped mirror list update at index ${rowIndex} (wasRedRow: ${wasRedRow}, isNowValid: ${isNowValid}, hasAdminEdits: ${hasAdminEdits})`);
      }
    }

    // === Overflow logic continues below as-is ===
    const isOverflowNeeded =
      cartItem?.SelectedOffer?.StockOverRequestedQuantity > 0 &&
      !cartItem.IsOverflowRow;

    const nextRow = this.gridApi.getDisplayedRowAtIndex(rowIndex + 1);
    const isNextAlreadyOverflow = nextRow?.data?.CartItem?.IsOverflowRow === true;

    console.log(`📊 OVERFLOW CHECK: StockOverRequestedQuantity=${cartItem?.SelectedOffer?.StockOverRequestedQuantity ?? 'NOT SET'}, IsOverflowRow=${cartItem?.IsOverflowRow}, isOverflowNeeded=${isOverflowNeeded}, isNextAlreadyOverflow=${isNextAlreadyOverflow}`);

    this.insertedOverflowUIIDs.clear();

    const uiid = cartItem?.UIID;
    if (!isOverflowNeeded || isNextAlreadyOverflow || this.insertedOverflowUIIDs.has(uiid)) {
      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
      return;
    }
  
    this.insertedOverflowUIIDs.add(uiid);
  
    const clonedCartItem = JSON.parse(JSON.stringify(cartItem));
    clonedCartItem.IsOverflowRow = true;
    clonedCartItem.Quantity = 0;
    clonedCartItem.LoadingCell = true;
  
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      CartItem: clonedCartItem,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    this.B2BService.GetIndustryProductByStockOverRequestedQuantity(request).subscribe(response => {
      // Check if no other offers are available (outside CartItem check - popup still shows even without CartItem)
      if(response["Status"] === 1 && response["NoOtherOffersAvailable"] == true){
        this.stockReadjustedMessage = response["NoOtherOffersMessage"];
        this.stockReadjustedProducts = [];
        this.showStockReadjustedPopup = true;
      }

      if (response["Status"] === 1 && response["CartItem"]) {
        const overflowItem = JSON.parse(JSON.stringify(response["CartItem"]));
        const overflowUIID = overflowItem?.UIID;

        if (!overflowUIID || this.insertedOverflowUIIDs.has(overflowUIID)) return;
        this.insertedOverflowUIIDs.add(overflowUIID);

        const newRow = {
          index: rowIndex + 2,
          brand: overflowItem?.Product?.Brand?.Name ?? '',
          partNumber: overflowItem?.Product?.PartNumber ?? '',
          name: overflowItem?.UIProductValue ?? '',
          quantity: overflowItem?.Quantity ?? '',
          price: (overflowItem?.SelectedOffer?.SellPrice?.UIUnitPrice > 0)
            ? overflowItem.SelectedOffer.SellPrice.UIUnitPrice
            : '',
          total: overflowItem?.Price?.UIQtyPrice ?? '',
          // ✅ Use UIOutput first (contains prolonged text from backend), fallback to simple format
          eta: overflowItem?.SelectedOffer?.LeadTime?.UIOutput ?? (overflowItem?.SelectedOffer?.LeadTime?.Units > 0
            ? `${overflowItem.SelectedOffer.LeadTime.Units} ${overflowItem.SelectedOffer.LeadTime.Type?.Name ?? ''}`
            : '------'),
          hscode: overflowItem?.Product?.Stats?.HSCode ?? '',
          coo: overflowItem?.Product?.Stats?.COO?.ShortName ?? '',
          weight: overflowItem?.Product?.Stats?.Weight != null
            ? `${overflowItem.Product.Stats.Weight} ${overflowItem.Product.Stats.WeightMeasurementType?.Name ?? ''}`
            : '',
          CartItem: overflowItem
        };

        this.gridApi.applyTransaction({ add: [newRow], addIndex: rowIndex + 1 });
        this.parent.Cart.Items.splice(rowIndex + 1, 0, overflowItem);
        this.saveCartToIDB();
      } else if (this.useAdminGrid) {
        // ✅ ADMIN FORCED SPLIT: Create a red overflow row from the UI when no backend offer exists
        // This allows admins to manually assign a supplier/price to the overflow quantity
        const overflowQty = cartItem?.SelectedOffer?.StockOverRequestedQuantity ?? 0;
        console.log(`🔴 ADMIN FORCED SPLIT: No alternative offer found. Creating red overflow row with qty=${overflowQty}`);

        const adminOverflowItem: any = JSON.parse(JSON.stringify(cartItem));
        adminOverflowItem.IsOverflowRow = true;
        adminOverflowItem.Quantity = overflowQty;
        adminOverflowItem.UIID = this.parent.newGuid();
        adminOverflowItem.SelectedOffer = null;
        adminOverflowItem.LoadingCell = false;
        adminOverflowItem.Price = {};
        adminOverflowItem.CartItemStatus = { ID: 0, Name: 'NotValid' };

        const adminNewRow = {
          index: rowIndex + 2,
          brand: adminOverflowItem?.Product?.Brand?.Name ?? '',
          partNumber: adminOverflowItem?.Product?.PartNumber ?? '',
          name: adminOverflowItem?.UIProductValue ?? '',
          quantity: adminOverflowItem.Quantity,
          price: '',
          total: '',
          eta: '------',
          hscode: adminOverflowItem?.Product?.Stats?.HSCode ?? '',
          coo: adminOverflowItem?.Product?.Stats?.COO?.ShortName ?? '',
          weight: adminOverflowItem?.Product?.Stats?.Weight != null
            ? `${adminOverflowItem.Product.Stats.Weight} ${adminOverflowItem.Product.Stats.WeightMeasurementType?.Name ?? ''}`
            : '',
          CartItem: adminOverflowItem
        };

        this.gridApi.applyTransaction({ add: [adminNewRow], addIndex: rowIndex + 1 });
        this.parent.Cart.Items.splice(rowIndex + 1, 0, adminOverflowItem);
        this.saveCartToIDB();
        console.log('✅ ADMIN FORCED SPLIT: Red overflow row inserted at index', rowIndex + 1);
      } else {
        clonedCartItem.LoadingCell = false;
        this.parent.openSnackBar(response["Message"] ?? "Overflow error", "OK", "alert-info-snackbar");
      }
    });
  
    this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
  }

  /**
   * ✅ Refresh offer data from database by OfferID
   * Fetches the latest DB values and overwrites the UI row
   * Called after each save operation to sync UI with DB state
   */
  private RefreshOfferFromDatabase(rowIndex: number, offerId: number, quantity: any): void {
    if (!offerId || offerId <= 0) {
      console.warn('⚠️ [REFRESH] Invalid OfferID, skipping refresh:', offerId);
      return;
    }

    console.log(`🔄 [REFRESH] Fetching latest DB values for OfferID: ${offerId} at row ${rowIndex}`);

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      PartnerID: this.parent.B2BPartner?.ID,
      OfferID: offerId,
      Quantity: quantity?.toString() || '1',
      Pager: { PageIndex: 1, PageSize: 15 },
      MaxRank: 10,
      ApplyClientRestrictions: this.applyClientRestrictions,
      BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
      StockStatus: this.StockStatus,
      B2BAdminSettings: {
        AdminToken: this.parent.User?.Id,
        PrefferedCurrency: this.selectedCurrency,
        ConfigStatus: this.selectedConfigStatus
      }
    };

    this.B2BService.GetIndustryProductB2BOfferWithMasterDataByOfferID(request).subscribe(response => {
      if (response["Status"] === 1) {
        const updatedCartItem = response["CartItem"];
        if (updatedCartItem) {
          console.log('✅ [REFRESH] Received updated DB values:', updatedCartItem);
          console.log('✅ [REFRESH] - Product.ID:', updatedCartItem.Product?.ID);
          console.log('✅ [REFRESH] - SelectedOffer.ID:', updatedCartItem.SelectedOffer?.ID);

          // ✅ FIX: If backend returned ManufacturerStock as StockQuantity, revert it
          // The B2B endpoint forces ManufacturerStock into StockQuantity + IsStockOffer=true
          // which is incorrect for admin quotation context — admin sets stock explicitly
          const offer = updatedCartItem?.SelectedOffer;
          const productOffer = updatedCartItem?.Product?.ListPriceOffers?.[0];
          if (offer?.IsManufacturerStockOffer === true) {
            console.log('⚠️ [REFRESH] ManufacturerStock was forced into StockQuantity — reverting');
            // Restore real stock from StockQuantityClone (the actual DB offer stock), or null
            offer.StockQuantity = offer.StockQuantityClone ?? null;
            offer.IsStockOffer = (offer.StockQuantity != null && offer.StockQuantity > 0);
            offer.IsManufacturerStockOffer = false;
            if (productOffer) {
              productOffer.StockQuantity = productOffer.StockQuantityClone ?? null;
              productOffer.IsStockOffer = (productOffer.StockQuantity != null && productOffer.StockQuantity > 0);
              productOffer.IsManufacturerStockOffer = false;
            }
          }

          // ✅ Overwrite UI row with DB values (source of truth)
          this.applyCartItemToRow(rowIndex, updatedCartItem);

          // ✅ Update CartItemRows list
          const existingUIID = this.CartItemRows[rowIndex]?.UIID;
          if (existingUIID) {
            updatedCartItem.UIID = existingUIID; // Preserve UIID for consistency
          }
          if (rowIndex >= 0 && rowIndex < this.CartItemRows.length) {
            this.CartItemRows[rowIndex] = updatedCartItem;
          }

          // ✅ Update parent.Cart.Items as well
          if (this.parent.Cart?.Items && rowIndex >= 0 && rowIndex < this.parent.Cart.Items.length) {
            this.parent.Cart.Items[rowIndex] = JSON.parse(JSON.stringify(updatedCartItem));
          }

          this.saveCartToLocalStorageOnly();
          console.log('✅ [REFRESH] UI updated with latest DB values');
        }
      } else {
        console.error('❌ [REFRESH] Failed to fetch DB values:', response["Message"]);
      }
    }, error => {
      console.error('❌ [REFRESH] HTTP error:', error);
    });
  }


  exportToExcel(): void {
   
  }

  onCellClicked: (event: any) => {
   
  }
  
   /** Parse numbers for quantity */
   numberParser(params: any): number {
    const val = Number(params.newValue);
    return isNaN(val) ? params.oldValue : val;
  }

  /** Format decimal values for price/total */
  decimalFormatter(params: any): string {
    return params.value != null ? parseFloat(params.value).toFixed(2) : '';
  }
  
  SelectedCartItem:any;

  SelectGridItem(item:any){
    this.SelectedCartItem = JSON.parse(JSON.stringify(item));
  }

  Filter: {[k: string]: any} = {};

  InitializeFilter(){

  this.Filter = {};
  this.Filter.SearchTerm = "";

  this.Filter.Pager = {};
  this.Filter.Pager.PageIndex = 1;
  this.Filter.Pager.PageSize = 50;
  this.Filter.Pager.TotalCount = 0;

  this.Filter.Company = {};
  this.Filter.Company = null;

  this.Filter.Brand = {};
  this.Filter.Brand = null;

  this.Filter.ProductBoxStatus = {};
  this.Filter.ProductBoxStatus = null;

  this.Filter.ProductConditionStatus = {};
  this.Filter.ProductConditionStatus = null;

  this.Filter.ProductManufacturingStatus = {};
  this.Filter.ProductManufacturingStatus = null;

  this.Filter.ProductVersionStatus = {};
  this.Filter.ProductVersionStatus = null;

  this.Filter.Creator = {};
  this.Filter.Creator = null;

  this.Filter.Updater = {};
  this.Filter.Updater = null;

  this.Filter.CombineProductsByID = true;
}



ShowSearchProductsPageMessage:boolean;

loadingProducts:boolean;
bulkConfigStatusLoading:boolean = false;
SearchedProducts:any[];
CartItemRows: {[k: string]: any} = [];

InitializeSearchProduct(item:any){

  this.CurrentCartItemRow = item;
  this.SelectItem(item);

  if((this.SearchedProducts == null || this.SearchedProducts.length == 0) && this.Filter.SearchTerm?.length > 2){
    this.SearchIndustryProducts();
  }
}

OfferPriorityStatuses:any[];
OfferPriorityStatus:any;

PopulateCartItems(rows = 50){
    
  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    Rows:rows
  };

  this.loadingProducts = true;

  this.B2BService.PopulateCartItems(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      var CI = JSON.parse(JSON.stringify(response["CartItems"]));
      this.CartItemRows = this.CartItemRows.concat(CI);

      if(this.OfferPriorityStatuses == null || this.OfferPriorityStatuses?.length == 0){
        this.OfferPriorityStatuses = response["OfferPriorityStatuses"];
        this.OfferPriorityStatus = this.OfferPriorityStatuses[0];
      }
    }

    this.loadingProducts = false;
   });
}

GetIndustryProductsStocksFiltered(){
    
  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    Filter:this.Filter
  };

  this.loadingProducts = true;

  this.managementService.GetIndustryProductsStocksFiltered(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      this.SearchedProducts = response["StockProducts"];
    }

    this.loadingProducts = false;
   });
}

AllSelected:boolean;

SelectAll(){
  this.parent.Cart.Items.forEach(r => {
    r.IsSelected = this.AllSelected;
  });

  this.CheckIfAnyChecked();
}

AnyChecked:boolean;

CheckIfAnyChecked(){
  this.AnyChecked = false;

  this.parent.Cart.Items.forEach(element => {
    if(element.IsSelected){
      this.AnyChecked = true;
    }
  });
}

SearchIndustryProducts(){
    
  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    Filter:this.Filter
  };

  this.loadingProducts = true;

  this.managementService.SearchIndustryProducts(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      this.SearchedProducts = response["SearchResults"];
    }

    this.loadingProducts = false;
   });
}

SelectItem(i:any){
  this.CartItemRows.forEach(cell => {
    if(cell.UIID == i.UIID){
      cell.IsSelected = !cell.IsSelected;
    }
    else{
      cell.IsSelected = false;
    }
  });
}

SplitGetIndustryProductsSearchQuery(searchQuery:string){

  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    SearchQuery:searchQuery,
    UIID:this.CurrentCartItemRow.UIID,
    CartItems:this.CartItemRows,
    Partner:this.parent.B2BPartner,
    BypassBlockedFlag: true // Allow employees to view prices for blocked B2B customers in Quotations
  };

  this.CartItemRows.forEach(cell => {
      if(cell.UIID == this.CurrentCartItemRow.UIID){
        cell.LoadingCell = true;
      }
  });

  this.B2BService.SplitGetIndustryProductsSearchQuery(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      this.CartItemRows = [];
      this.CartItemRows = JSON.parse(JSON.stringify(response["CartItems"]));

      this.CartItemRows.filter(x => !x.WasSearchedFor && x.UIProductValue?.length > 0).forEach(cell => {
        this.SearchIndustryProductsFromGridCell(cell);
      });
    }

    this.searchTermUpdate.next(null);
   });
}

SearchIndustryProductsFromGridCell(item:any){

  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    UIID:item.UIID,
    CartItem:item,
    BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
    B2BAdminSettings: {
      AdminToken: this.parent.User?.Id,
      PrefferedCurrency: this.selectedCurrency,
      ConfigStatus: this.selectedConfigStatus
    }
  };

  this.CartItemRows.forEach(cell => {
      if(cell.UIID == item.UIID){
        cell.LoadingCell = true;
      }
  });

  this.B2BService.SearchIndustryProductsFromGridCell(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){

      var Item = response["CartItem"];

      this.CartItemRows.forEach((cell, index) => {
        if(cell.UIID == item.UIID){
            this.CartItemRows[index] = JSON.parse(JSON.stringify(Item));
            this.GetGridCellItemData(JSON.parse(JSON.stringify(this.CartItemRows[index])));
        }
      });
    }

    this.CartItemRows.forEach(cell => {
      if(cell.UIID == item.UIID){
        cell.LoadingCell = false;
      }
    });

    this.searchTermUpdate.next(null);
   });
}

SelectGridSearchResult(s:any, item:any){

  item.SearchResults.forEach(element => {
    element.IsSelected = false;
  });

  // Find the index of the matching search result
const index = item.SearchResults.findIndex(searchResult => searchResult.UIID === s.UIID);

// If a matching search result is found
if (index !== -1) {
    // Set isSelected to true
    item.SearchResults[index].IsSelected = true;

    // Move the matching search result to the beginning of the array
    const selectedSearchResult = item.SearchResults.splice(index, 1)[0];
    item.SearchResults.unshift(selectedSearchResult);
}

  this.GetGridCellItemData(JSON.parse(JSON.stringify(item)));
  item.IsSelected = false;
}

GetGridCellItemData(c:any){

  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    newItem:c,
    Partner:this.parent.B2BPartner,
    BypassBlockedFlag: true // Allow employees to view prices for blocked B2B customers in Quotations
  };

  this.B2BService.GetGridCellItemData(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){

      var Item = response["Item"];

      this.CartItemRows.forEach((cell, index) => {
        if(cell.UIID == c.UIID){
            this.CartItemRows[index] = JSON.parse(JSON.stringify(Item));
        }
    });
    }
   });
}

// Helper: collect CartItems from the visible grid rows
private getCartItemsFromGrid(): any[] {
  const items: any[] = [];
  if (!this.gridApi) return items;

  this.gridApi.forEachNode((node) => {
    const ci = node?.data?.CartItem;
    // push only meaningful cart items
    if (ci && typeof ci === 'object' && Object.keys(ci).length > 0) {
      const clonedItem = JSON.parse(JSON.stringify(ci)); // deep copy to avoid accidental mutation
      clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
      items.push(clonedItem);
    }
  });

  return items;
}

  // More specific version with error handling
  downloadExcel = (filename: string, base64Data: string): boolean => {
    try {
      // Validate inputs
      if (!filename || !base64Data) {
        console.error('Filename and base64 data are required');
        return false;
      }
      
      // Ensure filename has .xlsx extension
      const finalFilename = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      
      const element = document.createElement('a');
      const dataUrl = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64Data}`;
      
      element.href = dataUrl;
      element.download = finalFilename;
      element.style.display = 'none';
      
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      
      return true;
    } catch (error) {
      console.error('Download failed:', error);
      return false;
    }
  };

// ✅ Download Excel + PDF in parallel
DownloadBothFiles(cart: any = null) {
  const cartItems = this.getCartItemsFromGrid();

  this.parent.loading = true;
  this.loadingProducts = true;

  // ✅ Excel export request
  const excelRequest = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItems: cartItems,
    Partner: this.parent.B2BPartner,
    IncludeListPrice: this.includeListPriceInExport,
    IncludeDiscount: this.includeDiscountInExport,
    IncludeETA: this.includeETAInExport,
    IncludeSupplier: this.includeSupplierInExport,
    IncludeHSCode: this.includeHSCodeInExport,
    IncludeCOO: this.includeCOOInExport,
    IncludeWeight: this.includeWeightInExport,
  };

    // ✅ IMPORTANT: Collect CartItems from grid BEFORE sending to preserve SaveToDB flag
    const items: any[] = [];
    this.gridApi.forEachNode((node) => {
      const cartItem = node.data?.CartItem;
      if (cartItem) {
        const clonedItem = JSON.parse(JSON.stringify(cartItem)); // deep copy to avoid reference issues
        clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
        items.push(clonedItem);
      }
    });

    // ✅ Call parent's service directly to avoid navigation
    if (!cart) {
      this.parent.GetCart();
    } else {
      this.parent.Cart = JSON.parse(JSON.stringify(cart));
    }

    // ✅ OVERRIDE Cart.Items with grid items to preserve SaveToDB flag
    this.parent.Cart.Items = items;

    this.parent.Offer.UserUploader = this.parent.User;
    this.parent.Offer.AgentManager = this.parent.B2BAgent;
    this.parent.Offer.Company = this.parent.Company;
    // ✅ Cart includes: PaymentType, PaymentTerm, ShippingResponsability, ShippingServiceType, ShippingCompany
    this.parent.Offer.Cart = this.parent.Cart;
    this.parent.Offer.Type = this.parent.CartMasterData.OrderTypes.find((t: any) => t.ID === 2); // ✅ Offer (ID = 2)
    this.parent.Offer.PaymentType = this.parent.Cart.PaymentType;
    this.parent.Offer.PaymentTerm = this.parent.Cart.PaymentTerm; // ✅ Include PaymentTerm
    this.parent.Offer.DeliveryPreference = this.parent.Cart.DeliveryPreference;
    this.parent.Offer.ConsolidationType = this.parent.Offer.ConsolidationType; // ✅ Include ConsolidationType
    this.parent.Offer.ShippingAddress = this.parent.Offer.ShippingAddress; // ✅ Include ShippingAddress
    this.parent.Offer.ShippingResponsability = this.parent.Cart.ShippingResponsability; // ✅ Include ShippingResponsability
    this.parent.Offer.ShippingServiceType = this.parent.Cart.ShippingServiceType; // ✅ Include ShippingServiceType
    this.parent.Offer.ShippingCompany = this.parent.Cart.ShippingCompany; // ✅ Include ShippingCompany
    this.parent.Offer.Partner = this.parent.B2BPartner; // ✅ Include Partner
    this.parent.Offer.PartnerAgents = this.selectedAgents; // ✅ Include PartnerAgents

  const pdfRequest = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Offer: this.parent.Offer
  };

  // ✅ Call Excel export
  this.B2BService.ExportB2BGrid(excelRequest).subscribe(excelResponse => {
    if (excelResponse["Status"] != 1) {
      this.parent.openSnackBar(excelResponse["Message"], "OK", "alert-info-snackbar");
    } else if (excelResponse["Status"] == 1) {
      // Download Excel file
      const excelDocument = excelResponse["Document"];
      this.downloadExcel(excelDocument.Name, excelDocument.PDFStream);

      // ✅ Track Excel export event
      this.TrackB2BUserGridEvent(5); // eB2BUserTrackingEventType.ExportXLSX
    }

    this.parent.loading = false;
    this.loadingProducts = false;
  });

  // ✅ Call PDF export in parallel using DownloadOfficialOffer
  this.B2BService.DownloadOfficialOffer(pdfRequest).subscribe(pdfResponse => {
    if (pdfResponse["Status"] === 1) {
      const pdfDocument = pdfResponse["Document"];
      this.parent.download(pdfDocument.Name, pdfDocument.PDFStream);
    }
  });
}

ExportB2BGrid(){
  const cartItems = this.getCartItemsFromGrid();

  // ✅ Excel export request
  const excelRequest = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItems: cartItems,
    IncludeListPrice: this.includeListPriceInExport,
    IncludeDiscount: this.includeDiscountInExport,
    IncludeETA: this.includeETAInExport,
    IncludeSupplier: this.includeSupplierInExport,
    IncludeHSCode: this.includeHSCodeInExport,
    IncludeCOO: this.includeCOOInExport,
    IncludeWeight: this.includeWeightInExport,
  };

  // ✅ Build Offer object for PDF export
  const offerForPdf = {
    UserUploader: this.parent.User,
    AgentManager: this.parent.B2BAgent,
    Company: this.parent.Company,
    Cart: {
      ...this.parent.Cart,
      Items: cartItems.map((ci: any) => ci.CartItem)
    },
    Partner: this.parent.B2BPartner,
    PartnerAgents: this.selectedAgents,
  };

  const pdfRequest = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Offer: offerForPdf
  };

  this.parent.loading = true;
  this.loadingProducts = true;

  // ✅ Call Excel export
  this.B2BService.ExportB2BGrid(excelRequest).subscribe(response => {
    if (response["Status"] != 1) {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    } else if (response["Status"] == 1) {
      const Document = response["Document"];
      this.downloadExcel(Document.Name, Document.PDFStream);

      // ✅ Track Excel export event
      this.TrackB2BUserGridEvent(5); // eB2BUserTrackingEventType.ExportXLSX
    }

    this.parent.loading = false;
    this.loadingProducts = false;
  });

  // ✅ Call PDF export in parallel
  this.managementService.ExportOffer(pdfRequest).subscribe(pdfResponse => {
    if (pdfResponse["Status"] === 1) {
      const pdfDocument = pdfResponse["Document"];
      this.parent.download(pdfDocument.Name, pdfDocument.PDFStream);
    }
  });
}

download(filename, text) {
  var element = document.createElement('a');
  element.setAttribute('href', 'data:application/pdf;base64,' + encodeURIComponent(text));
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

ShowAllProductsDetails:boolean = false;

ShowAllProductsDetailsFunction(e:boolean){
  this.ShowAllProductsDetails = e;
  this.parent.Cart.Items.forEach(element => {
    element.Product.ShowDetails = e;
  });
}

CloseShowSearchProductsPageMessage(){
  this.cookieService.set("SearchProductsPageMessage", "true");
  this.ShowSearchProductsPageMessage = false;
}

B2BHomePageMessage:string;

GetB2BHomePageMessage(){
    
  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
    Partner:this.parent.B2BPartner,
    Agent:this.parent.B2BAgent
  };

  this.loadingProducts = true;

  this.B2BService.GetB2BHomePageMessage(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      this.ShowSearchProductsPageMessage = true;
      this.B2BHomePageMessage = response["B2BHomePageMessage"];
      this.GetB2BListedBrands();
    }

    this.loadingProducts = false;
   });
}

Brands:any[];
loadingBrandIcons:boolean = false;

GetB2BListedBrands(){
    
  var request = {
    User:this.parent.User,
    Language:this.parent.getLanguage(),
  };

  this.loadingBrandIcons = true;

  this.B2BService.GetB2BListedBrands(request).subscribe(response => {

    if(response["Status"] != 1){
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
    else if(response["Status"] == 1){
      this.Brands = JSON.parse(JSON.stringify(response["Brands"]));
    }

    this.loadingBrandIcons = false;
   });
}

InitializeBrandFilter(){
  this.BrandFilter = {};
  this.BrandFilter.SearchTerm = "";
  this.BrandFilter.Pager = {};
  this.BrandFilter.Pager.PageIndex = 1;
  this.BrandFilter.Pager.PageSize = 50;
  this.BrandFilter.Pager.TotalCount = 0;
}

BrandsLoading:boolean = false;
BrandFilter: {[k: string]: any} = {};
ShowBrandCollateralNewProduct:boolean = false;

AttachBrandToNewProduct(b: any) {
  // 1) Get the live row node (avoid using a stale copy from ClickedRow)
  const rn = this.gridApi?.getDisplayedRowAtIndex(this.ClickedRowIndex);
  if (!rn) {
    return;
  }

  // 2) Current row data & cart item
  const current = rn.data || {};
  const cartItem = current.CartItem || {};

  // 3) Update CartItem product info
  cartItem.Product = cartItem.Product || {};
  cartItem.Product.Brand = JSON.parse(JSON.stringify(b));

  // Take UI text from the Name cell as product name if available
  const uiValue = (cartItem.UIProductValue ?? current.name ?? '').toString().trim();
  cartItem.UIProductValue = uiValue;                 // keep UI value in sync
  cartItem.Product.Name = uiValue;                   // show same as product name

  // 4) Update the TOP-LEVEL fields used by your columnDefs
  const newBrandName = b?.Name ?? b?.name ?? '';
  const updatedRow = {
    ...current,
    brand: newBrandName,                              // <-- this is what Brand column displays
    name: uiValue,                                    // <-- Name column displays this
    CartItem: JSON.parse(JSON.stringify(cartItem))    // deep copy to avoid ref issues
  };

  // 5) Apply to the grid row (cheap, no full rebind)
  rn.setData(updatedRow);

  // 6) Refresh just what’s needed
  this.gridApi.refreshCells({
    rowNodes: [rn],
    columns: ['brand', 'name'],   // must match your colDef.field values
    force: true
  });
  this.gridApi.redrawRows({ rowNodes: [rn] });

  // 7) Persist to Cart + IndexedDB (so a later full refresh matches the UI)
  if (this.parent.Cart?.Items) {
    this.parent.Cart.Items[this.ClickedRowIndex] = JSON.parse(JSON.stringify(cartItem));
    this.saveCartToIDB();
  }

  // 8) (Optional) If you want server-side normalization now, keep UpdateCart();
  //    It will re-run InitializeCart and re-render the whole grid.
  //    If you want only immediate local UI, you can remove the next line.
  this.UpdateCart();

  // 9) Close popup
  this.ShowBrandCollateralNewProduct = false;
}

NewProductBrandsList:any[];

GetBrandsFilteredWithSuggestion(searchTerm: string, page = 1, implicitProduct: string = null) {
  // keep filter values in sync
  this.BrandFilter.Pager.PageIndex = page;
  this.BrandFilter.SearchTerm = searchTerm ?? '';

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Filter: this.BrandFilter,
    Product: implicitProduct ?? null
  };

  this.LoadingSideOffers = true;

  this.managementService.GetBrandsFilteredWithSuggestion(request).subscribe(response => {
    try {
      if (response['Status'] !== 1) {
        this.parent.openSnackBar(response['Message'], 'OK', 'alert-info-snackbar');
        return;
      }

      // 1) Update source-of-truth list
      this.NewProductBrandsList = response['Brands'] ?? [];

      // 2) If we’re in a grid context (we set ClickedRowIndex on click), mirror to the row
      if (typeof this.ClickedRowIndex === 'number') {
        const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.ClickedRowIndex);
        if (rowNode?.data) {
          const ci = rowNode.data.CartItem ?? (rowNode.data.CartItem = {});
          ci.CollateralBrands = JSON.parse(JSON.stringify(this.NewProductBrandsList));

          // persist to Cart + IndexedDB if available
          if (this.parent.Cart?.Items?.[this.ClickedRowIndex]) {
            this.parent.Cart.Items[this.ClickedRowIndex].CollateralBrands = JSON.parse(JSON.stringify(this.NewProductBrandsList));
            this.saveCartToIDB();
          }

          // refresh only the brand cell (button state remains consistent)
          this.gridApi.refreshCells({
            rowNodes: [rowNode],
            columns: ['brand'],
            force: true
          });
        }
      }

      // 3) Feed the popup with the SAME model used elsewhere
      this.BrandSelectList = JSON.parse(JSON.stringify(this.Brands));
      this.ShowBrandCollateralNewProduct = true;    // ensure opened (in case it was closed)
    }
    finally {
      this.LoadingSideOffers = false;
    }
  });
}

onSelectSearchResultAlternative(s:any){
  s.Loading = true;
}

addMoreRows(count: number = 50): void {
  if (!this.gridApi) return;

  const currentCount = this.gridApi.getDisplayedRowCount();
  const newRows: any[] = [];

  for (let i = 0; i < count; i++) {
    const row = {
      index: currentCount + i + 1,
      brand: '',
      partNumber: '',
      name: '',
      quantity: null,
      price: null,
      total: null,
      eta: '',
      hscode: '',
      coo: '',
      weight: '',
      CartItem: {}
    };

    this.CartItemRows.push(row);     // if you still need it for logic
    newRows.push(row);
  }

  // 🔥 THIS is what updates the grid
  this.gridApi.applyTransaction({ add: newRows });
  console.log('✅ Adding rows:', newRows.length);
}

/**
 * ✅ Insert a new empty row at a specific grid index, pushing existing rows down.
 * Updates Cart.Items, OriginalCartItems mirror list, and persists to IndexedDB.
 * @param gridIndex 0-based index where the new row should be inserted
 */
insertRowAtIndex(gridIndex: number): void {
  if (!this.gridApi) return;

  const totalRows = this.gridApi.getDisplayedRowCount();
  if (gridIndex < 0) gridIndex = 0;
  if (gridIndex > totalRows) gridIndex = totalRows;

  // 1) Build an empty row object (same shape as addMoreRows)
  const emptyRow: any = {
    index: gridIndex + 1,
    brand: '',
    partNumber: '',
    name: '',
    quantity: null,
    price: null,
    total: null,
    eta: '',
    hscode: '',
    coo: '',
    weight: '',
    CartItem: {}
  };

  // 2) Insert into the AG Grid at the specified position
  this.gridApi.applyTransaction({ add: [emptyRow], addIndex: gridIndex });

  // 3) Splice an empty CartItem into parent.Cart.Items at the same position
  if (this.parent.Cart?.Items) {
    this.parent.Cart.Items.splice(gridIndex, 0, {});
  }

  // 4) Keep OriginalCartItems mirror list in sync (insert empty placeholder)
  if (this.OriginalCartItems && this.OriginalCartItems.length > 0) {
    this.OriginalCartItems.splice(gridIndex, 0, {});
    this.saveOriginalCartToIDB();
  }

  // 5) Re-index all rows so the display numbers stay sequential
  this.gridApi.forEachNode((node: any, idx: number) => {
    if (node.data) {
      node.data.index = idx + 1;
    }
  });
  this.gridApi.refreshCells({ columns: ['index'], force: true });

  // 6) Persist to IndexedDB
  this.saveCartToIDB();

  console.log(`✅ Inserted empty row at index ${gridIndex}, total rows: ${this.gridApi.getDisplayedRowCount()}`);
}

ClearGridQuestion:boolean;

AskClearGridQuestion(){
  this.ClearGridQuestion = true;
}

ClearGrid(): void {
  // ✅ Reset to exactly 50 empty rows
  const emptyRows = Array.from({ length: 50 }, (_, i) => ({
    index: i + 1,
    brand: '',
    partNumber: '',
    name: '',
    quantity: null,
    price: null,
    total: null,
    eta: '',
    hscode: '',
    coo: '',
    weight: '',
    CartItem: {}
  }));

  this.gridApi.setRowData(emptyRows);

  // ✅ Clear cart in parent
  this.parent.Cart = { Items: [], ShippingCost: { UIUnitPrice: null } };
  this.CartItemRows = [];

  // ✅ Reset GridID and delete tracking data from localStorage (fresh start for new grid)
  this.resetGridID();
  this.clearCartFromIDB(); // ✅ Clear cart from IndexedDB
  localStorage.removeItem(this.ADMIN_TRACKING_KEY);
  this.OriginalCartItems = []; // ✅ Clear in-memory mirror list

  console.log('🧹 Grid reset to 50 empty rows, cart emptied, all IndexedDB/localStorage data cleared.');
}

DeleteRedRowsQuestion:boolean;

AskDeleteRedRowsQuestion(){
  this.DeleteRedRowsQuestion = true;
}

DeleteAllRedRows(): void {
  if (!this.gridApi) return;

  // ✅ Collect all rows that are red (CartItemStatus.ID === 0)
  const rowsToDelete: any[] = [];
  this.gridApi.forEachNode((node: any) => {
    const cartItem = node.data?.CartItem;
    if (cartItem?.CartItemStatus?.ID === 0) {
      rowsToDelete.push(node.data);
    }
  });

  if (rowsToDelete.length === 0) {
    this.parent.openSnackBar(this.translate.instant('NoRedRowsToDelete'), 'OK', 'alert-info-snackbar');
    return;
  }

  // ✅ Remove red rows from parent.Cart.Items by UIID
  const uiidsToDelete = new Set(
    rowsToDelete.map((r: any) => r.CartItem?.UIID).filter((uiid: any) => uiid != null)
  );
  if (uiidsToDelete.size > 0 && this.parent.Cart?.Items) {
    this.parent.Cart.Items = this.parent.Cart.Items.filter((item: any) => !uiidsToDelete.has(item.UIID));
  }

  // ✅ Remove the red rows from the grid
  this.gridApi.applyTransaction({ remove: rowsToDelete });

  // ✅ Maintain a minimum of 50 rows in the grid (same padding as ClearGrid)
  const remainingRows = this.gridApi.getDisplayedRowCount();
  if (remainingRows < 50) {
    this.addMoreRows(50 - remainingRows);
  }

  // ✅ Persist the updated cart
  this.UpdateCart();

  this.parent.openSnackBar(`${rowsToDelete.length} ${this.translate.instant('RedRowsDeleted')}`, 'OK', 'alert-info-snackbar');
}

GetCart(): void {
  // ✅ Load cart from IndexedDB (async)
  this.loadCartFromIDB().then(storedCart => {
    if (!storedCart) return;

    this.parent.Cart = storedCart;
    const items = this.parent.Cart?.Items ?? [];

    // ✅ Debug: Log SaveToDB flags loaded from IndexedDB
    items.forEach((item: any, index: number) => {
      if (item?.CartItemStatus?.SaveToDB != null) {
        console.log(`📥 GetCart: Item ${index} SaveToDB=${item.CartItemStatus.SaveToDB}, UIID=${item.UIID}`);
      }
    });

    // ✅ Load mirror list from IndexedDB
    this.loadOriginalCartFromIDB().then(storedOriginalCart => {
      if (storedOriginalCart) {
        this.OriginalCartItems = storedOriginalCart;
        console.log(`🪞 Loaded mirror list from IndexedDB with ${this.OriginalCartItems.length} original CartItems`);
      } else {
        // ✅ If no mirror list exists, create one from current cart (deep clone)
        this.OriginalCartItems = JSON.parse(JSON.stringify(items));
        this.saveOriginalCartToIDB();
        console.log(`🪞 Created new mirror list with ${this.OriginalCartItems.length} original CartItems`);
      }

      this.populateGridFromCart(items);
    });
  });
}

private populateGridFromCart(items: any[]): void {
  // ✅ Debug: Log SaveToDB flags in items being populated
  const itemsWithSaveToDB = items.filter((item: any) => item?.CartItemStatus?.SaveToDB != null);
  console.log(`🔲🔲🔲 populateGridFromCart: ${items.length} items, ${itemsWithSaveToDB.length} have SaveToDB flag`);
  itemsWithSaveToDB.forEach((item: any) => {
    console.log(`🔲🔲🔲 populateGridFromCart: Item UIID=${item.UIID} SaveToDB=${item.CartItemStatus.SaveToDB}`);
  });

  const rows = items.map((cartItem, index) => {
    const product = cartItem?.Product ?? {};
    const stats = product?.Stats ?? {};
    const price = cartItem?.Price ?? {};
    const leadTime = cartItem?.SelectedOffer?.LeadTime;

    // ✅ Use UIOutput first (contains prolonged text from backend), fallback to simple format
    const eta = leadTime?.UIOutput ?? (leadTime?.Units && leadTime.Units > 0
      ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}`
      : '------');

    return {
      index: index + 1,
      brand: product?.Brand?.Name ?? '',
      partNumber: product?.PartNumber ?? '',
      name: cartItem?.UIProductValue ?? '',
      quantity: cartItem?.Quantity ?? null,
      price: price?.UIUnitPrice ?? '',
      total: price?.UIQtyPrice ?? '',
      eta,
      hscode: stats?.HSCode ?? '',
      coo: stats?.COO?.ShortName ?? '',
      weight: stats?.Weight != null
        ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}` : '',
      CartItem: JSON.parse(JSON.stringify(cartItem))
    };
  });

  // ✅ Ensure at least 50 rows (pad with empty rows if needed)
  const minRows = 50;
  if (rows.length < minRows) {
    for (let i = rows.length; i < minRows; i++) {
      rows.push({
        index: i + 1,
        brand: '',
        partNumber: '',
        name: '',
        quantity: null,
        price: null,
        total: null,
        eta: '',
        hscode: '',
        coo: '',
        weight: '',
        CartItem: {}
      });
    }
  }

  this.gridApi.setRowData(rows);
  console.log(`🛒 Loaded ${rows.length} items into the grid from localStorage.`);
}

deliveryPreferences: any[] = [
  { value: 1, label: 'Standard' },
  { value: 2, label: 'Express' },
  { value: 3, label: 'Pickup' }
];

selectedPref: any;

/**
 * Save cart to IndexedDB only (without calling InitializeCart)
 * Used when admin edits prices to avoid backend overwriting the changes
 */
saveCartToLocalStorageOnly(): void {
  const items: any[] = [];

  // Loop over all displayed rows in AG Grid
  this.gridApi.forEachNode((node) => {
    const cartItem = node.data?.CartItem;
    if (cartItem) {
      // ✅ Debug: Log SaveToDB before deep clone
      if (cartItem?.CartItemStatus?.SaveToDB != null) {
        console.log(`💾 saveCartToLocalStorageOnly: Row ${node.rowIndex} SaveToDB=${cartItem.CartItemStatus.SaveToDB}, UIID=${cartItem.UIID}`);
      }
      const clonedItem = JSON.parse(JSON.stringify(cartItem)); // deep copy to avoid reference issues
      clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
      items.push(clonedItem);
    }
  });

  this.parent.Cart.Items = items;

  // ✅ Cart totals are already correctly calculated by InitializeCart() - no local recalculation needed

  // ✅ Save directly to IndexedDB
  this.saveCartToIDB();
  console.log('💾 Cart saved to IndexedDB (admin price edit - skipping backend call)');
}

UpdateCart(isRedRowEdit: boolean = false){
  const items: any[] = [];

  // Loop over all displayed rows in AG Grid
  this.gridApi.forEachNode((node) => {
    const cartItem = node.data?.CartItem;
    if (cartItem) {
      const clonedItem = JSON.parse(JSON.stringify(cartItem)); // deep copy to avoid reference issues
      clonedItem.Index = (node.rowIndex ?? 0) + 1; // ✅ Set Index to 1-based row position for correct PDF ordering
      items.push(clonedItem);
    }
  });

  this.parent.Cart.Items = items;

  // ✅ Check if grid is effectively empty (no rows with CartItem information)
  if (items.length === 0) {
    this.resetGridID();
    localStorage.removeItem(this.ADMIN_TRACKING_KEY);
    console.log('🔄 Grid is empty - GridID and tracking data reset');
  }

  // ✅ Red row precheck gate: if edit came from a red row, check all red rows have bare minimum
  // Bare minimum = Brand + Name + Final-Price. If any red row is incomplete, skip InitializeCart
  // and only save to IndexedDB (data preserved on refresh, backend call deferred)
  if (isRedRowEdit) {
    const redRows = items.filter((ci: any) => ci.CartItemStatus?.ID === 0);
    const incompleteRedRows = redRows.filter((ci: any) => {
      const hasBrand = ci.Product?.Brand?.Name || ci.Product?.Brand?.ID > 0;
      const hasName = ci.Product?.Name || ci.UIProductValue;
      const hasFinalPrice = (ci.SelectedOffer?.SellPrice?.UIUnitPrice > 0) || (ci.Price?.UIUnitPrice > 0);
      return !hasBrand || !hasName || !hasFinalPrice;
    });

    if (incompleteRedRows.length > 0) {
      console.log(`🔴 Red row precheck: ${incompleteRedRows.length}/${redRows.length} red rows incomplete — skipping InitializeCart, saving to IDB only`);
      this.saveCartToIDB();
      return;
    }
    console.log(`✅ Red row precheck: All ${redRows.length} red rows have Brand + Name + Price — proceeding with InitializeCart`);
  }

  this.InitializeCart();
}

// GridID management for tracking
private getOrCreateGridID(): string {
  let gridID = localStorage.getItem(this.ADMIN_GRID_ID_KEY);

  if (!gridID || gridID.length < 35) {
    // Generate new unique GridID (35-50 characters)
    gridID = this.generateUniqueGridID();
    this.safeLocalStorageSet(this.ADMIN_GRID_ID_KEY, gridID);
  }

  return gridID;
}

private generateUniqueGridID(): string {
  // Generate a unique 40-character ID
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  const userPart = this.parent.User?.Id?.substring(0, 8) || 'guest';
  return `${timestamp}-${randomPart}-${userPart}`.substring(0, 45);
}

private resetGridID(): void {
  localStorage.removeItem(this.ADMIN_GRID_ID_KEY);
}

// Track B2B User Grid Event
TrackB2BUserGridEvent(eventTypeID: number): void {
  // ✅ ONLY track events from EMPLOYEE GRID (not admin grid)
  if (this.useAdminGrid) {
    console.log('⏭️ Skipping tracking - Admin Grid does not track events');
    return;
  }

  const gridID = this.getOrCreateGridID();

  // ✅ Get previously stored tracking data from localStorage
  let storedTracking: any = null;
  try {
    const storedData = localStorage.getItem(this.ADMIN_TRACKING_KEY);
    if (storedData) {
      storedTracking = JSON.parse(storedData);
    }
  } catch (e) {
    console.warn('Could not parse stored tracking data:', e);
  }

  // Collect all grid lines
  const lines: any[] = [];

  this.gridApi.forEachNode((node) => {
    const data = node.data;
    const cartItem = data?.CartItem;

    // Only include rows with meaningful data
    if (data && (data.name || data.partNumber || data.brand)) {
      const gridIndex = node.rowIndex + 1; // 1-based index

      // ✅ Find existing line ID from localStorage based on GridIndex
      let existingLineID = 0; // 0 = insert new, >0 = update existing
      if (storedTracking?.Lines && Array.isArray(storedTracking.Lines)) {
        const existingLine = storedTracking.Lines.find((l: any) => l.GridIndex === gridIndex);
        if (existingLine?.ID) {
          existingLineID = existingLine.ID;
        }
      }

      const line = {
        ID: existingLineID, // ✅ Include database ID (0 for new, existing ID for update)
        GridIndex: gridIndex,
        Brand: data.brand || '',
        PartNumber: data.partNumber || '',
        Name: data.name || '',
        Qty: cartItem?.Quantity || 0,
        ListPrice: this.extractNumericValue(cartItem?.Product?.Stats?.StandardListPrice?.UIUnitPrice),
        Discount: cartItem?.Product?.Stats?.StandardListPrice?.Discount?.Value || null,
        FinalPrice: this.extractNumericValue(cartItem?.SelectedOffer?.SellPrice?.UIUnitPrice),
        Total: this.extractNumericValue(cartItem?.Price?.UIQtyPrice),
        StandardDelivery: data.eta || '',
        HSCode: data.hscode || '',
        COO: data.coo || '',
        Weight: data.weight || '',
        DatasheetLink: cartItem?.Product?.Stats?.DatasheetLink || ''
        // ParentEventID, EventRegister, EventRegisterUI - handled by backend
      };

      lines.push(line);
    }
  });

  // Find the event type from parent
  const eventType = this.parent.B2BUserTrackingEventTypes?.find(e => e.ID === eventTypeID);

  // ✅ Get existing EventID from localStorage (0 for new, existing ID for update)
  let existingEventID = 0;
  if (storedTracking?.EventID) {
    existingEventID = storedTracking.EventID;
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Partner: this.parent.B2BPartner,
    B2BUserTracking: {
      EventID: existingEventID, // ✅ 0 = insert new event, >0 = update existing
      EventType: eventType || { ID: eventTypeID, Name: '' },
      EventRegister: new Date(),
      EventRegisterUI: new Date().toISOString(),
      IncomingURL: window.location.href,
      PartnerID: this.parent.B2BPartner?.ID?.toString() || '',
      PartnerName: this.parent.B2BPartner?.Name || '',
      IncomingUserID: this.parent.User?.Id || '',
      IncomingEmailAddress: this.parent.User?.Email || '',
      Status: 1,
      GridID: gridID,
      Lines: lines
      // IncomingCountryCode, IncomingIPAddress, IncomingAddressDetails - populated by backend
    }
  };

  // Send tracking request
  this.B2BService.B2BRUTE(request).subscribe(response => {
    if (response["Status"] === 1) {
      console.log('✅ Grid event tracked successfully:', eventType?.Name, lines.length, 'rows');

      // ✅ Store the backend response with database-generated IDs in localStorage
      if (response["B2BUserTracking"]) {
        const trackingData = response["B2BUserTracking"];
        this.safeLocalStorageSet(this.ADMIN_TRACKING_KEY, JSON.stringify(trackingData));
        console.log('💾 Stored tracking data with EventID:', trackingData.EventID);
      }
    } else {
      console.warn('⚠️ Grid event tracking failed:', response["Message"]);
    }
  });
}

// Helper to extract numeric values from formatted strings
private extractNumericValue(value: any): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const numStr = value.replace(/[^\d.-]/g, '');
    const num = parseFloat(numStr);
    return isNaN(num) ? null : num;
  }
  return null;
}

InitializeCart(): void {
  // ✅ FIX: Sanitize Weight on ALL cart items before processing
  // Weight can get corrupted to an object format {Units, Type, UIOutput} from the red row edit popup
  if (this.parent.Cart?.Items) {
    this.parent.Cart.Items.forEach((item: any, index: number) => {
      if (item?.Product?.Stats) {
        const stats = item.Product.Stats;
        if (stats.Weight != null && typeof stats.Weight === 'object') {
          console.log(`💾 [InitializeCart] [FIX] Cart.Items[${index}].Stats.Weight is object, extracting Units value:`, stats.Weight);
          const weightValue = stats.Weight.Units ?? stats.Weight.Value ?? null;
          if (stats.Weight.Type && !stats.WeightMeasurementType) {
            stats.WeightMeasurementType = stats.Weight.Type;
          }
          stats.Weight = weightValue;
        }
        delete stats.CartItem;
        delete stats.Stats;
        delete stats.Cart;
        delete stats.Items;
      }
    });
  }

  // ✅ Client-side cart initialization — no server round-trip needed
  // All logic from backend InitializeCart (B2BBL.cs) is replicated in cart-initializer.util.ts
  const result = initializeCartLocal(
    this.parent.Cart,
    this.parent.B2BPartner,
    this.parent.CartMasterData,
    this.parent.User
  );

  this.parent.Cart = result.cart;
  this.parent.CartMasterData = result.masterData;
  const hasChanges = result.hasChanges;

  // ✅ Initialize ShippingCost if null
  if (!this.parent.Cart.ShippingCost) {
    this.parent.Cart.ShippingCost = { UIUnitPrice: null };
  }

  // ✅ Initialize Cart.Total.Discount if null
  if (!this.parent.Cart.Total) {
    this.parent.Cart.Total = {};
  }
  if (!this.parent.Cart.Total.Discount) {
    this.parent.Cart.Total.Discount = { Type: null, Value: null };
  }

  // ✅ Check if stock was readjusted and show popup
  if (result.stockWasReadjusted) {
    this.stockReadjustedMessage = result.stockReadjustedMessage || 'Some quantities were adjusted due to stock limits.';
    this.stockReadjustedProducts = result.stockReadjustedProducts || [];
    this.showStockReadjustedPopup = true;
  }

  this.saveCartToIDB(); // ✅ Save cart to IndexedDB

  // ✅ Populate shipping config controls from Cart object
  this.PopulateShippingConfigFromCart();

  // ✅ Create/update mirror list of original CartItems ONLY if cart has items and no AdminPriceEdit flags
  const items = this.parent.Cart?.Items ?? [];
  const hasAnyEdits = items.some(item => item?.AdminPriceEdit === true);

  if (items.length > 0 && !hasAnyEdits) {
    this.OriginalCartItems = JSON.parse(JSON.stringify(items));
    this.saveOriginalCartToIDB();
    console.log(`🪞 Created/updated mirror list with ${this.OriginalCartItems.length} original CartItems (no edits detected)`);
  } else if (hasAnyEdits) {
    console.log(`⚠️ Skipped mirror list update - cart has admin edits`);
  }

  if (hasChanges && this.gridApi) {

    const rows = items.map((cartItem, index) => {
      const product = cartItem?.Product ?? {};
      const stats = product?.Stats ?? {};
      const leadTime = cartItem?.SelectedOffer?.LeadTime ?? {};
      const priceInfo = cartItem?.Price ?? {};

      const sellPrice = cartItem?.SelectedOffer?.SellPrice?.UIUnitPrice;
      // ✅ show just the number, no currency symbol
      const price = sellPrice > 0 ? sellPrice : '';

      // ✅ show just the number, no currency symbol
      const total = priceInfo?.UIQtyPrice ?? '';

      // ✅ Use UIOutput first (contains prolonged text from backend), fallback to simple format
      const eta = leadTime?.UIOutput ?? (leadTime?.Units > 0
          ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}`
          : '------');

      return {
        index: index + 1,
        brand: product?.Brand?.Name ?? '',
        partNumber: product?.PartNumber ?? '',
        name: cartItem?.UIProductValue ?? '',
        quantity: cartItem?.Quantity ?? null,
        price,
        total,
        eta,
        hscode: stats?.HSCode ?? '',
        coo: stats?.COO?.ShortName ?? '',
        weight:
          stats?.Weight != null
            ? `${stats.Weight} ${stats?.WeightMeasurementType?.Name ?? ''}`
            : '',
        CartItem: JSON.parse(JSON.stringify(cartItem))
      };
    });

    this.gridApi.setRowData(rows);
    console.log(`🔁 Grid re-rendered due to cart changes (${rows.length} rows).`);
  }

  this.loadingProducts = false;
  this.parent.loading = false;
}

// Partner Search Methods
onPartnerSearchFocus() {
  this.showPartnerResults = true;
  if (this.searchedPartners.length === 0) {
    this.GetPartnersFiltered();
  }
}

GetPartnersFiltered() {
  this.loadingPartners = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    SearchTerm: this.partnerSearchTerm || '',
    PageIndex: this.partnerPageIndex,
    PageSize: this.partnerPageSize
  };

  this.service.GetPartnersFiltered(request).subscribe((response: any) => {
    this.loadingPartners = false;

    if (response && response.Partners) {
      this.searchedPartners = response.Partners;
      this.partnerTotalPages = response.TotalPages || 1;
      this.showPartnerResults = true;
    } else {
      this.searchedPartners = [];
      this.partnerTotalPages = 1;
    }
  }, (error: any) => {
    this.loadingPartners = false;
    this.searchedPartners = [];
    console.error('Error fetching partners:', error);
  });
}

selectPartner(partner: any) {
  console.log('Selected partner (basic info):', partner);

  // ✅ Immediately remove cleared flag and cancel pending timers to prevent them from wiping this selection
  localStorage.removeItem(this.ADMIN_PARTNER_CLEARED_KEY);
  this.cancelPartnerClearedTimers();

  // ✅ Clear grid and reset to fresh 50 rows when switching partners
  // COMMENTED OUT: Uncomment if grid should be cleared on partner change
  // this.ClearGridAndResetToFresh();

  // ✅ Update UI immediately with basic info
  this.showPartnerResults = false;
  this.partnerSearchTerm = partner.Name;

  // ✅ Load partner contractual conditions FIRST (includes payment terms)
  // Then load last B2B settings (which won't overwrite contractual payment terms)
  this.GetPartnerContractualConditions(partner.ID, () => {
    // ✅ Callback: Load last used payment/shipment settings AFTER contractual conditions
    this.GetPartnerLastB2BPaymentShipmentSettings(partner.ID);
  });

  // ✅ Load country shipping options for this partner
  this.GetCountryShippingOptions();

  // ✅ Fetch full partner details from backend (includes Agents, ShippingAddresses, etc.)
  this.parent.loading = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: partner.ID,
    WithAllData: true
  };

  this.service.GetPartner(request).subscribe((response: any) => {
    this.parent.loading = false;

    if (response && response.Status === 1 && response.Partner) {
      console.log('✅ Full partner details received:', response.Partner);

      // ✅ Override parent's B2BPartner with FULL partner details (includes Agents, ShippingAddresses)
      this.parent.B2BPartner = response.Partner;

      // ✅ Save to localStorage for persistence across page refreshes
      this.SaveAdminSelectedPartner(response.Partner);

      // ✅ Show success message
      this.parent.openSnackBar(
        `Partner selected: ${response.Partner.Name} - All pricing will use this partner's settings`,
        'OK',
        'light-info-snackbar',
        3000
      );
    } else {
      console.error('❌ Failed to get full partner details:', response);
      this.parent.openSnackBar(
        response?.Message || 'Failed to load partner details',
        'OK',
        'alert-info-snackbar'
      );
    }
  }, (error: any) => {
    this.parent.loading = false;
    console.error('❌ Error fetching partner details:', error);
    this.parent.openSnackBar(
      'Error loading partner details',
      'OK',
      'alert-info-snackbar'
    );
  });
}

clearPartnerSearch() {
  this.partnerSearchTerm = '';
  this.searchedPartners = [];
  this.showPartnerResults = false;
  this.partnerPageIndex = 1;
}

previousPartnerPage() {
  if (this.partnerPageIndex > 1) {
    this.partnerPageIndex--;
    this.GetPartnersFiltered();
  }
}

nextPartnerPage() {
  if (this.partnerPageIndex < this.partnerTotalPages) {
    this.partnerPageIndex++;
    this.GetPartnersFiltered();
  }
}

// ✅ Admin Partner Override Management (localStorage-based)

/**
 * Load admin-selected partner from localStorage on page load/refresh
 * If found, override parent.B2BPartner with the stored partner
 * If not found, parent.B2BPartner keeps the admin's default connected partner
 */
LoadAdminSelectedPartner() {
  try {
    const storedPartner = localStorage.getItem(this.ADMIN_SELECTED_PARTNER_KEY);
    const wasCleared = localStorage.getItem(this.ADMIN_PARTNER_CLEARED_KEY) === 'true';

    if (storedPartner) {
      const partner = JSON.parse(storedPartner);

      // ✅ Override parent's B2BPartner with stored partner
      this.parent.B2BPartner = partner;

      // ✅ Update search bar to show selected partner name
      this.partnerSearchTerm = partner.Name || '';

      // ✅ Load partner contractual conditions (includes payment terms)
      if (partner.ID) {
        this.GetPartnerContractualConditions(partner.ID, () => {
          // ✅ Callback: Load last B2B settings after contractual conditions
          this.GetPartnerLastB2BPaymentShipmentSettings(partner.ID);
        });
      }

      console.log('Admin partner override loaded from localStorage:', partner.Name);
    } else if (wasCleared) {
      // ✅ User intentionally cleared the partner - stay in "No Partner" state
      this.parent.B2BPartner = null;
      this.partnerSearchTerm = '';
      console.log('Partner was intentionally cleared - staying in No Partner state');

      // ✅ Re-clear after delay to neutralize parent.GetB2BUserConnectedPartner() async response
      // The parent fires GetB2BUserConnectedPartner in its constructor which asynchronously
      // sets parent.B2BPartner. We must override it after it resolves.
      // Timers are tracked so they can be cancelled if user selects a partner or navigates away.
      this.cancelPartnerClearedTimers();
      this.partnerClearedTimers.push(setTimeout(() => {
        if (localStorage.getItem(this.ADMIN_PARTNER_CLEARED_KEY) === 'true') {
          this.parent.B2BPartner = null;
          this.partnerSearchTerm = '';
        }
      }, 1000));
      this.partnerClearedTimers.push(setTimeout(() => {
        if (localStorage.getItem(this.ADMIN_PARTNER_CLEARED_KEY) === 'true') {
          this.parent.B2BPartner = null;
          this.partnerSearchTerm = '';
        }
      }, 2500));
    } else {
      console.log('No admin partner override found - using default connected partner');
      // ✅ parent.B2BPartner already set by parent.GetB2BUserConnectedPartner() in ngOnInit

      // ✅ Load contractual conditions for default partner (with delay to ensure parent.B2BPartner is loaded)
      setTimeout(() => {
        if (this.parent.B2BPartner?.ID) {
          console.log('🔍 Loading contractual conditions for default partner:', this.parent.B2BPartner.Name);
          this.GetPartnerContractualConditions(this.parent.B2BPartner.ID, () => {
            // ✅ Callback: Load last B2B settings after contractual conditions
            this.GetPartnerLastB2BPaymentShipmentSettings(this.parent.B2BPartner.ID);
          });
        }
      }, 500);
    }
  } catch (error) {
    console.error('Error loading admin selected partner from localStorage:', error);
  }
}

/**
 * ✅ Set default ConfigStatus based on grid mode (no localStorage)
 * Admin Grid: VisibleOnlyByAdmin (ID=-1)
 * Employee Grid: Active (ID=1)
 */
setDefaultConfigStatus() {
  if (!this.parent.masterData?.ConfigStatuses) {
    // console.log('⚠️ Master data not loaded yet - will set default ConfigStatus after load');
    return;
  }

  // ✅ Determine which ConfigStatus to use based on grid mode
  const targetId = this.useAdminGrid ? -1 : 1; // Admin: -1 (VisibleOnlyByAdmin), Employee: 1 (Active)
  const targetName = this.useAdminGrid ? 'VisibleOnlyByAdmin' : 'Active';

  // ✅ Only set if not already set to the correct value
  if (this.selectedConfigStatus?.ID === targetId) {
    // Already set to correct value, no need to update
    return;
  }

  const configStatus = this.parent.masterData.ConfigStatuses.find((s: any) => s.ID === targetId);

  if (configStatus) {
    this.selectedConfigStatus = configStatus;
    console.log(`✅ ConfigStatus set to ${targetName} (ID=${targetId}) for ${this.useAdminGrid ? 'Admin' : 'Employee'} Grid:`, configStatus.Name, configStatus.Description);
  } else {
    console.warn(`⚠️ ${targetName} ConfigStatus (ID=${targetId}) not found in masterData. Available:`,
      this.parent.masterData.ConfigStatuses.map((s: any) => `${s.Name} (ID=${s.ID})`));
  }
}

/**
 * Save selected partner to localStorage for persistence
 */
SaveAdminSelectedPartner(partner: any) {
  try {
    this.safeLocalStorageSet(this.ADMIN_SELECTED_PARTNER_KEY, JSON.stringify(partner));
    // ✅ Remove cleared flag since a new partner is being explicitly selected
    localStorage.removeItem(this.ADMIN_PARTNER_CLEARED_KEY);
    console.log('Admin selected partner saved to localStorage:', partner.Name);
  } catch (error) {
    console.error('Error saving admin selected partner to localStorage:', error);
  }
}

/**
 * Clear admin partner override and revert to default connected partner.
 * Called programmatically after successful Offer/OrderConfirmation registration,
 * and also from the UI "X" button via ClearAdminSelectedPartnerByUser().
 */
ClearAdminSelectedPartner() {
  try {
    // ✅ Clear grid and reset to fresh 50 rows
    // COMMENTED OUT: Grid should NOT be cleared when X'ing partner, only when placing an order
    // this.ClearGridAndResetToFresh();

    // ✅ Remove partner from localStorage
    localStorage.removeItem(this.ADMIN_SELECTED_PARTNER_KEY);

    // ✅ Remove cleared flag (programmatic reset = fresh start, default partner should load on next refresh)
    localStorage.removeItem(this.ADMIN_PARTNER_CLEARED_KEY);

    // ✅ Clear parent's B2BPartner (disable grid until new partner selected)
    this.parent.B2BPartner = null;

    // ✅ Clear search term
    this.partnerSearchTerm = '';

    // ✅ Show info message
    this.parent.openSnackBar(
      'Partner cleared - Please select a partner to continue',
      'OK',
      'light-info-snackbar',
      3000
    );

    console.log('✅ Admin partner override cleared - reverted to no partner selected');
  } catch (error) {
    console.error('Error clearing admin selected partner:', error);
  }
}

/**
 * ✅ User-initiated partner clear (X button).
 * Sets a "cleared" flag so the component remembers the intentional clear across page refreshes,
 * preventing the default connected partner from being auto-assigned.
 */
ClearAdminSelectedPartnerByUser() {
  this.ClearAdminSelectedPartner();
  // ✅ Set cleared flag ONLY for explicit user action
  this.safeLocalStorageSet(this.ADMIN_PARTNER_CLEARED_KEY, 'true');
  console.log('✅ User-initiated partner clear - cleared flag set');
}

/**
 * ✅ Cancel any pending partner-cleared timers.
 * Called when user selects a new partner or component is destroyed,
 * to prevent stale timeouts from nulling parent.B2BPartner.
 */
cancelPartnerClearedTimers() {
  this.partnerClearedTimers.forEach(t => clearTimeout(t));
  this.partnerClearedTimers = [];
}

ngOnDestroy(): void {
  // ✅ Cancel partner-cleared timers to prevent leaking into other pages
  this.cancelPartnerClearedTimers();
}

/**
 * ✅ Open Partner Details Popup - Shows full partner information in a popup
 */
OpenPartnerDetailsPopup() {
  if (!this.parent.B2BPartner?.ID) {
    console.log('⚠️ No partner selected');
    return;
  }

  console.log('🔍 Opening partner details popup for:', this.parent.B2BPartner.Name);

  // ✅ Initialize tab states (default to Billing Info)
  this.ShowBillingInfo = true;
  this.ShowShippingInfo = false;
  this.ShowAgentsInfo = false;
  this.ShowProfitSettings = false;
  this.ShowProcessingSettings = false;
  this.ShowB2BRestrictions = false;
  this.ShowContractualConditions = false;
  this.ShowAcceptedEmailDomains = false;

  // ✅ Reset Accepted Email Domains state
  this.AcceptedDomains = [];
  this.NewAcceptedEmailDomain = '';
  this.DeletingAcceptedDomain = null;
  this.ShowDeleteAcceptedDomainQuestion = false;
  this.InvalidAcceptedDomainFormat = false;

  // ✅ Load full partner details from backend with WithAllData flag
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.parent.B2BPartner.ID,
    WithAllData: true
  };

  this.parent.loading = true;

  this.service.GetPartner(request).subscribe((response: any) => {
    this.parent.loading = false;

    if (response["Status"] === 1) {
      this.PartnerDetails = response["Partner"];
      this.ShowPartnerDetailsPopup = true;
      console.log('✅ Partner details loaded:', this.PartnerDetails);

      // ✅ Load shipping addresses and shipping master data on popup open
      this.LoadPartnerShippingAddresses();

      // ✅ Load partner master data (Countries, Banks)
      this.LoadPartnerMasterData();
    } else {
      console.error('❌ Failed to load partner details:', response);
      this.parent.openSnackBar(
        response?.Message || 'Failed to load partner details',
        'OK',
        'alert-info-snackbar'
      );
    }
  }, (error: any) => {
    this.parent.loading = false;
    console.error('❌ Error loading partner details:', error);
    this.parent.openSnackBar(
      'Error loading partner details',
      'OK',
      'alert-info-snackbar'
    );
  });
}

/**
 * ✅ Close Partner Details Popup
 */
ClosePartnerDetailsPopup() {
  this.ShowPartnerDetailsPopup = false;
  this.PartnerDetails = null;
}

/**
 * ✅ Load Partner Shipping Addresses (called on popup open)
 */
LoadPartnerShippingAddresses() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.service.GetPartnerShippingAddresses(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      this.PartnerDetails.ShippingAddresses = response["ShippingAddresses"];
      // Set first shipping address as current
      if (this.PartnerDetails.ShippingAddresses?.length > 0) {
        this.CurrentPartnerShippingAddress = this.PartnerDetails.ShippingAddresses[0];
      }
      console.log('✅ Shipping addresses loaded:', this.PartnerDetails.ShippingAddresses);
    }
  });

  // Also load shipping master data
  this.managementService.GetShippingMasterData(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      console.log('✅ Shipping master data loaded');
    }
  });
}

/**
 * ✅ Load Partner Agents (called when clicking Agents tab)
 */
LoadPartnerAgents() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;
  this.service.GetPartnerAgents(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerAgentsList = response["PartnerAgents"] || [];
      this.PartnerDetails.Agents = this.PartnerAgentsList;
      // Set first agent as current if available
      if (this.PartnerAgentsList.length > 0) {
        this.CurrentPartnerAgent = this.PartnerAgentsList[0];
      }
      console.log('✅ Partner agents loaded:', this.PartnerAgentsList);
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Load Partner Profit Settings (called when clicking Profit Settings tab)
 */
LoadPartnerProfitSettings() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;
  this.service.GetPartnerProfitSettings(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      // Filter to only show profit settings for this specific partner
      let profitSettings = response["PartnerProfitSettings"] || [];
      this.PartnerDetails.PartnerProfitSettings = profitSettings.filter((x: any) => x.PartnerID == this.PartnerDetails.ID);
      console.log('✅ Profit settings loaded for partner ' + this.PartnerDetails.ID + ':', this.PartnerDetails.PartnerProfitSettings);
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Load Partner Processing Settings (called when clicking Processing Settings tab)
 */
LoadPartnerProcessingSettings() {
  if (!this.PartnerDetails?.ID) return;

  // Initialize ProcessingSettings if null to prevent template errors
  if (!this.PartnerDetails.ProcessingSettings) {
    this.PartnerDetails.ProcessingSettings = {
      NotifiesWithAttachments: false,
      AutoPrinting: false,
      AllocateAutomaticShippingCost: false,
      GenerateInvoice: false,
      GenerateTrackingLabel: false,
      SeparateOrderInvoices: false,
      SplitShippingCostOnAllInvoices: false,
      GenerateQWCertificate: false,
      PrintOnlyDeliveryNote: false,
      SendInvoiceOnlyToAccountingDepartment: false,
      SellThroughPartnerCompany: false,
      Currency: null,
      DecimalPrecision: null,
      PartnerCompany: null,
      UnpaidTolleranceDays: 0
    };
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Partner: this.PartnerDetails
  };

  this.parent.loading = true;

  // Load Companies for the SellThroughPartnerCompany dropdown
  this.service.GetAllCompanies(request).subscribe((response: any) => {
    if (response["Status"] === 1 && response["Companies"]) {
      this.Companies = response["Companies"];
      console.log('✅ Companies loaded:', this.Companies);
    }
  });

  this.service.GetPartnerProcessingSettings(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      if (response["ProcessingSettings"]) {
        this.PartnerDetails.ProcessingSettings = response["ProcessingSettings"];
        console.log('✅ Processing settings loaded:', this.PartnerDetails.ProcessingSettings);
      }
      // Load dropdown data for Currencies and DecimalPrecisions
      if (response["Currencies"]) {
        this.Currencies = response["Currencies"];
        console.log('✅ Currencies loaded:', this.Currencies);
      }
      if (response["DecimalPrecisions"]) {
        this.DecimalPrecisions = response["DecimalPrecisions"];
        console.log('✅ DecimalPrecisions loaded:', this.DecimalPrecisions);
      }
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Load Partner B2B Settings & Restrictions (called when clicking B2B tab)
 */
LoadPartnerB2BData() {
  if (!this.PartnerDetails?.ID) return;

  // Initialize B2BSettings if null to prevent template errors
  if (!this.PartnerDetails.B2BSettings) {
    this.PartnerDetails.B2BSettings = {
      B2BPartnerCategory: null,
      PrioritizePriceHistory: false,
      CanDownloadPriceLists: false,
      Blocked: false,
      YearSettings: null
    };
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;

  // Load B2BPartnerCategories master data
  this.service.GetPartnersMasterData(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      if (response["B2BPartnerCategories"]) {
        this.B2BPartnerCategories = response["B2BPartnerCategories"];
        console.log('✅ B2BPartnerCategories loaded:', this.B2BPartnerCategories);
      }
    }
  });

  // Load B2B Settings
  this.service.GetPartnerB2BSettings(request).subscribe((response: any) => {
    if (response["Status"] === 1 && response["B2BSettings"]) {
      this.PartnerDetails.B2BSettings = response["B2BSettings"];
      console.log('✅ B2B settings loaded:', this.PartnerDetails.B2BSettings);
    }
  });

  // Load B2B Year Settings
  this.service.GetPartnersB2BHistoryYearSettings(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      if (this.PartnerDetails.B2BSettings) {
        this.PartnerDetails.B2BSettings.YearSettings = response["YearSettings"];
      }
      console.log('✅ B2B year settings loaded');
    }
  });

  // Load B2B Restrictions
  this.service.GetPartnerB2BRestictions(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerDetails.B2BRestrictions = response["B2BRestrictions"];
      console.log('✅ B2B restrictions loaded:', this.PartnerDetails.B2BRestrictions);
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Initialize new year settings for adding
 */
InitializeNewYearsSettings() {
  this.NewYearSettings = {};
  this.NewYearSettings.DBID = 0;
  this.NewYearSettings.PartnerID = this.PartnerDetails?.ID;
  this.NewYearSettings.YearsBehind = 1;
  this.NewYearSettings.YearsBehindTargetYear = "";
  this.NewYearSettings.ExtraAddedPercentageValue = 0;
  this.NewYearSettings.OnlyPerosnalHistory = true;
  this.NewYearSettings.CompareHistoryPriceWithListPrice = false;
  const currentYear = new Date().getFullYear();
  this.NewYearSettings.YearsBehindTargetYear = (currentYear - this.NewYearSettings.YearsBehind).toString();
}

/**
 * ✅ Handle YearsBehind change for year settings
 */
onYearsBehindChange(setting: any) {
  const currentYear = new Date().getFullYear();
  if (setting) {
    setting.YearsBehindTargetYear = (currentYear - setting.YearsBehind).toString();
  }
}

/**
 * ✅ Restrict non-numeric input
 */
restrictNonNumericInput(event: KeyboardEvent) {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
}

/**
 * ✅ Allow only numbers and dot (for decimal input like weight)
 */
allowOnlyNumbersAndDot(event: KeyboardEvent) {
  const charCode = event.which ? event.which : event.keyCode;
  const inputValue = (event.target as HTMLInputElement).value;

  // Allow: numbers (48-57), dot/period (46)
  // Prevent: multiple dots
  if (charCode === 46) {
    // Allow dot only if there isn't one already
    if (inputValue.indexOf('.') !== -1) {
      event.preventDefault();
    }
  } else if (charCode < 48 || charCode > 57) {
    // Block anything that's not a number
    event.preventDefault();
  }
}

/**
 * ✅ Add or update a year setting
 */
AddUpdateHistoryYearSetting(s: any) {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    YearSetting: s
  };

  this.parent.loading = true;
  this.service.AddUpdateHistoryYearSetting(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] !== 1) {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    } else {
      const setting = response["YearSetting"];
      const index = this.PartnerDetails.B2BSettings?.YearSettings?.findIndex((x: any) => x.DBID === setting.DBID);
      if (index !== -1) {
        this.PartnerDetails.B2BSettings.YearSettings[index] = setting;
      } else {
        if (!this.PartnerDetails.B2BSettings.YearSettings) {
          this.PartnerDetails.B2BSettings.YearSettings = [];
        }
        this.PartnerDetails.B2BSettings.YearSettings.push(setting);
      }
      this.NewYearSettings = {};
      this.parent.openSnackBar(response["Message"], "OK", "light-info-snackbar");
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Prepare year settings for deletion
 */
PrepareYearSettingsDelete(s: any) {
  this.CurrentYearSettings = JSON.parse(JSON.stringify(s));
  this.ShowDeleteYearSettingsQuestion = true;
}

/**
 * ✅ Delete year setting
 */
DeletePartnersB2BHistoryYearSettings(s: any) {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    DBID: s.DBID
  };

  this.parent.loading = true;
  this.service.DeletePartnersB2BHistoryYearSettings(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] !== 1) {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    } else {
      this.PartnerDetails.B2BSettings.YearSettings = this.PartnerDetails.B2BSettings.YearSettings.filter((x: any) => x.DBID !== s.DBID);
      this.parent.openSnackBar(response["Message"], "OK", "light-info-snackbar");
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Open brand change popup for existing year setting
 */
OpenYearSettingsBrandChange() {
  this.ShowYearSettingsBrandChange = true;
}

/**
 * ✅ Open brand change popup for new year setting
 */
OpenNewYearSettingsBrandChange() {
  this.ShowNewYearSettingsBrandChange = true;
}

/**
 * ✅ Select brand for existing year setting
 */
SelectBrandForYearsSetting(b: any) {
  if (this.CurrentYearSetting) {
    this.CurrentYearSetting.Brand = JSON.parse(JSON.stringify(b));
    this.ShowYearSettingsBrandChange = false;
  }
}

/**
 * ✅ Select brand for new year setting
 */
SelectBrandForNewYearsSetting(b: any) {
  if (this.NewYearSettings) {
    this.NewYearSettings.Brand = JSON.parse(JSON.stringify(b));
    this.ShowNewYearSettingsBrandChange = false;
  }
}

/**
 * ✅ Search brands for year settings
 */
SearchBrandsForYearSettings(searchTerm: string) {
  if (!searchTerm || searchTerm.length < 1) {
    this.YearSettingBrands = [];
    return;
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Filter: {
      SearchTerm: searchTerm,
      Pager: { PageIndex: 1, PageSize: 20 }
    }
  };

  this.managementService.GetBrandsFiltered(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      this.YearSettingBrands = response["Brands"] || [];
    }
  });
}

/**
 * ✅ Load Partner Contractual Conditions (called when clicking Contractual Conditions tab)
 */
LoadPartnerContractualConditions() {
  if (!this.PartnerDetails?.ID) return;

  // Initialize ContractualConditions if null to prevent template errors
  if (!this.PartnerDetails.ContractualConditions) {
    this.PartnerDetails.ContractualConditions = {
      BuyPaymentTerms: null,
      SellPaymentTerms: null,
      ShippingCompany: null,
      ShippingResponsability: null,
      PaymentType: null,
      ShippingServiceType: null,
      ConsolidationType: null,
      DeliveryPreference: null,
      HasContract: false,
      RequiresContract: false,
      SignedByOurCompany: false,
      ContractSignedDate: null,
      ContractExpirationDate: null,
      ContractPath: null
    };
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;
  this.service.GetPartnerContractualConditions(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1 && response["ContractualConditions"]) {
      this.PartnerDetails.ContractualConditions = response["ContractualConditions"];
      console.log('✅ Contractual conditions loaded:', this.PartnerDetails.ContractualConditions);
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Partner B2B Settings
 */
AddUpdatePartnerB2BSettings() {
  if (!this.PartnerDetails?.B2BSettings) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    B2BSettings: this.PartnerDetails.B2BSettings
  };

  this.parent.loading = true;
  this.service.AddUpdatePartnerB2BSettings(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] !== 1) {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    } else {
      this.PartnerDetails.B2BSettings = JSON.parse(JSON.stringify(response["B2BSettings"]));
      this.parent.openSnackBar("B2B settings saved successfully", "OK", "alert-success-snackbar");
      console.log('✅ B2B settings saved:', this.PartnerDetails.B2BSettings);
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Update Partner (Billing Info)
 */
UpdatePartnerDetails() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Partner: this.PartnerDetails
  };

  this.parent.loading = true;
  this.service.UpdatePartner(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerDetails = response["Partner"];
      this.parent.openSnackBar(response["Message"] || 'Partner updated successfully', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to update partner', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Shipping Address
 */
AddUpdatePartnerShippingAddress() {
  if (!this.CurrentPartnerShippingAddress || !this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    ShippingAddress: this.CurrentPartnerShippingAddress,
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;
  this.service.AddUpdateShippingAddress(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.CurrentPartnerShippingAddress = response["ShippingAddress"];
      // Update in list
      const index = this.PartnerDetails.ShippingAddresses?.findIndex((a: any) => a.ID === this.CurrentPartnerShippingAddress.ID);
      if (index !== -1 && index !== undefined) {
        this.PartnerDetails.ShippingAddresses[index] = this.CurrentPartnerShippingAddress;
      } else if (this.CurrentPartnerShippingAddress.ID > 0) {
        this.PartnerDetails.ShippingAddresses = this.PartnerDetails.ShippingAddresses || [];
        this.PartnerDetails.ShippingAddresses.push(this.CurrentPartnerShippingAddress);
      }
      this.parent.openSnackBar(response["Message"] || 'Address saved', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to save address', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Generate Initial Shipping Address
 */
GeneratePartnerInitialShippingAddress() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Partner: this.PartnerDetails
  };

  this.parent.loading = true;
  this.service.GenerateInitialShippingAddress(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerDetails = response["Partner"];
      const addressesLength = this.PartnerDetails.ShippingAddresses?.length || 0;
      if (addressesLength > 0) {
        this.CurrentPartnerShippingAddress = this.PartnerDetails.ShippingAddresses[addressesLength - 1];
      }
      this.parent.openSnackBar(response["Message"] || 'Address generated', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to generate address', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Initialize Partner Agent
 */
InitializePartnerAgent() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;
  this.service.InitializePartnerAgent(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.CurrentPartnerAgent = response["PartnerAgent"];
      this.parent.openSnackBar(response["Message"] || 'New agent initialized', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to initialize agent', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Partner Agent
 */
AddUpdatePartnerAgent() {
  if (!this.CurrentPartnerAgent) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerAgent: this.CurrentPartnerAgent
  };

  this.parent.loading = true;
  this.service.AddUpdatePartnerAgent(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.CurrentPartnerAgent = response["PartnerAgent"];
      // Update in list
      const index = this.PartnerAgentsList?.findIndex((a: any) => a.ID === this.CurrentPartnerAgent.ID);
      if (index !== -1 && index !== undefined) {
        this.PartnerAgentsList[index] = this.CurrentPartnerAgent;
      } else {
        this.PartnerAgentsList = this.PartnerAgentsList || [];
        this.PartnerAgentsList.push(this.CurrentPartnerAgent);
      }
      this.parent.openSnackBar(response["Message"] || 'Agent saved', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to save agent', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Processing Settings
 */
AddUpdatePartnerProcessingSettings() {
  if (!this.PartnerDetails?.ProcessingSettings) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    ProcessingSettings: this.PartnerDetails.ProcessingSettings
  };

  this.parent.loading = true;
  this.service.AddUpdatePartnerProcessingSettings(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerDetails.ProcessingSettings = response["ProcessingSettings"];
      this.parent.openSnackBar(response["Message"] || 'Processing settings saved', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to save settings', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Contractual Conditions
 */
AddUpdatePartnerContractualConditions() {
  if (!this.PartnerDetails?.ContractualConditions) return;

  this.PartnerDetails.ContractualConditions.PartnerID = this.PartnerDetails.ID;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    ContractualConditions: this.PartnerDetails.ContractualConditions
  };

  this.parent.loading = true;
  this.service.AddUpdatePartnerContractualConditions(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      this.PartnerDetails.ContractualConditions = response["ContractualConditions"];
      this.parent.openSnackBar(response["Message"] || 'Contractual conditions saved', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to save conditions', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Save Contractual Conditions from Grid - uses current grid shipping/payment selections
 */
SavePartnerContractualConditionsFromGrid() {
  if (!this.parent.B2BPartner?.ID) {
    this.parent.openSnackBar('No partner selected', 'OK', 'alert-info-snackbar');
    return;
  }

  // Build contractual conditions from current grid selections
  const contractualConditions: any = {
    PartnerID: this.parent.B2BPartner.ID,
    SellPaymentTerms: this.selectedPaymentTerms || null,
    ShippingCompany: this.selectedShippingCompany || null,
    ShippingResponsability: this.selectedShippingResponsability || null,
    PaymentType: this.selectedPaymentType || null,
    ShippingServiceType: this.selectedShippingServiceType || null,
    ConsolidationType: this.selectedOrderConsolidationType || null,
    DeliveryPreference: this.selectedOrderDeliveryPreference?.ID > 0 ? this.selectedOrderDeliveryPreference : null
  };

  // Preserve existing values from partnerContractualConditions if loaded
  if (this.partnerContractualConditions) {
    contractualConditions.BuyPaymentTerms = this.partnerContractualConditions.BuyPaymentTerms || null;
    contractualConditions.HasContract = this.partnerContractualConditions.HasContract || false;
    contractualConditions.RequiresContract = this.partnerContractualConditions.RequiresContract || false;
    contractualConditions.SignedByOurCompany = this.partnerContractualConditions.SignedByOurCompany || false;
    contractualConditions.ContractSignedDate = this.partnerContractualConditions.ContractSignedDate || null;
    contractualConditions.ContractExpirationDate = this.partnerContractualConditions.ContractExpirationDate || null;
    contractualConditions.ContractPath = this.partnerContractualConditions.ContractPath || null;
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    ContractualConditions: contractualConditions
  };

  this.parent.loading = true;
  this.service.AddUpdatePartnerContractualConditions(request).subscribe((response: any) => {
    this.parent.loading = false;
    if (response["Status"] === 1) {
      // Update local cache
      this.partnerContractualConditions = response["ContractualConditions"];
      this.parent.openSnackBar(response["Message"] || 'Contractual conditions saved', 'OK', 'light-info-snackbar');
    } else {
      this.parent.openSnackBar(response["Message"] || 'Failed to save conditions', 'OK', 'alert-info-snackbar');
    }
  }, () => { this.parent.loading = false; });
}

// ✅ Accepted Email Domains Methods

/**
 * ✅ Load Partner Accepted Email Domains (called when clicking Accepted Email Domains tab)
 */
LoadPartnerAcceptedDomains() {
  if (!this.PartnerDetails?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.PartnerDetails.ID
  };

  this.parent.loading = true;

  this.service.GetPartnerAcceptedDomains(request).subscribe((response: any) => {
    this.parent.loading = false;

    if (response["Status"] === 1) {
      this.AcceptedDomains = JSON.parse(JSON.stringify(response["Domains"]));
    } else {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Add/Update Partner Accepted Domain
 */
AddUpdatePartnerAcceptedDomain() {
  // Validate format: must start with @ and follow @xxxxxx.yyy pattern
  const domainPattern = /^@[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;

  if (!this.NewAcceptedEmailDomain || !domainPattern.test(this.NewAcceptedEmailDomain.trim())) {
    this.InvalidAcceptedDomainFormat = true;
    return;
  }

  this.InvalidAcceptedDomainFormat = false;

  const domain = {
    DBID: 0,
    FKPartnerID: this.PartnerDetails.ID,
    EmailDomain: this.NewAcceptedEmailDomain.trim()
  };

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Domain: domain
  };

  this.parent.loading = true;

  this.service.AddUpdatePartnerAcceptedDomain(request).subscribe((response: any) => {
    this.parent.loading = false;

    if (response["Status"] === 1) {
      this.NewAcceptedEmailDomain = '';
      this.LoadPartnerAcceptedDomains();
    } else {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Prepare for deleting an accepted domain (show confirmation)
 */
PrepareAcceptedDomainDelete(domain: any) {
  this.DeletingAcceptedDomain = JSON.parse(JSON.stringify(domain));
  this.ShowDeleteAcceptedDomainQuestion = true;
}

/**
 * ✅ Delete Partner Accepted Domain
 */
DeletePartnerAcceptedDomain(domain: any) {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    DBID: domain.DBID
  };

  this.parent.loading = true;

  this.service.DeletePartnerAcceptedDomain(request).subscribe((response: any) => {
    this.parent.loading = false;

    if (response["Status"] === 1) {
      this.AcceptedDomains = this.AcceptedDomains.filter(x => x.DBID != domain.DBID);
      this.ShowDeleteAcceptedDomainQuestion = false;
    } else {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }
  }, () => { this.parent.loading = false; });
}

/**
 * ✅ Load Partner Master Data (Countries, Banks)
 */
LoadPartnerMasterData() {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage()
  };

  this.service.GetMasterData(request).subscribe((response: any) => {
    if (response["Status"] === 1) {
      this.PartnerCountries = response["Countries"] || [];
      this.PartnerBanks = response["Banks"] || [];
      console.log('✅ Partner master data loaded');
    }
  });
}

/**
 * Clear grid and reset to fresh 50 empty rows
 * Called when switching partners or clearing partner selection
 */
ClearGridAndResetToFresh() {
  try {
    // ✅ Clear cart from IndexedDB
    this.clearCartFromIDB();

    // ✅ Clear cart in parent
    this.parent.Cart = { Items: [], ShippingCost: { UIUnitPrice: null } };

    // ✅ Clear grid data
    this.CartItemRows = [];

    // ✅ Reset grid to 50 fresh empty rows
    if (this.gridApi) {
      const emptyRows = Array.from({ length: 50 }, (_, i) => ({
        GridIndex: i,
        Brand: null,
        PartNumber: null,
        Name: null,
        Qty: null,
        ListPrice: null,
        Discount: null,
        FinalPrice: null,
        Total: null,
        StandardDelivery: null,
        HSCode: null,
        COO: null,
        Weight: null,
        DatasheetLink: null
      }));

      this.gridApi.setRowData(emptyRows);
    }

    console.log('Grid cleared and reset to 50 fresh rows');
  } catch (error) {
    console.error('Error clearing grid:', error);
  }
}

/**
 * Parse number from any format (handles different cultures)
 * Examples:
 * - "1,029.99" (US format) → 1029.99
 * - "1.029,99" (EU format) → 1029.99
 * - "1 029,99" (French format) → 1029.99
 * - "1029.99" → 1029.99
 * - "1029,99" → 1029.99
 */
parseNumberFromAnyFormat(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Convert to string
  let str = String(value).trim();

  // Remove any whitespace
  str = str.replace(/\s+/g, '');

  // If empty after trimming, return null
  if (str === '') {
    return null;
  }

  // Count dots and commas
  const dotCount = (str.match(/\./g) || []).length;
  const commaCount = (str.match(/,/g) || []).length;

  // Determine format based on separators
  if (dotCount === 0 && commaCount === 0) {
    // No separators - just a number
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  } else if (dotCount === 1 && commaCount === 0) {
    // Only dot - assume decimal separator (US format)
    // Example: "1029.99" or "1.99"
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  } else if (dotCount === 0 && commaCount === 1) {
    // Only comma - could be thousands or decimal separator
    // Check position: if last 3 chars or less, it's decimal
    const commaPos = str.indexOf(',');
    const afterComma = str.substring(commaPos + 1);

    if (afterComma.length <= 2) {
      // Decimal separator (EU format)
      // Example: "1029,99" → 1029.99
      str = str.replace(',', '.');
    } else {
      // Thousands separator
      // Example: "1,029" → 1029
      str = str.replace(',', '');
    }

    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  } else if (dotCount > 1 && commaCount === 0) {
    // Multiple dots - assume thousands separators (EU format)
    // Example: "1.029.999" → 1029999
    str = str.replace(/\./g, '');
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  } else if (commaCount > 1 && dotCount === 0) {
    // Multiple commas - assume thousands separators (rare)
    // Example: "1,029,999" → 1029999
    str = str.replace(/,/g, '');
    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  } else if (dotCount >= 1 && commaCount >= 1) {
    // Both dots and commas - determine which is decimal separator
    const lastDotPos = str.lastIndexOf('.');
    const lastCommaPos = str.lastIndexOf(',');

    if (lastDotPos > lastCommaPos) {
      // Dot is decimal separator (US format)
      // Example: "1,029.99" → 1029.99
      str = str.replace(/,/g, ''); // Remove commas (thousands)
    } else {
      // Comma is decimal separator (EU format)
      // Example: "1.029,99" → 1029.99
      str = str.replace(/\./g, ''); // Remove dots (thousands)
      str = str.replace(',', '.'); // Replace comma with dot
    }

    const num = parseFloat(str);
    return isNaN(num) ? null : num;
  }

  return null;
}

/**
 * Recalculate discount percentage based on list price and new final price
 * Formula: discount% = ((listPrice - finalPrice) / listPrice) * 100
 */
recalculateDiscountFromFinalPrice(rowData: any) {
  try {
    const cartItem = rowData.CartItem;

    if (!cartItem || !cartItem.SelectedOffer) {
      return;
    }

    const listPrice = cartItem.SelectedOffer.ListPrice || 0;
    const finalPrice = rowData.price || 0;

    if (listPrice <= 0) {
      // Can't calculate discount without list price
      return;
    }

    // ✅ Calculate discount percentage
    const discountAmount = listPrice - finalPrice;
    const discountPercentage = (discountAmount / listPrice) * 100;

    // ✅ Update the discount in the cart item
    if (cartItem.SelectedOffer.Discount) {
      cartItem.SelectedOffer.Discount.Value = Math.max(0, Math.min(100, discountPercentage));
    } else {
      cartItem.SelectedOffer.Discount = {
        Value: Math.max(0, Math.min(100, discountPercentage))
      };
    }

    // ✅ Update the final price in the cart item
    cartItem.SelectedOffer.FinalPrice = finalPrice;

    // ✅ Update SellPrice to match the edited final price
    if (cartItem.SelectedOffer.SellPrice) {
      cartItem.SelectedOffer.SellPrice.UIUnitPrice = finalPrice;
    } else {
      cartItem.SelectedOffer.SellPrice = { UIUnitPrice: finalPrice };
    }

    // ✅ Update cartItem.Price.UIUnitPrice (used by purchase column cellStyle)
    if (cartItem.Price) {
      cartItem.Price.UIUnitPrice = finalPrice;
      cartItem.Price.UIQtyPrice = finalPrice * (rowData.quantity || cartItem.Qty || 0);
    } else {
      cartItem.Price = {
        UIUnitPrice: finalPrice,
        UIQtyPrice: finalPrice * (rowData.quantity || cartItem.Qty || 0)
      };
    }

    // ✅ Recalculate total
    const quantity = rowData.quantity || cartItem.Qty || 0;
    rowData.total = finalPrice * quantity;
    cartItem.Total = rowData.total;

    // ✅ Update the grid to show new discount
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }

    console.log(`✅ Recalculated discount: ${discountPercentage.toFixed(2)}% (List: ${listPrice}, Final: ${finalPrice})`);
  } catch (error) {
    console.error('Error recalculating discount from final price:', error);
  }
}

/**
 * Recalculate final price based on list price and new discount percentage
 * Formula: finalPrice = listPrice * (1 - discount% / 100)
 */
recalculateFinalPriceFromDiscount(rowData: any) {
  try {
    const cartItem = rowData.CartItem;

    if (!cartItem || !cartItem.SelectedOffer) {
      return;
    }

    const listPrice = cartItem.SelectedOffer.ListPrice || 0;
    const discountPercentage = cartItem?.Product?.Stats?.StandardListPrice?.Discount?.Value || 0;

    if (listPrice <= 0) {
      // Can't calculate final price without list price
      return;
    }

    // ✅ Calculate final price from discount
    const finalPrice = listPrice * (1 - discountPercentage / 100);

    // ✅ Update the final price in the cart item
    cartItem.SelectedOffer.FinalPrice = finalPrice;

    // ✅ Update SellPrice to match the calculated final price
    if (cartItem.SelectedOffer.SellPrice) {
      cartItem.SelectedOffer.SellPrice.UIUnitPrice = finalPrice;
    } else {
      cartItem.SelectedOffer.SellPrice = { UIUnitPrice: finalPrice };
    }

    // ✅ Update the discount in SelectedOffer.Discount
    if (cartItem.SelectedOffer.Discount) {
      cartItem.SelectedOffer.Discount.Value = discountPercentage;
    } else {
      cartItem.SelectedOffer.Discount = {
        Value: discountPercentage
      };
    }

    // ✅ Update cartItem.Price.UIUnitPrice (used by purchase column cellStyle)
    if (cartItem.Price) {
      cartItem.Price.UIUnitPrice = finalPrice;
      cartItem.Price.UIQtyPrice = finalPrice * (rowData.quantity || cartItem.Qty || 0);
    } else {
      cartItem.Price = {
        UIUnitPrice: finalPrice,
        UIQtyPrice: finalPrice * (rowData.quantity || cartItem.Qty || 0)
      };
    }

    // ✅ Update rowData.price (displayed in grid)
    rowData.price = finalPrice;

    // ✅ Recalculate total
    const quantity = rowData.quantity || cartItem.Qty || 0;
    rowData.total = finalPrice * quantity;
    cartItem.Total = rowData.total;

    // ✅ Update the grid to show new final price
    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }

    console.log(`✅ Recalculated final price: ${finalPrice.toFixed(2)} (List: ${listPrice}, Discount: ${discountPercentage.toFixed(2)}%)`);
  } catch (error) {
    console.error('Error recalculating final price from discount:', error);
  }
}

/**
 * Send edited CartItem to backend and update row with response
 * Called after admin edits price or discount
 * @param cartItem - The cart item to update
 * @param rowIndex - The row index in the grid
 * @param editType - 'price' or 'discount' to indicate which cell was edited
 */
AdminEditGridCell(cartItem: any, rowIndex: number, editType: 'price' | 'discount'): void {
  console.log('🔵 AdminEditGridCell called for row:', rowIndex, 'editType:', editType, 'CartItemStatus:', cartItem?.CartItemStatus);

  // ✅ Prevent duplicate calls while processing
  if (this.isProcessingGridEdit) {
    console.log('⚠️ Already processing a grid edit, skipping duplicate call');
    return;
  }

  this.isProcessingGridEdit = true;

  // ✅ Show loading spinner
  this.parent.loading = true;

  // ✅ Get the original CartItem from mirror list for comparison
  const originalCartItem = this.OriginalCartItems?.[rowIndex] ?? null;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItem: cartItem,
    OriginalCartItem: originalCartItem, // ✅ Send original for backend comparison
    Partner: this.parent.B2BPartner,
    AdminPrefferedCurrency: this.selectedCurrency,
    FinalPriceEdit: editType === 'price',
    DiscountEdit: editType === 'discount'
  };

  console.log(`📤 Sending ${editType} edit to backend...`, request);
  console.log(`📤 OriginalCartItem sent:`, originalCartItem);

  this.B2BService.AdminEditGridCell(request).subscribe(response => {
    // ✅ Hide loading spinner
    this.parent.loading = false;
    this.isProcessingGridEdit = false;

    if (response["Status"] !== 1) {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
      console.error('❌ AdminEditGridCell failed:', response["Message"]);
      return;
    }

    // ✅ Get updated CartItem from backend
    const updatedCartItem = response["CartItem"];

    if (!updatedCartItem) {
      console.error('❌ No CartItem in response');
      return;
    }

    console.log('✅ Received updated CartItem from backend:', updatedCartItem);

    // ✅ Stop editing to prevent conflicts with grid refresh
    this.gridApi.stopEditing();

    // ✅ Mark this row as being updated from backend (prevent re-triggering onCellValueChanged)
    this.rowsBeingUpdatedFromBackend.add(rowIndex);

    // ✅ Update the row data with backend response
    let updatedNode: any = null;
    let updatedIsRedRow: boolean = false;
    this.gridApi.forEachNode((node) => {
      if (node.rowIndex === rowIndex) {
        // ✅ Update the CartItem in the row data
        node.data.CartItem = JSON.parse(JSON.stringify(updatedCartItem));

        // ✅ CRITICAL: Set IsForcedPrice to TRUE after backend overwrites it
        // This ensures the flag is preserved when admin edits price/discount
        if (!node.data.CartItem.Price) {
          node.data.CartItem.Price = {};
        }
        node.data.CartItem.Price.IsForcedPrice = true;
        console.log('✅ Set CartItem.Price.IsForcedPrice = true after backend response');

        // ✅ Log SaveToDB value from backend
        console.log('✅ SaveToDB from backend:', node.data.CartItem?.CartItemStatus?.SaveToDB);

        // ✅ Update display fields from the updated CartItem
        const product = updatedCartItem?.Product ?? {};
        const stats = product?.Stats ?? {};
        const leadTime = updatedCartItem?.SelectedOffer?.LeadTime ?? {};
        const priceInfo = updatedCartItem?.Price ?? {};

        const sellPrice = updatedCartItem?.SelectedOffer?.SellPrice?.UIUnitPrice;
        node.data.price = sellPrice > 0 ? sellPrice : '';
        node.data.total = priceInfo?.UIQtyPrice ?? '';

        // ✅ Use UIOutput first (contains prolonged text from backend), fallback to simple format
        const eta = leadTime?.UIOutput ?? (leadTime?.Units > 0
          ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}`
          : '------');
        node.data.eta = eta;

        // ✅ CRITICAL: Preserve weight display field from CartItem
        node.data.weight = stats?.Weight != null
          ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}`
          : '';

        // ✅ Preserve other display fields
        node.data.hscode = stats?.HSCode ?? '';
        node.data.coo = stats?.COO?.ShortName ?? '';
        node.data.supplier = updatedCartItem?.SelectedOffer?.Configuration?.Partner?.Name ?? '';
        node.data.purchase = updatedCartItem?.SelectedOffer?.ListPrice ?? '';

        // ✅ CRITICAL: For RED ROWS (CartItemStatus.ID === 0), preserve existing name/partNumber
        // Backend returns empty Product.Name for red rows since they have no matched product
        const isRedRow = updatedCartItem?.CartItemStatus?.ID === 0;
        if (isRedRow) {
          // ✅ For red rows, use UIProductValue (user's typed text) instead of Product.Name
          node.data.name = updatedCartItem?.UIProductValue ?? node.data.name ?? '';
          node.data.partNumber = updatedCartItem?.Product?.PartNumber ?? node.data.partNumber ?? '';
        } else {
          node.data.name = product?.Name ?? '';
          node.data.partNumber = product?.PartNumber ?? '';
        }
        node.data.pn = node.data.partNumber;

        updatedNode = node;
        updatedIsRedRow = isRedRow; // ✅ Track if this was a red row edit
        console.log('✅ Row updated with backend data at index:', rowIndex);
        console.log('✅ Weight preserved:', node.data.weight);
      }
    });

    // ✅ Refresh ONLY the updated row (not the entire grid)
    // ✅ IMPORTANT: Refresh relevant columns including 'delete' to update floppy disk icon based on SaveToDB
    if (updatedNode) {
      this.gridApi.refreshCells({
        rowNodes: [updatedNode],
        columns: ['price', 'total', 'officialListPrice', 'officialDiscount', 'purchase', 'purchaseDiscount', 'delete', 'weight', 'hscode', 'coo', 'supplier', 'name', 'pn'], // ✅ Refresh all affected columns
        force: true
      });

      // ✅ CRITICAL: Directly update parent.Cart.Items[rowIndex] with the updated CartItem
      // This ensures SaveToDB flag is preserved in the cart object before saving to IndexedDB
      if (this.parent.Cart?.Items && rowIndex >= 0 && rowIndex < this.parent.Cart.Items.length) {
        this.parent.Cart.Items[rowIndex] = JSON.parse(JSON.stringify(updatedNode.data.CartItem));
        console.log(`✅ Updated parent.Cart.Items[${rowIndex}] with SaveToDB=${this.parent.Cart.Items[rowIndex]?.CartItemStatus?.SaveToDB}`);
      }
    }

    // ✅ Remove row from tracking set after a short delay (allow refresh to complete)
    setTimeout(() => {
      this.rowsBeingUpdatedFromBackend.delete(rowIndex);
      console.log('✅ Row removed from backend update tracking:', rowIndex);
    }, 100);

    // ✅ Save directly to IndexedDB (not via saveCartToLocalStorageOnly which re-reads from grid)
    this.saveCartToIDB();

    console.log('💾 Cart saved to IndexedDB after backend update');

    // ✅ FIX: Call UpdateCart to recalculate cart totals after ANY cell edit (price/discount)
    // UpdateCart gathers all items from grid into parent.Cart.Items and calls InitializeCart
    // This ensures parent.Cart.Total is recalculated and the UI totals are refreshed
    console.log('🔄 Calling UpdateCart to recalculate cart totals after AdminEditGridCell');
    this.UpdateCart(updatedIsRedRow);

    // ❌ REMOVED: RefreshOfferFromDatabase call - this was causing unnecessary API calls on every cell edit
    // ✅ The expensive GetIndustryProductB2BOfferWithMasterDataByOfferID API should ONLY be called when:
    //    1. Clicking the floppy disk icon in the grid (SaveAdminQuotationProduct method)
    //    2. Clicking OK/Save As New in the Red Row Edit Popup (SaveRedRowEditAndSaveToDatabase method)
    // ✅ Cell edits (price/discount) already receive updated CartItem from AdminEditGridCell backend call
    //    so there's no need to fetch from DB again

  }, error => {
    // ✅ Hide loading spinner on error
    this.parent.loading = false;
    this.isProcessingGridEdit = false;
    console.error('❌ AdminEditGridCell error:', error);
    this.parent.openSnackBar('Error updating grid cell', "OK", "alert-danger-snackbar");
  });
}

/**
 * ✅ BULK VERSION: Process multiple cart items in parallel and update all rows
 */
AdminEditGridCellBulk(items: { cartItem: any, rowIndex: number }[], editType: 'price' | 'discount'): void {
  if (!items || items.length === 0) return;

  console.log(`📦 AdminEditGridCellBulk: Processing ${items.length} items in parallel`);

  // ✅ Show loading spinner
  this.parent.loading = true;

  // ✅ Create all requests
  const requests = items.map(item => {
    const originalCartItem = this.OriginalCartItems?.[item.rowIndex] ?? null;
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      CartItem: item.cartItem,
      OriginalCartItem: originalCartItem,
      Partner: this.parent.B2BPartner,
      AdminPrefferedCurrency: this.selectedCurrency,
      FinalPriceEdit: editType === 'price',
      DiscountEdit: editType === 'discount'
    };
    return this.B2BService.AdminEditGridCell(request);
  });

  // ✅ Execute all requests in parallel using forkJoin
  forkJoin(requests).subscribe(responses => {
    this.parent.loading = false;

    const updatedNodes: any[] = [];

    responses.forEach((response: any, index: number) => {
      const item = items[index];
      const rowIndex = item.rowIndex;

      if (response["Status"] !== 1) {
        console.warn(`⚠️ AdminEditGridCellBulk failed for row ${rowIndex}:`, response["Message"]);
        return;
      }

      const updatedCartItem = response["CartItem"];
      if (!updatedCartItem) {
        console.warn(`⚠️ No CartItem in response for row ${rowIndex}`);
        return;
      }

      // ✅ Mark row as being updated from backend
      this.rowsBeingUpdatedFromBackend.add(rowIndex);

      // ✅ Update the row data
      this.gridApi.forEachNode((node) => {
        if (node.rowIndex === rowIndex) {
          node.data.CartItem = JSON.parse(JSON.stringify(updatedCartItem));

          if (!node.data.CartItem.Price) node.data.CartItem.Price = {};
          node.data.CartItem.Price.IsForcedPrice = true;

          // ✅ Update display fields
          const product = updatedCartItem?.Product ?? {};
          const stats = product?.Stats ?? {};
          const leadTime = updatedCartItem?.SelectedOffer?.LeadTime ?? {};
          const priceInfo = updatedCartItem?.Price ?? {};

          const sellPrice = updatedCartItem?.SelectedOffer?.SellPrice?.UIUnitPrice;
          node.data.price = sellPrice > 0 ? sellPrice : '';
          node.data.total = priceInfo?.UIQtyPrice ?? '';
          node.data.eta = leadTime?.UIOutput ?? (leadTime?.Units > 0 ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}` : '------');
          node.data.weight = stats?.Weight != null ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}` : '';
          node.data.hscode = stats?.HSCode ?? '';
          node.data.coo = stats?.COO?.ShortName ?? '';
          node.data.supplier = updatedCartItem?.SelectedOffer?.Configuration?.Partner?.Name ?? '';
          node.data.purchase = updatedCartItem?.SelectedOffer?.ListPrice ?? '';

          // ✅ CRITICAL: For RED ROWS (CartItemStatus.ID === 0), preserve existing name/partNumber
          const isRedRow = updatedCartItem?.CartItemStatus?.ID === 0;
          if (isRedRow) {
            node.data.name = updatedCartItem?.UIProductValue ?? node.data.name ?? '';
            node.data.partNumber = updatedCartItem?.Product?.PartNumber ?? node.data.partNumber ?? '';
          } else {
            node.data.name = product?.Name ?? '';
            node.data.partNumber = product?.PartNumber ?? '';
          }
          node.data.pn = node.data.partNumber;

          updatedNodes.push(node);

          // ✅ Update parent.Cart.Items
          if (this.parent.Cart?.Items && rowIndex >= 0 && rowIndex < this.parent.Cart.Items.length) {
            this.parent.Cart.Items[rowIndex] = JSON.parse(JSON.stringify(node.data.CartItem));
          }
        }
      });
    });

    // ✅ Stop editing and refresh all updated nodes
    this.gridApi.stopEditing();

    if (updatedNodes.length > 0) {
      this.gridApi.refreshCells({
        rowNodes: updatedNodes,
        columns: ['price', 'total', 'officialListPrice', 'officialDiscount', 'purchase', 'purchaseDiscount', 'delete', 'weight', 'hscode', 'coo', 'supplier'],
        force: true
      });
    }

    // ✅ Remove rows from tracking after delay
    setTimeout(() => {
      items.forEach(item => this.rowsBeingUpdatedFromBackend.delete(item.rowIndex));
    }, 100);

    // ✅ Save to IndexedDB
    this.saveCartToIDB();
    console.log(`✅ AdminEditGridCellBulk: ${updatedNodes.length}/${items.length} items updated successfully`);

    // ✅ FIX: Call UpdateCart to recalculate cart totals after ANY bulk cell edit
    // UpdateCart gathers all items from grid into parent.Cart.Items and calls InitializeCart
    // ✅ Pass isRedRowEdit if ANY item in the bulk is a red row
    const hasRedRowInBulk = items.some(item => item.cartItem?.CartItemStatus?.ID === 0);
    console.log('🔄 Calling UpdateCart to recalculate cart totals after AdminEditGridCellBulk');
    this.UpdateCart(hasRedRowInBulk);

  }, error => {
    this.parent.loading = false;
    console.error('❌ AdminEditGridCellBulk error:', error);
    this.parent.openSnackBar('Error updating grid cells', "OK", "alert-danger-snackbar");
  });
}

/**
 * Get currencies from backend and set default from AdminCart
 */
GetCurrencies() {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
  };

  this.B2BService.GetOrdersMasterData(request).subscribe(async response => {
    if (response["Status"] === 1) {
      this.currencies = JSON.parse(JSON.stringify(response["Currencies"]));
      this.filteredCurrencies = [...this.currencies];

      // ✅ Try to get AdminCart from IndexedDB
      const adminCart = await this.loadCartFromIDB();

      // ✅ Set default currency from AdminCart if available
      if (adminCart?.Currency?.ID) {
        this.selectedCurrency = this.currencies.find(c => c.ID === adminCart.Currency.ID);
      }

      // ✅ If no cart currency, use international currency as default
      if (!this.selectedCurrency) {
        this.selectedCurrency = this.currencies.find(c => c.IsInternationalCurrency === true);
      }

      // ✅ Update search term to show selected currency
      if (this.selectedCurrency) {
        this.currencySearchTerm = `${this.selectedCurrency.Symbol} - ${this.selectedCurrency.Name}`;

        // ✅ Update parent.Cart.Currency so GridTotals shows the currency
        if (!this.parent.Cart) {
          this.parent.Cart = { Items: [], ShippingCost: { UIUnitPrice: null } };
        }
        this.parent.Cart.Currency = this.selectedCurrency;
      }

      // ✅ Update column headers immediately after loading currencies
      this.updateColumnHeaders();

      console.log('✅ Currencies loaded. Selected:', this.selectedCurrency?.Symbol);
    }
  });
}

/**
 * ✅ NEW: Get Offer Master Data using Offers.BL (OrderConsolidationTypes, etc.)
 */
GetOfferMasterData() {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
  };

  this.managementService.GetOfferMasterData(request).subscribe(response => {
    if (response["Status"] === 1) {
      this.OfferMasterData = response;

      // ✅ Pre-select "Consolidated" (ID = -1) as default
      if (this.OfferMasterData["OrderConsolidationTypes"]) {
        const consolidated = this.OfferMasterData["OrderConsolidationTypes"].find((t: any) => t.ID === -1);
        if (consolidated) {
          this.parent.Offer.ConsolidationType = consolidated;
          this.selectedOrderConsolidationType = consolidated; // ✅ Also set local selection
        }
      }

      // ✅ Pre-select "PartialDelivery" (ID = -1) as default
      if (this.OfferMasterData["OrderDeliveryPreferences"]) {
        const partialDelivery = this.OfferMasterData["OrderDeliveryPreferences"].find((d: any) => d.ID === -1);
        if (partialDelivery) {
          this.parent.Cart.DeliveryPreference = partialDelivery;
          this.selectedOrderDeliveryPreference = partialDelivery; // ✅ Also set local selection
        }
      }

      console.log('✅ Offer Master Data loaded. OrderConsolidationTypes:', this.OfferMasterData["OrderConsolidationTypes"]);
      console.log('✅ Offer Master Data loaded. OrderDeliveryPreferences:', this.OfferMasterData["OrderDeliveryPreferences"]);
    }
  });
}

/**
 * ⚠️ ORIGINAL: Get Offer Request Master Data using OfferRequests.BL (kept for compatibility)
 */
GetOfferMasterDataRequest() {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
  };

  this.B2BService.GetOfferMasterData(request).subscribe(response => {
    if (response["Status"] === 1) {
      this.OfferRequestMasterData = response;

      // ✅ Pre-select "Consolidated" (ID = -1) as default
      if (this.OfferRequestMasterData["OrderConsolidationTypes"]) {
        const consolidated = this.OfferRequestMasterData["OrderConsolidationTypes"].find((t: any) => t.ID === -1);
        if (consolidated) {
          this.parent.OfferRequest.ConsolidationType = consolidated;
        }
      }

      // ✅ Pre-select "PartialDelivery" (ID = -1) as default
      if (this.OfferRequestMasterData["OrderDeliveryPreferences"]) {
        const partialDelivery = this.OfferRequestMasterData["OrderDeliveryPreferences"].find((d: any) => d.ID === -1);
        if (partialDelivery) {
          this.parent.Cart.DeliveryPreference = partialDelivery;
        }
      }

      console.log('✅ Offer Request Master Data loaded. OrderConsolidationTypes:', this.OfferRequestMasterData["OrderConsolidationTypes"]);
    }
  });
}

/**
 * ✅ NEW: Get LeadTimeTypes, WeightMeasurementTypes, ProductBoxStatuses, ProductConditionStatuses, ProductManufacturingStatuses, ProductVersionStatuses for Red Row popups
 */
GetLeadTimeTypes() {
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
  };

  this.managementService.GetIndustryProductsMasterData(request).subscribe(response => {
    if (response["Status"] === 1) {
      this.LeadTimeTypes = response["LeadTimeTypes"] || [];
      this.WeightMeasurementTypes = response["WeightMeasurementTypes"] || [];
      this.ProductBoxStatuses = response["ProductBoxStatuses"] || [];
      this.ProductConditionStatuses = response["ProductConditionStatuses"] || [];
      this.ProductManufacturingStatuses = response["ProductManufacturingStatuses"] || [];
      this.ProductVersionStatuses = response["ProductVersionStatuses"] || [];

      // ✅ Store in parent.masterData for future use
      if (!this.parent.masterData) {
        this.parent.masterData = {};
      }
      this.parent.masterData.WeightMeasurementTypes = this.WeightMeasurementTypes;
      this.parent.masterData.Countries = response["Countries"] || [];

      console.log('✅ LeadTimeTypes loaded:', this.LeadTimeTypes);
      console.log('✅ WeightMeasurementTypes loaded:', this.WeightMeasurementTypes);
      console.log('✅ ProductBoxStatuses loaded:', this.ProductBoxStatuses);
      console.log('✅ ProductConditionStatuses loaded:', this.ProductConditionStatuses);
      console.log('✅ ProductManufacturingStatuses loaded:', this.ProductManufacturingStatuses);
      console.log('✅ ProductVersionStatuses loaded:', this.ProductVersionStatuses);
      console.log('✅ Countries loaded:', this.parent.masterData.Countries?.length);
    }
  });
}

/**
 * ✅ NEW: Handle Lead Time Type selection change
 */
OnLeadTimeTypeChange() {
  if (!this.CurrentRedRowCartItem) return;

  // ✅ If units is 0, ignore type selection (will be cleared by OnLeadTimeUnitsChange)
  if (this.SelectedLeadTimeUnits === 0) {
    return;
  }

  // ✅ Ensure SelectedOffer exists
  if (!this.CurrentRedRowCartItem.SelectedOffer) {
    this.CurrentRedRowCartItem.SelectedOffer = {};
  }

  // ✅ Ensure LeadTime exists
  if (!this.CurrentRedRowCartItem.SelectedOffer.LeadTime) {
    this.CurrentRedRowCartItem.SelectedOffer.LeadTime = {};
  }

  // ✅ Store the selected type
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Type = this.SelectedLeadTimeType;
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Units = this.SelectedLeadTimeUnits;

  console.log('✅ Lead Time Type changed:', this.SelectedLeadTimeType);

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.parent.UpdateCart(true);
}

/**
 * ✅ NEW: Handle Lead Time Units selection change
 */
OnLeadTimeUnitsChange() {
  if (!this.CurrentRedRowCartItem) return;

  // ✅ If units is 0, clear everything
  if (this.SelectedLeadTimeUnits === 0) {
    this.SelectedLeadTimeType = null;

    // ✅ Set LeadTime to null
    if (this.CurrentRedRowCartItem.SelectedOffer) {
      this.CurrentRedRowCartItem.SelectedOffer.LeadTime = null;
    }

    console.log('✅ Lead Time cleared (units = 0)');

    // ✅ Save to localStorage (red row edit — precheck gate applies)
    this.parent.UpdateCart(true);
    return;
  }

  // ✅ Ensure SelectedOffer exists
  if (!this.CurrentRedRowCartItem.SelectedOffer) {
    this.CurrentRedRowCartItem.SelectedOffer = {};
  }

  // ✅ Ensure LeadTime exists
  if (!this.CurrentRedRowCartItem.SelectedOffer.LeadTime) {
    this.CurrentRedRowCartItem.SelectedOffer.LeadTime = {};
  }

  // ✅ Store the selected units
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Units = this.SelectedLeadTimeUnits;
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Type = this.SelectedLeadTimeType;

  console.log('✅ Lead Time Units changed:', this.SelectedLeadTimeUnits);

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.parent.UpdateCart(true);
}

/**
 * ✅ NEW: Save Red Row Lead Time and call backend
 */
SaveRedRowLeadTime() {
  if (!this.CurrentRedRowCartItem || this.CurrentRedRowIndex < 0) {
    console.log('❌ No red row cart item or row index to save');
    return;
  }

  console.log('✅ Saving Red Row Lead Time to backend via AdminEditGridCell');

  // ✅ Call AdminEditGridCell to save to backend
  this.AdminEditGridCell(this.CurrentRedRowCartItem, this.CurrentRedRowIndex, 'price');

  // ✅ Close the popup
  this.ShowRedRowETAPopup = false;

  // ✅ Clear current red row tracking
  this.CurrentRedRowCartItem = null;
  this.CurrentRedRowIndex = -1;

  // ✅ Clear smart input and inline suggestion
  this.LeadTimeSmartInput = '';
  this.LeadTimeInlineSuggestion = '';
  this.LeadTimeSuggestions = [];
}

/**
 * ✅ NEW: Handle smart input change for lead time autocomplete
 */
OnLeadTimeSmartInputChange(value: string) {
  if (!value || value.trim().length === 0) {
    this.LeadTimeSuggestions = [];
    this.LeadTimeInlineSuggestion = '';
    return;
  }

  const input = value.trim().toLowerCase();
  const suggestions: any[] = [];

  // ✅ Parse input: extract number and text
  const match = input.match(/^(\d+)\s*(.*)$/);

  if (match) {
    const units = parseInt(match[1], 10);
    const typeText = match[2].trim();

    // ✅ Filter LeadTimeTypes by matching the typeText
    const matchingTypes = this.LeadTimeTypes.filter(type =>
      type.Name.toLowerCase().startsWith(typeText) || typeText === ''
    );

    // ✅ Create suggestions
    matchingTypes.forEach(type => {
      suggestions.push({
        display: `${units} ${type.Name}`,
        units: units,
        type: type
      });
    });
  } else {
    // ✅ No number yet, just show all types as suggestions
    this.LeadTimeTypes.forEach(type => {
      suggestions.push({
        display: type.Name,
        units: null,
        type: type
      });
    });
  }

  this.LeadTimeSuggestions = suggestions;

  // ✅ Set inline suggestion (only the remaining part after what user typed)
  if (suggestions.length > 0) {
    const fullSuggestion = suggestions[0].display;
    const userInput = value.trim();

    // ✅ Show only the part that comes AFTER what the user typed
    if (fullSuggestion.toLowerCase().startsWith(userInput.toLowerCase())) {
      this.LeadTimeInlineSuggestion = userInput + fullSuggestion.substring(userInput.length);
    } else {
      this.LeadTimeInlineSuggestion = fullSuggestion;
    }
  } else {
    this.LeadTimeInlineSuggestion = '';
  }
}

/**
 * ✅ NEW: Handle TAB key to autocomplete first suggestion
 */
OnLeadTimeSmartInputTab(event: KeyboardEvent) {
  if (this.LeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.SelectLeadTimeSuggestion(this.LeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Handle ENTER key to autocomplete first suggestion
 */
OnLeadTimeSmartInputEnter(event: KeyboardEvent) {
  if (this.LeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.SelectLeadTimeSuggestion(this.LeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Handle Arrow Right key to autocomplete inline suggestion
 */
OnLeadTimeSmartInputArrowRight(event: KeyboardEvent) {
  if (this.LeadTimeInlineSuggestion && this.LeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.SelectLeadTimeSuggestion(this.LeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Select a suggestion and populate dropdowns
 */
SelectLeadTimeSuggestion(suggestion: any) {
  // ✅ Set the smart input to the selected suggestion
  this.LeadTimeSmartInput = suggestion.display;

  // ✅ Populate the dropdowns
  if (suggestion.units !== null) {
    this.SelectedLeadTimeUnits = suggestion.units;
  }
  this.SelectedLeadTimeType = suggestion.type;

  // ✅ Update the CartItem
  if (!this.CurrentRedRowCartItem.SelectedOffer) {
    this.CurrentRedRowCartItem.SelectedOffer = {};
  }
  if (!this.CurrentRedRowCartItem.SelectedOffer.LeadTime) {
    this.CurrentRedRowCartItem.SelectedOffer.LeadTime = {};
  }
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Type = this.SelectedLeadTimeType;
  this.CurrentRedRowCartItem.SelectedOffer.LeadTime.Units = this.SelectedLeadTimeUnits;

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.parent.UpdateCart(true);

  // ✅ Clear suggestions and inline suggestion
  this.LeadTimeSuggestions = [];
  this.LeadTimeInlineSuggestion = '';

  console.log('✅ Lead Time autocompleted:', suggestion.display);
}

/**
 * ✅ NEW: Clear lead time (Red X button)
 */
ClearLeadTime() {
  if (!this.CurrentRedRowCartItem) return;

  // ✅ Clear the smart input and inline suggestion
  this.LeadTimeSmartInput = '';
  this.LeadTimeInlineSuggestion = '';
  this.LeadTimeSuggestions = [];

  // ✅ Clear the dropdowns
  this.SelectedLeadTimeType = null;
  this.SelectedLeadTimeUnits = 0;

  // ✅ Set LeadTime to null in CartItem
  if (this.CurrentRedRowCartItem.SelectedOffer) {
    this.CurrentRedRowCartItem.SelectedOffer.LeadTime = null;
  }

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.parent.UpdateCart(true);

  console.log('✅ Lead Time cleared');
}

// ✅ Revert row to original values
ConfirmRevertRow() {
  console.log('🔄 ConfirmRevertRow called for row index:', this.RevertRowIndex);
  console.log('🔄 OriginalCartItems length:', this.OriginalCartItems?.length);

  if (this.RevertRowIndex < 0 || !this.OriginalCartItems || this.OriginalCartItems.length === 0) {
    console.log('❌ No original cart items to revert to');
    this.ShowRevertConfirmationPopup = false;
    return;
  }

  const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.RevertRowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found at index:', this.RevertRowIndex);
    this.ShowRevertConfirmationPopup = false;
    return;
  }

  console.log('🔄 Current CartItem:', rowNode.data?.CartItem);

  // ✅ Get the original CartItem from mirror list
  const originalCartItem = this.OriginalCartItems[this.RevertRowIndex];
  if (!originalCartItem) {
    console.log('❌ Original CartItem not found at index', this.RevertRowIndex);
    console.log('❌ Available indices:', Object.keys(this.OriginalCartItems));
    this.ShowRevertConfirmationPopup = false;
    return;
  }

  console.log('🔄 Original CartItem:', originalCartItem);

  // ✅ Deep copy the original CartItem back to the grid row
  const restoredCartItem = JSON.parse(JSON.stringify(originalCartItem));

  // ✅ Remove AdminPriceEdit flag
  delete restoredCartItem.AdminPriceEdit;

  // ✅ Restore original purchase price from OriginalCartItems (mirror list)
  const originalPurchasePrice = originalCartItem?.SelectedOffer?.ListPrice;
  const originalProductListPrice = originalCartItem?.Product?.ListPriceOffers?.[0]?.ListPrice;

  if (originalPurchasePrice !== undefined) {
    if (restoredCartItem.SelectedOffer) {
      restoredCartItem.SelectedOffer.ListPrice = originalPurchasePrice;
    }
    console.log('🔄 Restored SelectedOffer.ListPrice to:', originalPurchasePrice);
  }

  if (originalProductListPrice !== undefined) {
    if (restoredCartItem.Product?.ListPriceOffers?.[0]) {
      restoredCartItem.Product.ListPriceOffers[0].ListPrice = originalProductListPrice;
    }
    console.log('🔄 Restored Product.ListPriceOffers[0].ListPrice to:', originalProductListPrice);
  }

  // ✅ Remove temporary storage
  delete restoredCartItem.OriginalPurchasePrice;

  console.log('🔄 Restored CartItem (without AdminPriceEdit):', restoredCartItem);

  // ✅ Update the grid row data
  const product = restoredCartItem?.Product ?? {};
  const stats = product?.Stats ?? {};
  const price = restoredCartItem?.SelectedOffer?.SellPrice ?? restoredCartItem?.Price ?? {};
  const leadTime = restoredCartItem?.SelectedOffer?.LeadTime;

  // ✅ Use UIOutput first (contains prolonged text from backend), fallback to simple format
  const eta = leadTime?.UIOutput ?? (leadTime?.Units && leadTime.Units > 0
    ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}`
    : '------');

  const newRowData = {
    index: this.RevertRowIndex + 1,
    brand: product?.Brand?.Name ?? '',
    partNumber: product?.PartNumber ?? '',
    name: restoredCartItem?.UIProductValue ?? '',
    quantity: restoredCartItem?.Quantity ?? null,
    price: price?.UIUnitPrice ?? '',
    total: price?.UIQtyPrice ?? '',
    eta,
    hscode: stats?.HSCode ?? '',
    coo: stats?.COO?.ShortName ?? '',
    weight: stats?.Weight != null
      ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}` : '',
    CartItem: restoredCartItem
  };

  console.log('🔄 New row data:', newRowData);

  rowNode.setData(newRowData);

  // ✅ Refresh the entire row to update all cells
  if (this.gridApi) {
    setTimeout(() => {
      this.gridApi.refreshCells({
        rowNodes: [rowNode],
        force: true
      });
      console.log('✅ Grid cells refreshed');
    }, 0);
  }

  // ✅ Update parent.Cart and save to localStorage (red row edit — precheck gate applies)
  this.UpdateCart(true);

  console.log(`✅ Reverted row ${this.RevertRowIndex} to original values`);

  // ✅ Close popup
  this.ShowRevertConfirmationPopup = false;
  this.RevertRowIndex = -1;
}

CancelRevertRow() {
  this.ShowRevertConfirmationPopup = false;
  this.RevertRowIndex = -1;
}

// ✅ Toggle SaveToDB for red rows
ToggleRedRowSaveToDB(rowIndex: number) {
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found at index:', rowIndex);
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  if (!cartItem || cartItem?.CartItemStatus?.ID !== 0) {
    console.log('❌ Not a red row or CartItem not found');
    return;
  }

  // ✅ Only toggle if SaveToDB is NOT null
  if (cartItem.CartItemStatus.SaveToDB != null) {
    cartItem.CartItemStatus.SaveToDB = !cartItem.CartItemStatus.SaveToDB;
    console.log(`✅ Toggled SaveToDB for row ${rowIndex}: ${!cartItem.CartItemStatus.SaveToDB} → ${cartItem.CartItemStatus.SaveToDB}`);
  } else {
    console.log('⚠️ SaveToDB is null - cannot toggle (no modifications detected)');
    return;
  }

  // ✅ Refresh the delete column to update icon
  if (this.gridApi) {
    this.gridApi.refreshCells({
      rowNodes: [rowNode],
      columns: ['delete'],
      force: true
    });
  }

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.UpdateCart(true);
}



// ✅ Partner search for red rows
OpenPartnerSearchPopup(rowIndex: number) {
  console.log('🔍 OpenPartnerSearchPopup called with rowIndex:', rowIndex);
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found at index:', rowIndex);
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  console.log('🔍 CartItem:', cartItem);
  console.log('🔍 CartItemStatus.ID:', cartItem?.CartItemStatus?.ID);

  if (!cartItem || cartItem?.CartItemStatus?.ID !== 0) {
    console.log('❌ Not a red row or CartItem not found');
    return;
  }

  this.PartnerSearchRowIndex = rowIndex;

  // ✅ If partner already selected, show name instead of search term
  const existingPartner = cartItem?.SelectedOffer?.Partner;
  if (existingPartner && existingPartner.Name) {
    this.PartnerSearchTerm = existingPartner.Name;
    console.log('🔍 Existing partner found:', existingPartner.Name);
  } else {
    this.PartnerSearchTerm = '';
    console.log('🔍 No existing partner, starting with empty search');
  }

  this.PartnerSearchResults = [];
  this.ShowPartnerSearchPopup = true;

  // ✅ Load initial partners (like onPartnerSearchFocus does)
  console.log('🔍 Loading initial partners...');
  this.SearchPartners();

  console.log(`✅ Opened partner search popup for row ${rowIndex}, ShowPartnerSearchPopup:`, this.ShowPartnerSearchPopup);
}

SearchPartners() {
  console.log('🔍 SearchPartners called, search term:', this.PartnerSearchTerm);

  // ✅ Allow empty search term for initial load (like onPartnerSearchFocus)
  // Only require 2+ characters if user has started typing
  if (this.PartnerSearchTerm && this.PartnerSearchTerm.length > 0 && this.PartnerSearchTerm.length < 2) {
    console.log('🔍 Search term too short (1 character), clearing results');
    this.PartnerSearchResults = [];
    return;
  }

  const request = {
    SearchTerm: this.PartnerSearchTerm || '', // ✅ Allow empty string for initial load
    CountryID: null,
    PageIndex: 1,
    PageSize: 10, // ✅ Get 10 partners initially (as requested)
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CompanyID: null
  };

  console.log('🔍 Calling GetPartnersFiltered with request:', request);
  this.LoadingPartners = true;

  this.service.GetPartnersFiltered(request).subscribe(response => {
    console.log('🔍 GetPartnersFiltered response:', response);
    if (response["Status"] == 1) {
      this.PartnerSearchResults = response["Partners"];
      console.log(`✅ Found ${this.PartnerSearchResults.length} partners`);
    } else {
      console.log('❌ Response status not 1:', response["Status"], response["Message"]);
      this.PartnerSearchResults = [];
    }
    this.LoadingPartners = false;
  });
}

SelectPartner(partner: any) {
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.PartnerSearchRowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found');
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  if (!cartItem) {
    console.log('❌ CartItem not found');
    return;
  }

  // ✅ Ensure SelectedOffer exists
  if (!cartItem.SelectedOffer) {
    cartItem.SelectedOffer = {};
  }

  // ✅ Store partner in CartItem.SelectedOffer.Partner
  cartItem.SelectedOffer.Partner = JSON.parse(JSON.stringify(partner));

  console.log(`✅ Selected partner for row ${this.PartnerSearchRowIndex}:`, partner.Name);

  // ✅ Refresh the supplier column to show partner name
  if (this.gridApi) {
    this.gridApi.refreshCells({
      rowNodes: [rowNode],
      columns: ['supplier'],
      force: true
    });
  }

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.UpdateCart(true);

  // ✅ Close popup
  this.ClosePartnerSearchPopup();

  // ✅ Ensure SellPrice and Currency exist for AdminEditGridCell
  if (!cartItem.SelectedOffer.SellPrice) {
    cartItem.SelectedOffer.SellPrice = {};
  }
  if (!cartItem.SelectedOffer.SellPrice.Currency) {
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ Call AdminEditGridCell to update SaveToDB flag
  this.AdminEditGridCell(cartItem, this.PartnerSearchRowIndex, 'price');
}

ClosePartnerSearchPopup() {
  this.ShowPartnerSearchPopup = false;
  this.PartnerSearchRowIndex = -1;
  this.PartnerSearchTerm = '';
  this.PartnerSearchResults = [];
}

// ✅ Clear supplier from red row
ClearSupplier(rowIndex: number) {
  console.log('🗑️ ClearSupplier called for row:', rowIndex);
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found');
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  if (!cartItem || cartItem?.CartItemStatus?.ID !== 0) {
    console.log('❌ Not a red row or CartItem not found');
    return;
  }

  // ✅ Clear the partner
  if (cartItem.SelectedOffer) {
    cartItem.SelectedOffer.Partner = null;
    console.log('✅ Partner cleared from CartItem.SelectedOffer');
  }

  // ✅ Refresh the supplier column to update the button
  this.gridApi.refreshCells({
    rowNodes: [rowNode],
    columns: ['supplier'],
    force: true
  });

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.UpdateCart(true);

  console.log('✅ Supplier cleared and grid updated');

  // ✅ Ensure SellPrice and Currency exist for AdminEditGridCell
  if (!cartItem.SelectedOffer) {
    cartItem.SelectedOffer = {};
  }
  if (!cartItem.SelectedOffer.SellPrice) {
    cartItem.SelectedOffer.SellPrice = {};
  }
  if (!cartItem.SelectedOffer.SellPrice.Currency) {
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ Call AdminEditGridCell to update SaveToDB flag
  this.AdminEditGridCell(cartItem, rowIndex, 'price');
}

// ✅ Open COO search popup for red row and valid row
OpenCOOSearchPopup(rowIndex: number) {
  console.log('🌍 OpenCOOSearchPopup called for row:', rowIndex);
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found');
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  const statusId = cartItem?.CartItemStatus?.ID;

  // ✅ Allow both red rows (ID === 0) and valid rows (ID === 1)
  if (!cartItem || (statusId !== 0 && statusId !== 1)) {
    console.log('❌ Not a red row or valid row, or CartItem not found');
    return;
  }

  this.COOSearchRowIndex = rowIndex;

  // ✅ If COO already selected, show country name instead of search term
  const existingCOO = cartItem?.Product?.Stats?.COO;
  if (existingCOO && existingCOO.ShortName) {
    this.COOSearchTerm = existingCOO.ShortName;
  } else {
    this.COOSearchTerm = '';
  }

  this.COOSearchResults = [];
  this.ShowCOOSearchPopup = true;

  // ✅ Load initial countries (show all countries initially)
  this.SearchCountries();

  console.log('✅ COO search popup opened');
}

// ✅ Search countries from parent.masterData.Countries
SearchCountries() {
  console.log('🔍 SearchCountries called with term:', this.COOSearchTerm);

  // ✅ Filter countries from parent.masterData.Countries
  if (!this.parent.masterData || !this.parent.masterData.Countries) {
    console.log('❌ Countries not available in parent.masterData');
    this.COOSearchResults = [];
    return;
  }

  const searchTerm = (this.COOSearchTerm || '').toLowerCase().trim();

  if (searchTerm.length === 0) {
    // ✅ Show all countries if search term is empty
    this.COOSearchResults = this.parent.masterData.Countries.slice(0, 20); // Limit to 20 for performance
  } else {
    // ✅ Filter countries by Name (e.g., "germany" finds "DE")
    this.COOSearchResults = this.parent.masterData.Countries.filter((c: any) =>
      c.Name && c.Name.toLowerCase().includes(searchTerm)
    ).slice(0, 20); // Limit to 20 results
  }

  console.log('✅ Found', this.COOSearchResults.length, 'countries');
}

// ✅ Select country for red row and valid row
SelectCountry(country: any) {
  console.log('✅ SelectCountry called:', country);
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.COOSearchRowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found');
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  const statusId = cartItem?.CartItemStatus?.ID;

  // ✅ Allow both red rows (ID === 0) and valid rows (ID === 1)
  if (!cartItem || (statusId !== 0 && statusId !== 1)) {
    console.log('❌ Not a red row or valid row, or CartItem not found');
    return;
  }

  // ✅ Ensure Product.Stats exists
  if (!cartItem.Product) {
    cartItem.Product = {};
  }
  if (!cartItem.Product.Stats) {
    cartItem.Product.Stats = {};
  }

  // ✅ Store the country in COO
  cartItem.Product.Stats.COO = country;
  console.log('✅ COO set to:', country.ShortName);

  // ✅ Update the row data 'coo' field so the cell displays the ShortName immediately
  rowNode.data.coo = country.ShortName;
  console.log('✅ Row data.coo updated to:', country.ShortName);

  // ✅ Close the popup
  this.ShowCOOSearchPopup = false;
  this.COOSearchTerm = '';
  this.COOSearchResults = [];

  // ✅ Refresh the COO column to update the button and display
  this.gridApi.refreshCells({
    rowNodes: [rowNode],
    columns: ['coo'],
    force: true
  });

  // ✅ Save to localStorage (red row edit — precheck gate applies)
  this.UpdateCart(true);

  console.log('✅ COO selected and grid updated');

  // ✅ Ensure SelectedOffer, SellPrice and Currency exist for AdminEditGridCell
  if (!cartItem.SelectedOffer) {
    cartItem.SelectedOffer = {};
  }
  if (!cartItem.SelectedOffer.SellPrice) {
    cartItem.SelectedOffer.SellPrice = {};
  }
  if (!cartItem.SelectedOffer.SellPrice.Currency) {
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ Call AdminEditGridCell to update SaveToDB flag
  this.AdminEditGridCell(cartItem, this.COOSearchRowIndex, 'price');
}

// ✅ Select purchase currency for a row (or all selected rows)
selectPurchaseCurrency(currency: any) {
  console.log('💱 selectPurchaseCurrency called:', currency);

  // ✅ Close the popup first
  this.showPurchaseCurrencyDropdown = false;

  // ✅ Check for multi-select bulk update
  const selectedItems = this.getSelectedCartItems();
  const isBulkUpdate = selectedItems.length > 1;

  // ✅ Helper function to apply currency to a single item
  const applyCurrencyToItem = (itemCartItem: any, itemNode: any): boolean => {
    if (!itemCartItem) return false;

    // ✅ Ensure SelectedOffer exists
    if (!itemCartItem.SelectedOffer) {
      itemCartItem.SelectedOffer = {};
    }

    // ✅ Set the PurchaseCurrency on SelectedOffer
    itemCartItem.SelectedOffer.PurchaseCurrency = currency;

    // ✅ Also set on Product.ListPriceOffers[0] if exists
    if (itemCartItem.Product?.ListPriceOffers?.length > 0) {
      itemCartItem.Product.ListPriceOffers[0].PurchaseCurrency = currency;
    }

    // ✅ Update display field
    if (itemNode?.data) {
      itemNode.data.purchaseCurrency = currency?.Symbol || currency?.Name || null;
    }

    // ✅ Update parent.Cart.Items
    const cartIndex = this.parent.Cart.Items.findIndex((ci: any) => ci.UIID === itemCartItem.UIID);
    if (cartIndex >= 0) {
      this.parent.Cart.Items[cartIndex] = itemCartItem;
    }

    return true;
  };

  if (isBulkUpdate) {
    // ✅ BULK UPDATE: Apply currency to all selected rows
    console.log(`📦 BULK UPDATE: Applying currency ${currency.Symbol} to ${selectedItems.length} selected items`);

    const updatedNodes: any[] = [];
    const bulkItems: { cartItem: any, rowIndex: number }[] = [];

    selectedItems.forEach((item: any) => {
      const itemCartItem = item.data?.CartItem;
      const itemNode = item.node;

      if (applyCurrencyToItem(itemCartItem, itemNode)) {
        updatedNodes.push(itemNode);
        bulkItems.push({ cartItem: itemCartItem, rowIndex: item.rowIndex });
      }
    });

    // ✅ Refresh all updated rows
    if (updatedNodes.length > 0) {
      this.gridApi.refreshCells({
        rowNodes: updatedNodes,
        columns: ['purchaseCurrency'],
        force: true
      });
    }

    // ✅ Save to localStorage
    this.saveCartToLocalStorageOnly();

    // ✅ Call bulk method to update SaveToDB flag for all items
    if (bulkItems.length > 0) {
      this.AdminEditGridCellBulk(bulkItems, 'price');
    }

    console.log(`💱 BULK UPDATE: ${updatedNodes.length} items updated with currency ${currency.Symbol}`);
  } else {
    // ✅ Single item update
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.purchaseCurrencyRowIndex);
    if (!rowNode) {
      console.log('❌ Row node not found');
      return;
    }

    const cartItem = rowNode.data?.CartItem;
    if (!cartItem) {
      console.log('❌ CartItem not found');
      return;
    }

    // ✅ Check if PurchaseCurrency actually changed (avoid unnecessary API calls)
    const currentPurchaseCurrencyId = cartItem.SelectedOffer?.PurchaseCurrency?.ID;
    if (currentPurchaseCurrencyId === currency.ID) {
      console.log('💱 Same purchase currency selected, no change needed');
      return;
    }

    applyCurrencyToItem(cartItem, rowNode);

    console.log('💱 PurchaseCurrency changed from', currentPurchaseCurrencyId, 'to:', currency.ID, currency.Symbol);

    // ✅ Refresh the currency column to update the display
    this.gridApi.refreshCells({
      rowNodes: [rowNode],
      columns: ['purchaseCurrency'],
      force: true
    });

    // ✅ Save to localStorage
    this.saveCartToLocalStorageOnly();

    // ✅ Call AdminEditGridCell to update SaveToDB flag (show save button)
    this.AdminEditGridCell(cartItem, this.purchaseCurrencyRowIndex, 'price');

    console.log('💱 PurchaseCurrency selected, grid updated, and SaveToDB triggered');
  }
}

// ✅ Open Weight popup for red row and valid row
OpenWeightPopup(rowIndex: number) {
  console.log('⚖️ OpenWeightPopup called for row:', rowIndex);
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
  if (!rowNode) {
    console.log('❌ Row node not found');
    return;
  }

  const cartItem = rowNode.data?.CartItem;
  const statusId = cartItem?.CartItemStatus?.ID;

  // ✅ Allow both red rows (ID === 0) and valid rows (ID === 1)
  if (!cartItem || (statusId !== 0 && statusId !== 1)) {
    console.log('❌ Not a red row or valid row, or CartItem not found');
    return;
  }

  this.CurrentWeightRowCartItem = cartItem;
  this.CurrentWeightRowIndex = rowIndex;

  // ✅ Load WeightMeasurementTypes - try parent.masterData first, then API call
  if (this.parent.masterData && this.parent.masterData.WeightMeasurementTypes && this.parent.masterData.WeightMeasurementTypes.length > 0) {
    this.WeightMeasurementTypes = this.parent.masterData.WeightMeasurementTypes;
    console.log('✅ WeightMeasurementTypes loaded from parent.masterData:', this.WeightMeasurementTypes);
    this.OpenWeightPopupAfterDataLoaded(cartItem);
  } else {
    // ✅ Load from API if not in parent.masterData
    console.log('⚠️ WeightMeasurementTypes not in parent.masterData, loading from API...');
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
    };

    this.managementService.GetIndustryProductsMasterData(request).subscribe(response => {
      if (response["Status"] === 1) {
        this.WeightMeasurementTypes = response["WeightMeasurementTypes"] || [];
        console.log('✅ WeightMeasurementTypes loaded from API:', this.WeightMeasurementTypes);

        // ✅ Store in parent.masterData for future use
        if (!this.parent.masterData) {
          this.parent.masterData = {};
        }
        this.parent.masterData.WeightMeasurementTypes = this.WeightMeasurementTypes;

        this.OpenWeightPopupAfterDataLoaded(cartItem);
      } else {
        console.log('❌ Failed to load WeightMeasurementTypes from API');
        this.WeightMeasurementTypes = [];
      }
    });
  }
}

// ✅ Helper method to open weight popup after data is loaded
OpenWeightPopupAfterDataLoaded(cartItem: any) {
  // ✅ Pre-fill existing weight values if they exist
  const existingWeight = cartItem?.Product?.Stats?.Weight;
  const existingWeightType = cartItem?.Product?.Stats?.WeightMeasurementType;

  if (existingWeightType && existingWeightType.ID) {
    // Find the matching type from the list
    this.SelectedWeightType = this.WeightMeasurementTypes.find(t => t.ID === existingWeightType.ID) || null;
    console.log('✅ Existing WeightType found:', this.SelectedWeightType);
  } else {
    this.SelectedWeightType = null;
    console.log('✅ No existing WeightType - dropdown empty');
  }

  if (existingWeight != null && existingWeight >= 0 && existingWeight <= 100) {
    this.SelectedWeightUnits = existingWeight;
    console.log('✅ Existing Weight found:', this.SelectedWeightUnits);
  } else {
    this.SelectedWeightUnits = 0;
    console.log('✅ No existing Weight - units set to 0');
  }

  this.ShowWeightPopup = true;
  this.ShowETAAlternatives = false;
  this.ShowSearchResultsAlternatives = false;
  this.ShowFallbackSelection = false;
  this.ShowBrandCollateral = false;
  this.ShowRedRowETAPopup = false;

  console.log('✅ Weight popup opened');
}

// ✅ Save weight configuration for red row
SaveWeight() {
  console.log('💾 SaveWeight called');
  console.log('💾 SelectedWeightType:', this.SelectedWeightType);
  console.log('💾 SelectedWeightUnits:', this.SelectedWeightUnits);

  if (!this.CurrentWeightRowCartItem) {
    console.log('❌ No CartItem to update');
    return;
  }

  // ✅ If units is 0, set weight to null
  if (this.SelectedWeightUnits === 0) {
    console.log('⚠️ Weight units is 0, setting weight to null');
    if (this.CurrentWeightRowCartItem.Product && this.CurrentWeightRowCartItem.Product.Stats) {
      this.CurrentWeightRowCartItem.Product.Stats.Weight = null;
      this.CurrentWeightRowCartItem.Product.Stats.WeightMeasurementType = null;
    }
  } else {
    // ✅ Ensure Product.Stats exists
    if (!this.CurrentWeightRowCartItem.Product) {
      this.CurrentWeightRowCartItem.Product = {};
    }
    if (!this.CurrentWeightRowCartItem.Product.Stats) {
      this.CurrentWeightRowCartItem.Product.Stats = {};
    }

    // ✅ Store the weight and type
    this.CurrentWeightRowCartItem.Product.Stats.Weight = this.SelectedWeightUnits;
    this.CurrentWeightRowCartItem.Product.Stats.WeightMeasurementType = this.SelectedWeightType;
    console.log('✅ Weight set to:', this.SelectedWeightUnits, this.SelectedWeightType?.Name);
  }

  // ✅ Update the row data 'weight' field so the cell displays immediately
  const rowNode = this.gridApi?.getDisplayedRowAtIndex(this.CurrentWeightRowIndex);
  if (rowNode) {
    if (this.SelectedWeightUnits === 0 || !this.SelectedWeightType) {
      rowNode.data.weight = '';
    } else {
      rowNode.data.weight = `${this.SelectedWeightUnits} ${this.SelectedWeightType.Name}`;
    }
    console.log('✅ Row data.weight updated to:', rowNode.data.weight);

    // ✅ Refresh the weight cell to show the new value immediately
    this.gridApi.refreshCells({
      rowNodes: [rowNode],
      columns: ['weight'],
      force: true
    });
    console.log('✅ Weight cell refreshed');
  }

  // ✅ Call AdminEditGridCell to save to backend
  this.AdminEditGridCell(this.CurrentWeightRowCartItem, this.CurrentWeightRowIndex, 'price');

  // ✅ Close the popup
  this.ShowWeightPopup = false;

  // ✅ Clear current weight row tracking
  this.CurrentWeightRowCartItem = null;
  this.CurrentWeightRowIndex = -1;

  console.log('✅ Weight saved and popup closed');
}

// ========================================
// ✅ RED ROW EDIT POPUP - Comprehensive Edit Dialog
// ========================================

/**
 * Handle double-click on grid row - open Red Row Edit Popup for RED ROWS only
 */
/**
 * Open the comprehensive Red Row Edit Popup
 */
OpenRedRowEditPopup(cartItem: any, rowIndex: number): void {
  this.CurrentRedRowEditCartItem = cartItem;
  this.CurrentRedRowEditRowIndex = rowIndex;

  // ✅ Create deep clone of CartItem - all HTML controls bind directly to this clone
  this.RedRowEditCartItemClone = JSON.parse(JSON.stringify(cartItem));

  // ✅ Ensure all nested objects exist in the clone for direct property bindings
  if (!this.RedRowEditCartItemClone.Product) this.RedRowEditCartItemClone.Product = {};
  if (!this.RedRowEditCartItemClone.Product.Stats) this.RedRowEditCartItemClone.Product.Stats = {};
  if (!this.RedRowEditCartItemClone.Product.Brand) this.RedRowEditCartItemClone.Product.Brand = {};

  // ✅ FIX: Copy UIProductValue to Product.Name if Product.Name is empty
  // This ensures the popup shows the value typed by the user in the grid Name column
  if (!this.RedRowEditCartItemClone.Product.Name && this.RedRowEditCartItemClone.UIProductValue) {
    this.RedRowEditCartItemClone.Product.Name = this.RedRowEditCartItemClone.UIProductValue;
    console.log('✅ [POPUP] Copied UIProductValue to Product.Name:', this.RedRowEditCartItemClone.UIProductValue);
  }
  if (!this.RedRowEditCartItemClone.Product.Stats.COO) this.RedRowEditCartItemClone.Product.Stats.COO = {};
  // ✅ Weight: Handle legacy primitive number or new object format
  const existingWeight = this.RedRowEditCartItemClone.Product.Stats.Weight;
  const existingWeightType = this.RedRowEditCartItemClone.Product.Stats.WeightMeasurementType;
  if (typeof existingWeight === 'number') {
    // Convert primitive to object format
    const weightTypeName = existingWeightType?.Name || 'kg';
    this.RedRowEditCartItemClone.Product.Stats.Weight = {
      Units: existingWeight,
      Type: existingWeightType || { ID: 1, Name: 'kg' },
      UIOutput: `${existingWeight} ${weightTypeName}`
    };
  } else if (!existingWeight || typeof existingWeight !== 'object') {
    this.RedRowEditCartItemClone.Product.Stats.Weight = { Units: null, Type: null, UIOutput: '' };
  } else if (!existingWeight.UIOutput && existingWeight.Units != null) {
    // Object exists but no UIOutput - build it
    const weightTypeName = existingWeight.Type?.Name || existingWeightType?.Name || 'kg';
    existingWeight.UIOutput = `${existingWeight.Units} ${weightTypeName}`;
  }
  if (!this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime) this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime = {};
  if (!this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime) this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime = {};
  if (!this.RedRowEditCartItemClone.SelectedOffer) this.RedRowEditCartItemClone.SelectedOffer = {};
  if (!this.RedRowEditCartItemClone.SelectedOffer.Partner) this.RedRowEditCartItemClone.SelectedOffer.Partner = {};
  if (!this.RedRowEditCartItemClone.SelectedOffer.SellPrice) this.RedRowEditCartItemClone.SelectedOffer.SellPrice = {};
  if (!this.RedRowEditCartItemClone.SelectedOffer.LeadTime) this.RedRowEditCartItemClone.SelectedOffer.LeadTime = {};
  if (!this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency) this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency = this.selectedCurrency || {};

  // ✅ Clear suggestions
  this.RedRowEditLeadTimeSuggestions = [];
  this.RedRowEditLeadTimeInlineSuggestion = '';
  this.RedRowEditBrandSearchResults = [];
  this.RedRowEditPartnerSearchResults = [];
  this.RedRowEditOfferIdFromSideOffer = 0; // ✅ Reset - will be set if user selects a side offer

  // ✅ Initialize intermediate variables for Offer BoxStatus and ConditionStatus (avoids template binding to ListPriceOffers[0])
  const listPriceOffers = cartItem?.Product?.ListPriceOffers;
  const firstOffer = listPriceOffers && listPriceOffers.length > 0 ? listPriceOffers[0] : null;
  this.RedRowEditOfferBoxStatus = firstOffer?.BoxStatus || null;
  this.RedRowEditOfferConditionStatus = firstOffer?.ConditionStatus || null;
  console.log('✅ [POPUP] ListPriceOffers:', listPriceOffers);
  console.log('✅ [POPUP] First Offer:', firstOffer);
  console.log('✅ [POPUP] Offer BoxStatus:', this.RedRowEditOfferBoxStatus);
  console.log('✅ [POPUP] Offer ConditionStatus:', this.RedRowEditOfferConditionStatus);

  // ✅ Track original offer ID and partner ID to detect supplier change
  this.RedRowEditOriginalOfferId = cartItem?.SelectedOffer?.ID || 0;
  this.RedRowEditOriginalPartnerId = cartItem?.SelectedOffer?.Partner?.ID || 0;
  this.RedRowEditSupplierChanged = false; // ✅ Reset supplier changed flag

  // ✅ Initialize price fields using SAME properties as grid:
  // - ListPrice: Product.Stats.StandardListPrice.UIUnitPrice
  // - Discount: Product.Stats.StandardListPrice.Discount.Value
  // - FinalPrice: SelectedOffer.SellPrice.UIUnitPrice
  const sellPrice = cartItem?.SelectedOffer?.SellPrice;
  const standardListPrice = cartItem?.Product?.Stats?.StandardListPrice;
  const discountObj = standardListPrice?.Discount ?? cartItem?.SelectedOffer?.Discount;

  // ✅ Ensure Product.Stats.StandardListPrice exists
  if (!this.RedRowEditCartItemClone.Product.Stats.StandardListPrice) {
    this.RedRowEditCartItemClone.Product.Stats.StandardListPrice = {};
  }

  // ListPrice: use StandardListPrice.UIUnitPrice (same as grid)
  this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.UIUnitPrice = standardListPrice?.UIUnitPrice
    ?? sellPrice?.ListPrice
    ?? sellPrice?.OriginalPrice
    ?? 0;

  // Discount: use StandardListPrice.Discount (same as grid)
  const discountValue = discountObj?.Value ?? (typeof sellPrice?.Discount === 'number' ? sellPrice.Discount : sellPrice?.Discount?.Value) ?? 0;
  this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount = {
    Type: discountObj?.Type ?? sellPrice?.Discount?.Type ?? { ID: 1, Name: '%' },
    Value: typeof discountValue === 'number' ? discountValue : 0
  };

  // FinalPrice: use SelectedOffer.SellPrice.UIUnitPrice (same as grid)
  this.RedRowEditCartItemClone.SelectedOffer.SellPrice.UIUnitPrice = sellPrice?.UIUnitPrice
    ?? sellPrice?.Price
    ?? cartItem?.SelectedOffer?.FinalPrice
    ?? cartItem?.Price?.UIUnitPrice
    ?? 0;

  console.log('✅ Price initialization - ListPrice:', this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.UIUnitPrice,
    'Discount:', this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount?.Value,
    'FinalPrice:', this.RedRowEditCartItemClone.SelectedOffer.SellPrice.UIUnitPrice);

  // ✅ Pre-populate Currency in the clone (bound directly)
  const existingCurrency = cartItem?.SelectedOffer?.SellPrice?.Currency;
  if (existingCurrency && existingCurrency.ID) {
    this.RedRowEditCartItemClone.SelectedOffer.SellPrice.Currency = this.currencies.find(c => c.ID === existingCurrency.ID) || this.selectedCurrency;
  } else {
    this.RedRowEditCartItemClone.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ Pre-populate Purchase Currency in the clone (bound directly)
  const existingPurchaseCurrency = cartItem?.SelectedOffer?.PurchaseCurrency ?? cartItem?.SelectedOffer?.Currency;
  if (existingPurchaseCurrency && existingPurchaseCurrency.ID) {
    this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency = this.currencies.find(c => c.ID === existingPurchaseCurrency.ID) || this.selectedCurrency;
  } else {
    this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency = this.selectedCurrency;
  }
  console.log('✅ Purchase initialization - PurchasePrice:', this.RedRowEditCartItemClone.SelectedOffer.ListPrice, 'PurchaseCurrency:', this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency?.Symbol);

  // ✅ Clear lead time suggestion overlays
  this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = '';
  this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = '';

  // ✅ Default to first tab (General)
  this.RedRowEditSelectedTab = 'General';

  // ✅ Close all dropdowns (Lead Time dropdowns removed - now using smart autocomplete)
  this.RedRowEditWeightTypeDropdownOpen = false;
  this.RedRowEditWeightUnitsDropdownOpen = false;
  this.RedRowEditBoxStatusDropdownOpen = false;
  this.RedRowEditConditionStatusDropdownOpen = false;
  this.RedRowEditCurrencyDropdownOpen = false;
  this.RedRowEditPurchaseCurrencyDropdownOpen = false;

  // ✅ Position popup in center of screen
  this.RedRowEditPopupPosition = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    'z-index': 10000
  };

  // ✅ Show popup
  this.ShowRedRowEditPopup = true;
  this.ShowETAAlternatives = false;
  this.ShowSearchResultsAlternatives = false;
  this.ShowFallbackSelection = false;
  this.ShowBrandCollateral = false;
  this.ShowRedRowETAPopup = false;
  this.ShowWeightPopup = false;
  this.ShowPartnerSearchPopup = false;
  this.ShowCOOSearchPopup = false;

  // ✅ Load supplier alternatives for the popup (only if product has an ID)
  if (cartItem?.Product?.ID) {
    this.RedRowEditLoadSupplierAlternatives();
  } else {
    this.RedRowEditSideOffers = [];
  }

  // ✅ Fetch raw DB values (MOQ, LeadTime) for the current offer
  this.RedRowEditFetchDBOfferData(cartItem);

  console.log('✅ Red Row Edit Popup opened');
}

/**
 * Fetch raw DB values (MOQ, LeadTimeType, LeadTimeUnits) for the current offer
 * Uses GetIndustryProductByID with WithListPriceOffers = true
 */
RedRowEditFetchDBOfferData(cartItem: any): void {
  // ✅ Reset DB values
  this.RedRowEditDBOfferMOQ = null;
  this.RedRowEditDBOfferLeadTimeUIOutput = '';
  this.RedRowEditDBOfferLoading = false;

  // ✅ Guard: Check if product.id exists and is valid
  const productId = cartItem?.Product?.ID;
  if (!productId || productId <= 0) {
    console.log('⚠️ RedRowEditFetchDBOfferData - No valid product ID, skipping fetch');
    return;
  }

  // ✅ Guard: Check if selectedOffer.id exists and is valid
  const selectedOfferId = cartItem?.SelectedOffer?.ID;
  if (!selectedOfferId || selectedOfferId <= 0) {
    console.log('⚠️ RedRowEditFetchDBOfferData - No valid selectedOffer ID, skipping fetch');
    return;
  }

  console.log(`✅ RedRowEditFetchDBOfferData - Fetching DB values for Product ID: ${productId}, Offer ID: ${selectedOfferId}`);

  this.RedRowEditDBOfferLoading = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    ID: productId,
    WithListPriceOffers: true
  };

  this.managementService.GetIndustryProductByID(request).subscribe(response => {
    this.RedRowEditDBOfferLoading = false;

    if (response["Status"] !== 1) {
      console.log('⚠️ RedRowEditFetchDBOfferData - API returned non-success status');
      return;
    }

    // ✅ Try both "Product" and "IndustryProduct" keys (API returns "Product")
    const product = response["Product"] || response["IndustryProduct"];
    if (!product) {
      console.log('⚠️ RedRowEditFetchDBOfferData - No product returned. Response keys:', Object.keys(response));
      return;
    }

    const listPriceOffers = product.ListPriceOffers || [];
    console.log(`✅ RedRowEditFetchDBOfferData - Found ${listPriceOffers.length} offers`);
    console.log(`✅ RedRowEditFetchDBOfferData - Looking for selectedOfferId: ${selectedOfferId} (type: ${typeof selectedOfferId})`);
    console.log(`✅ RedRowEditFetchDBOfferData - Available offer IDs:`, listPriceOffers.map((o: any) => ({ id: o.ID, type: typeof o.ID, moq: o.MOQ })));

    // ✅ Find the current offer by ID
    const currentOffer = listPriceOffers.find((o: any) => o.ID === selectedOfferId);
    if (!currentOffer) {
      console.log(`⚠️ RedRowEditFetchDBOfferData - Offer ID ${selectedOfferId} not found in list`);
      return;
    }

    console.log(`✅ RedRowEditFetchDBOfferData - Found matching offer:`, currentOffer);

    // ✅ Extract raw DB values
    this.RedRowEditDBOfferMOQ = currentOffer.MOQ ?? null;
    console.log(`✅ RedRowEditFetchDBOfferData - Extracted MOQ from currentOffer.MOQ: ${currentOffer.MOQ} -> RedRowEditDBOfferMOQ: ${this.RedRowEditDBOfferMOQ}`);

    // ✅ Build LeadTime UIOutput from LeadTimeUnits and LeadTimeType
    const leadTimeUnits = currentOffer.LeadTimeUnits ?? currentOffer.LeadTime?.Units ?? 0;
    const leadTimeType = currentOffer.LeadTimeType ?? currentOffer.LeadTime?.Type;
    const leadTimeTypeName = leadTimeType?.Name || '';

    if (leadTimeUnits > 0 && leadTimeTypeName) {
      this.RedRowEditDBOfferLeadTimeUIOutput = `${leadTimeUnits} ${leadTimeTypeName}`;
    } else if (leadTimeUnits > 0) {
      this.RedRowEditDBOfferLeadTimeUIOutput = `${leadTimeUnits}`;
    } else {
      this.RedRowEditDBOfferLeadTimeUIOutput = '';
    }

    console.log(`✅ RedRowEditFetchDBOfferData - Clone SelectedOffer.MOQ BEFORE auto-populate: ${this.RedRowEditCartItemClone?.SelectedOffer?.MOQ}`);

    // ✅ Auto-populate the editable clone fields if they're empty (use DB values as defaults)
    if (this.RedRowEditCartItemClone?.SelectedOffer) {
      // Populate MOQ if currently empty/null/0
      if (!this.RedRowEditCartItemClone.SelectedOffer.MOQ && this.RedRowEditDBOfferMOQ != null) {
        this.RedRowEditCartItemClone.SelectedOffer.MOQ = this.RedRowEditDBOfferMOQ;
        console.log(`✅ Auto-populated MOQ from DB: ${this.RedRowEditDBOfferMOQ}`);
      } else {
        console.log(`⚠️ MOQ NOT auto-populated - Clone MOQ: ${this.RedRowEditCartItemClone.SelectedOffer.MOQ}, DB MOQ: ${this.RedRowEditDBOfferMOQ}`);
      }

      // Populate LeadTime if currently empty
      if (!this.RedRowEditCartItemClone.SelectedOffer.LeadTime?.UIOutput && this.RedRowEditDBOfferLeadTimeUIOutput) {
        if (!this.RedRowEditCartItemClone.SelectedOffer.LeadTime) {
          this.RedRowEditCartItemClone.SelectedOffer.LeadTime = {};
        }
        this.RedRowEditCartItemClone.SelectedOffer.LeadTime.UIOutput = this.RedRowEditDBOfferLeadTimeUIOutput;
        this.RedRowEditCartItemClone.SelectedOffer.LeadTime.Units = leadTimeUnits;
        this.RedRowEditCartItemClone.SelectedOffer.LeadTime.Type = leadTimeType;
        console.log(`✅ Auto-populated LeadTime from DB: ${this.RedRowEditDBOfferLeadTimeUIOutput}`);
      }
    }

    console.log(`✅ RedRowEditFetchDBOfferData - Clone SelectedOffer.MOQ AFTER auto-populate: ${this.RedRowEditCartItemClone?.SelectedOffer?.MOQ}`);
    console.log(`✅ RedRowEditFetchDBOfferData - FINAL DB MOQ: ${this.RedRowEditDBOfferMOQ}, DB LeadTime: ${this.RedRowEditDBOfferLeadTimeUIOutput}`);
  }, error => {
    this.RedRowEditDBOfferLoading = false;
    console.error('❌ RedRowEditFetchDBOfferData - Error fetching product:', error);
  });
}

/**
 * Search brands for Red Row Edit Popup
 */
RedRowEditSearchBrands(): void {
  const brandName = this.RedRowEditCartItemClone?.Product?.Brand?.Name;
  if (!brandName || brandName.trim().length === 0) {
    this.RedRowEditBrandSearchResults = [];
    return;
  }

  this.RedRowEditLoadingBrands = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    Filter: {
      SearchTerm: brandName,
      Pager: {
        PageIndex: 1,
        PageSize: 20
      }
    }
  };

  this.managementService.GetBrandsFiltered(request).subscribe(response => {
    if (response["Status"] === 1) {
      this.RedRowEditBrandSearchResults = response["Brands"] || [];
    } else {
      this.RedRowEditBrandSearchResults = [];
    }
    this.RedRowEditLoadingBrands = false;
  });
}

/**
 * Select brand in Red Row Edit Popup
 */
RedRowEditSelectBrand(brand: any): void {
  this.RedRowEditCartItemClone.Product.Brand = brand;
  this.RedRowEditBrandSearchResults = [];
  console.log('✅ Brand selected:', brand.Name);
}

/**
 * ✅ NEW: Handle smart input change for lead time autocomplete in Red Row Edit Popup
 */
RedRowEditOnLeadTimeSmartInputChange(value: string) {
  if (!value || value.trim().length === 0) {
    this.RedRowEditLeadTimeSuggestions = [];
    this.RedRowEditLeadTimeInlineSuggestion = '';
    return;
  }

  const input = value.trim().toLowerCase();
  const suggestions: any[] = [];

  // ✅ Parse input: extract number and text
  const match = input.match(/^(\d+)\s*(.*)$/);

  if (match) {
    const units = parseInt(match[1], 10);
    const typeText = match[2].trim();

    // ✅ Filter LeadTimeTypes by matching the typeText
    const matchingTypes = this.LeadTimeTypes.filter(type =>
      type.Name.toLowerCase().startsWith(typeText) || typeText === ''
    );

    // ✅ Create suggestions
    matchingTypes.forEach(type => {
      suggestions.push({
        display: `${units} ${type.Name}`,
        units: units,
        type: type
      });
    });
  } else {
    // ✅ No number yet, just show all types as suggestions
    this.LeadTimeTypes.forEach(type => {
      suggestions.push({
        display: type.Name,
        units: null,
        type: type
      });
    });
  }

  this.RedRowEditLeadTimeSuggestions = suggestions;

  // ✅ Set inline suggestion (only the remaining part after what user typed)
  if (suggestions.length > 0) {
    const fullSuggestion = suggestions[0].display;
    const userInput = value.trim();

    // ✅ Show only the part that comes AFTER what the user typed
    if (fullSuggestion.toLowerCase().startsWith(userInput.toLowerCase())) {
      this.RedRowEditLeadTimeInlineSuggestion = userInput + fullSuggestion.substring(userInput.length);
    } else {
      this.RedRowEditLeadTimeInlineSuggestion = fullSuggestion;
    }
  } else {
    this.RedRowEditLeadTimeInlineSuggestion = '';
  }
}

/**
 * ✅ NEW: Handle TAB key for autocomplete in Red Row Edit Popup
 */
RedRowEditOnLeadTimeSmartInputTab(event: KeyboardEvent) {
  if (this.RedRowEditLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectLeadTimeSuggestion(this.RedRowEditLeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Handle ENTER key for autocomplete in Red Row Edit Popup
 */
RedRowEditOnLeadTimeSmartInputEnter(event: KeyboardEvent) {
  if (this.RedRowEditLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectLeadTimeSuggestion(this.RedRowEditLeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Handle Arrow Right key for autocomplete in Red Row Edit Popup
 */
RedRowEditOnLeadTimeSmartInputArrowRight(event: KeyboardEvent) {
  if (this.RedRowEditLeadTimeInlineSuggestion && this.RedRowEditLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectLeadTimeSuggestion(this.RedRowEditLeadTimeSuggestions[0]);
  }
}

/**
 * ✅ NEW: Select a suggestion and populate internal values in Red Row Edit Popup
 */
RedRowEditSelectLeadTimeSuggestion(suggestion: any) {
  // ✅ Set the LeadTime object in the clone
  if (!this.RedRowEditCartItemClone.SelectedOffer.LeadTime) {
    this.RedRowEditCartItemClone.SelectedOffer.LeadTime = {};
  }
  this.RedRowEditCartItemClone.SelectedOffer.LeadTime.UIOutput = suggestion.display;
  this.RedRowEditCartItemClone.SelectedOffer.LeadTime.Type = suggestion.type;
  if (suggestion.units !== null) {
    this.RedRowEditCartItemClone.SelectedOffer.LeadTime.Units = suggestion.units;
  }

  // ✅ Clear suggestions and inline suggestion
  this.RedRowEditLeadTimeSuggestions = [];
  this.RedRowEditLeadTimeInlineSuggestion = '';

  console.log('✅ Lead Time autocompleted in Red Row Edit:', suggestion.display);
}

/**
 * ✅ NEW: Clear lead time in Red Row Edit Popup
 */
RedRowEditClearLeadTime() {
  // ✅ Clear the LeadTime object in the clone
  this.RedRowEditCartItemClone.SelectedOffer.LeadTime = { UIOutput: '' };

  // ✅ Clear inline suggestion and suggestions
  this.RedRowEditLeadTimeInlineSuggestion = '';
  this.RedRowEditLeadTimeSuggestions = [];

  console.log('✅ Lead Time cleared in Red Row Edit');
}

/**
 * Search partners (suppliers) for Red Row Edit Popup
 */
RedRowEditSearchPartners(): void {
  const partnerName = this.RedRowEditCartItemClone?.SelectedOffer?.Partner?.Name;

  // ✅ Mark supplier as changed when user types - triggers button change to "Save New"
  this.RedRowEditSupplierChanged = true;

  // ✅ Clear Partner.ID when user types - forces new offer creation
  if (this.RedRowEditCartItemClone?.SelectedOffer?.Partner?.ID) {
    delete this.RedRowEditCartItemClone.SelectedOffer.Partner.ID;
  }

  if (!partnerName || partnerName.trim().length === 0) {
    this.RedRowEditPartnerSearchResults = [];
    return;
  }

  this.RedRowEditLoadingPartners = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    SearchTerm: partnerName,
    PageIndex: 1,
    PageSize: 20
  };

  console.log('🔍 RedRowEdit - Searching partners with term:', partnerName);

  this.service.GetPartnersFiltered(request).subscribe(response => {
    console.log('🔍 RedRowEdit - Partner search response:', response);
    if (response && response["Partners"]) {
      this.RedRowEditPartnerSearchResults = response["Partners"] || [];
      console.log(`✅ RedRowEdit - Found ${this.RedRowEditPartnerSearchResults.length} partners`);
    } else {
      this.RedRowEditPartnerSearchResults = [];
      console.log('⚠️ RedRowEdit - No partners found');
    }
    this.RedRowEditLoadingPartners = false;
  }, error => {
    console.error('❌ RedRowEdit - Partner search error:', error);
    this.RedRowEditPartnerSearchResults = [];
    this.RedRowEditLoadingPartners = false;
  });
}

/**
 * Select partner (supplier) in Red Row Edit Popup
 */
RedRowEditSelectPartner(partner: any): void {
  // ✅ Deep clone partner to avoid reference issues with search results
  this.RedRowEditCartItemClone.SelectedOffer.Partner = JSON.parse(JSON.stringify(partner));
  this.RedRowEditPartnerSearchResults = [];
  this.RedRowEditShowSupplierAlternatives = false; // ✅ Hide supplier alternatives dropdown

  // ✅ Check if partner changed from original
  if (partner?.ID !== this.RedRowEditOriginalPartnerId) {
    this.RedRowEditSupplierChanged = true;
    this.RedRowEditOfferIdFromSideOffer = 0; // Manual partner selection = new offer
    console.log('✅ Partner selected manually:', partner.Name, '- will create new offer (ID=0)');
  } else {
    // Same partner selected - reset supplier changed flag
    this.RedRowEditSupplierChanged = false;
    console.log('✅ Original partner re-selected:', partner.Name);
  }
}

/**
 * Clear supplier in Red Row Edit Popup (X button)
 */
RedRowEditClearSupplier(): void {
  // ✅ Set to empty object instead of null to avoid binding errors
  this.RedRowEditCartItemClone.SelectedOffer.Partner = {};
  this.RedRowEditPartnerSearchResults = [];
  this.RedRowEditSupplierChanged = true; // Clearing = changing supplier
  console.log('✅ Supplier cleared - will create new offer');
}

/**
 * Load supplier alternatives for Red Row Edit Popup
 */
RedRowEditLoadSupplierAlternatives(): void {
  const cartItem = this.CurrentRedRowEditCartItem;
  if (!cartItem) {
    console.log('❌ No CartItem for supplier alternatives');
    return;
  }

  const productId = cartItem?.Product?.ID;
  const offerId = cartItem?.SelectedOffer?.ID || cartItem?.Product?.ListPriceOffers?.[0]?.ID;

  if (!productId) {
    console.log('⚠️ No Product ID for supplier alternatives');
    this.RedRowEditSideOffers = [];
    return;
  }

  console.log('🔍 RedRowEdit - Loading supplier alternatives for product:', productId);

  this.RedRowEditLoadingSideOffers = true;
  this.RedRowEditSideOffers = [];

  // ✅ Use ConfigStatus ID=-9000 (VisibleOnlyForAdminPurchases) for admin grid
  let configStatusToUse = this.selectedConfigStatus;
  if (this.useAdminGrid) {
    const adminPurchasesStatus = this.parent.masterData?.ConfigStatuses?.find((s: any) => s.ID === -9000);
    if (adminPurchasesStatus) {
      configStatusToUse = adminPurchasesStatus;
    }
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    PartnerID: this.parent.B2BPartner?.ID,
    IndustryProductID: productId,
    SelectedOfferID: offerId,
    ApplyClientRestrictions: this.applyClientRestrictions,
    BypassBlockedFlag: true, // Allow employees to view prices for blocked B2B customers in Quotations
    StockStatus: this.StockStatus,
    B2BAdminSettings: {
      AdminToken: this.parent.User?.Id,
      PrefferedCurrency: this.selectedCurrency,
      ConfigStatus: configStatusToUse,
      UserViewsPurchasePrices: true
    }
  };

  this.B2BService.GetIndustryProductCalculatedSideOffers(request).subscribe(response => {
    if (response["Status"] === 1) {
      const cartItems = JSON.parse(JSON.stringify(response["CartItems"]));
      // ✅ Sort by ListPrice ascending (best price first)
      this.RedRowEditSideOffers = cartItems.sort((a: any, b: any) => {
        const priceA = a?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
        const priceB = b?.SelectedOffer?.ListPrice ?? Number.MAX_VALUE;
        return priceA - priceB;
      });
      console.log(`✅ RedRowEdit - Loaded ${this.RedRowEditSideOffers.length} supplier alternatives`);
    } else {
      this.RedRowEditSideOffers = [];
      console.log('⚠️ RedRowEdit - No supplier alternatives found');
    }
    this.RedRowEditLoadingSideOffers = false;
  }, error => {
    console.error('❌ RedRowEdit - Supplier alternatives error:', error);
    this.RedRowEditSideOffers = [];
    this.RedRowEditLoadingSideOffers = false;
  });
}

/**
 * Select a supplier alternative in Red Row Edit Popup
 */
RedRowEditSelectSideOffer(selectedCartItem: any): void {
  console.log('✅ RedRowEdit - Side offer selected:', selectedCartItem);

  // ✅ Deep clone Partner to avoid reference issues with RedRowEditSideOffers array
  this.RedRowEditCartItemClone.SelectedOffer.Partner = selectedCartItem?.SelectedOffer?.Partner
    ? JSON.parse(JSON.stringify(selectedCartItem.SelectedOffer.Partner))
    : {};

  // ✅ Deep clone PurchaseCurrency to avoid reference issues
  const purchaseCurrency = selectedCartItem?.SelectedOffer?.PurchaseCurrency ||
    selectedCartItem?.SelectedOffer?.Currency || this.selectedCurrency;
  this.RedRowEditCartItemClone.SelectedOffer.PurchaseCurrency = purchaseCurrency
    ? JSON.parse(JSON.stringify(purchaseCurrency))
    : this.selectedCurrency;

  // ✅ Update purchase price in the clone (primitive value, no need to clone)
  this.RedRowEditCartItemClone.SelectedOffer.ListPrice = selectedCartItem?.SelectedOffer?.ListPrice || 0;

  // ✅ Track the selected side offer ID (will be used in applyRedRowEditToCartItem)
  this.RedRowEditOfferIdFromSideOffer = selectedCartItem?.SelectedOffer?.ID || 0;
  console.log('✅ RedRowEdit - Tracked side offer ID:', this.RedRowEditOfferIdFromSideOffer);

  // ✅ Hide both dropdown containers after selection
  this.RedRowEditPartnerSearchResults = [];
  this.RedRowEditShowSupplierAlternatives = false;

  console.log('✅ RedRowEdit - Supplier set to:', this.RedRowEditCartItemClone.SelectedOffer.Partner?.Name);
  console.log('✅ RedRowEdit - Purchase price set to:', this.RedRowEditCartItemClone.SelectedOffer.ListPrice);
}

/**
 * Search countries (COO) for Red Row Edit Popup
 */
RedRowEditSearchCountries(): void {
  const cooShortName = this.RedRowEditCartItemClone?.Product?.Stats?.COO?.ShortName;

  // ✅ Clear COO.ID when user types - forces re-identification on blur
  if (this.RedRowEditCartItemClone?.Product?.Stats?.COO?.ID) {
    delete this.RedRowEditCartItemClone.Product.Stats.COO.ID;
    delete this.RedRowEditCartItemClone.Product.Stats.COO.Name;
  }

  if (!cooShortName || cooShortName.trim().length === 0) {
    this.RedRowEditCOOSearchResults = [];
    return;
  }

  if (!this.parent.masterData || !this.parent.masterData.Countries) {
    console.log('❌ Countries not available in parent.masterData');
    this.RedRowEditCOOSearchResults = [];
    return;
  }

  const searchTerm = cooShortName.toLowerCase();
  this.RedRowEditCOOSearchResults = this.parent.masterData.Countries.filter((country: any) =>
    country.Name.toLowerCase().includes(searchTerm) ||
    country.ShortName.toLowerCase().includes(searchTerm)
  );
}

/**
 * Select country (COO) in Red Row Edit Popup
 */
RedRowEditSelectCountry(country: any): void {
  this.RedRowEditCartItemClone.Product.Stats.COO = country;
  this.RedRowEditCOOSearchResults = [];
  console.log('✅ COO selected:', country.ShortName);
}

/**
 * Handle Weight Type change in Red Row Edit Popup
 */
RedRowEditOnWeightTypeChange(): void {
  // No special handling needed
}

/**
 * Handle Weight Units change in Red Row Edit Popup
 */
RedRowEditOnWeightUnitsChange(): void {
  // If units is 0, clear the type selection
  if (this.RedRowEditSelectedWeightUnits === 0) {
    this.RedRowEditSelectedWeightType = null;
    console.log('⚠️ Weight units set to 0, clearing type selection');
  }
}

/**
 * ✅ Identify Brand via backend when user finishes editing (blur)
 */
RedRowEditIdentifyBrand(): void {
  const brandText = this.RedRowEditCartItemClone?.Product?.Brand?.Name?.trim();
  if (!brandText) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: brandText
  };

  console.log('📤 RedRowEdit - Calling IdentifyGridBrand:', brandText);

  this.managementService.IdentifyGridBrand(request).subscribe(response => {
    if (response["Status"] === 1 && response["Brand"]) {
      this.RedRowEditCartItemClone.Product.Brand = response["Brand"];
      console.log('✅ RedRowEdit - Brand identified:', response["Brand"].Name);
    } else {
      console.log('⚠️ RedRowEdit - Could not identify brand');
    }
  }, error => {
    console.error('❌ RedRowEdit - Brand identification error:', error);
  });
}

/**
 * ✅ Identify Lead Time via backend when user finishes editing (blur)
 */
RedRowEditIdentifyLeadTime(): void {
  const leadTimeText = this.RedRowEditCartItemClone?.SelectedOffer?.LeadTime?.UIOutput?.trim();
  if (!leadTimeText) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: leadTimeText,
    IsProlonged: false
  };

  console.log('📤 RedRowEdit - Calling IdentifyGridLeadTime:', leadTimeText);

  this.managementService.IdentifyGridLeadTime(request).subscribe(response => {
    if (response["Status"] === 1 && response["LeadTime"]) {
      const leadTime = response["LeadTime"];
      this.RedRowEditCartItemClone.SelectedOffer.LeadTime = leadTime;
      console.log('✅ RedRowEdit - Lead time identified:', leadTime.UIOutput);
    } else {
      console.log('⚠️ RedRowEdit - Could not identify lead time');
    }
  }, error => {
    console.error('❌ RedRowEdit - Lead time identification error:', error);
  });
}

/**
 * ✅ Smart input change handler for ManufacturerStockLeadTime
 */
RedRowEditOnManufacturerStockLeadTimeSmartInputChange(value: string): void {
  if (!value || value.trim().length === 0) {
    this.RedRowEditManufacturerStockLeadTimeSuggestions = [];
    this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = '';
    return;
  }

  const input = value.trim().toLowerCase();
  const suggestions: any[] = [];
  const match = input.match(/^(\d+)\s*(.*)$/);

  if (match) {
    const units = parseInt(match[1], 10);
    const typeText = match[2].trim();
    const matchingTypes = this.LeadTimeTypes.filter(type =>
      type.Name.toLowerCase().startsWith(typeText) || typeText === ''
    );
    matchingTypes.forEach(type => {
      suggestions.push({ display: `${units} ${type.Name}`, units: units, type: type });
    });
  } else {
    this.LeadTimeTypes.forEach(type => {
      suggestions.push({ display: `1 ${type.Name}`, units: 1, type: type });
    });
  }

  this.RedRowEditManufacturerStockLeadTimeSuggestions = suggestions;
  if (suggestions.length > 0) {
    const fullSuggestion = suggestions[0].display;
    const userInput = value.trim();
    if (fullSuggestion.toLowerCase().startsWith(userInput.toLowerCase())) {
      this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = userInput + fullSuggestion.substring(userInput.length);
    } else {
      this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = fullSuggestion;
    }
  } else {
    this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = '';
  }
}

RedRowEditOnManufacturerStockLeadTimeSmartInputTab(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerStockLeadTimeSuggestion(this.RedRowEditManufacturerStockLeadTimeSuggestions[0]);
  }
}

RedRowEditOnManufacturerStockLeadTimeSmartInputEnter(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerStockLeadTimeSuggestion(this.RedRowEditManufacturerStockLeadTimeSuggestions[0]);
  }
}

RedRowEditOnManufacturerStockLeadTimeSmartInputArrowRight(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerStockLeadTimeInlineSuggestion && this.RedRowEditManufacturerStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerStockLeadTimeSuggestion(this.RedRowEditManufacturerStockLeadTimeSuggestions[0]);
  }
}

RedRowEditSelectManufacturerStockLeadTimeSuggestion(suggestion: any): void {
  // ✅ Set the ManufacturerStockLeadTime object in the clone
  if (!this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime) {
    this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime = {};
  }
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime.UIOutput = suggestion.display;
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime.Type = suggestion.type;
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime.Units = suggestion.units ?? 0;
  this.RedRowEditManufacturerStockLeadTimeSuggestions = [];
  this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = '';
}

RedRowEditClearManufacturerStockLeadTime(): void {
  // ✅ Clear the ManufacturerStockLeadTime object in the clone
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime = { UIOutput: '' };
  this.RedRowEditManufacturerStockLeadTimeInlineSuggestion = '';
  this.RedRowEditManufacturerStockLeadTimeSuggestions = [];
}

RedRowEditIdentifyManufacturerStockLeadTime(): void {
  const leadTimeText = this.RedRowEditCartItemClone?.Product?.Stats?.ManufacturerStockLeadTime?.UIOutput?.trim();
  if (!leadTimeText) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: leadTimeText,
    IsProlonged: false
  };

  this.managementService.IdentifyGridLeadTime(request).subscribe(response => {
    if (response["Status"] === 1 && response["LeadTime"]) {
      this.RedRowEditCartItemClone.Product.Stats.ManufacturerStockLeadTime = response["LeadTime"];
    }
  });
}

/**
 * ✅ Smart input change handler for ManufacturerOverrequestStockLeadTime
 */
RedRowEditOnManufacturerOverrequestStockLeadTimeSmartInputChange(value: string): void {
  if (!value || value.trim().length === 0) {
    this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions = [];
    this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = '';
    return;
  }

  const input = value.trim().toLowerCase();
  const suggestions: any[] = [];
  const match = input.match(/^(\d+)\s*(.*)$/);

  if (match) {
    const units = parseInt(match[1], 10);
    const typeText = match[2].trim();
    const matchingTypes = this.LeadTimeTypes.filter(type =>
      type.Name.toLowerCase().startsWith(typeText) || typeText === ''
    );
    matchingTypes.forEach(type => {
      suggestions.push({ display: `${units} ${type.Name}`, units: units, type: type });
    });
  } else {
    this.LeadTimeTypes.forEach(type => {
      suggestions.push({ display: `1 ${type.Name}`, units: 1, type: type });
    });
  }

  this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions = suggestions;
  if (suggestions.length > 0) {
    const fullSuggestion = suggestions[0].display;
    const userInput = value.trim();
    if (fullSuggestion.toLowerCase().startsWith(userInput.toLowerCase())) {
      this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = userInput + fullSuggestion.substring(userInput.length);
    } else {
      this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = fullSuggestion;
    }
  } else {
    this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = '';
  }
}

RedRowEditOnManufacturerOverrequestStockLeadTimeSmartInputTab(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerOverrequestStockLeadTimeSuggestion(this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions[0]);
  }
}

RedRowEditOnManufacturerOverrequestStockLeadTimeSmartInputEnter(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerOverrequestStockLeadTimeSuggestion(this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions[0]);
  }
}

RedRowEditOnManufacturerOverrequestStockLeadTimeSmartInputArrowRight(event: KeyboardEvent): void {
  if (this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion && this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions.length > 0) {
    event.preventDefault();
    this.RedRowEditSelectManufacturerOverrequestStockLeadTimeSuggestion(this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions[0]);
  }
}

RedRowEditSelectManufacturerOverrequestStockLeadTimeSuggestion(suggestion: any): void {
  // ✅ Set the ManufacturerOverrequestStockLeadTime object in the clone
  if (!this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime) {
    this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime = {};
  }
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime.UIOutput = suggestion.display;
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime.Type = suggestion.type;
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime.Units = suggestion.units ?? 0;
  this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions = [];
  this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = '';
}

RedRowEditClearManufacturerOverrequestStockLeadTime(): void {
  // ✅ Clear the ManufacturerOverrequestStockLeadTime object in the clone
  this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime = { UIOutput: '' };
  this.RedRowEditManufacturerOverrequestStockLeadTimeInlineSuggestion = '';
  this.RedRowEditManufacturerOverrequestStockLeadTimeSuggestions = [];
}

RedRowEditIdentifyManufacturerOverrequestStockLeadTime(): void {
  const leadTimeText = this.RedRowEditCartItemClone?.Product?.Stats?.ManufacturerOverrequestStockLeadTime?.UIOutput?.trim();
  if (!leadTimeText) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: leadTimeText,
    IsProlonged: false
  };

  this.managementService.IdentifyGridLeadTime(request).subscribe(response => {
    if (response["Status"] === 1 && response["LeadTime"]) {
      this.RedRowEditCartItemClone.Product.Stats.ManufacturerOverrequestStockLeadTime = response["LeadTime"];
    }
  });
}

/**
 * ✅ Identify Supplier via backend when user finishes editing (blur)
 */
RedRowEditIdentifySupplier(): void {
  const supplierText = this.RedRowEditCartItemClone?.SelectedOffer?.Partner?.Name?.trim();
  if (!supplierText) return;

  // If already has an ID, skip identification (already resolved)
  if (this.RedRowEditCartItemClone?.SelectedOffer?.Partner?.ID) return;

  // Search for partner matching the text
  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    SearchTerm: supplierText,
    PageIndex: 1,
    PageSize: 1
  };

  console.log('📤 RedRowEdit - Identifying supplier:', supplierText);

  this.service.GetPartnersFiltered(request).subscribe(response => {
    if (response && response["Partners"]?.length > 0) {
      this.RedRowEditCartItemClone.SelectedOffer.Partner = response["Partners"][0];

      // ✅ Supplier identified from typed text = new offer, reset tracked side offer ID to 0
      this.RedRowEditOfferIdFromSideOffer = 0;
      console.log('✅ RedRowEdit - Supplier identified:', response["Partners"][0].Name, '- will create new offer (ID=0)');
    } else {
      console.log('⚠️ RedRowEdit - Could not identify supplier');
    }
  }, error => {
    console.error('❌ RedRowEdit - Supplier identification error:', error);
  });
}

/**
 * ✅ Identify Weight via backend when user finishes editing (blur)
 */
RedRowEditIdentifyWeight(): void {
  const weightText = this.RedRowEditCartItemClone?.Product?.Stats?.Weight?.UIOutput?.trim();
  if (!weightText) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: weightText
  };

  console.log('📤 RedRowEdit - Calling IdentifyGridWeight:', weightText);

  this.managementService.IdentifyGridWeight(request).subscribe(response => {
    if (response["Status"] === 1) {
      // ✅ Update the weight object in the clone
      if (!this.RedRowEditCartItemClone.Product.Stats.Weight) {
        this.RedRowEditCartItemClone.Product.Stats.Weight = {};
      }
      if (response["WeightMeasurementType"]) {
        this.RedRowEditCartItemClone.Product.Stats.Weight.Type = response["WeightMeasurementType"];
      }
      if (response["Weight"] != null) {
        this.RedRowEditCartItemClone.Product.Stats.Weight.Units = response["Weight"];
        // ✅ Update UIOutput with formatted weight result
        const unit = response["WeightMeasurementType"]?.Name || 'kg';
        this.RedRowEditCartItemClone.Product.Stats.Weight.UIOutput = `${response["Weight"]} ${unit}`;
      }
      console.log('✅ RedRowEdit - Weight identified:', this.RedRowEditCartItemClone.Product.Stats.Weight.UIOutput);
    } else {
      console.log('⚠️ RedRowEdit - Could not identify weight');
    }
  }, error => {
    console.error('❌ RedRowEdit - Weight identification error:', error);
  });
}

/**
 * ✅ Identify COO via backend when user finishes editing (blur)
 */
RedRowEditIdentifyCOO(): void {
  const cooText = this.RedRowEditCartItemClone?.Product?.Stats?.COO?.ShortName?.trim();
  if (!cooText) return;

  // If already has an ID, skip identification (already resolved)
  if (this.RedRowEditCartItemClone?.Product?.Stats?.COO?.ID) return;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CellText: cooText
  };

  console.log('📤 RedRowEdit - Calling IdentifyGridCOO:', cooText);

  this.managementService.IdentifyGridCOO(request).subscribe(response => {
    if (response["Status"] === 1 && response["Country"]) {
      this.RedRowEditCartItemClone.Product.Stats.COO = response["Country"];
      this.RedRowEditCOOSearchResults = []; // ✅ Hide dropdown after identification
      console.log('✅ RedRowEdit - COO identified:', response["Country"].ShortName);
    } else {
      console.log('⚠️ RedRowEdit - Could not identify COO');
    }
  }, error => {
    console.error('❌ RedRowEdit - COO identification error:', error);
  });
}

/**
 * ✅ Recalculate Final Price when List Price or Discount changes
 * Uses same properties as grid: Product.Stats.StandardListPrice for ListPrice/Discount, SelectedOffer.SellPrice.UIUnitPrice for FinalPrice
 */
RedRowEditRecalculateFinalPrice(): void {
  if (!this.RedRowEditCartItemClone?.Product?.Stats?.StandardListPrice) return;
  if (!this.RedRowEditCartItemClone?.SelectedOffer?.SellPrice) return;
  const listPrice = this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.UIUnitPrice || 0;
  const discount = this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount?.Value || 0;
  // ✅ Round to 2 decimal places to respect grid precision
  const finalPrice = Math.round((listPrice - (listPrice * discount / 100)) * 100) / 100;
  this.RedRowEditCartItemClone.SelectedOffer.SellPrice.UIUnitPrice = finalPrice;
  console.log('💰 RedRowEdit - Final price recalculated:', finalPrice);
}

/**
 * ✅ Recalculate Discount when List Price or Final Price changes
 * Uses same properties as grid: Product.Stats.StandardListPrice for ListPrice/Discount, SelectedOffer.SellPrice.UIUnitPrice for FinalPrice
 */
RedRowEditRecalculateDiscount(): void {
  if (!this.RedRowEditCartItemClone?.Product?.Stats?.StandardListPrice) return;
  if (!this.RedRowEditCartItemClone?.SelectedOffer?.SellPrice) return;
  // Ensure Discount object exists
  if (!this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount) {
    this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount = { Type: { ID: 1, Name: '%' }, Value: 0 };
  }
  const listPrice = this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.UIUnitPrice || 0;
  const finalPrice = this.RedRowEditCartItemClone.SelectedOffer.SellPrice.UIUnitPrice || 0;
  if (listPrice > 0) {
    // ✅ Round to 2 decimal places to respect grid precision
    this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount.Value = Math.round(((listPrice - finalPrice) / listPrice) * 100 * 100) / 100;
  } else {
    this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount.Value = 0;
  }
  console.log('💰 RedRowEdit - Discount recalculated:', this.RedRowEditCartItemClone.Product.Stats.StandardListPrice.Discount.Value);
}

/**
 * Handle MOQ change in Red Row Edit Popup
 */
OnRedRowEditMOQChange(moqValue: any): void {
  console.log('🔢 MOQ changed to:', moqValue);

  if (!this.CurrentRedRowEditCartItem?.SelectedOffer) {
    console.log('❌ No SelectedOffer to update MOQ');
    return;
  }

  // ✅ If MOQ > 1, set ChangedQunatityFromMOQ = true
  if (moqValue > 1) {
    this.CurrentRedRowEditCartItem.SelectedOffer.ChangedQunatityFromMOQ = true;
    console.log('✅ MOQ > 1, setting ChangedQunatityFromMOQ = true');
  } else {
    this.CurrentRedRowEditCartItem.SelectedOffer.ChangedQunatityFromMOQ = false;
    console.log('⚠️ MOQ <= 1, setting ChangedQunatityFromMOQ = false');
  }
}

/**
 * Save all changes from Red Row Edit Popup (local only, no DB save)
 * Applies popup changes to grid row and refreshes the grid
 */
SaveRedRowEdit(): void {
  console.log('💾 SaveRedRowEdit called (local only, no DB save)');

  if (!this.RedRowEditCartItemClone || !this.CurrentRedRowEditCartItem) {
    console.log('❌ No CartItem clone or original to update');
    return;
  }

  // ✅ SYNC intermediate variables back to clone BEFORE copying to original
  // This ensures BoxStatus and ConditionStatus from Conditions tab are saved
  this.syncOfferStatusesToClone();

  const originalCartItem = this.CurrentRedRowEditCartItem;
  const rowIndex = this.CurrentRedRowEditRowIndex;

  // ✅ FIRST: Copy all values from clone back to original cartItem (BEFORE applyRedRowEditToCartItem)
  // This ensures the original reference in parent.Cart.Items gets updated
  Object.assign(originalCartItem, JSON.parse(JSON.stringify(this.RedRowEditCartItemClone)));

  // ✅ Apply special fields that need intermediate variables (Brand, Partner, COO, LeadTime, etc.)
  // Now passing the ORIGINAL cartItem (which is in parent.Cart.Items) so saveCartToIDB works correctly
  this.applyRedRowEditToCartItem(originalCartItem, rowIndex);

  // ✅ Refresh the grid row to show the changes
  if (this.gridApi && rowIndex >= 0) {
    const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
    if (rowNode) {
      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
      console.log('✅ Grid row refreshed at index:', rowIndex);
    }
  }

  // ✅ Close the popup
  this.CloseRedRowEditPopup();

  console.log('✅ Red Row Edit applied locally and popup closed');
}

/**
 * Save Red Row Edit and also save to database (same as clicking + icon)
 * Shows spinner during save, closes popup when complete
 */
SaveRedRowEditAndSaveToDatabase(): void {
  console.log('═══════════════════════════════════════════════════════════════');
  console.log('💾 SaveRedRowEditAndSaveToDatabase STARTED (from popup)');
  console.log('═══════════════════════════════════════════════════════════════');

  try {
    if (!this.RedRowEditCartItemClone) {
      console.error('❌ [BLOCKED] No CartItem clone to save - RedRowEditCartItemClone is null');
      return;
    }

    // ✅ SYNC intermediate variables back to clone BEFORE saving
    // This ensures BoxStatus and ConditionStatus from Conditions tab are saved
    console.log('💾 [POPUP] BEFORE syncOfferStatusesToClone:');
    console.log('💾 [POPUP] - RedRowEditOfferBoxStatus:', this.RedRowEditOfferBoxStatus);
    console.log('💾 [POPUP] - RedRowEditOfferConditionStatus:', this.RedRowEditOfferConditionStatus);
    console.log('💾 [POPUP] - Clone ListPriceOffers:', this.RedRowEditCartItemClone?.Product?.ListPriceOffers);

    this.syncOfferStatusesToClone();

    console.log('💾 [POPUP] AFTER syncOfferStatusesToClone:');
    console.log('💾 [POPUP] - Clone ListPriceOffers[0]:', this.RedRowEditCartItemClone?.Product?.ListPriceOffers?.[0]);
    console.log('💾 [POPUP] - Clone ListPriceOffers[0].BoxStatus:', this.RedRowEditCartItemClone?.Product?.ListPriceOffers?.[0]?.BoxStatus);
    console.log('💾 [POPUP] - Clone ListPriceOffers[0].ConditionStatus:', this.RedRowEditCartItemClone?.Product?.ListPriceOffers?.[0]?.ConditionStatus);

    // ✅ Store row index before any popup operations
    const rowIndex = this.CurrentRedRowEditRowIndex;
    console.log('💾 [POPUP] Step 1: rowIndex =', rowIndex);
    console.log('💾 [POPUP] Step 1: RedRowEditCartItemClone:', JSON.parse(JSON.stringify(this.RedRowEditCartItemClone)));

    // ⚠️ IMPORTANT: Do NOT call applyRedRowEditToCartItem here!
    // Previously this was called before the HTTP request, which caused grid row data to be modified
    // even when the save failed. Now we only apply changes to the grid row AFTER successful save
    // via applyCartItemToRow() in the success callback.
    console.log('💾 [POPUP] Step 2: Skipping applyRedRowEditToCartItem (will apply on success only)');

    // ✅ Start spinner
    this.RedRowEditSaving = true;
    this.parent.loading = true;

    // ✅ Use the clone directly for the request - it already has all the bound values
    const requestCartItem = JSON.parse(JSON.stringify(this.RedRowEditCartItemClone));
    console.log('💾 [POPUP] Step 3: requestCartItem created');
    console.log('💾 [POPUP] Step 3: CartItemStatus:', requestCartItem?.CartItemStatus);
    console.log('💾 [POPUP] Step 3: CartItemStatus.ID:', requestCartItem?.CartItemStatus?.ID);
    console.log('💾 [POPUP] Step 3: Product:', requestCartItem?.Product);
    console.log('💾 [POPUP] Step 3: Product.ID:', requestCartItem?.Product?.ID);
    console.log('💾 [POPUP] Step 3: Product.Name:', requestCartItem?.Product?.Name);
    console.log('💾 [POPUP] Step 3: UIProductValue:', requestCartItem?.UIProductValue);
    console.log('💾 [POPUP] Step 3: Product.Brand:', requestCartItem?.Product?.Brand);
    console.log('💾 [POPUP] Step 3: SelectedOffer:', requestCartItem?.SelectedOffer);

    // ✅ Ensure Product object exists
    if (!requestCartItem.Product) {
      requestCartItem.Product = {};
    }

    // ✅ CRITICAL: For RED ROWS, copy UIProductValue to Product.Name (backend needs Product.Name!)
    const isRedRowPopup = requestCartItem?.CartItemStatus?.ID === 0;
    if (isRedRowPopup && requestCartItem.UIProductValue && !requestCartItem.Product.Name) {
      console.log('💾 [POPUP] [FIX] RED ROW: Copying UIProductValue to Product.Name:', requestCartItem.UIProductValue);
      requestCartItem.Product.Name = requestCartItem.UIProductValue;
    }

    // ✅ FIX: Sanitize Product.Stats to ensure Weight is a number (not a corrupted object)
    if (requestCartItem.Product?.Stats) {
      const stats = requestCartItem.Product.Stats;
      // Check if Weight is an object with Units/Type/UIOutput (Red Row popup format) - extract the primitive value
      if (stats.Weight !== null && stats.Weight !== undefined && typeof stats.Weight === 'object') {
        console.log('💾 [POPUP] [FIX] Stats.Weight is object, extracting Units value:', stats.Weight);
        // ✅ Extract the Units value from the object
        if (stats.Weight.Units !== null && stats.Weight.Units !== undefined) {
          const weightUnits = stats.Weight.Units;
          const weightType = stats.Weight.Type;
          stats.Weight = weightUnits;  // Replace object with primitive number
          if (weightType) {
            stats.WeightMeasurementType = weightType;
          }
          console.log('💾 [POPUP] [FIX] Stats.Weight extracted to:', stats.Weight, 'Type:', stats.WeightMeasurementType?.Name);
        } else {
          // No Units value - set to null
          stats.Weight = null;
          console.log('💾 [POPUP] [FIX] Stats.Weight had no Units, set to null');
        }
      }
      // Remove any circular reference properties that shouldn't exist in Stats
      delete stats.CartItem;
      delete stats.Stats;

      // ✅ FIX: If COO.ID is 0 or invalid, set COO to null (FK constraint on tblCountries)
      if (stats.COO && (!stats.COO.ID || stats.COO.ID === 0)) {
        console.log('💾 [POPUP] [FIX] COO.ID is 0 or invalid, setting COO to null');
        stats.COO = null;
      }
    }

    // ✅ Ensure Currency is set on SelectedOffer (backend reads from here!)
    if (!requestCartItem.SelectedOffer?.Currency && this.selectedCurrency) {
      console.log('💾 [POPUP] [FIX] Setting SelectedOffer.Currency');
      if (!requestCartItem.SelectedOffer) requestCartItem.SelectedOffer = {};
      requestCartItem.SelectedOffer.Currency = this.selectedCurrency;
    }

    // ✅ Ensure PurchaseCurrency is set on SelectedOffer
    if (!requestCartItem.SelectedOffer?.PurchaseCurrency) {
      console.log('💾 [POPUP] [FIX] Setting SelectedOffer.PurchaseCurrency');
      requestCartItem.SelectedOffer.PurchaseCurrency = requestCartItem.SelectedOffer?.Currency || this.selectedCurrency;
    }

    // ✅ Also ensure SellPrice.Currency is set
    if (!requestCartItem.SelectedOffer?.SellPrice) {
      console.log('💾 [POPUP] [FIX] Creating SelectedOffer.SellPrice');
      requestCartItem.SelectedOffer.SellPrice = {};
    }
    if (!requestCartItem.SelectedOffer?.SellPrice?.Currency && this.selectedCurrency) {
      console.log('💾 [POPUP] [FIX] Setting SelectedOffer.SellPrice.Currency');
      requestCartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
    }

    // ✅ Copy StockQuantityClone to StockQuantity on REQUEST object
    if (requestCartItem.SelectedOffer) {
      requestCartItem.SelectedOffer.StockQuantity = requestCartItem.SelectedOffer.StockQuantityClone;
    }

    // ✅ Handle Supplier (Partner) offer ID logic - ONLY set ID=0 when supplier changes
    // This logic was previously in applyRedRowEditToCartItem, now applying to requestCartItem only
    if (requestCartItem.SelectedOffer?.Partner?.ID) {
      const currentPartnerId = requestCartItem.SelectedOffer?.Partner?.ID || 0;
      const supplierChanged = currentPartnerId !== this.RedRowEditOriginalPartnerId;

      if (this.RedRowEditOfferIdFromSideOffer > 0) {
        // User selected a side offer → use that offer's ID
        requestCartItem.SelectedOffer.ID = this.RedRowEditOfferIdFromSideOffer;
        if (requestCartItem?.Product?.ListPriceOffers?.[0]) {
          requestCartItem.Product.ListPriceOffers[0].ID = this.RedRowEditOfferIdFromSideOffer;
        }
        console.log('💾 [POPUP] [FIX] Using side offer ID:', this.RedRowEditOfferIdFromSideOffer);
      } else if (supplierChanged) {
        // Supplier changed (manually typed or selected different partner) → create new offer
        requestCartItem.SelectedOffer.ID = 0;
        requestCartItem.SelectedOffer.Configuration = null;
        if (requestCartItem?.Product?.ListPriceOffers?.[0]) {
          requestCartItem.Product.ListPriceOffers[0].ID = 0;
        }
        console.log('💾 [POPUP] [FIX] Supplier changed, creating new offer (ID=0)');
      } else {
        // Supplier NOT changed → retain original offer ID
        requestCartItem.SelectedOffer.ID = this.RedRowEditOriginalOfferId;
        if (requestCartItem?.Product?.ListPriceOffers?.[0]) {
          requestCartItem.Product.ListPriceOffers[0].ID = this.RedRowEditOriginalOfferId;
        }
        console.log('💾 [POPUP] [FIX] Supplier unchanged, retaining offer ID:', this.RedRowEditOriginalOfferId);
      }
    }

    // ✅ Ensure Price object is properly set for backend
    if (this.useAdminGrid) {
      if (!requestCartItem.Product.Stats) requestCartItem.Product.Stats = {};
      if (!requestCartItem.Product.Stats.StandardListPrice) {
        requestCartItem.Product.Stats.StandardListPrice = {};
      }
      const finalPrice = requestCartItem.SelectedOffer?.SellPrice?.UIUnitPrice || 0;
      if (!requestCartItem.Price) {
        requestCartItem.Price = {};
      }
      requestCartItem.Price.UIUnitPrice = finalPrice;
      const quantity = requestCartItem.Quantity || 0;
      requestCartItem.Price.UIQtyPrice = Math.round(finalPrice * quantity * 100) / 100;
    }

    console.log('💾 [POPUP] Step 4: RequestCartItem FINAL before save:');
    console.log('💾 [POPUP] Step 4: - CartItemStatus.ID:', requestCartItem?.CartItemStatus?.ID);
    console.log('💾 [POPUP] Step 4: - Product.ID:', requestCartItem?.Product?.ID);
    console.log('💾 [POPUP] Step 4: - Product.Brand.ID:', requestCartItem?.Product?.Brand?.ID);
    console.log('💾 [POPUP] Step 4: - SelectedOffer.ID:', requestCartItem?.SelectedOffer?.ID);
    console.log('💾 [POPUP] Step 4: - SelectedOffer.SellPrice:', requestCartItem?.SelectedOffer?.SellPrice);
    console.log('💾 [POPUP] Step 4: - Product.ListPriceOffers:', requestCartItem?.Product?.ListPriceOffers);
    console.log('💾 [POPUP] Step 4: - Product.ListPriceOffers[0]:', requestCartItem?.Product?.ListPriceOffers?.[0]);
    console.log('💾 [POPUP] Step 4: - Product.ListPriceOffers[0].BoxStatus:', requestCartItem?.Product?.ListPriceOffers?.[0]?.BoxStatus);
    console.log('💾 [POPUP] Step 4: - Product.ListPriceOffers[0].ConditionStatus:', requestCartItem?.Product?.ListPriceOffers?.[0]?.ConditionStatus);
    console.log('💾 [POPUP] Step 4: - SelectedOffer.BoxStatus:', requestCartItem?.SelectedOffer?.BoxStatus);
    console.log('💾 [POPUP] Step 4: - SelectedOffer.ConditionStatus:', requestCartItem?.SelectedOffer?.ConditionStatus);
    console.log('💾 [POPUP] Step 4: Full requestCartItem:', JSON.parse(JSON.stringify(requestCartItem)));

    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      CartItem: requestCartItem
    };

    console.log('💾 [POPUP] Step 5: About to call B2BService.SaveAdminQuotationProduct');
    console.log('💾 [POPUP] Step 5: Full request:', JSON.parse(JSON.stringify(request)));

    this.B2BService.SaveAdminQuotationProduct(request).subscribe(response => {
      console.log('💾 [POPUP] Step 6: Response received from backend');
      console.log('💾 [POPUP] Step 6: Full response:', JSON.parse(JSON.stringify(response)));
      this.RedRowEditSaving = false;
      this.parent.loading = false;

      if (response["Status"] !== 1) {
        console.error('═══════════════════════════════════════════════════════════════');
        console.error('❌ [POPUP] [FAILED] SaveAdminQuotationProduct failed!');
        console.error('❌ [POPUP] [FAILED] Status:', response["Status"]);
        console.error('❌ [POPUP] [FAILED] Message:', response["Message"]);
        console.error('❌ [POPUP] [FAILED] Full response:', JSON.parse(JSON.stringify(response)));
        console.error('═══════════════════════════════════════════════════════════════');
        this.parent.openSnackBar(response["Message"], "OK", "alert-danger-snackbar");
        // ✅ Close popup even on error
        this.CloseRedRowEditPopup();
        return;
      }

      const updatedCartItem = response["CartItem"];
      if (!updatedCartItem) {
        console.error('❌ [POPUP] [ERROR] No CartItem in response');
        return;
      }

      console.log('✅ [POPUP] [SUCCESS] Product saved!');
      console.log('✅ [POPUP] [SUCCESS] - Product.ID:', updatedCartItem.Product?.ID);
      console.log('✅ [POPUP] [SUCCESS] - Product.Stats.ID:', updatedCartItem.Product?.Stats?.ID);
      console.log('✅ [POPUP] [SUCCESS] - SelectedOffer.ID:', updatedCartItem.SelectedOffer?.ID);
      console.log('✅ [POPUP] [SUCCESS] - CartItemStatus.ID:', updatedCartItem.CartItemStatus?.ID);

      // ✅ Use applyCartItemToRow for comprehensive grid update (updates all fields and triggers proper re-render)
      this.applyCartItemToRow(rowIndex, updatedCartItem);
      console.log('✅ [POPUP] Grid row updated at index:', rowIndex);

      // ✅ Update CartItemRows list
      const index = this.CartItemRows.findIndex((item: any) => item.UIID === updatedCartItem.UIID);
      if (index >= 0) {
        this.CartItemRows[index] = updatedCartItem;
        console.log('✅ [POPUP] Updated CartItemRows list at index:', index);
      }

      this.saveCartToLocalStorageOnly();
      this.parent.openSnackBar(response["Message"], "OK", "alert-success-snackbar");

      // ❌ REMOVED: RefreshOfferFromDatabase after save
      // The backend already returns the correct CartItem with updated IDs and preserved stock values.
      // Re-fetching via the B2B endpoint re-applies ManufacturerStock overwrite logic.

      // ✅ Close popup after successful save
      this.CloseRedRowEditPopup();
      console.log('═══════════════════════════════════════════════════════════════');
      console.log('💾 SaveRedRowEditAndSaveToDatabase COMPLETED SUCCESSFULLY');
      console.log('═══════════════════════════════════════════════════════════════');

    }, error => {
      this.RedRowEditSaving = false;
      this.parent.loading = false;
      console.error('═══════════════════════════════════════════════════════════════');
      console.error('❌ [POPUP] [HTTP ERROR] SaveAdminQuotationProduct error!');
      console.error('❌ [POPUP] [HTTP ERROR] Error object:', error);
      console.error('❌ [POPUP] [HTTP ERROR] Error message:', error?.message);
      console.error('❌ [POPUP] [HTTP ERROR] Error status:', error?.status);
      console.error('❌ [POPUP] [HTTP ERROR] Error body:', error?.error);
      console.error('❌ [POPUP] [HTTP ERROR] Validation errors:', JSON.stringify(error?.error?.errors, null, 2));
      console.error('═══════════════════════════════════════════════════════════════');
      const errorMessage = error?.error?.Message || error?.error?.title || 'Error saving product';
      this.parent.openSnackBar(errorMessage, "OK", "alert-danger-snackbar");
      // ✅ Close popup even on HTTP error
      this.CloseRedRowEditPopup();
    });

  } catch (e) {
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('❌ [POPUP] [EXCEPTION] SaveRedRowEditAndSaveToDatabase EXCEPTION:', e);
    console.error('═══════════════════════════════════════════════════════════════');
    this.RedRowEditSaving = false;
    this.parent.loading = false;
    this.parent.openSnackBar('Error in save: ' + (e as Error).message, "OK", "alert-danger-snackbar");
    // ✅ Close popup even on exception
    this.CloseRedRowEditPopup();
  }
}

/**
 * Apply computed fields and handle offer ID logic for the CartItem clone
 * All fields are now bound directly to RedRowEditCartItemClone in HTML
 * @param skipAdminEditGridCell - if true, skip calling AdminEditGridCell (used when SaveRedRowEditAndSaveToDatabase calls this)
 */
private applyRedRowEditToCartItem(cartItem: any, rowIndex: number, skipAdminEditGridCell: boolean = false): void {
  // ✅ Ensure Product and Stats objects exist
  if (!cartItem.Product) cartItem.Product = {};
  if (!cartItem.Product.Stats) cartItem.Product.Stats = {};
  if (!cartItem.SelectedOffer) cartItem.SelectedOffer = {};
  if (!cartItem.SelectedOffer.SellPrice) cartItem.SelectedOffer.SellPrice = {};

  // ✅ Ensure Currency is set for RED ROWS
  if (!cartItem.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ Sync UIProductValue and Product.Name bidirectionally
  // For RED ROWS: UIProductValue contains the typed name, copy it TO Product.Name
  // For VALID ROWS: Product.Name is from DB, copy it TO UIProductValue
  const isRedRowForName = cartItem?.CartItemStatus?.ID === 0;
  if (isRedRowForName) {
    // RED ROW: User typed name into UIProductValue → copy to Product.Name for backend
    if (cartItem.UIProductValue && !cartItem.Product.Name) {
      cartItem.Product.Name = cartItem.UIProductValue;
      console.log('🔴 [RED ROW] Copied UIProductValue to Product.Name:', cartItem.Product.Name);
    }
  } else {
    // VALID ROW: Product.Name from DB → copy to UIProductValue
    cartItem.UIProductValue = cartItem.Product.Name;
  }

  // ✅ Update Prices (only if admin grid) - sync derived values from popup-bound fields
  // Popup binds to:
  //   - List Price: CartItem.Product.Stats.StandardListPrice.UIUnitPrice
  //   - Discount %: CartItem.Product.Stats.StandardListPrice.Discount.Value
  //   - Final Price: CartItem.SelectedOffer.SellPrice.UIUnitPrice
  if (this.useAdminGrid) {
    if (!cartItem.Product.Stats.StandardListPrice) {
      cartItem.Product.Stats.StandardListPrice = {};
    }
    if (!cartItem.Product.Stats.StandardListPrice.Discount) {
      cartItem.Product.Stats.StandardListPrice.Discount = { Type: { ID: 1, Name: '%' }, Value: null };
    }
    // ✅ Read from the SAME paths the popup binds to
    const listPrice = cartItem.Product?.Stats?.StandardListPrice?.UIUnitPrice || 0;
    const discount = cartItem.Product?.Stats?.StandardListPrice?.Discount?.Value || 0;
    const finalPrice = cartItem.SelectedOffer?.SellPrice?.UIUnitPrice || 0;

    // ✅ Sync Price object for backend compatibility
    if (!cartItem.Price) {
      cartItem.Price = {};
    }
    cartItem.Price.UIUnitPrice = finalPrice;
    const quantity = cartItem.Quantity || cartItem.Qty || 0;
    cartItem.Price.UIQtyPrice = Math.round(finalPrice * quantity * 100) / 100;
  }

  // ✅ Handle Supplier (Partner) offer ID logic - ONLY set ID=0 when supplier changes
  if (cartItem.SelectedOffer?.Partner?.ID) {
    const currentPartnerId = this.RedRowEditCartItemClone?.SelectedOffer?.Partner?.ID || 0;
    const supplierChanged = currentPartnerId !== this.RedRowEditOriginalPartnerId;

    if (this.RedRowEditOfferIdFromSideOffer > 0) {
      // User selected a side offer → use that offer's ID
      cartItem.SelectedOffer.ID = this.RedRowEditOfferIdFromSideOffer;
      if (cartItem?.Product?.ListPriceOffers?.[0]) {
        cartItem.Product.ListPriceOffers[0].ID = this.RedRowEditOfferIdFromSideOffer;
      }
      console.log('✅ applyRedRowEditToCartItem - Using side offer ID:', this.RedRowEditOfferIdFromSideOffer);
    } else if (supplierChanged) {
      // Supplier changed (manually typed or selected different partner) → create new offer
      cartItem.SelectedOffer.ID = 0;
      cartItem.SelectedOffer.Configuration = null;
      if (cartItem?.Product?.ListPriceOffers?.[0]) {
        cartItem.Product.ListPriceOffers[0].ID = 0;
      }
      console.log('✅ applyRedRowEditToCartItem - Supplier changed, creating new offer (ID=0)');
    } else {
      // Supplier NOT changed → retain original offer ID
      cartItem.SelectedOffer.ID = this.RedRowEditOriginalOfferId;
      if (cartItem?.Product?.ListPriceOffers?.[0]) {
        cartItem.Product.ListPriceOffers[0].ID = this.RedRowEditOriginalOfferId;
      }
      console.log('✅ applyRedRowEditToCartItem - Supplier unchanged, retaining offer ID:', this.RedRowEditOriginalOfferId);
    }
  }

  // ✅ Handle Weight - extract Units and Type from Weight object to backend format
  // Backend expects: Product.Stats.Weight = decimal? (number), Product.Stats.WeightMeasurementType = object
  const weightObj = cartItem.Product?.Stats?.Weight;
  if (weightObj && typeof weightObj === 'object') {
    // Extract WeightMeasurementType first (before we overwrite Weight)
    if (weightObj.Type) {
      cartItem.Product.Stats.WeightMeasurementType = weightObj.Type;
    }
    // Extract Units as primitive number, or set to null if no Units
    cartItem.Product.Stats.Weight = (weightObj.Units != null) ? weightObj.Units : null;
    console.log('✅ Weight extracted: Weight =', cartItem.Product.Stats.Weight, ', WeightMeasurementType =', cartItem.Product.Stats.WeightMeasurementType?.Name);
  }

  // ✅ Update CartItem in parent.Cart.Items array
  // Use UIID for reliable lookup, fallback to rowIndex if UIID not found
  if (this.parent.Cart?.Items) {
    let cartIndex = -1;

    // Try to find by UIID first (most reliable)
    if (cartItem.UIID) {
      cartIndex = this.parent.Cart.Items.findIndex((item: any) => item.UIID === cartItem.UIID);
    }

    // Fallback to reference comparison
    if (cartIndex === -1) {
      cartIndex = this.parent.Cart.Items.findIndex((item: any) => item === cartItem);
    }

    // Fallback to rowIndex
    if (cartIndex === -1 && rowIndex >= 0 && rowIndex < this.parent.Cart.Items.length) {
      cartIndex = rowIndex;
    }

    if (cartIndex !== -1) {
      this.parent.Cart.Items[cartIndex] = cartItem;
      console.log('✅ Updated parent.Cart.Items at index:', cartIndex);
    } else {
      console.warn('⚠️ Could not find CartItem in parent.Cart.Items to update');
    }
  }

  // ✅ Save to IndexedDB
  this.saveCartToIDB();

  // ✅ Update grid row data
  if (this.gridApi) {
    const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
    if (rowNode) {
      if (!rowNode.data.CartItem) rowNode.data.CartItem = {};
      if (!rowNode.data.CartItem.Product) rowNode.data.CartItem.Product = {};
      if (!rowNode.data.CartItem.Product.Stats) rowNode.data.CartItem.Product.Stats = {};
      if (!rowNode.data.CartItem.SelectedOffer) rowNode.data.CartItem.SelectedOffer = {};

      rowNode.data.CartItem.Product.Brand = cartItem.Product.Brand;
      rowNode.data.CartItem.Product.Name = cartItem.Product.Name;
      rowNode.data.CartItem.Product.PartNumber = cartItem.Product.PartNumber;
      rowNode.data.CartItem.Product.Stats.HSCode = cartItem.Product.Stats.HSCode;
      rowNode.data.CartItem.Product.Stats.COO = cartItem.Product.Stats.COO;
      rowNode.data.CartItem.Product.Stats.Weight = cartItem.Product.Stats.Weight;
      rowNode.data.CartItem.Product.Stats.WeightMeasurementType = cartItem.Product.Stats.WeightMeasurementType;

      // ✅ Update LeadTime including UIOutput for ETACellRenderer
      if (cartItem.SelectedOffer.LeadTime) {
        rowNode.data.CartItem.SelectedOffer.LeadTime = {
          Type: cartItem.SelectedOffer.LeadTime.Type,
          Units: cartItem.SelectedOffer.LeadTime.Units,
          UIOutput: cartItem.SelectedOffer.LeadTime.UIOutput
        };
      } else {
        rowNode.data.CartItem.SelectedOffer.LeadTime = null;
      }

      // ✅ Update rowNode.data.eta for ETACellRenderer (uses params.data.eta)
      const leadTime = cartItem.SelectedOffer?.LeadTime;
      rowNode.data.eta = leadTime?.UIOutput ?? (leadTime?.Units > 0 ? `${leadTime.Units} ${leadTime.Type?.Name ?? ''}` : '');

      rowNode.data.CartItem.SelectedOffer.Partner = cartItem.SelectedOffer.Partner;
      rowNode.data.CartItem.SelectedOffer.BoxStatus = cartItem.SelectedOffer.BoxStatus;
      rowNode.data.CartItem.SelectedOffer.ConditionStatus = cartItem.SelectedOffer.ConditionStatus;

      // ✅ Update StandardListPrice for List-Price and Discount cell renderers
      if (!rowNode.data.CartItem.Product.Stats.StandardListPrice) {
        rowNode.data.CartItem.Product.Stats.StandardListPrice = {};
      }
      rowNode.data.CartItem.Product.Stats.StandardListPrice.UIUnitPrice = cartItem.Product?.Stats?.StandardListPrice?.UIUnitPrice;
      rowNode.data.CartItem.Product.Stats.StandardListPrice.Discount = cartItem.Product?.Stats?.StandardListPrice?.Discount;

      // ✅ Update SellPrice for Final Price cell renderer
      if (!rowNode.data.CartItem.SelectedOffer.SellPrice) {
        rowNode.data.CartItem.SelectedOffer.SellPrice = {};
      }
      rowNode.data.CartItem.SelectedOffer.SellPrice.UIUnitPrice = cartItem.SelectedOffer?.SellPrice?.UIUnitPrice;
      rowNode.data.CartItem.SelectedOffer.SellPrice.Currency = cartItem.SelectedOffer?.SellPrice?.Currency;

      rowNode.data.brand = cartItem.Product.Brand?.Name ?? '';
      rowNode.data.partNumber = cartItem.Product.PartNumber ?? '';
      rowNode.data.name = cartItem.Product.Name ?? '';
      rowNode.data.hscode = cartItem.Product.Stats.HSCode ?? '';
      rowNode.data.coo = cartItem.Product.Stats.COO?.ShortName ?? '';
      // ✅ Weight is now a primitive number after extraction - format for grid display
      const weightVal = cartItem.Product.Stats.Weight;
      const weightTypeName = cartItem.Product.Stats.WeightMeasurementType?.Name ?? '';
      rowNode.data.weight = weightVal != null ? `${weightVal} ${weightTypeName}`.trim() : '';

      // ✅ Update price and total fields used by priceCellRenderer (params.value)
      const finalPrice = cartItem.SelectedOffer?.SellPrice?.UIUnitPrice || 0;
      const quantity = cartItem.Quantity || cartItem.Qty || rowNode.data.quantity || 0;
      rowNode.data.price = finalPrice > 0 ? finalPrice : '';
      rowNode.data.total = finalPrice > 0 && quantity > 0 ? Math.round(finalPrice * quantity * 100) / 100 : '';

      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
    }
  }

  // ✅ Call AdminEditGridCell to update SaveToDB flag (skip if caller will handle it separately)
  // ⚠️ IMPORTANT: Skip AdminEditGridCell for RED ROWS (CartItemStatus.ID === 0) - they don't have valid Product IDs yet!
  //    Red rows should only be saved via "Save As New" button or PLUS button (SaveAdminQuotationProduct)
  const isRedRow = cartItem?.CartItemStatus?.ID === 0;
  if (!skipAdminEditGridCell && !isRedRow) {
    this.AdminEditGridCell(cartItem, rowIndex, 'price');
  } else if (isRedRow) {
    console.log('🔴 [RED ROW] Skipping AdminEditGridCell - Red rows must be saved via "Save As New" or PLUS button');
    // ✅ For red rows, just ensure SaveToDB flag is set so the PLUS button shows
    if (cartItem.CartItemStatus) {
      cartItem.CartItemStatus.SaveToDB = true;
      console.log('🔴 [RED ROW] Set SaveToDB = true on cartItem.CartItemStatus');
    }
    // ✅ Update the grid row to show the PLUS button
    if (this.gridApi) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
      if (rowNode) {
        console.log('🔴 [RED ROW] Found rowNode at index:', rowIndex);
        console.log('🔴 [RED ROW] Before update - rowNode.data.CartItem.CartItemStatus:', rowNode.data?.CartItem?.CartItemStatus);
        rowNode.data.CartItem.CartItemStatus = cartItem.CartItemStatus;
        console.log('🔴 [RED ROW] After update - rowNode.data.CartItem.CartItemStatus:', rowNode.data?.CartItem?.CartItemStatus);
        console.log('🔴 [RED ROW] SaveToDB value in grid:', rowNode.data?.CartItem?.CartItemStatus?.SaveToDB);
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
        console.log('🔴 [RED ROW] Grid cells refreshed - PLUS button should now be visible');
      } else {
        console.warn('🔴 [RED ROW] ⚠️ rowNode NOT FOUND at index:', rowIndex);
      }
    } else {
      console.warn('🔴 [RED ROW] ⚠️ gridApi not available');
    }
  }
}

/**
 * Close all Red Row Edit dropdowns (Lead Time dropdowns removed - now using smart autocomplete)
 */
closeAllRedRowEditDropdowns(): void {
  this.RedRowEditWeightTypeDropdownOpen = false;
  this.RedRowEditWeightUnitsDropdownOpen = false;
  this.RedRowEditBoxStatusDropdownOpen = false;
  this.RedRowEditConditionStatusDropdownOpen = false;
  this.RedRowEditCurrencyDropdownOpen = false;
  this.RedRowEditShowSupplierAlternatives = false;
  // ✅ NEW: Close Conditions tab dropdowns
  this.showRedRowEditManufacturingStatusDropdown = false;
  this.showRedRowEditVersionStatusDropdown = false;
  this.showRedRowEditOfferBoxStatusDropdown = false;
  this.showRedRowEditOfferConditionStatusDropdown = false;
}

/**
 * ✅ NEW: Toggle Manufacturing Status dropdown
 */
toggleRedRowEditManufacturingStatusDropdown(): void {
  this.showRedRowEditManufacturingStatusDropdown = !this.showRedRowEditManufacturingStatusDropdown;
  // Close other dropdowns
  this.showRedRowEditVersionStatusDropdown = false;
  this.showRedRowEditOfferBoxStatusDropdown = false;
  this.showRedRowEditOfferConditionStatusDropdown = false;
}

/**
 * ✅ NEW: Select Manufacturing Status
 */
selectRedRowEditManufacturingStatus(status: any): void {
  if (!this.RedRowEditCartItemClone?.Product) {
    this.RedRowEditCartItemClone.Product = {};
  }
  this.RedRowEditCartItemClone.Product.ManufacturingStatus = status;
  this.showRedRowEditManufacturingStatusDropdown = false;
  console.log('✅ Manufacturing Status selected:', status);
}

/**
 * ✅ NEW: Toggle Version Status dropdown
 */
toggleRedRowEditVersionStatusDropdown(): void {
  this.showRedRowEditVersionStatusDropdown = !this.showRedRowEditVersionStatusDropdown;
  // Close other dropdowns
  this.showRedRowEditManufacturingStatusDropdown = false;
  this.showRedRowEditOfferBoxStatusDropdown = false;
  this.showRedRowEditOfferConditionStatusDropdown = false;
}

/**
 * ✅ NEW: Select Version Status
 */
selectRedRowEditVersionStatus(status: any): void {
  if (!this.RedRowEditCartItemClone?.Product) {
    this.RedRowEditCartItemClone.Product = {};
  }
  this.RedRowEditCartItemClone.Product.VersionStatus = status;
  this.showRedRowEditVersionStatusDropdown = false;
  console.log('✅ Version Status selected:', status);
}

/**
 * ✅ NEW: Toggle Offer Box Status dropdown
 */
toggleRedRowEditOfferBoxStatusDropdown(): void {
  this.showRedRowEditOfferBoxStatusDropdown = !this.showRedRowEditOfferBoxStatusDropdown;
  // Close other dropdowns
  this.showRedRowEditManufacturingStatusDropdown = false;
  this.showRedRowEditVersionStatusDropdown = false;
  this.showRedRowEditOfferConditionStatusDropdown = false;
}

/**
 * ✅ NEW: Select Offer Box Status
 * Uses intermediate variable to avoid template binding issues with ListPriceOffers[0]
 * Syncs to BOTH ListPriceOffers[0] AND SelectedOffer for backend compatibility
 */
selectRedRowEditOfferBoxStatus(status: any): void {
  console.log('🔵 selectRedRowEditOfferBoxStatus CALLED with:', status);

  // Update intermediate variable (used by template)
  this.RedRowEditOfferBoxStatus = status;
  console.log('🔵 RedRowEditOfferBoxStatus set to:', this.RedRowEditOfferBoxStatus);

  // Sync to ListPriceOffers[0]
  if (!this.RedRowEditCartItemClone?.Product) {
    console.log('🔵 Creating Product object in clone');
    this.RedRowEditCartItemClone.Product = {};
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers) {
    console.log('🔵 Creating ListPriceOffers array in clone');
    this.RedRowEditCartItemClone.Product.ListPriceOffers = [{}];
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers[0]) {
    console.log('🔵 Creating ListPriceOffers[0] object in clone');
    this.RedRowEditCartItemClone.Product.ListPriceOffers[0] = {};
  }
  this.RedRowEditCartItemClone.Product.ListPriceOffers[0].BoxStatus = status;
  console.log('🔵 Clone ListPriceOffers[0].BoxStatus set to:', this.RedRowEditCartItemClone.Product.ListPriceOffers[0].BoxStatus);

  // ✅ ALSO sync to SelectedOffer (backend may read from here!)
  if (!this.RedRowEditCartItemClone.SelectedOffer) {
    console.log('🔵 Creating SelectedOffer object in clone');
    this.RedRowEditCartItemClone.SelectedOffer = {};
  }
  this.RedRowEditCartItemClone.SelectedOffer.BoxStatus = status;
  console.log('🔵 Clone SelectedOffer.BoxStatus set to:', this.RedRowEditCartItemClone.SelectedOffer.BoxStatus);

  this.showRedRowEditOfferBoxStatusDropdown = false;
}

/**
 * ✅ NEW: Toggle Offer Condition Status dropdown
 */
toggleRedRowEditOfferConditionStatusDropdown(): void {
  this.showRedRowEditOfferConditionStatusDropdown = !this.showRedRowEditOfferConditionStatusDropdown;
  // Close other dropdowns
  this.showRedRowEditManufacturingStatusDropdown = false;
  this.showRedRowEditVersionStatusDropdown = false;
  this.showRedRowEditOfferBoxStatusDropdown = false;
}

/**
 * ✅ NEW: Select Offer Condition Status
 * Uses intermediate variable to avoid template binding issues with ListPriceOffers[0]
 * Syncs to BOTH ListPriceOffers[0] AND SelectedOffer for backend compatibility
 */
selectRedRowEditOfferConditionStatus(status: any): void {
  console.log('🟣 selectRedRowEditOfferConditionStatus CALLED with:', status);

  // Update intermediate variable (used by template)
  this.RedRowEditOfferConditionStatus = status;
  console.log('🟣 RedRowEditOfferConditionStatus set to:', this.RedRowEditOfferConditionStatus);

  // Sync to ListPriceOffers[0]
  if (!this.RedRowEditCartItemClone?.Product) {
    console.log('🟣 Creating Product object in clone');
    this.RedRowEditCartItemClone.Product = {};
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers) {
    console.log('🟣 Creating ListPriceOffers array in clone');
    this.RedRowEditCartItemClone.Product.ListPriceOffers = [{}];
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers[0]) {
    console.log('🟣 Creating ListPriceOffers[0] object in clone');
    this.RedRowEditCartItemClone.Product.ListPriceOffers[0] = {};
  }
  this.RedRowEditCartItemClone.Product.ListPriceOffers[0].ConditionStatus = status;
  console.log('🟣 Clone ListPriceOffers[0].ConditionStatus set to:', this.RedRowEditCartItemClone.Product.ListPriceOffers[0].ConditionStatus);

  // ✅ ALSO sync to SelectedOffer (backend may read from here!)
  if (!this.RedRowEditCartItemClone.SelectedOffer) {
    console.log('🟣 Creating SelectedOffer object in clone');
    this.RedRowEditCartItemClone.SelectedOffer = {};
  }
  this.RedRowEditCartItemClone.SelectedOffer.ConditionStatus = status;
  console.log('🟣 Clone SelectedOffer.ConditionStatus set to:', this.RedRowEditCartItemClone.SelectedOffer.ConditionStatus);

  this.showRedRowEditOfferConditionStatusDropdown = false;
}

/**
 * ✅ Sync intermediate Offer status variables back to RedRowEditCartItemClone
 * Called before save to ensure BoxStatus and ConditionStatus are properly stored
 * NOTE: We sync to BOTH ListPriceOffers[0] AND SelectedOffer because the backend
 * may read from either location depending on the save path
 */
syncOfferStatusesToClone(): void {
  if (!this.RedRowEditCartItemClone) {
    console.log('⚠️ syncOfferStatusesToClone: No clone to sync to');
    return;
  }

  // Ensure Product and ListPriceOffers exist
  if (!this.RedRowEditCartItemClone.Product) {
    this.RedRowEditCartItemClone.Product = {};
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers) {
    this.RedRowEditCartItemClone.Product.ListPriceOffers = [{}];
  }
  if (!this.RedRowEditCartItemClone.Product.ListPriceOffers[0]) {
    this.RedRowEditCartItemClone.Product.ListPriceOffers[0] = {};
  }

  // Ensure SelectedOffer exists
  if (!this.RedRowEditCartItemClone.SelectedOffer) {
    this.RedRowEditCartItemClone.SelectedOffer = {};
  }

  // Sync BoxStatus from intermediate variable to BOTH locations
  if (this.RedRowEditOfferBoxStatus) {
    this.RedRowEditCartItemClone.Product.ListPriceOffers[0].BoxStatus = this.RedRowEditOfferBoxStatus;
    this.RedRowEditCartItemClone.SelectedOffer.BoxStatus = this.RedRowEditOfferBoxStatus;
    console.log('✅ syncOfferStatusesToClone: BoxStatus synced to ListPriceOffers[0] AND SelectedOffer:', this.RedRowEditOfferBoxStatus);
  }

  // Sync ConditionStatus from intermediate variable to BOTH locations
  if (this.RedRowEditOfferConditionStatus) {
    this.RedRowEditCartItemClone.Product.ListPriceOffers[0].ConditionStatus = this.RedRowEditOfferConditionStatus;
    this.RedRowEditCartItemClone.SelectedOffer.ConditionStatus = this.RedRowEditOfferConditionStatus;
    console.log('✅ syncOfferStatusesToClone: ConditionStatus synced to ListPriceOffers[0] AND SelectedOffer:', this.RedRowEditOfferConditionStatus);
  }

  console.log('✅ syncOfferStatusesToClone: ListPriceOffers[0] after sync:', this.RedRowEditCartItemClone.Product.ListPriceOffers[0]);
  console.log('✅ syncOfferStatusesToClone: SelectedOffer after sync:', this.RedRowEditCartItemClone.SelectedOffer);
}

/**
 * ✅ Handle TrustedManufacturerLeadTime toggle change
 * Just updates local state - save happens on OK button click
 */
onTrustedManufacturerLeadTimeToggle(newValue: boolean): void {
  console.log('🔄 TrustedManufacturerLeadTime toggled to:', newValue);
  this.RedRowEditTrustedManufacturerLeadTime = newValue;
}

/**
 * Close Red Row Edit Popup
 */
CloseRedRowEditPopup(): void {
  this.ShowRedRowEditPopup = false;
  this.CurrentRedRowEditCartItem = null;
  this.CurrentRedRowEditRowIndex = -1;

  // ✅ Clear the clone to prevent lingering references and state corruption
  this.RedRowEditCartItemClone = null;
  this.RedRowEditOriginalOfferId = 0;
  this.RedRowEditOriginalPartnerId = 0;
  this.RedRowEditOfferIdFromSideOffer = 0;

  // Clear all search terms and results
  this.RedRowEditBrandSearchTerm = '';
  this.RedRowEditBrandSearchResults = [];
  this.RedRowEditPartnerSearchTerm = '';
  this.RedRowEditPartnerSearchResults = [];
  this.RedRowEditCOOSearchTerm = '';
  this.RedRowEditCOOSearchResults = [];

  // ✅ Clear supplier alternatives
  this.RedRowEditSideOffers = [];
  this.RedRowEditLoadingSideOffers = false;
  this.RedRowEditShowSupplierAlternatives = false;

  // Close all dropdowns
  this.closeAllRedRowEditDropdowns();
}

/**
 * Open the Delete Offer confirmation popup (stacked over Red Row Edit popup)
 */
OpenDeleteOfferConfirmation(): void {
  if (!this.RedRowEditCartItemClone?.SelectedOffer?.ID || this.RedRowEditCartItemClone.SelectedOffer.ID <= 0) {
    console.warn('Cannot delete offer: No valid offer ID');
    return;
  }
  this.ShowDeleteOfferConfirmation = true;
}

/**
 * Cancel delete offer - close only the confirmation popup
 */
CancelDeleteOffer(): void {
  this.ShowDeleteOfferConfirmation = false;
}

/**
 * Confirm delete offer - call backend and close both popups on success
 */
ConfirmDeleteOffer(): void {
  const offerId = this.RedRowEditCartItemClone?.SelectedOffer?.ID;
  if (!offerId || offerId <= 0) {
    console.warn('Cannot delete offer: No valid offer ID');
    this.ShowDeleteOfferConfirmation = false;
    return;
  }

  this.RedRowEditDeletingOffer = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    OfferID: offerId
  };

  this.managementService.DeleteIndustryProductListPriceOffer(request).subscribe(
    (response: any) => {
      this.RedRowEditDeletingOffer = false;

      if (response?.Status === 1) {
        // ✅ Success - close both popups
        this.ShowDeleteOfferConfirmation = false;
        this.CloseRedRowEditPopup();
        this.parent.openSnackBar(response?.Message || 'Offer deleted successfully', 'OK', 'alert-success-snackbar');

        // ✅ Refresh the grid to reflect the deleted offer
        if (this.gridApi) {
          this.gridApi.refreshCells({ force: true });
        }
      } else {
        // ✅ Error from backend - show message, keep confirmation popup closed but Red Row Edit open
        this.ShowDeleteOfferConfirmation = false;
        this.parent.openSnackBar(response?.Message || 'Error deleting offer', 'OK', 'alert-danger-snackbar');
      }
    },
    (error: any) => {
      this.RedRowEditDeletingOffer = false;
      this.ShowDeleteOfferConfirmation = false;
      console.error('Error deleting offer:', error);
      this.parent.openSnackBar(error?.error?.Message || 'Error deleting offer', 'OK', 'alert-danger-snackbar');
    }
  );
}

/**
 * Show currency dropdown when clicking on currency search bar
 */
onCurrencySearchFocus() {
  this.showCurrencyResults = true;
  this.filteredCurrencies = [...this.currencies];
}

/**
 * Select a currency from the dropdown
 */
selectCurrency(currency: any) {
  console.log('Selected currency:', currency);

  this.selectedCurrency = currency;
  this.currencySearchTerm = `${currency.Symbol} - ${currency.Name}`;
  this.showCurrencyResults = false;

  // ✅ Update parent.Cart.Currency so GridTotals shows the new currency
  if (!this.parent.Cart) {
    this.parent.Cart = { Items: [], ShippingCost: { UIUnitPrice: null } };
  }
  this.parent.Cart.Currency = currency;

  // ✅ Update column headers with new currency symbol
  this.updateColumnHeaders();

  // ✅ Call AdminGridCurrencyRowsChange to recalculate prices in new currency
  this.AdminGridCurrencyRowsChange();

  console.log(`✅ Currency selected: ${currency.Symbol} - ${currency.Name}`);
}

/**
 * Call backend to recalculate all cart items with new currency
 */
async AdminGridCurrencyRowsChange() {
  // ✅ Get AdminCart from IndexedDB
  const adminCart = await this.loadCartFromIDB();

  const cartItems = adminCart?.Items ?? [];

  if (cartItems.length === 0) {
    console.log('⚠️ No AdminCart items to recalculate');
    return;
  }

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItems: cartItems,  // ✅ Send AdminCart.Items
    SelectedUICurrency: this.selectedCurrency  // ✅ Send selected currency from dropdown
  };

  this.parent.loading = true;

  this.B2BService.AdminGridCurrencyRowsChange(request).subscribe(response => {
    if (response["Status"] === 1) {
      const updatedItems = response["CartItems"];

      if (updatedItems && Array.isArray(updatedItems)) {
        // ✅ Update AdminCart with recalculated items
        adminCart.Items = updatedItems;
        adminCart.Currency = this.selectedCurrency;

        // ✅ Save AdminCart back to IndexedDB
        this.parent.Cart = adminCart;
        this.saveCartToIDB();

        // ✅ Re-populate grid with updated data
        this.GetCart();

        console.log(`✅ AdminCart recalculated for currency: ${this.selectedCurrency?.Symbol}`);
      }
    } else {
      this.parent.openSnackBar(response["Message"], "OK", "alert-info-snackbar");
    }

    this.parent.loading = false;
  });
}

/**
 * Update column headers with current currency symbol
 */
updateColumnHeaders() {
  if (this.gridApi && this.columnApi) {
    const listPriceCol = this.columnApi.getColumn('officialListPrice');
    const finalPriceCol = this.columnApi.getColumn('price');
    const totalCol = this.columnApi.getColumn('total');
    const purchaseCol = this.columnApi.getColumn('purchase');

    if (listPriceCol) {
      const colDef = listPriceCol.getColDef();
      colDef.headerName = this.getListPriceHeader();
    }

    if (finalPriceCol) {
      const colDef = finalPriceCol.getColDef();
      colDef.headerName = this.getFinalPriceHeader();
    }

    if (totalCol) {
      const colDef = totalCol.getColDef();
      colDef.headerName = this.getTotalHeader();
    }

    if (purchaseCol) {
      const colDef = purchaseCol.getColDef();
      colDef.headerName = this.getPurchaseHeader();
    }

    // Refresh headers
    this.gridApi.refreshHeader();

    // Re-inject header checkbox after refresh
    setTimeout(() => this.injectHeaderCheckbox(), 50);

    console.log('✅ Column headers updated with currency:', this.selectedCurrency?.Symbol);
  } else {
    console.log('⚠️ Cannot update column headers - grid not ready yet');
  }
}

/**
 * ✅ Save Admin Quotation Product - called on double-click of floppy disk icon
 * Sends CartItem to backend, gets back updated product with IDs, updates grid
 */
SaveAdminQuotationProduct(cartItem: any, rowIndex: number): void {
  console.log('═══════════════════════════════════════════════════════════════');
  console.log('💾 SaveAdminQuotationProduct STARTED for row:', rowIndex);
  console.log('═══════════════════════════════════════════════════════════════');

  // ✅ DEBUG: Log the full CartItem structure
  console.log('💾 [DEBUG] Full CartItem:', JSON.parse(JSON.stringify(cartItem || {})));
  console.log('💾 [DEBUG] CartItemStatus:', cartItem?.CartItemStatus);
  console.log('💾 [DEBUG] CartItemStatus.ID:', cartItem?.CartItemStatus?.ID);
  console.log('💾 [DEBUG] CartItemStatus.SaveToDB:', cartItem?.CartItemStatus?.SaveToDB);
  console.log('💾 [DEBUG] Product:', cartItem?.Product);
  console.log('💾 [DEBUG] Product.ID:', cartItem?.Product?.ID);
  console.log('💾 [DEBUG] Product.Name:', cartItem?.Product?.Name);
  console.log('💾 [DEBUG] Product.Brand:', cartItem?.Product?.Brand);
  console.log('💾 [DEBUG] Product.PartNumber:', cartItem?.Product?.PartNumber);
  console.log('💾 [DEBUG] UIProductValue:', cartItem?.UIProductValue);
  console.log('💾 [DEBUG] SelectedOffer:', cartItem?.SelectedOffer);
  console.log('💾 [DEBUG] SelectedOffer.ID:', cartItem?.SelectedOffer?.ID);
  console.log('💾 [DEBUG] SelectedOffer.SellPrice:', cartItem?.SelectedOffer?.SellPrice);
  console.log('💾 [DEBUG] SelectedOffer.Partner:', cartItem?.SelectedOffer?.Partner);

  if (!cartItem) {
    console.error('❌ [BLOCKED] No CartItem to save - cartItem is null/undefined');
    return;
  }

  // ✅ Ensure Product object exists
  if (!cartItem.Product) {
    console.log('💾 [FIX] Creating empty Product object');
    cartItem.Product = {};
  }

  // ✅ CRITICAL: For RED ROWS, copy UIProductValue to Product.Name (backend needs Product.Name!)
  const isRedRow = cartItem?.CartItemStatus?.ID === 0;
  if (isRedRow && cartItem.UIProductValue && !cartItem.Product.Name) {
    console.log('💾 [FIX] RED ROW: Copying UIProductValue to Product.Name:', cartItem.UIProductValue);
    cartItem.Product.Name = cartItem.UIProductValue;
  }

  // ✅ FIX: Sanitize Product.Stats to ensure Weight is a number (not a corrupted object)
  if (cartItem.Product?.Stats) {
    const stats = cartItem.Product.Stats;
    // Check if Weight is an object with Units/Type/UIOutput (Red Row popup format) - extract the primitive value
    if (stats.Weight !== null && stats.Weight !== undefined && typeof stats.Weight === 'object') {
      console.log('💾 [FIX] Stats.Weight is object, extracting Units value:', stats.Weight);
      // ✅ Extract the Units value from the object
      if (stats.Weight.Units !== null && stats.Weight.Units !== undefined) {
        const weightUnits = stats.Weight.Units;
        const weightType = stats.Weight.Type;
        stats.Weight = weightUnits;  // Replace object with primitive number
        if (weightType) {
          stats.WeightMeasurementType = weightType;
        }
        console.log('💾 [FIX] Stats.Weight extracted to:', stats.Weight, 'Type:', stats.WeightMeasurementType?.Name);
      } else {
        // No Units value - set to null
        stats.Weight = null;
        console.log('💾 [FIX] Stats.Weight had no Units, set to null');
      }
    }
    // Remove any circular reference properties that shouldn't exist in Stats
    delete stats.CartItem;
    delete stats.Stats;

    // ✅ FIX: If COO.ID is 0 or invalid, set COO to null (FK constraint on tblCountries)
    if (stats.COO && (!stats.COO.ID || stats.COO.ID === 0)) {
      console.log('💾 [FIX] COO.ID is 0 or invalid, setting COO to null');
      stats.COO = null;
    }
  }

  // ✅ Ensure SelectedOffer exists
  if (!cartItem.SelectedOffer) {
    console.log('💾 [FIX] Creating empty SelectedOffer object');
    cartItem.SelectedOffer = {};
  }

  // ✅ Ensure Currency is set on SelectedOffer (backend reads from here!)
  if (!cartItem.SelectedOffer.Currency && this.selectedCurrency) {
    console.log('💾 [FIX] Setting SelectedOffer.Currency from selectedCurrency:', this.selectedCurrency);
    cartItem.SelectedOffer.Currency = this.selectedCurrency;
  }

  // ✅ Ensure PurchaseCurrency is set on SelectedOffer (defaults to Currency if not set)
  if (!cartItem.SelectedOffer.PurchaseCurrency) {
    console.log('💾 [FIX] Setting SelectedOffer.PurchaseCurrency');
    cartItem.SelectedOffer.PurchaseCurrency = cartItem.SelectedOffer.Currency || this.selectedCurrency;
  }

  // ✅ Also ensure SellPrice.Currency is set
  if (!cartItem.SelectedOffer.SellPrice) {
    console.log('💾 [FIX] Creating empty SelectedOffer.SellPrice object');
    cartItem.SelectedOffer.SellPrice = {};
  }
  if (!cartItem.SelectedOffer.SellPrice.Currency && this.selectedCurrency) {
    console.log('💾 [FIX] Setting SelectedOffer.SellPrice.Currency');
    cartItem.SelectedOffer.SellPrice.Currency = this.selectedCurrency;
  }

  // ✅ If SelectedOffer.ID is 0 (supplier was changed), also reset ListPriceOffers[0].ID to 0
  if (cartItem.SelectedOffer?.ID === 0 || !cartItem.SelectedOffer?.ID) {
    console.log('💾 [FIX] Clearing Configuration for new offer (SelectedOffer.ID is 0 or null)');
    cartItem.SelectedOffer.Configuration = null;  // ✅ Clear Configuration when creating new offer
  }

  console.log('💾 [PREPARED] CartItem before API call:');
  console.log('💾 [PREPARED] - SelectedOffer.ID:', cartItem.SelectedOffer?.ID);
  console.log('💾 [PREPARED] - SelectedOffer.Currency:', cartItem.SelectedOffer?.Currency);
  console.log('💾 [PREPARED] - SelectedOffer.SellPrice:', cartItem.SelectedOffer?.SellPrice);
  console.log('💾 [PREPARED] - ListPriceOffers[0].ID:', cartItem?.Product?.ListPriceOffers?.[0]?.ID);
  console.log('💾 [PREPARED] - Product.Brand.ID:', cartItem?.Product?.Brand?.ID);

  this.parent.loading = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItem: cartItem
  };

  console.log('💾 [REQUEST] Sending to backend:', JSON.parse(JSON.stringify(request)));

  this.B2BService.SaveAdminQuotationProduct(request).subscribe(response => {
    this.parent.loading = false;
    console.log('💾 [RESPONSE] Received from backend:', response);

    if (response["Status"] !== 1) {
      console.error('❌ [FAILED] SaveAdminQuotationProduct failed!');
      console.error('❌ [FAILED] Status:', response["Status"]);
      console.error('❌ [FAILED] Message:', response["Message"]);
      console.error('❌ [FAILED] Full response:', JSON.parse(JSON.stringify(response)));
      this.parent.openSnackBar(response["Message"], "OK", "alert-danger-snackbar");
      return;
    }

    const updatedCartItem = response["CartItem"];
    if (!updatedCartItem) {
      console.error('❌ [ERROR] No CartItem in response');
      return;
    }

    console.log('✅ [SUCCESS] Product saved!');
    console.log('✅ [SUCCESS] - Product.ID:', updatedCartItem.Product?.ID);
    console.log('✅ [SUCCESS] - Product.Stats.ID:', updatedCartItem.Product?.Stats?.ID);
    console.log('✅ [SUCCESS] - SelectedOffer.ID:', updatedCartItem.SelectedOffer?.ID);
    console.log('✅ [SUCCESS] - CartItemStatus.ID:', updatedCartItem.CartItemStatus?.ID);

    // ✅ Update grid row
    const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);
    if (rowNode) {
      console.log('💾 [GRID] Updating row node at index:', rowIndex);
      console.log('💾 [GRID] CartItemStatus BEFORE:', rowNode.data.CartItem?.CartItemStatus?.ID);

      // ✅ Update CartItem with all backend data (including CartItemStatus)
      rowNode.data.CartItem = updatedCartItem;

      console.log('💾 [GRID] CartItemStatus AFTER:', updatedCartItem?.CartItemStatus?.ID);

      const product = updatedCartItem?.Product ?? {};
      const stats = product?.Stats ?? {};

      rowNode.data.brand = product?.Brand?.Name ?? '';
      rowNode.data.partNumber = product?.PartNumber ?? '';
      rowNode.data.name = product?.Name ?? '';
      rowNode.data.supplier = updatedCartItem?.SelectedOffer?.Configuration?.Partner?.Name ?? '';
      rowNode.data.purchase = updatedCartItem?.SelectedOffer?.ListPrice ?? '';
      // ✅ Weight with measurement type
      rowNode.data.weight = stats?.Weight != null
        ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}`
        : '';
      rowNode.data.hscode = stats?.HSCode ?? '';
      // ✅ COO can be either COO object or CountryOfOrigin
      rowNode.data.coo = stats?.COO?.ShortName ?? stats?.CountryOfOrigin?.Name ?? '';

      // ✅ Refresh cells to update displayed values
      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

      // ✅ CRITICAL: Redraw rows to re-evaluate rowClassRules (removes red highlighting)
      this.gridApi.redrawRows({ rowNodes: [rowNode] });

      console.log('✅ [GRID] Grid row updated and redrawn successfully');
    } else {
      console.warn('⚠️ [GRID] Row node not found at index:', rowIndex);
    }

    // ✅ Update CartItemRows list
    const index = this.CartItemRows.findIndex(item => item.UIID === updatedCartItem.UIID);
    if (index >= 0) {
      this.CartItemRows[index] = updatedCartItem;
      console.log('✅ [CART] Updated CartItemRows list at index:', index);
    }

    this.saveCartToLocalStorageOnly();
    this.parent.openSnackBar(response["Message"], "OK", "alert-success-snackbar");

    // ❌ REMOVED: RefreshOfferFromDatabase after save
    // The backend already returns the correct CartItem with updated IDs and preserved stock values.
    // Re-fetching via the B2B endpoint re-applies ManufacturerStock overwrite logic (ConvertGridDBRowToCartItem),
    // which incorrectly overwrites the admin-set stock values.

    console.log('═══════════════════════════════════════════════════════════════');
    console.log('💾 SaveAdminQuotationProduct COMPLETED SUCCESSFULLY');
    console.log('═══════════════════════════════════════════════════════════════');
  }, error => {
    this.parent.loading = false;
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('❌ [HTTP ERROR] SaveAdminQuotationProduct HTTP error!');
    console.error('❌ [HTTP ERROR] Error object:', error);
    console.error('❌ [HTTP ERROR] Error message:', error?.message);
    console.error('❌ [HTTP ERROR] Error status:', error?.status);
    console.error('❌ [HTTP ERROR] Error body:', error?.error);
    console.error('═══════════════════════════════════════════════════════════════');
    this.parent.openSnackBar(error?.error?.Message || 'Error saving product', "OK", "alert-danger-snackbar");
  });
}

/**
 * ✅ BULK Save Admin Quotation Products - saves multiple cart items in one API call
 * Called from Ctrl+S handler to save all items with SaveToDB flag enabled
 */
BulkSaveAdminQuotationProducts(itemsToSave: { cartItem: any, rowIndex: number }[]): void {
  console.log('═══════════════════════════════════════════════════════════════');
  console.log(`💾 BulkSaveAdminQuotationProducts STARTED for ${itemsToSave.length} items`);
  console.log('═══════════════════════════════════════════════════════════════');

  if (!itemsToSave || itemsToSave.length === 0) {
    console.error('❌ [BLOCKED] No items to save');
    return;
  }

  // ✅ Prepare all cart items for bulk save (apply same sanitization as SaveAdminQuotationProduct)
  const cartItemsToSave = itemsToSave.map(item => {
    const ci = JSON.parse(JSON.stringify(item.cartItem));

    // ✅ For RED ROWS, copy UIProductValue to Product.Name
    if (ci?.CartItemStatus?.ID === 0 && ci.UIProductValue && !ci.Product?.Name) {
      if (!ci.Product) ci.Product = {};
      ci.Product.Name = ci.UIProductValue;
    }

    // ✅ Sanitize Weight (object → primitive)
    if (ci.Product?.Stats?.Weight != null && typeof ci.Product.Stats.Weight === 'object') {
      const wu = ci.Product.Stats.Weight.Units;
      const wt = ci.Product.Stats.Weight.Type;
      ci.Product.Stats.Weight = (wu != null) ? wu : null;
      if (wt) ci.Product.Stats.WeightMeasurementType = wt;
    }

    // ✅ COO.ID 0 → null
    if (ci.Product?.Stats?.COO && (!ci.Product.Stats.COO.ID || ci.Product.Stats.COO.ID === 0)) {
      ci.Product.Stats.COO = null;
    }

    // ✅ Ensure SelectedOffer exists with Currency
    if (!ci.SelectedOffer) ci.SelectedOffer = {};
    if (!ci.SelectedOffer.Currency && this.selectedCurrency) ci.SelectedOffer.Currency = this.selectedCurrency;
    if (!ci.SelectedOffer.PurchaseCurrency) ci.SelectedOffer.PurchaseCurrency = ci.SelectedOffer.Currency || this.selectedCurrency;
    if (!ci.SelectedOffer.SellPrice) ci.SelectedOffer.SellPrice = {};
    if (!ci.SelectedOffer.SellPrice.Currency && this.selectedCurrency) ci.SelectedOffer.SellPrice.Currency = this.selectedCurrency;

    // ✅ Clear Configuration for new offers
    if (ci.SelectedOffer?.ID === 0 || !ci.SelectedOffer?.ID) {
      ci.SelectedOffer.Configuration = null;
    }

    return ci;
  });

  this.parent.loading = true;

  const request = {
    User: this.parent.User,
    Language: this.parent.getLanguage(),
    CartItems: cartItemsToSave
  };

  console.log('💾 [BULK REQUEST] Sending to backend:', JSON.parse(JSON.stringify(request)));

  this.B2BService.BulkSaveAdminQuotationProducts(request).subscribe(response => {
    this.parent.loading = false;
    console.log('💾 [BULK RESPONSE] Received from backend:', response);

    if (response["Status"] !== 1) {
      console.error('❌ [BULK FAILED] BulkSaveAdminQuotationProducts failed!');
      console.error('❌ [BULK FAILED] Status:', response["Status"]);
      console.error('❌ [BULK FAILED] Message:', response["Message"]);
      this.parent.openSnackBar(response["Message"], "OK", "alert-danger-snackbar");
      return;
    }

    const updatedCartItems = response["CartItems"];
    const updatedCount = response["UpdatedCount"];

    if (!updatedCartItems || updatedCartItems.length === 0) {
      console.error('❌ [BULK ERROR] No CartItems in response');
      return;
    }

    console.log(`✅ [BULK SUCCESS] ${updatedCount} products saved!`);

    // ✅ Create UIID-to-rowIndex lookup map from original items
    const uiidToRowIndexMap = new Map<string, number>();
    itemsToSave.forEach(item => {
      if (item.cartItem?.UIID) {
        uiidToRowIndexMap.set(item.cartItem.UIID, item.rowIndex);
      }
    });

    // ✅ Update all grid rows with returned cart items using UIID matching
    updatedCartItems.forEach((updatedCartItem: any) => {
      if (!updatedCartItem?.UIID) {
        console.warn('⚠️ [BULK GRID] CartItem missing UIID, skipping');
        return;
      }

      // ✅ Find the original rowIndex using UIID
      const rowIndex = uiidToRowIndexMap.get(updatedCartItem.UIID);
      if (rowIndex === undefined) {
        console.warn(`⚠️ [BULK GRID] No matching rowIndex found for UIID: ${updatedCartItem.UIID}`);
        return;
      }

      const rowNode = this.gridApi?.getDisplayedRowAtIndex(rowIndex);

      if (rowNode) {
        console.log(`💾 [BULK GRID] Updating row node at index: ${rowIndex} (UIID: ${updatedCartItem.UIID})`);
        console.log(`💾 [BULK GRID] CartItemStatus BEFORE: ${rowNode.data.CartItem?.CartItemStatus?.ID}`);

        // ✅ Update CartItem with all backend data (including CartItemStatus)
        rowNode.data.CartItem = updatedCartItem;

        console.log(`💾 [BULK GRID] CartItemStatus AFTER: ${updatedCartItem?.CartItemStatus?.ID}`);

        const product = updatedCartItem?.Product ?? {};
        const stats = product?.Stats ?? {};

        rowNode.data.brand = product?.Brand?.Name ?? '';
        rowNode.data.partNumber = product?.PartNumber ?? '';
        rowNode.data.name = product?.Name ?? '';
        rowNode.data.supplier = updatedCartItem?.SelectedOffer?.Configuration?.Partner?.Name ?? '';
        rowNode.data.purchase = updatedCartItem?.SelectedOffer?.ListPrice ?? '';
        // ✅ Weight with measurement type (same as single-item save)
        rowNode.data.weight = stats?.Weight != null
          ? `${stats.Weight} ${stats.WeightMeasurementType?.Name ?? ''}`
          : '';
        rowNode.data.hscode = stats?.HSCode ?? '';
        // ✅ COO can be either COO object or CountryOfOrigin (same as single-item save)
        rowNode.data.coo = stats?.COO?.ShortName ?? stats?.CountryOfOrigin?.Name ?? '';

        // ✅ Refresh cells to update displayed values
        this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });

        // ✅ CRITICAL: Redraw rows to re-evaluate rowClassRules (removes red highlighting)
        this.gridApi.redrawRows({ rowNodes: [rowNode] });

        console.log(`✅ [BULK GRID] Grid row updated and redrawn for UIID: ${updatedCartItem.UIID}`);

        // ❌ REMOVED: RefreshOfferFromDatabase after bulk save
        // The backend already returns correct CartItems with updated IDs and preserved stock values.
        // Re-fetching via B2B endpoint re-applies ManufacturerStock overwrite logic.
      } else {
        console.warn(`⚠️ [BULK GRID] Row node not found at index: ${rowIndex}`);
      }

      // ✅ Update CartItemRows list using UIID
      const cartItemRowIndex = this.CartItemRows.findIndex((item: any) => item.UIID === updatedCartItem.UIID);
      if (cartItemRowIndex >= 0) {
        this.CartItemRows[cartItemRowIndex] = updatedCartItem;
        console.log(`✅ [BULK CART] Updated CartItemRows list for UIID: ${updatedCartItem.UIID}`);
      }
    });

    // ✅ Save to localStorage
    this.saveCartToLocalStorageOnly();
    this.parent.openSnackBar(`Successfully saved ${updatedCount} product(s)`, "OK", "alert-success-snackbar");

    console.log('═══════════════════════════════════════════════════════════════');
    console.log(`💾 BulkSaveAdminQuotationProducts COMPLETED SUCCESSFULLY - ${updatedCount} items saved`);
    console.log('═══════════════════════════════════════════════════════════════');
  }, error => {
    this.parent.loading = false;
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('❌ [BULK HTTP ERROR] BulkSaveAdminQuotationProducts HTTP error!');
    console.error('❌ [BULK HTTP ERROR] Error object:', error);
    console.error('❌ [BULK HTTP ERROR] Error message:', error?.message);
    console.error('❌ [BULK HTTP ERROR] Error status:', error?.status);
    console.error('❌ [BULK HTTP ERROR] Error body:', error?.error);
    console.error('═══════════════════════════════════════════════════════════════');
    this.parent.openSnackBar(error?.error?.Message || 'Error saving products', "OK", "alert-danger-snackbar");
  });
}

// ✅ Add New Partner Popup Methods
OpenAddNewPartnerPopup() {
  this.NewPartnerName = this.partnerSearchTerm || ''; // ✅ Pre-fill with search term
  this.NewPartnerVAT = '';
  this.NewPartnerAddressLine = '';
  this.NewPartnerEmail = '';  // ✅ Reset email field
  this.NewPartnerSelectedCountry = null;
  this.NewPartnerCountrySearchTerm = '';
  this.NewPartnerFilteredCountries = []; // ✅ Don't show results until user clicks the field
  this.ShowAddNewPartnerPopup = true;

  // ✅ Pre-load countries if not already loaded (but don't show dropdown yet)
  if (!this.PartnerCountries || this.PartnerCountries.length === 0) {
    const request = {
      User: this.parent.User,
      Language: this.parent.getLanguage()
    };
    this.service.GetMasterData(request).subscribe((response: any) => {
      if (response["Status"] === 1) {
        this.PartnerCountries = response["Countries"] || [];
        console.log('✅ Countries loaded for Add New Partner popup:', this.PartnerCountries.length);
      }
    });
  }
}

CloseAddNewPartnerPopup() {
  this.ShowAddNewPartnerPopup = false;
  this.NewPartnerName = '';
  this.NewPartnerVAT = '';
  this.NewPartnerAddressLine = '';
  this.NewPartnerEmail = '';  // ✅ Reset email field
  this.NewPartnerSelectedCountry = null;
  this.NewPartnerCountrySearchTerm = '';
  this.NewPartnerFilteredCountries = [];
}

NewPartnerSearchCountries() {
  const term = (this.NewPartnerCountrySearchTerm || '').toLowerCase().trim();
  if (!this.PartnerCountries || this.PartnerCountries.length === 0) {
    this.NewPartnerFilteredCountries = [];
    return;
  }
  if (term.length === 0) {
    this.NewPartnerFilteredCountries = this.PartnerCountries.slice(0, 20);
  } else {
    this.NewPartnerFilteredCountries = this.PartnerCountries.filter((c: any) =>
      (c.Name && c.Name.toLowerCase().includes(term)) ||
      (c.ShortName && c.ShortName.toLowerCase().includes(term))
    ).slice(0, 20);
  }
}

NewPartnerSelectCountry(country: any) {
  this.NewPartnerSelectedCountry = country;
  this.NewPartnerCountrySearchTerm = country?.Name || '';
  this.NewPartnerFilteredCountries = [];
}

NewPartnerClearCountry() {
  this.NewPartnerSelectedCountry = null;
  this.NewPartnerCountrySearchTerm = '';
  this.NewPartnerFilteredCountries = []; // ✅ Don't show dropdown on clear, only on focus
}

AddNewPartner() {
  if (!this.NewPartnerName || this.NewPartnerName.trim().length === 0) {
    this.parent.openSnackBar('Partner name is required', 'OK', 'alert-info-snackbar');
    return;
  }
  if (!this.NewPartnerSelectedCountry) {
    this.parent.openSnackBar('Country is required', 'OK', 'alert-info-snackbar');
    return;
  }
  if (!this.NewPartnerAddressLine || this.NewPartnerAddressLine.trim().length === 0) {
    this.parent.openSnackBar('Address is required', 'OK', 'alert-info-snackbar');
    return;
  }

  const request = {
    Partner: {
      Name: this.NewPartnerName.trim(),
      VAT: this.NewPartnerVAT?.trim() || '',
      BillingAddress: {
        ID: 0,
        Country: this.NewPartnerSelectedCountry,
        StateProvince: '',
        City: '',
        AddressLine: this.NewPartnerAddressLine.trim(),
        ZipCode: '',
        IsSelected: true
      },
      ContactInfo: {
        Email: this.NewPartnerEmail?.trim() || ''  // ✅ Include email in ContactInfo
      },
      Company: this.parent.Company
    },
    User: this.parent.User,
    Language: this.parent.getLanguage()
  };

  this.NewPartnerSaving = true;
  this.service.AddNewPartner(request).subscribe((response: any) => {
    if (response["Status"] !== 1) {
      this.NewPartnerSaving = false;
      this.parent.openSnackBar(response["Message"] || 'Error adding partner', 'OK', 'alert-info-snackbar');
      return;
    }

    // ✅ Partner created - get the ID from response
    const newPartnerID = response["ID"];
    console.log('✅ Partner created with ID:', newPartnerID);

    if (!newPartnerID) {
      this.NewPartnerSaving = false;
      this.parent.openSnackBar('Partner created but no ID returned', 'OK', 'alert-info-snackbar');
      this.CloseAddNewPartnerPopup();
      return;
    }

    // ✅ Duplicate detected - partner already exists, skip shipping address generation, load existing partner from DB
    if (response["DuplicateDetected"]) {
      this.NewPartnerSaving = false;
      this.parent.openSnackBar(this.parent.translate.instant("Management.DuplicatePartnerDetected") + ": " + (response["Partner"]?.Name || ""), "OK", "alert-danger-snackbar");
      this.CloseAddNewPartnerPopup();
      // Use only ID and Name to trigger selectPartner which fetches full data from DB
      const existingPartnerID = response["ID"];
      const existingPartnerName = response["Partner"]?.Name || "";
      if (existingPartnerID) {
        this.selectPartner({ ID: existingPartnerID, Name: existingPartnerName });
      }
      return;
    }

    // ✅ Build minimal partner object for GenerateInitialShippingAddress
    const partnerForShipping = {
      ID: newPartnerID,
      Name: this.NewPartnerName.trim(),
      BillingAddress: {
        ID: 0,
        Country: this.NewPartnerSelectedCountry,
        StateProvince: '',
        City: '',
        AddressLine: this.NewPartnerAddressLine.trim(),
        ZipCode: '',
        IsSelected: true
      }
    };

    // ✅ Generate initial shipping address
    const generateAddressRequest = {
      User: this.parent.User,
      Language: this.parent.getLanguage(),
      Partner: partnerForShipping,
      AutoAdd:true,
    };

    this.service.GenerateInitialShippingAddress(generateAddressRequest).subscribe((genResponse: any) => {
      if (genResponse["Status"] !== 1) {
        this.NewPartnerSaving = false;
        this.parent.openSnackBar('Partner created but failed to generate shipping address: ' + (genResponse["Message"] || ''), 'OK', 'alert-info-snackbar');
        this.CloseAddNewPartnerPopup();
        return;
      }

      // ✅ Success - shipping address was generated and saved by backend
      const NewShippingAddress = genResponse["NewShippingAddress"];
      console.log('✅ Partner with shipping address:', NewShippingAddress);

      // ✅ If email was provided, create a PartnerAgent with the email
      const emailTrimmed = this.NewPartnerEmail?.trim();
      if (emailTrimmed && emailTrimmed.length > 0) {
        const newAgent = {
          ID: 0,
          Name: emailTrimmed,
          Email: emailTrimmed,
          Phone: null,
          PartnerID: newPartnerID,
          AllocatedShippingAddress: NewShippingAddress,
          Department: null,
          IsSelected: false,
          Disabled: false,
          LastModified: false
        };

        const agentRequest = {
          User: this.parent.User,
          Language: this.parent.getLanguage(),
          PartnerAgent: newAgent
        };

        this.service.AddUpdatePartnerAgent(agentRequest).subscribe((agentResponse: any) => {
          this.NewPartnerSaving = false;
          if (agentResponse["Status"] === 1) {
            console.log('✅ Partner agent created:', agentResponse["PartnerAgent"]);
            this.parent.openSnackBar('Partner, shipping address and agent added successfully', 'OK', 'light-info-snackbar');
          } else {
            console.warn('⚠️ Partner agent creation failed:', agentResponse["Message"]);
            this.parent.openSnackBar('Partner and shipping address added, but agent creation failed', 'OK', 'alert-info-snackbar');
          }
          this.CloseAddNewPartnerPopup();
        }, () => {
          this.NewPartnerSaving = false;
          this.parent.openSnackBar('Partner and shipping address added, but error creating agent', 'OK', 'alert-info-snackbar');
          this.CloseAddNewPartnerPopup();
        });
      } else {
        // ✅ No email provided, just close
        this.NewPartnerSaving = false;
        this.parent.openSnackBar('Partner and shipping address added successfully', 'OK', 'light-info-snackbar');
        this.CloseAddNewPartnerPopup();
      }
    }, () => {
      this.NewPartnerSaving = false;
      this.parent.openSnackBar('Partner created but error generating shipping address', 'OK', 'alert-info-snackbar');
      this.CloseAddNewPartnerPopup();
    });
  }, () => {
    this.NewPartnerSaving = false;
    this.parent.openSnackBar('Error adding partner', 'OK', 'alert-info-snackbar');
  });
}

}

